import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  TextField,
  Link,
  Tooltip,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListSubheader,
  OutlinedInput,
  Dialog,
  IconButton,
  Collapse,
  Checkbox,
  ListItemText,
  Avatar,
  InputAdornment,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ArrowUpward,
  ArrowDownward,
  Check as CheckIcon,
  Flag as FlagIcon,
  OutlinedFlag as OutlinedFlagIcon,
  PendingActions as PendingActionsIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  ArrowDropUp as ArrowDropUpIcon,
  ArrowDropDown as ArrowDropDownIcon,
  NotificationsActive as NotificationsActiveIcon,
  Notifications as NotificationsIcon,
  Save as SaveIcon,
  DeleteOutline as DeleteOutlineIcon,
  FilterAltOff as FilterAltOffIcon,
  LabelOutlined as LabelOutlinedIcon,
} from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import { alpha } from '@mui/material/styles';
import { usePrivacyMask } from '../contexts/PrivacyMaskContext';
import { usePaperMode } from '../contexts/PaperModeContext';
import { isMarketOpen } from '../utils/marketHours';
import { aggregateClosedPositions, getUnderlyingSymbol, classifyStrategy, groupTradesByStrategy, parseOptionSymbol, formatOptionLabel, reconcileTradesForDisplay, contractTagKey, parseSymbolFilter, getEntityTags, collectEntityTags, entityMatchesTagFilter, strategyTypeMatchesFilter, filterClosedPositionRows, getClosedTradeNetPL, isBankCashFlowRow } from '../utils/tradeUtils';
import { buildOCCSymbol, roundSPXTo5Cents } from '../utils/optionSymbol';
import { fetchNearestWeeklyExpiration } from '../utils/optionExpirations';
import { loadFilterPresets, persistFilterPresets, readLocalPresets } from '../utils/filterPresets';
import { positionUnrealizedPL } from '../utils/positionValuation';
import { generatePositionsReport } from '../utils/pdfReport';
import PayoffDiagram from '../components/PayoffDiagram';
import PositionPLChart from '../components/PositionPLChart';
import UnderlyingPriceChart from '../components/UnderlyingPriceChart';
import MonthPLCalendar from '../components/MonthPLCalendar';
import TradeDialog from '../components/TradeDialog';

// Strategy filter options
const STRATEGY_OPTIONS = [
  { value: 'all', label: 'All Strategies' },
  { value: 'Iron Condor', label: 'Iron Condor' },
  { value: 'Put Credit Spread', label: 'Put Credit Spread' },
  { value: 'Put Debit Spread', label: 'Put Debit Spread' },
  { value: 'Call Credit Spread', label: 'Call Credit Spread' },
  { value: 'Call Debit Spread', label: 'Call Debit Spread' },
  { value: 'Long Straddle', label: 'Long Straddle' },
  { value: 'Short Straddle', label: 'Short Straddle' },
  { value: 'Long Strangle', label: 'Long Strangle' },
  { value: 'Short Strangle', label: 'Short Strangle' },
  { value: 'Short Call', label: 'Short Call' },
  { value: 'Long Call', label: 'Long Call' },
  { value: 'Short Put', label: 'Short Put' },
  { value: 'Long Put', label: 'Long Put' },
  { value: 'Equity', label: 'Equity' },
  { value: 'Futures Option', label: 'Futures Option' },
  { value: 'Dividend', label: 'Dividend' }
];

// Order statuses treated as still-working/cancellable. Includes Schwab's
// states plus Robinhood's (live, confirmed, unconfirmed, partially_filled, ...).
const WORKING_ORDER_STATUSES = ['WORKING', 'PENDING_ACTIVATION', 'QUEUED', 'ACKNOWLEDGED', 'LIVE', 'PENDING', 'CONFIRMED', 'UNCONFIRMED', 'PARTIALLY_FILLED', 'ACCEPTED', 'RECEIVED', 'OPEN'];
const TERMINAL_ORDER_STATUSES = ['FILLED', 'CANCELLED', 'REJECTED', 'EXPIRED', 'REPLACED', 'VOIDED', 'FAILED'];

// --- Helper Functions (Moved outside component for stability) ---

// Compute days to earnings from an ISO date string (e.g., 2026-07-22)
const getDaysToEarnings = (earningsDate) => {
  if (!earningsDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Parse earnings date as local time by appending T00:00:00 to avoid UTC interpretation
  const date = new Date(earningsDate + 'T00:00:00');
  date.setHours(0, 0, 0, 0);
  const diffMs = date.getTime() - today.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return diffDays >= 0 ? diffDays : null;
};

// Small brokerage logo shown above account name
const BROKER_LOGOS = {
  robinhood: '/assets/robinhood_logo.png',
  'robinhood-api': '/assets/robinhood_logo.png',
  'robinhood-mcp': '/assets/robinhood_logo.png',
  schwab: '/assets/schwab_logo.jpg',
  tradier: '/assets/tradier-logo.jpg',
  'tradier-mcp': '/assets/tradier-logo.jpg',
  plaid: '/assets/plaid_logo.svg',
  pershing: '/assets/bnym_pershing.png',
};

const BrokerLogo = ({ apiType, size = 14 }) => {
  const type = String(apiType || '').toLowerCase();
  const logoSrc = BROKER_LOGOS[type];
  if (logoSrc) {
    return (
      <Box
        component="img"
        src={logoSrc}
        alt={type}
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          display: 'block',
        }}
      />
    );
  }
  const configs = {
    robinhood: { color: '#00C805', initial: 'R' },
    'robinhood-mcp': { color: '#00C805', initial: 'R' },
    schwab: { color: '#0076CF', initial: 'S' },
    tradier: { color: '#54565A', initial: 'T' },
    'tradier-mcp': { color: '#54565A', initial: 'T' },
    plaid: { color: '#111111', initial: 'P' },
    pershing: { color: '#1B3668', initial: 'P' },
  };
  const cfg = configs[type] || { color: '#888888', initial: (type[0] || '?').toUpperCase() };
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: `${size * 0.55}px`,
        fontWeight: 'bold',
        bgcolor: cfg.color,
        color: '#fff',
      }}
    >
      {cfg.initial}
    </Avatar>
  );
};

const AccountNameWithLogo = ({ accountName, accountId, accounts, size = 40 }) => {
  const account = accounts?.find(a => String(a.id) === String(accountId));
  let apiType = account?.apiType || account?.api_type;
  const name = accountName || account?.name || account?.accountName || account?.account_id || '-';
  // Detect Pershing accounts (manual type with "Pershing" in the name) for logo
  if ((!apiType || apiType === 'manual') && /pershing/i.test(name)) {
    apiType = 'pershing';
  }
  return (
    <Tooltip title={name} arrow>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <BrokerLogo apiType={apiType} size={size} />
        <Typography variant="caption" noWrap sx={{ maxWidth: 120 }}>{name}</Typography>
      </Box>
    </Tooltip>
  );
};

// Build a normalized key for matching a position to its opening trade(s).
// Options key on underlying|expiration|type|strike; stocks on the ticker.
const positionContractKey = (symbol) => {
  const raw = String(symbol || '').trim();
  const info = parseOptionSymbol(raw);
  if (info && info.isOption && info.expiration && info.type && info.strike != null) {
    return `${String(info.underlying).toUpperCase()}|${info.expiration}|${info.type}|${info.strike}`;
  }
  const normalized = raw.replace(/_/g, ' ').trim().toUpperCase();
  // Futures-option symbols ("/MS4EV26_P7600:XCME") don't parse as options —
  // keep the full contract so calls/puts and different strikes never merge.
  if (raw.startsWith('/')) return `FUTOPT|${normalized}`;
  const tokens = normalized.split(/\s+/).filter(Boolean);
  return `${tokens[0] || normalized}|STOCK`;
};

// Format a date string (e.g. 'YYYY-MM-DD' or ISO) as MM/DD/YY without
// timezone shifting that would otherwise roll a date back a day.
const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const s = String(dateStr);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1].slice(2)}`;
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
};

// Open Date column: positions opened today show the fill time instead of the
// date (the day is implied). tsStr is the earliest opening fill timestamp.
const formatOpenDate = (dateStr, tsStr) => {
  // tsStr without a time component would render a meaningless midnight
  const ts = (tsStr && /[T ]\d{1,2}:\d{2}/.test(String(tsStr))) ? new Date(tsStr) : null;
  if (ts && !isNaN(ts.getTime())) {
    const now = new Date();
    if (ts.getFullYear() === now.getFullYear() && ts.getMonth() === now.getMonth() && ts.getDate() === now.getDate()) {
      return ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
  }
  return formatShortDate(dateStr || tsStr);
};

// Distinct opening-order ids for a leg or position. Order-split legs and
// manual-group carved legs carry _orderIds; merged legs keep per-part ids via
// _parts; everything else falls back to the position's surviving order lots.
const legOrderIds = (leg) => {
  const ids = new Set();
  (leg._parts || [leg]).forEach(p => {
    if (Array.isArray(p._orderIds)) { p._orderIds.forEach(id => ids.add(String(id))); return; }
    (p.orderLots || []).forEach(l => l.orderId != null && ids.add(String(l.orderId)));
  });
  return [...ids];
};

// Union of tags across an entity and its nested trades/legs — a grouped
// position's tags are the union of everything underneath it.
// API endpoints that own this entity's tags: paper positions update via
// paper-trading routes; db-reconciled positions and closed strategies via
// their underlying trades; live broker positions via account+symbol matching
// of their open trades. Returns { url, extra } — extra merges into the body.
const getTagTargets = (entity) => {
  const targets = [];
  const seen = new Set();
  const push = (url, extra) => {
    const key = url + JSON.stringify(extra || '');
    if (!seen.has(key)) { seen.add(key); targets.push({ url, extra }); }
  };
  const collect = (e) => {
    if (!e) return;
    if (e.isPaperTrade && e.id) {
      push(`/api/paper-trading/positions/${e.id}/tags`);
      return;
    }
    if (Array.isArray(e.legs) && e.legs.length > 0) {
      e.legs.forEach(collect);
      return;
    }
    const tradeIds = (Array.isArray(e.trades) ? e.trades : []).map(t => t.id).filter(Boolean);
    if (tradeIds.length > 0) {
      tradeIds.forEach(id => push(`/api/trades/${id}/tags`));
      return;
    }
    // Only bare positions (no trade records of their own) fall back to
    // account+symbol matching — a closed strategy with id-less trades must
    // not tag unrelated open trades for the same symbol.
    const accountId = e.accountId || e.account_id;
    const symbol = e.originalSymbol || e.original_symbol || e.symbol;
    const hasTradeRecords = Array.isArray(e.trades) && e.trades.length > 0;
    if (!hasTradeRecords && accountId && symbol) {
      push('/api/trades/tag-position', { accountId, symbol });
    }
  };
  collect(entity);
  return targets;
};

const StockSummary = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { paperMode } = usePaperMode();
  // Persisted settings are stored per trading mode — live uses the bare key,
  // paper prefixes it — so each mode remembers its own filters.
  const modeKey = (name) => `${paperMode ? 'paper_' : ''}${name}`;
  const modeGet = (name, fallbackName) =>
    localStorage.getItem(modeKey(name)) ?? localStorage.getItem(name) ?? (fallbackName ? localStorage.getItem(fallbackName) : null);
  const isDark = theme.palette.mode === 'dark';
  const rowAltBg = isDark ? '#1e1e1e' : '#f5f5f5';
  const rowSubBg = isDark ? '#252525' : '#fafafa';
  const expHeaderBg = isDark ? alpha(theme.palette.primary.main, 0.12) : '#e3f2fd';
  const optLegBg = isDark ? alpha('#ff9800', 0.08) : '#fff3e0';
  const favorableOptionBg = isDark ? alpha('#4caf50', 0.15) : alpha('#4caf50', 0.10);
  const unfavorableOptionBg = isDark ? alpha('#f44336', 0.15) : alpha('#f44336', 0.08);
  const [loading, setLoading] = useState(true);
  const [symbolData, setSymbolData] = useState([]);
  // Raw trades (open + closed) used only for tag lookup — symbolData rows
  // exclude still-open opening trades, so they can never carry their tags.
  const [allTradesForTags, setAllTradesForTags] = useState([]);
  const [openPositions, setOpenPositions] = useState([]);
  const openPositionsRef = useRef([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  // Pending (recently-closed, not-yet-settled) positions from broker orders API
  const [pendingPositions, setPendingPositions] = useState([]);
  // Working (unfilled) closing orders from broker API — used to flag open positions
  const [workingCloseOrders, setWorkingCloseOrders] = useState([]);
  // Recent orders (working + filled today) for "New Trades" section
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentOrdersLoading, setRecentOrdersLoading] = useState(false);
  const [expandedNewTrades, setExpandedNewTrades] = useState(new Set());
  const [newTradeQuotes, setNewTradeQuotes] = useState({});
  const [timePeriod, setTimePeriod] = useState(() => modeGet('global_time_period') || 'month');
  const [customStartDate, setCustomStartDate] = useState(() => modeGet('global_custom_start') || '');
  const [customEndDate, setCustomEndDate] = useState(() => modeGet('global_custom_end') || '');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(() => {
    try {
      const saved = modeGet('global_selected_account');
      return saved ? JSON.parse(saved) : ['all'];
    } catch {
      return ['all'];
    }
  });
  const [draftAccount, setDraftAccount] = useState(() => {
    try {
      const saved = modeGet('global_selected_account');
      return saved ? JSON.parse(saved) : ['all'];
    } catch {
      return ['all'];
    }
  });
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const accountsRef = useRef([]);
  const lastFetchTimesRef = useRef({});
  const [closedGroupBy, setClosedGroupBy] = useState(() => modeGet('stocksummary_closed_group_by') || 'month'); // columns granularity: 'week' | 'month' | 'quarter' | 'year'
  const [closedPeriodColumns, setClosedPeriodColumns] = useState([]); // dynamic period columns for pivot
  const [closedTotalsRow, setClosedTotalsRow] = useState(null); // top totals row across periods
  const [groupByMode, setGroupByMode] = useState(() => modeGet('stocksummary_group_by_mode') || 'symbol'); // 'symbol' | 'week' | 'calendar'
  const [calendarViewMode, setCalendarViewMode] = useState(() => modeGet('stocksummary_calendar_view') || 'calendar'); // 'calendar' | 'detail' — sub-view for Month mode
  const [expandedWeeks, setExpandedWeeks] = useState(new Set()); // track expanded periods (week or month keys)
  const [expandedOpenClose, setExpandedOpenClose] = useState(new Set()); // track expanded open/close groups
  const [expandedNested, setExpandedNested] = useState(new Set()); // track expanded nested date groups

  // Helper to get week ending date (Friday) from date
  const getIsoWeek = (dateStr) => {
    // Normalize to YYYY-MM-DD (handles both date-only and timestamp formats)
    const dateOnly = String(dateStr).slice(0, 10);
    // Parse as local time to avoid UTC offset shifting the day
    const date = new Date(dateOnly + 'T00:00:00');
    const day = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 5 = Friday, 6 = Saturday
    const daysUntilFriday = (5 - day + 7) % 7; // Days until next Friday (0 if already Friday)
    const weekEnding = new Date(date);
    weekEnding.setDate(date.getDate() + daysUntilFriday);
    // Format as YYYY-MM-DD using local time (not toISOString which uses UTC)
    const yr = weekEnding.getFullYear();
    const mo = String(weekEnding.getMonth() + 1).padStart(2, '0');
    const dy = String(weekEnding.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${dy}`;
  };

  // Toggle week expansion
  const toggleWeek = (weekKey) => {
    setExpandedWeeks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(weekKey)) {
        newSet.delete(weekKey);
      } else {
        newSet.add(weekKey);
      }
      return newSet;
    });
  };

  // Toggle open/close group expansion
  const toggleOpenClose = (groupKey) => {
    setExpandedOpenClose(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  // Toggle nested group expansion
  const toggleNested = (groupKey) => {
    setExpandedNested(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  // Group trades by closedDate, then openDate — returns nested structure
  // Returns: [{ closeDate, totalPL, openGroups: [{ openDate, totalPL, holdDays, trades: [] }] }]
  const groupTradesByClosedOpenDate = (trades) => {
    const closeMap = new Map();

    trades.forEach(trade => {
      // Use openDate (set by reconciliation on closing legs) if available,
      // otherwise fall back to tradeDate (for opening legs and aggregate rows).
      const openDate = (trade.openDate || trade.tradeDate || '').slice(0, 10);
      // Use closingDate if available, otherwise for closing legs tradeDate IS the close date
      const tt = String(trade.tradeType || trade.trade_type || '').toUpperCase();
      const ttNorm = tt.replace(/_/g, ' ');
      const pe = String(trade.positionEffect || trade.position_effect || '').toUpperCase().replace(/_/g, ' ');
      const isClosingLeg = tt === 'STC' || tt === 'BTC' || ttNorm === 'BUY TO CLOSE' || ttNorm === 'SELL TO CLOSE' ||
        pe.includes('BUY TO CLOSE') || pe.includes('SELL TO CLOSE');
      const closeDate = (trade.closingDate || (isClosingLeg ? trade.tradeDate : '') || '').slice(0, 10);
      if (!closeDate || !openDate) return;

      if (!closeMap.has(closeDate)) {
        closeMap.set(closeDate, { closeDate, totalPL: 0, openMap: new Map() });
      }
      const closeGroup = closeMap.get(closeDate);
      const tradePL = getClosedTradeNetPL(trade);
      closeGroup.totalPL += tradePL;

      if (!closeGroup.openMap.has(openDate)) {
        const openD = openDate ? new Date(openDate + 'T00:00:00') : null;
        const closeD = new Date(closeDate + 'T00:00:00');
        const holdDays = openD ? Math.round((closeD - openD) / 86400000) : 0;
        closeGroup.openMap.set(openDate, { openDate, totalPL: 0, holdDays, trades: [] });
      }
      const openGroup = closeGroup.openMap.get(openDate);
      openGroup.totalPL += tradePL;
      openGroup.trades.push(trade);
    });

    // Convert to sorted array — closeDate desc, then openDate desc within each
    return Array.from(closeMap.values())
      .sort((a, b) => b.closeDate.localeCompare(a.closeDate))
      .map(cg => ({
        closeDate: cg.closeDate,
        totalPL: cg.totalPL,
        openGroups: Array.from(cg.openMap.values())
          .sort((a, b) => (b.openDate || '').localeCompare(a.openDate || '')),
      }));
  };

  // Group closed positions by (tradeDate, closingDate) — open/close date pairs
  const getOpenCloseGroupedData = () => {
    const ocGroups = new Map();

    filteredSymbolData.forEach(row => {
      const trades = row.trades || [];
      trades.forEach(trade => {
        // Use openDate (set by reconciliation on closing legs) if available
        const openDate = (trade.openDate || trade.tradeDate || '').slice(0, 10);
        const tt = String(trade.tradeType || trade.trade_type || '').toUpperCase();
        const ttNorm = tt.replace(/_/g, ' ');
        const pe = String(trade.positionEffect || trade.position_effect || '').toUpperCase().replace(/_/g, ' ');
        const isClosingLeg = tt === 'STC' || tt === 'BTC' || ttNorm === 'BUY TO CLOSE' || ttNorm === 'SELL TO CLOSE' ||
          pe.includes('BUY TO CLOSE') || pe.includes('SELL TO CLOSE');
        const closeDate = (trade.closingDate || (isClosingLeg ? trade.tradeDate : '') || '').slice(0, 10);
        if (!openDate || !closeDate) return;

        // Only include option trades
        const raw = trade.originalSymbol || trade.symbol || '';
        const info = parseOptionSymbol(raw);
        if (!info.isOption) return;

        const groupKey = `${openDate}|${closeDate}`;
        if (!ocGroups.has(groupKey)) {
          const openD = new Date(openDate + 'T00:00:00');
          const closeD = new Date(closeDate + 'T00:00:00');
          const holdDays = Math.round((closeD - openD) / 86400000);
          ocGroups.set(groupKey, {
            groupKey,
            openDate,
            closeDate,
            holdDays,
            totalPL: 0,
            trades: [],
          });
        }

        const group = ocGroups.get(groupKey);
        const tradePL = getClosedTradeNetPL(trade);
        group.totalPL += tradePL;
        group.trades.push(trade);
      });
    });

    // Sort by open date desc, then close date desc
    return Array.from(ocGroups.values()).sort((a, b) => {
      if (a.openDate !== b.openDate) return b.openDate.localeCompare(a.openDate);
      return b.closeDate.localeCompare(a.closeDate);
    });
  };

  // Group closed positions by a period key — ISO week-ending date ('week' mode)
  // or YYYY-MM month ('calendar' mode's detail view). Rows carry periodKey /
  // periodLabel so the period table renders either granularity.
  const getPeriodGroupedData = (keyFn, labelFn) => {
    const periodGroups = new Map();

    filteredSymbolData.forEach(row => {
      // Get all trades for this symbol
      const trades = row.trades || [];
      trades.forEach(trade => {
        const tradeDate = trade.closingDate || trade.tradeDate;
        if (!tradeDate) return;

        const periodKey = keyFn(tradeDate);
        if (!periodGroups.has(periodKey)) {
          periodGroups.set(periodKey, {
            periodKey,
            periodLabel: labelFn(periodKey),
            totalPL: 0,
            totalDividendOnly: 0,
            trades: []
          });
        }

        const group = periodGroups.get(periodKey);
        const tradeType = (trade.tradeType || trade.trade_type || '').toLowerCase();
        const isDividend = tradeType === 'dividend' || tradeType === 'div';
        const tradeDiv = parseFloat(trade.dividend || trade.dividend_amount || 0);
        
        // For dividend trades, use dividend amount as P/L
        let tradePL = isDividend ? tradeDiv : getClosedTradeNetPL(trade);

        group.totalPL += tradePL;
        group.totalDividendOnly += tradeDiv;
        group.trades.push(trade);
      });
    });

    // Calculate cumulative totals from bottom to top
    let periodArray = Array.from(periodGroups.values()).sort((a, b) => b.periodKey.localeCompare(a.periodKey));

    // When a strategy filter is active, remove periods that contain no matching strategy groups
    if (strategyFilter && strategyFilter !== 'all') {
      periodArray = periodArray.filter(period => {
        const strategies = groupTradesByStrategy(period.trades);
        return strategies.some(s => strategyMatchesFilter(s.strategyType));
      });
    }

    let cumulativeTotal = 0;
    let cumulativeDividend = 0;
    for (let i = periodArray.length - 1; i >= 0; i--) {
      const closedPL = (periodArray[i].totalPL || 0) - (periodArray[i].totalDividendOnly || 0);
      cumulativeTotal += closedPL;
      cumulativeDividend += (periodArray[i].totalDividendOnly || 0);
      periodArray[i].cumulativeTotal = cumulativeTotal;
      periodArray[i].cumulativeDividend = cumulativeDividend;
    }

    return periodArray;
  };

  const getWeeklyGroupedData = () => getPeriodGroupedData(
    getIsoWeek,
    (key) => new Date(key.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  );

  const getMonthlyGroupedData = () => getPeriodGroupedData(
    (dateStr) => String(dateStr).slice(0, 7),
    (key) => new Date(key + '-01T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  );

  // Restore the incoming mode's saved settings on live <-> paper toggle.
  // Must run before the persist effects below so it reads the new mode's keys
  // before they are overwritten by same-commit persist writes.
  const prevPaperModeRef = useRef(paperMode);
  useEffect(() => {
    if (prevPaperModeRef.current === paperMode) return;
    prevPaperModeRef.current = paperMode;
    try {
      const json = (k, fb) => { const v = modeGet(k); return v != null ? JSON.parse(v) : fb; };
      setTimePeriod(modeGet('global_time_period') || 'month');
      setCustomStartDate(modeGet('global_custom_start') || '');
      setCustomEndDate(modeGet('global_custom_end') || '');
      const acct = json('global_selected_account', ['all']);
      setSelectedAccount(acct);
      setDraftAccount(acct);
      setPositionFilter(json('global_position_filter', ['all']));
      setStrategyFilter(modeGet('global_strategy_filter') || 'all');
      setSymbolFilter(modeGet('global_symbol_filter') || '');
      setOpenDateStart(modeGet('global_open_date_start') || '');
      setOpenDateEnd(modeGet('global_open_date_end') || '');
      setShowFilters(modeGet('stocksummary_show_filters') !== 'false');
      setTagFilter(json('global_tag_filter', []));
      setTagFilterExclude(json('global_tag_filter_exclude', []));
      setExpandedGroups(new Set(json('stocksummary_expanded_groups', [])));
      setClosedGroupBy(modeGet('stocksummary_closed_group_by') || 'month');
      setGroupByMode(modeGet('stocksummary_group_by_mode') || 'symbol');
    } catch (_) {}
  }, [paperMode]);

  // Keep the account selection valid for the current mode — paper mode
  // collapses to 'all' (the Paper Trading pseudo account), live mode drops
  // non-investment (bank/credit) account ids.
  useEffect(() => {
    if (!paperMode && !accountsLoaded) return; // don't wipe saved selection before accounts load
    const valid = new Set(['all', ...displayAccounts.map(a => String(a.id))]);
    const sanitize = (list) => {
      const next = (Array.isArray(list) ? list : ['all']).filter(id => valid.has(String(id)));
      return next.length ? next : ['all'];
    };
    setSelectedAccount(prev => { const n = sanitize(prev); return n.length === prev.length && n.every((v, i) => String(prev[i]) === String(v)) ? prev : n; });
    setDraftAccount(prev => { const n = sanitize(prev); return n.length === prev.length && n.every((v, i) => String(prev[i]) === String(v)) ? prev : n; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperMode, accountsLoaded]);

  // Persist time period selection
  useEffect(() => {
    try { localStorage.setItem(modeKey('global_time_period'), timePeriod); } catch { }
  }, [timePeriod, paperMode]);

  // Persist custom date range
  useEffect(() => {
    try {
      localStorage.setItem(modeKey('global_custom_start'), customStartDate || '');
      localStorage.setItem(modeKey('global_custom_end'), customEndDate || '');
    } catch { }
  }, [customStartDate, customEndDate, paperMode]);

  // Persist selected account
  useEffect(() => {
    try { localStorage.setItem(modeKey('global_selected_account'), JSON.stringify(selectedAccount)); } catch { }
  }, [selectedAccount, paperMode]);

  // Cache helper functions
  const getCacheKey = () => {
    // Cache key is now account-independent - we cache all data and filter in UI
    return `stocksummary_v2_${paperMode ? 'paper' : 'live'}_${timePeriod}_${closedGroupBy}_${customStartDate}_${customEndDate}`;
  };

  const getOpenPositionsCacheKey = () => {
    // Cache key is now account-independent - we cache all data and filter in UI.
    // NOTE: bump the version suffix whenever the position-sourcing logic changes so
    // stale caches (e.g. phantom DB positions for API accounts) are invalidated.
    // Paper and live positions are cached separately so toggling paperMode never
    // shows the other mode's stale cached positions.
    return `openpositions_v7_${paperMode ? 'paper' : 'live'}`;
  };

  const getCachedData = () => {
    try {
      const key = getCacheKey();
      const cached = localStorage.getItem(key);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const cacheAge = Date.now() - timestamp;
        // Use cache if less than 5 minutes old
        if (cacheAge < 5 * 60 * 1000) {
          return data;
        }
      }
    } catch (e) {
      console.error('Error reading cache:', e);
    }
    return null;
  };

  const setCachedData = (data) => {
    try {
      const key = getCacheKey();
      localStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('Error writing cache:', e);
    }
  };

  const getCachedOpenPositions = () => {
    try {
      const key = getOpenPositionsCacheKey();
      const cached = localStorage.getItem(key);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const cacheAge = Date.now() - timestamp;
        // Use cache if less than 24 hours old
        if (cacheAge < 24 * 60 * 60 * 1000) {
          return data;
        } else {
          // Cache expired
        }
      }
    } catch (e) {
      console.error('Error reading open positions cache:', e);
    }
    return null;
  };

  const setCachedOpenPositions = (data) => {
    try {
      const key = getOpenPositionsCacheKey();
      localStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('Error writing open positions cache:', e);
    }
  };

  const getRangeForPeriod = (p) => {
    const today = new Date();
    const endIso = today.toISOString().split('T')[0];
    const lower = (days) => {
      const d = new Date(today);
      d.setDate(today.getDate() - days);
      return d.toISOString().split('T')[0];
    };
    switch (p) {
      case '30d': return { startDate: lower(30), endDate: endIso };
      case '60d': return { startDate: lower(60), endDate: endIso };
      case '90d': return { startDate: lower(90), endDate: endIso };
      case '180d': return { startDate: lower(180), endDate: endIso };
      case 'current_month': {
        const y = today.getFullYear();
        const m = today.getMonth() + 1;
        const startIso = `${y}-${String(m).padStart(2, '0')}-01`;
        return { startDate: startIso, endDate: endIso };
      }
      case 'previous_month': {
        const y = today.getFullYear();
        const m = today.getMonth();
        const prevY = m === 0 ? y - 1 : y;
        const prevM = m === 0 ? 12 : m;
        const startIso = `${prevY}-${String(prevM).padStart(2, '0')}-01`;
        const lastDay = new Date(prevY, prevM, 0).getDate();
        const end = `${prevY}-${String(prevM).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        return { startDate: startIso, endDate: end };
      }
      case 'ytd': {
        const y = today.getFullYear();
        const startIso = `${y}-01-01`;
        return { startDate: startIso, endDate: endIso };
      }
      case 'last_year': {
        const y = today.getFullYear() - 1;
        const startIso = `${y}-01-01`;
        const end = `${y}-12-31`;
        return { startDate: startIso, endDate: end };
      }
      case 'custom':
        return (customStartDate && customEndDate)
          ? { startDate: customStartDate, endDate: customEndDate }
          : null;
      default:
        return null;
    }
  };

  // Spread detection utility functions
  // parseOptionSymbol and getTradeStatus removed (using module-scope definitions)


  const detectSpreadType = (positions) => {
    if (positions.length < 2) return null;

    const calls = positions.filter(p => p.optionInfo?.type === 'C').sort((a, b) => a.optionInfo.strike - b.optionInfo.strike);
    const puts = positions.filter(p => p.optionInfo?.type === 'P').sort((a, b) => a.optionInfo.strike - b.optionInfo.strike);

    // Vertical Call Spread
    if (calls.length === 2 && puts.length === 0) {
      const [lower, higher] = calls;
      if (lower.quantity > 0 && higher.quantity < 0) {
        return { type: 'Call Debit Spread', legs: [lower, higher] };
      } else if (lower.quantity < 0 && higher.quantity > 0) {
        return { type: 'Call Credit Spread', legs: [lower, higher] };
      }
    }

    // Vertical Put Spread
    if (puts.length === 2 && calls.length === 0) {
      const [lower, higher] = puts;
      if (lower.quantity < 0 && higher.quantity > 0) {
        return { type: 'Put Debit Spread', legs: [lower, higher] };
      } else if (lower.quantity > 0 && higher.quantity < 0) {
        return { type: 'Put Credit Spread', legs: [lower, higher] };
      }
    }

    // Iron Condor
    if (calls.length === 2 && puts.length === 2) {
      return { type: 'Iron Condor', legs: [...puts, ...calls] };
    }

    // 3-Leg Combo: 1 call + 2 puts
    if (calls.length === 1 && puts.length === 2) {
      const call = calls[0];
      const [lowerPut, higherPut] = puts;
      // Put credit spread: buy lower strike put, sell higher strike put
      // Put debit spread: sell lower strike put, buy higher strike put
      if (call.quantity < 0 && lowerPut.quantity > 0 && higherPut.quantity < 0) {
        return { type: 'Short Call + Put Credit Spread', legs: [call, lowerPut, higherPut] };
      }
      if (call.quantity < 0 && lowerPut.quantity < 0 && higherPut.quantity > 0) {
        return { type: 'Short Call + Put Debit Spread', legs: [call, lowerPut, higherPut] };
      }
      if (call.quantity > 0 && lowerPut.quantity > 0 && higherPut.quantity < 0) {
        return { type: 'Long Call + Put Credit Spread', legs: [call, lowerPut, higherPut] };
      }
      if (call.quantity > 0 && lowerPut.quantity < 0 && higherPut.quantity > 0) {
        return { type: 'Long Call + Put Debit Spread', legs: [call, lowerPut, higherPut] };
      }
      return { type: '3-Leg Combo', legs: [call, ...puts] };
    }

    // 3-Leg Combo: 2 calls + 1 put
    if (calls.length === 2 && puts.length === 1) {
      const [lowerCall, higherCall] = calls;
      const put = puts[0];
      // Call credit spread: buy lower strike call, sell higher strike call
      // Call debit spread: sell lower strike call, buy higher strike call
      if (put.quantity < 0 && lowerCall.quantity > 0 && higherCall.quantity < 0) {
        return { type: 'Short Put + Call Credit Spread', legs: [lowerCall, higherCall, put] };
      }
      if (put.quantity < 0 && lowerCall.quantity < 0 && higherCall.quantity > 0) {
        return { type: 'Short Put + Call Debit Spread', legs: [lowerCall, higherCall, put] };
      }
      if (put.quantity > 0 && lowerCall.quantity > 0 && higherCall.quantity < 0) {
        return { type: 'Long Put + Call Credit Spread', legs: [lowerCall, higherCall, put] };
      }
      if (put.quantity > 0 && lowerCall.quantity < 0 && higherCall.quantity > 0) {
        return { type: 'Long Put + Call Debit Spread', legs: [lowerCall, higherCall, put] };
      }
      return { type: '3-Leg Combo', legs: [...calls, put] };
    }

    // Straddle/Strangle
    if (calls.length === 1 && puts.length === 1) {
      const call = calls[0];
      const put = puts[0];
      if (call.optionInfo.strike === put.optionInfo.strike) {
        return { type: call.quantity > 0 ? 'Long Straddle' : 'Short Straddle', legs: [call, put] };
      } else {
        return { type: call.quantity > 0 ? 'Long Strangle' : 'Short Strangle', legs: [call, put] };
      }
    }

    // No recognized spread pattern — return null so positions stay individual
    return null;
  };

  // Render a strategy/spread type label, wrapping onto two lines when the
  // full name is longer than the reference string 'Short Call'.
  const renderStrategyLabel = (type) => {
    if (!type) return type;
    if (type.length <= 'Short Call'.length) return <span style={{ fontWeight: 'bold', fontSize: isMobile ? '0.7rem' : undefined }}>{type}</span>;
    const words = type.split(' ');
    const splitAt = Math.ceil(words.length / 2);
    const line1 = words.slice(0, splitAt).join(' ');
    const line2 = words.slice(splitAt).join(' ');
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1, py: 0.4, fontWeight: 'bold', fontSize: isMobile ? '0.7rem' : undefined, whiteSpace: 'nowrap' }}>
        <span>{line1}</span>
        <span>{line2}</span>
      </Box>
    );
  };

  // Detect strategy type from Schwab order legs (orderLegCollection format)
  const classifyOrderLegs = (orderLegs) => {
    if (!orderLegs || orderLegs.length === 0) return 'Unknown';
    // Roll orders mix closing legs (old position) with opening legs (new
    // position) in a single order — classifying all legs together misfires
    // (e.g. BTC old put + STO new put looks like a debit spread). The opening
    // side is what you now hold, so label the roll by it.
    const instrU = (l) => String(l.instruction || '').toUpperCase();
    const openLegs = orderLegs.filter(l => /(_TO_OPEN|SELL_SHORT)$/.test(instrU(l)));
    const closeLegs = orderLegs.filter(l => /(_TO_CLOSE|_TO_COVER)$/.test(instrU(l)));
    if (openLegs.length && closeLegs.length) {
      return `Roll ${detectStrategyFromOrderLegs(openLegs)}`;
    }
    if (orderLegs.length === 1) {
      const leg = orderLegs[0];
      const instr = leg.instrument || {};
      const sym = instr.symbol || '';
      const info = parseOptionSymbol(sym);
      if (info.isOption) {
        const isBuy = leg.instruction === 'BUY_TO_OPEN' || leg.instruction === 'BUY_TO_CLOSE';
        return `${isBuy ? 'Long' : 'Short'} ${info.type === 'C' ? 'Call' : 'Put'}`;
      }
      return 'Stock';
    }

    // Parse all legs
    const parsed = orderLegs.map(l => {
      const info = parseOptionSymbol(l.instrument?.symbol || '');
      const isBuy = l.instruction === 'BUY_TO_OPEN' || l.instruction === 'BUY_TO_CLOSE';
      return { ...info, isBuy, qty: l.quantity, instruction: l.instruction };
    });

    const calls = parsed.filter(p => p.isOption && p.type === 'C').sort((a, b) => a.strike - b.strike);
    const puts = parsed.filter(p => p.isOption && p.type === 'P').sort((a, b) => a.strike - b.strike);

    // Iron Condor: 2 calls + 2 puts
    if (calls.length === 2 && puts.length === 2) return 'Iron Condor';

    // Vertical Call Spread
    if (calls.length === 2 && puts.length === 0) {
      const [lower, higher] = calls;
      if (lower.isBuy && !higher.isBuy) return 'Call Debit Spread';
      if (!lower.isBuy && higher.isBuy) return 'Call Credit Spread';
      return 'Call Spread';
    }

    // Vertical Put Spread
    if (puts.length === 2 && calls.length === 0) {
      const [lower, higher] = puts;
      if (!lower.isBuy && higher.isBuy) return 'Put Debit Spread';
      if (lower.isBuy && !higher.isBuy) return 'Put Credit Spread';
      return 'Put Spread';
    }

    // Straddle/Strangle
    if (calls.length === 1 && puts.length === 1) {
      const call = calls[0];
      const put = puts[0];
      if (call.strike === put.strike) {
        return call.isBuy ? 'Long Straddle' : 'Short Straddle';
      }
      return call.isBuy ? 'Long Strangle' : 'Short Strangle';
    }

    // 3-Leg Combo: 1 call + 2 puts
    if (calls.length === 1 && puts.length === 2) {
      const call = calls[0];
      const [lowerPut, higherPut] = puts;
      // Put credit: buy lower, sell higher | Put debit: sell lower, buy higher
      if (!call.isBuy && lowerPut.isBuy && !higherPut.isBuy) return 'Short Call + Put Credit Spread';
      if (!call.isBuy && !lowerPut.isBuy && higherPut.isBuy) return 'Short Call + Put Debit Spread';
      if (call.isBuy && lowerPut.isBuy && !higherPut.isBuy) return 'Long Call + Put Credit Spread';
      if (call.isBuy && !lowerPut.isBuy && higherPut.isBuy) return 'Long Call + Put Debit Spread';
      return '3-Leg Combo';
    }

    // 3-Leg Combo: 2 calls + 1 put
    if (calls.length === 2 && puts.length === 1) {
      const [lowerCall, higherCall] = calls;
      const put = puts[0];
      // Call credit: buy lower, sell higher | Call debit: sell lower, buy higher
      if (!put.isBuy && lowerCall.isBuy && !higherCall.isBuy) return 'Short Put + Call Credit Spread';
      if (!put.isBuy && !lowerCall.isBuy && higherCall.isBuy) return 'Short Put + Call Debit Spread';
      if (put.isBuy && lowerCall.isBuy && !higherCall.isBuy) return 'Long Put + Call Credit Spread';
      if (put.isBuy && !lowerCall.isBuy && higherCall.isBuy) return 'Long Put + Call Debit Spread';
      return '3-Leg Combo';
    }

    return 'Multi-Leg';
  };

  // Futures-option orders ("/…_P|C<strike>:<venue>" legs) keep their structure
  // label but get a Futures marker so they're filterable — including rolls
  // ("Roll Put Credit Spread" → "Roll Futures Put Credit Spread").
  const detectStrategyFromOrderLegs = (orderLegs) => {
    const label = classifyOrderLegs(orderLegs);
    const hasFutures = (orderLegs || []).some(l =>
      parseOptionSymbol(l.instrument?.symbol || '').isFuturesOption);
    if (!hasFutures || label === 'Stock' || label === 'Unknown') return label;
    return label.startsWith('Roll ') ? `Roll Futures ${label.slice(5)}` : `Futures ${label}`;
  };

  // Format spread display label
  const formatSpreadLabel = (group) => {
    if (!group || !group.legs || group.legs.length < 2) return group.spreadType;

    const { underlying, expiration, spreadType, legs } = group;

    // Format expiration from YYMMDD to M/D/YY
    let formattedExpiration = expiration;
    if (expiration && expiration.length === 6) {
      const year = expiration.substring(0, 2);
      const month = parseInt(expiration.substring(2, 4), 10);
      const day = parseInt(expiration.substring(4, 6), 10);
      formattedExpiration = `${month}/${day}/${year}`;
    }

    // Get strike prices from legs
    const strikes = legs
      .map(leg => leg.optionInfo?.strike)
      .filter(strike => strike !== undefined)
      .sort((a, b) => a - b);

    if (strikes.length >= 2) {
      const strikeStr = strikes.join('/');

      // Determine spread abbreviation
      let spreadAbbr = '';
      if (spreadType.includes('Put Credit')) {
        spreadAbbr = 'PCS';
      } else if (spreadType.includes('Put Debit')) {
        spreadAbbr = 'PDS';
      } else if (spreadType.includes('Call Credit')) {
        spreadAbbr = 'CCS';
      } else if (spreadType.includes('Call Debit')) {
        spreadAbbr = 'CDS';
      } else if (spreadType.includes('Iron Condor')) {
        spreadAbbr = 'IC';
      } else if (spreadType.includes('Straddle')) {
        spreadAbbr = 'Straddle';
      } else if (spreadType.includes('Strangle')) {
        spreadAbbr = 'Strangle';
      } else if (spreadType.includes('3-Leg')) {
        spreadAbbr = '3-Leg';
      } else if (spreadType === 'Multi-Leg Position') {
        spreadAbbr = 'Multi-Leg';
      } else {
        spreadAbbr = 'Spread';
      }

      return `${formattedExpiration} ${strikeStr} ${spreadAbbr}`;
    }

    return spreadType;
  };

  // Format individual option display label
  // formatOptionLabel removed (using module-scope definition)


  const calculateSpreadMetrics = (spread) => {
    if (!spread || !spread.legs) return {};

    let netDebit = 0;
    let totalMarketValue = 0;
    let totalQuantity = 0;
    let netQuantity = 0;
    let totalUnrealizedPL = 0;

    spread.legs.forEach(leg => {
      const legCost = (leg.cost_basis || 0);
      const legMarketValue = leg.marketValue || 0;
      const quantity = leg.quantity || 0;

      // For spreads, we calculate net debit/credit
      // Positive quantity = long position (debit)
      // Negative quantity = short position (credit)
      // Since cost_basis is now signed (Negative for Short), we just sum them up.
      netDebit += legCost;

      // Calculate P&L for each leg using the same logic as individual positions
      // Long positions: P&L = market_value - cost_basis
      // Short positions: P&L = |cost_basis| - |market_value|
      const isShort = quantity < 0;
      const legUnrealizedPL = positionUnrealizedPL(leg);

      // Net market value for spread = (Short MV) - (Long MV)
      totalMarketValue += isShort ? Math.abs(legMarketValue) : -Math.abs(legMarketValue);
      totalQuantity += Math.abs(quantity);
      netQuantity += quantity;
      totalUnrealizedPL += legUnrealizedPL;
    });

    // For spreads, avgCost represents the net debit/credit per contract
    const avgCost = totalQuantity > 0 ? Math.abs(netDebit) / (totalQuantity / spread.legs.length) : 0;
    // Use the sum of individual leg P&L instead of simple market value - net debit
    const unrealizedPL = totalUnrealizedPL;

    // Calculate breakeven points for common spreads
    let breakeven = null;
    if (spread.type.includes('Call') && spread.legs.length === 2) {
      const strikes = spread.legs.map(leg => leg.optionInfo?.strike).filter(s => s).sort((a, b) => a - b);
      if (strikes.length === 2) {
        breakeven = spread.type.includes('Debit') ?
          strikes[0] + Math.abs(avgCost) :
          strikes[1] - Math.abs(avgCost);
      }
    } else if (spread.type.includes('Put') && spread.legs.length === 2) {
      const strikes = spread.legs.map(leg => leg.optionInfo?.strike).filter(s => s).sort((a, b) => a - b);
      if (strikes.length === 2) {
        breakeven = spread.type.includes('Debit') ?
          strikes[1] - Math.abs(avgCost) :
          strikes[0] + Math.abs(avgCost);
      }
    }

    // Calculate Day P/L for the spread
    let dayPL = 0;
    spread.legs.forEach(leg => {
      // Calculate day change value for this leg
      const change = leg.change || 0;
      const multiplier = leg.contractMultiplier || leg.optionInfo?.multiplier || (leg.optionInfo?.isOption ? 100 : 1);
      dayPL += change * leg.quantity * multiplier;
    });

    // BP Effect Calculation
    let bpEffect = 0;

    // Helper to get valid strike
    const getStrike = (leg) => {
      if (leg.optionInfo && typeof leg.optionInfo.strike === 'number') return leg.optionInfo.strike;
      return 0;
    };

    if (spread.legs.length === 2) {
      const strike1 = getStrike(spread.legs[0]);
      const strike2 = getStrike(spread.legs[1]);
      // Use the contract's own multiplier — futures options aren't ×100.
      const spreadMult = spread.legs[0].contractMultiplier || spread.legs[0].optionInfo?.multiplier || 100;

      if (strike1 > 0 && strike2 > 0) {
        const qty = Math.abs(spread.legs[0].quantity);
        bpEffect = Math.abs(strike1 - strike2) * spreadMult * qty;
      }
    } else if (spread.legs.length >= 3) {
      // Iron condor / double spread (2+ calls AND 2+ puts): Reg-T margin is
      // the WIDER of the two sides' widths, not both.
      const calls = spread.legs.filter(l => l.optionInfo?.type === 'C');
      const puts = spread.legs.filter(l => l.optionInfo?.type === 'P');
      if (calls.length >= 2 && puts.length >= 2) {
        const mult = spread.legs[0].contractMultiplier || spread.legs[0].optionInfo?.multiplier || 100;
        const qty = Math.max(...spread.legs.map(l => Math.abs(l.quantity) || 0));
        const widthOf = ls => {
          const s = ls.map(getStrike).filter(v => v > 0);
          return s.length >= 2 ? Math.max(...s) - Math.min(...s) : 0;
        };
        bpEffect = Math.max(widthOf(calls), widthOf(puts)) * mult * qty;
      }
    }

    return {
      totalCost: Math.abs(netDebit),
      netDebit,
      totalMarketValue,
      avgCost,
      unrealizedPL,
      dayPL,
      totalQuantity,
      netQuantity,
      breakeven,
      isCredit: netDebit < 0,
      bpEffect
    };
  };

  // Sorting state for closed positions
  const [closedPositionsSort, setClosedPositionsSort] = useState({ field: 'closedPL', direction: 'desc' });
  // Sorting for Open Positions table
  const [openPositionsSort, setOpenPositionsSort] = useState({ field: 'marketValue', direction: 'desc' });
  // {pl: {chain_id: pl}, fillChain: {trade_id: chain_id}} for the Chain P/L column
  const [chainPLMap, setChainPLMap] = useState({ pl: {}, fillChain: {} });
  // symbol -> close on/before YTD anchor, for the duration P/L column
  const [durPrices, setDurPrices] = useState({});
  const handleOpenPositionsSort = (field) => {
    setOpenPositionsSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Spread detection and grouping state
  const [groupedPositions, setGroupedPositions] = useState([]);
  const [expandedSpreads, setExpandedSpreads] = useState(new Set());
  const [showIndividualPositions, setShowIndividualPositions] = useState(false);
  const [positionFilter, setPositionFilter] = useState(() => {
    try {
      // Shared with Dashboard — the pages must show the same filtered view.
      const saved = modeGet('global_position_filter', 'stocksummary_position_filter');
      return saved ? JSON.parse(saved) : ['all'];
    } catch {
      return ['all'];
    }
  }); // 'all', 'options', 'stocks', 'dividends'

  // Payoff diagram popover state (replaced with click-based dialog)
  const [positionDetailsOpen, setPositionDetailsOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [editingPositionTags, setEditingPositionTags] = useState(false);
  const [positionTagDraft, setPositionTagDraft] = useState([]);
  const [savingPositionTags, setSavingPositionTags] = useState(false);
  const [retradeOpen, setRetradeOpen] = useState(false);
  const [retradeAlert, setRetradeAlert] = useState(null);
  const [positionGreeks, setPositionGreeks] = useState(null); // For single option
  const [legGreeks, setLegGreeks] = useState({}); // For multi-leg spreads: { legIndex: greeksData }
  const [loadingGreeks, setLoadingGreeks] = useState(false);

  const [groupOpenByUnderlying, setGroupOpenByUnderlying] = useState(false);
  // Start with all sections collapsed; restore from localStorage if available
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = modeGet('stocksummary_expanded_groups');
      if (saved) return new Set(JSON.parse(saved));
    } catch (_) {}
    return new Set();
  });
  const [expandedAssignedPositions, setExpandedAssignedPositions] = useState(new Set());

  // Persist expandedGroups to localStorage so it survives page switches
  useEffect(() => {
    try {
      localStorage.setItem(modeKey('stocksummary_expanded_groups'), JSON.stringify([...expandedGroups]));
    } catch (_) {}
  }, [expandedGroups, paperMode]);

  // Additional filter states
  const [symbolFilter, setSymbolFilter] = useState(() => modeGet('global_symbol_filter') || '');
  const [strategyFilter, setStrategyFilter] = useState(() => modeGet('global_strategy_filter', 'stocksummary_strategy_filter') || 'all');
  const [openDateStart, setOpenDateStart] = useState(() => modeGet('global_open_date_start', 'stocksummary_open_date_start') || '');
  const [openDateEnd, setOpenDateEnd] = useState(() => modeGet('global_open_date_end', 'stocksummary_open_date_end') || '');
  const [showFilters, setShowFilters] = useState(() => modeGet('stocksummary_show_filters') === 'true');
  const [showDetailCols, setShowDetailCols] = useState(() => modeGet('stocksummary_detail_cols') === 'true');
  const [tagFilter, setTagFilter] = useState(() => {
    try { return JSON.parse(modeGet('global_tag_filter', 'stocksummary_tag_filter') || '[]'); } catch { return []; }
  });
  const [tagFilterExclude, setTagFilterExclude] = useState(() => {
    try { return JSON.parse(modeGet('global_tag_filter_exclude', 'stocksummary_tag_filter_exclude') || '[]'); } catch { return []; }
  });

  // Expanded closed position symbols for showing underlying trades
  const [expandedClosedSymbols, setExpandedClosedSymbols] = useState(new Set());
  // Sorting state for expanded closed position trades
  const [expandedTradesSort, setExpandedTradesSort] = useState({ field: 'tradeDate', direction: 'desc' });
  const [strategySort, setStrategySort] = useState({ field: 'closeDate', direction: 'desc' });

  // Assignment Dialog State
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignQuantity, setAssignQuantity] = useState('');
  const [selectedTradeForAssignment, setSelectedTradeForAssignment] = useState(null);
  const [selectedPositionForAssignment, setSelectedPositionForAssignment] = useState(null);
  const [assignFlagType, setAssignFlagType] = useState('assigned'); // 'assigned' | 'cash'
  const [assignmentError, setAssignmentError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Closed-position P/L edit dialog: { trade, cost, proceeds, realizedPL, closingDate, saving, error }
  const [plEdit, setPlEdit] = useState(null);
  // Tag editor dialog (closed strategies): { entity, draft, input, saving, error }
  const [tagEditDialog, setTagEditDialog] = useState(null);

  // Persist open date range and filter panel visibility
  useEffect(() => {
    try {
      localStorage.setItem(modeKey('global_open_date_start'), openDateStart || '');
      localStorage.setItem(modeKey('global_open_date_end'), openDateEnd || '');
      localStorage.setItem(modeKey('stocksummary_show_filters'), showFilters ? 'true' : 'false');
      localStorage.setItem(modeKey('stocksummary_detail_cols'), showDetailCols ? 'true' : 'false');
      localStorage.setItem(modeKey('global_symbol_filter'), symbolFilter || '');
      localStorage.setItem(modeKey('global_strategy_filter'), strategyFilter || 'all');
      localStorage.setItem(modeKey('global_position_filter'), JSON.stringify(positionFilter));
      localStorage.setItem(modeKey('global_tag_filter'), JSON.stringify(tagFilter));
      localStorage.setItem(modeKey('global_tag_filter_exclude'), JSON.stringify(tagFilterExclude));
      localStorage.setItem(modeKey('stocksummary_closed_group_by'), closedGroupBy || 'month');
      localStorage.setItem(modeKey('stocksummary_group_by_mode'), groupByMode || 'symbol');
      localStorage.setItem(modeKey('stocksummary_calendar_view'), calendarViewMode || 'calendar');
    } catch { }
  }, [openDateStart, openDateEnd, showFilters, showDetailCols, symbolFilter, strategyFilter, positionFilter, tagFilter, tagFilterExclude, closedGroupBy, groupByMode, calendarViewMode, paperMode]);

  // Saved filter presets — persisted server-side (user_preferences) so they
  // follow the user across devices; localStorage is the offline cache.
  const [savedFilters, setSavedFilters] = useState(readLocalPresets);
  const [selectedPreset, setSelectedPreset] = useState(() => modeGet('stocksummary_selected_preset') || '');
  const [presetDialog, setPresetDialog] = useState({ open: false, name: '' });

  useEffect(() => {
    let cancelled = false;
    loadFilterPresets().then(p => { if (!cancelled) setSavedFilters(p); });
    return () => { cancelled = true; };
  }, []);

  const DEFAULT_FILTERS = {
    accounts: ['all'],
    timePeriod: 'month',
    customStartDate: '',
    customEndDate: '',
    closedGroupBy: 'month',
    positionFilter: ['all'],
    groupOpenByUnderlying: false,
    strategyFilter: 'all',
    symbolFilter: '',
    tagFilter: [],
    tagFilterExclude: [],
    openDateStart: '',
    openDateEnd: '',
  };

  const getCurrentFilterPreset = () => ({
    accounts: selectedAccount,
    timePeriod,
    customStartDate,
    customEndDate,
    closedGroupBy,
    positionFilter,
    groupOpenByUnderlying,
    strategyFilter,
    symbolFilter,
    tagFilter,
    tagFilterExclude,
    openDateStart,
    openDateEnd,
  });

  const applyFilterPreset = (preset) => {
    const p = { ...DEFAULT_FILTERS, ...preset };
    setSelectedAccount(p.accounts);
    setDraftAccount(p.accounts);
    setTimePeriod(p.timePeriod);
    setCustomStartDate(p.customStartDate);
    setCustomEndDate(p.customEndDate);
    setClosedGroupBy(p.closedGroupBy);
    setPositionFilter(p.positionFilter);
    setGroupOpenByUnderlying(p.groupOpenByUnderlying);
    setStrategyFilter(p.strategyFilter);
    setSymbolFilter(p.symbolFilter);
    setTagFilter(p.tagFilter || []);
    setTagFilterExclude(p.tagFilterExclude || []);
    setOpenDateStart(p.openDateStart);
    setOpenDateEnd(p.openDateEnd);
  };

  const handleSelectPreset = (name) => {
    setSelectedPreset(name);
    localStorage.setItem(modeKey('stocksummary_selected_preset'), name || '');
    if (name && savedFilters[name]) applyFilterPreset(savedFilters[name]);
  };

  const handleSavePreset = () => {
    const name = presetDialog.name.trim();
    if (!name) return;
    const next = { ...savedFilters, [name]: getCurrentFilterPreset() };
    setSavedFilters(next);
    persistFilterPresets(next);
    setSelectedPreset(name);
    localStorage.setItem(modeKey('stocksummary_selected_preset'), name);
    setPresetDialog({ open: false, name: '' });
  };

  const handleDeletePreset = () => {
    if (!selectedPreset || !savedFilters[selectedPreset]) return;
    const next = { ...savedFilters };
    delete next[selectedPreset];
    setSavedFilters(next);
    persistFilterPresets(next);
    setSelectedPreset('');
    localStorage.setItem(modeKey('stocksummary_selected_preset'), '');
  };

  const resetFilters = () => {
    applyFilterPreset(DEFAULT_FILTERS);
    setSelectedPreset('');
    localStorage.setItem(modeKey('stocksummary_selected_preset'), '');
  };

  // Persist a tag draft to every endpoint that owns this entity's tags (paper
  // position rows, underlying trades, or open trades via account+symbol).
  // Returns the trade ids that were updated so callers can patch local state.
  const persistEntityTags = async (entity, draft) => {
    const targets = getTagTargets(entity);
    if (targets.length === 0) {
      throw new Error('No writable trades found for this position');
    }
    const patchedIds = new Set();
    const results = await Promise.all(targets.map(t => apiRequest(t.url, {
      method: 'PUT',
      body: JSON.stringify({ tags: draft, ...(t.extra || {}) }),
    })));
    targets.forEach(t => {
      const m = t.url.match(/\/api\/trades\/(\d+)\/tags/);
      if (m) patchedIds.add(parseInt(m[1]));
    });
    results.forEach(r => (r?.data?.tradeIds || r?.tradeIds || []).forEach(id => patchedIds.add(id)));
    return patchedIds;
  };

  // Patch edited trade ids in symbolData so tag changes show immediately in
  // the closed view and the open-position tag lookup without a refetch.
  const patchTradeTags = (patchedIds, draft) => {
    if (!patchedIds || patchedIds.size === 0) return;
    setSymbolData(prev => prev.map(row => ({
      ...row,
      trades: (row.trades || []).map(t => patchedIds.has(t.id) ? { ...t, tags: [...draft] } : t),
    })));
  };

  const savePositionTags = async () => {
    setSavingPositionTags(true);
    try {
      const patchedIds = await persistEntityTags(selectedPosition, positionTagDraft);
      patchTradeTags(patchedIds, positionTagDraft);
      setSelectedPosition(prev => prev ? { ...prev, tags: [...positionTagDraft] } : prev);
      setEditingPositionTags(false);
      fetchOpenPositions(true);
    } catch (e) {
      setAssignmentError(e.message || 'Failed to save tags');
    } finally {
      setSavingPositionTags(false);
    }
  };

  const openTagEditDialog = (entity) => {
    setTagEditDialog({ entity, draft: getAllTags(entity), saving: false, error: null });
  };

  const saveTagEditDialog = async () => {
    if (!tagEditDialog) return;
    const { entity, draft } = tagEditDialog;
    setTagEditDialog(d => ({ ...d, saving: true, error: null }));
    try {
      const patchedIds = await persistEntityTags(entity, draft);
      patchTradeTags(patchedIds, draft);
      setTagEditDialog(null);
    } catch (e) {
      setTagEditDialog(d => ({ ...d, saving: false, error: e.message || 'Failed to save tags' }));
    }
  };

  // Roll/Close dialog state
  const [rollCloseDialog, setRollCloseDialog] = useState({
    open: false,
    mode: 'close', // 'close' or 'roll'
    underlying: '',
    accountId: null,
    accountName: '',
    // legs: array of { expiration, strike, putCall, instruction, quantity, isClose, isNew }
    legs: [],
    // Order params
    orderType: 'LIMIT',
    duration: 'DAY',
    price: '',
    quantity: 1,
    loading: false,
    error: null,
    result: null,
    orderId: null, // tracked orderId for modify/cancel
    orderStatus: null, // WORKING, CANCELLED, REJECTED, FILLED, etc.
    isModifyMode: false,
    // Multi-target exits
    multiTargetEnabled: false,
    exitTargets: [
      { qty: '', price: '', enabled: true },
      { qty: '', price: '', enabled: true },
      { qty: '', price: '', enabled: true },
      { qty: '', price: '', enabled: true },
    ],
  });
  const [rollCloseQuotes, setRollCloseQuotes] = useState({});
  const [rollCloseAutoPrice, setRollCloseAutoPrice] = useState(true);
  const rollCloseQuoteIntervalRef = useRef(null);
  const rollCloseStatusIntervalRef = useRef(null);
  // Cancel confirmation dialog
  const [cancelOrderConfirm, setCancelOrderConfirm] = useState({ open: false, orderId: null, accountId: null, positionLabel: '' });
  // Per-row actions dropdown in the open positions table — a single "Actions"
  // button per row opens this menu (Roll/Close/Add/Alert, or Modify/Cancel when
  // a working order exists) instead of four always-visible buttons.
  const [rowActionsMenu, setRowActionsMenu] = useState(null); // { anchor, position }

  // Price alert dialog state
  const [priceAlertDialog, setPriceAlertDialog] = useState({ open: false, position: null, targetPrice: '', condition: 'above', alertType: 'option', optionPrice: null, underlyingPrice: null, loading: false, error: null });
  const [activePriceAlerts, setActivePriceAlerts] = useState([]);
  const [tradeGroups, setTradeGroups] = useState([]);
  const [groupSelectMode, setGroupSelectMode] = useState(false);
  const [selectedGroupLegs, setSelectedGroupLegs] = useState(new Set());
  const priceAlertCheckRef = useRef(null);

  // Refresh handlers
  const handleRefreshOpenPositions = async () => {
    localStorage.removeItem(getOpenPositionsCacheKey());
    localStorage.removeItem(getCacheKey());
    await fetchOpenPositions(false);
  };

  // Toggle closed symbol expansion
  const toggleClosedSymbol = (symbol) => {
    const newExpanded = new Set(expandedClosedSymbols);
    if (newExpanded.has(symbol)) {
      newExpanded.delete(symbol);
    } else {
      newExpanded.add(symbol);
    }
    setExpandedClosedSymbols(newExpanded);
  };

  // Handle sorting for expanded closed position trades
  const handleExpandedTradesSort = (field) => {
    setExpandedTradesSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleStrategySort = (field) => {
    setStrategySort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Detect strategy from a group of trades
  const detectStrategyFromTrades = (trades) => {
    if (!trades || trades.length === 0) return null;

    // Group trades by expiration and detect strategy
    const byExpiration = {};
    trades.forEach(trade => {
      const optionInfo = parseOptionSymbol(trade.originalSymbol || trade.symbol);
      if (optionInfo.isOption) {
        const exp = optionInfo.expiration || 'Unknown';
        if (!byExpiration[exp]) byExpiration[exp] = [];
        byExpiration[exp].push({
          ...trade,
          optionInfo,
          quantity: parseFloat(trade.quantity) || 0
        });
      }
    });

    // Check for strategies in each expiration group
    for (const [exp, legs] of Object.entries(byExpiration)) {
      if (legs.length >= 2) {
        const spread = detectSpreadType(legs);
        if (spread) {
          return legs.some(l => l.optionInfo?.isFuturesOption)
            ? `Futures ${spread.type}` : spread.type;
        }
      }
    }

    // Check for single options or equity
    const optionInfos = trades.map(t => parseOptionSymbol(t.originalSymbol || t.symbol));
    const hasOptions = optionInfos.some(i => i.isOption);
    if (!hasOptions) return 'Equity';
    if (optionInfos.filter(i => i.isOption).every(i => i.isFuturesOption)) return 'Futures Option';

    return 'Single Options';
  };

  const handleRefreshClosedPositions = () => {
    localStorage.removeItem(getCacheKey());
    setRefreshKey(prev => prev + 1);
    setLoading(true);
  };

  // Fetch Greeks for a single option leg
  const fetchLegGreeks = async (leg, legIndex) => {
    if (!leg || !leg.optionInfo?.isOption) {
      return null;
    }

    try {
      const schwabAccount = accounts.find(a => a.apiType === 'schwab');
      if (!schwabAccount) {
        return null;
      }

      let symbol = leg.optionInfo.underlying || leg.underlying;
      // Map SPX/SPXW to $SPX for Schwab API
      if (symbol === 'SPXW' || symbol === 'SPX') {
        symbol = '$SPX';
      }
      const strike = leg.optionInfo.strike;
      const expiration = leg.optionInfo.expiration;
      const putCall = leg.optionInfo.type === 'C' ? 'CALL' : 'PUT';

      let expDate = '';
      if (expiration && expiration.length === 6) {
        const year = 2000 + parseInt(expiration.substring(0, 2));
        const month = expiration.substring(2, 4);
        const day = expiration.substring(4, 6);
        expDate = `${year}-${month}-${day}`;
      }

      const response = await apiRequest(`/api/schwab-api/chains?accountId=${schwabAccount.id}&symbol=${symbol}&contractType=${putCall}&strike=${strike}&includeUnderlyingQuote=true&strategy=SINGLE&fromDate=${expDate}&toDate=${expDate}`);

      const isSuccess = response.success || (response.status === 200 && response.data);
      if (!isSuccess || !response.data) {
        return null;
      }

      const chainsData = response.data.data || response.data;
      const trimmedPutCall = putCall ? putCall.trim() : '';
      const expDateMap = trimmedPutCall === 'CALL' ? chainsData.callExpDateMap : chainsData.putExpDateMap;

      if (!expDateMap || Object.keys(expDateMap).length === 0) {
        return null;
      }

      for (const [expKey, strikes] of Object.entries(expDateMap || {})) {
        if (expKey.startsWith(expDate + ':') || expKey === expDate) {
          const strikeKey = String(strike);
          if (strikes[strikeKey] && strikes[strikeKey].length > 0) {
            const optionData = strikes[strikeKey][0];
            return {
              iv: optionData.volatility,
              delta: optionData.delta,
              gamma: optionData.gamma,
              theta: optionData.theta,
              vega: optionData.vega,
              rho: optionData.rho,
              mark: optionData.mark,
              bid: optionData.bid,
              ask: optionData.ask,
              breakEven: optionData.breakEven,
              inTheMoney: optionData.inTheMoney
            };
          }
          const fuzzyStrike = Object.keys(strikes).find(k => parseFloat(k) === parseFloat(strike));
          if (fuzzyStrike && strikes[fuzzyStrike] && strikes[fuzzyStrike].length > 0) {
            const optionData = strikes[fuzzyStrike][0];
            return {
              iv: optionData.volatility,
              delta: optionData.delta,
              gamma: optionData.gamma,
              theta: optionData.theta,
              vega: optionData.vega,
              rho: optionData.rho,
              mark: optionData.mark,
              bid: optionData.bid,
              ask: optionData.ask,
              breakEven: optionData.breakEven,
              inTheMoney: optionData.inTheMoney
            };
          }
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching leg Greeks:', error);
      return null;
    }
  };

  // Fetch Greeks for a position (single option or multi-leg spread)
  const fetchPositionGreeks = async (position) => {
    setLoadingGreeks(true);
    setPositionGreeks(null);
    setLegGreeks({});

    try {
      // Handle multi-leg spreads
      if (position.isSpread && position.legs && position.legs.length > 0) {
        const greeksPromises = position.legs.map((leg, index) => fetchLegGreeks(leg, index));
        const greeksResults = await Promise.all(greeksPromises);
        
        const legGreeksMap = {};
        greeksResults.forEach((greeks, index) => {
          if (greeks) {
            legGreeksMap[index] = greeks;
          }
        });
        setLegGreeks(legGreeksMap);
        return;
      }

      // Handle single options
      if (!position || !position.optionInfo?.isOption) {
        return;
      }

      const schwabAccount = accounts.find(a => a.apiType === 'schwab');
      if (!schwabAccount) {
        console.warn('No Schwab account found for Greeks data');
        return;
      }

      let symbol = position.optionInfo.underlying || position.underlying;
      // Map SPX/SPXW to $SPX for Schwab API
      if (symbol === 'SPXW' || symbol === 'SPX') {
        symbol = '$SPX';
      }
      const strike = position.optionInfo.strike;
      const expiration = position.optionInfo.expiration;
      const putCall = position.optionInfo.type === 'C' ? 'CALL' : 'PUT';

      let expDate = '';
      if (expiration && expiration.length === 6) {
        const year = 2000 + parseInt(expiration.substring(0, 2));
        const month = expiration.substring(2, 4);
        const day = expiration.substring(4, 6);
        expDate = `${year}-${month}-${day}`;
      }

      const response = await apiRequest(`/api/schwab-api/chains?accountId=${schwabAccount.id}&symbol=${symbol}&contractType=${putCall}&strike=${strike}&includeUnderlyingQuote=true&strategy=SINGLE&fromDate=${expDate}&toDate=${expDate}`);

      const isSuccess = response.success || (response.status === 200 && response.data);
      if (!isSuccess) {
        console.warn('Greeks data not available for this symbol:', symbol);
        return;
      }

      if (isSuccess && response.data) {
        const chainsData = response.data.data || response.data;
        const trimmedPutCall = putCall ? putCall.trim() : '';
        const expDateMap = trimmedPutCall === 'CALL' ? chainsData.callExpDateMap : chainsData.putExpDateMap;

        if (!expDateMap || Object.keys(expDateMap).length === 0) {
          return;
        }

        let found = false;
        for (const [expKey, strikes] of Object.entries(expDateMap || {})) {
          if (expKey.startsWith(expDate + ':') || expKey === expDate) {
            const strikeKey = String(strike);
            if (strikes[strikeKey] && strikes[strikeKey].length > 0) {
              const optionData = strikes[strikeKey][0];
              setPositionGreeks({
                iv: optionData.volatility,
                delta: optionData.delta,
                gamma: optionData.gamma,
                theta: optionData.theta,
                vega: optionData.vega,
                rho: optionData.rho,
                mark: optionData.mark,
                bid: optionData.bid,
                ask: optionData.ask,
                breakEven: optionData.breakEven,
                inTheMoney: optionData.inTheMoney
              });
              found = true;
              return;
            }
            const fuzzyStrike = Object.keys(strikes).find(k => parseFloat(k) === parseFloat(strike));
            if (fuzzyStrike && strikes[fuzzyStrike] && strikes[fuzzyStrike].length > 0) {
              const optionData = strikes[fuzzyStrike][0];
              setPositionGreeks({
                iv: optionData.volatility,
                delta: optionData.delta,
                gamma: optionData.gamma,
                theta: optionData.theta,
                vega: optionData.vega,
                rho: optionData.rho,
                mark: optionData.mark,
                bid: optionData.bid,
                ask: optionData.ask,
                breakEven: optionData.breakEven,
                inTheMoney: optionData.inTheMoney
              });
              found = true;
              return;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching Greeks:', error);
    } finally {
      setLoadingGreeks(false);
    }
  };

  // Account Summaries (Cash Balances) State
  const [accountSummaries, setAccountSummaries] = useState([]);
  const [totalCash, setTotalCash] = useState(0);
  const [summariesLoading, setSummariesLoading] = useState(false);

  // Position pages only deal with investment/brokerage accounts — hide Plaid
  // banking/credit/loan rows. Paper mode shows a single pseudo account since
  // paper positions aren't tied to real accounts.
  const categoryOf = (a) => {
    const cat = String(a.category || '').toLowerCase();
    if (['investment', 'banking', 'credit', 'loan', 'other'].includes(cat)) return cat;
    if (String(a.apiType || a.api_type || '').toLowerCase() === 'plaid') {
      return /credit\s*card|visa|mastercard|amex|discover/i.test(a.name || '') ? 'credit' : 'banking';
    }
    return 'investment';
  };
  const investmentIds = new Set(accounts.filter(a => categoryOf(a) === 'investment').map(a => String(a.id)));
  const displayAccounts = paperMode
    ? [{ id: 'paper', name: 'Paper Trading', accountType: 'paper' }]
    : accounts.filter(a => investmentIds.has(String(a.id)));

  // Grouped Accounts for dropdown
  const taxableAccounts = displayAccounts.filter(a => a.accountType === 'taxable');
  const nonTaxableAccounts = displayAccounts.filter(a => a.accountType === 'non-taxable');

  const handleAccountCategorySelect = (category) => {
    const categoryAccounts = category === 'taxable' ? taxableAccounts : nonTaxableAccounts;
    const categoryIds = categoryAccounts.map(a => a.id);

    // Check if all accounts in this category are already selected
    const allSelected = categoryIds.every(id => draftAccount.includes(id));

    let newDraft;
    if (allSelected) {
      // Unselect all in category
      newDraft = draftAccount.filter(id => !categoryIds.includes(id));
    } else {
      // Select all in category
      newDraft = Array.from(new Set([...draftAccount, ...categoryIds])).filter(id => id !== 'all');
    }

    if (newDraft.length === 0) newDraft = ['all'];
    setDraftAccount(newDraft);
  };

  const handleOpenAssignDialog = async (position) => {
    setSelectedTradeForAssignment(null);
    setSelectedPositionForAssignment(position);
    setAssignFlagType('assigned');
    setAssignDialogOpen(true);
    setAssignmentError(null);
    setAssignQuantity(Math.abs(position.quantity).toString());

    try {
      // Fetch open trades for this symbol and account to find the database ID
      const symbol = (position.symbol || '').trim();
      const accountId = position.accountId || position.account_id;

      if (!symbol) {
        setAssignmentError("Invalid position: missing symbol.");
        return;
      }

      // We search globally first if symbol-specific search fails
      const searchForTrade = async (searchSymbol) => {
        const url = searchSymbol
          ? `/api/trades?symbol=${encodeURIComponent(searchSymbol)}&accountId=${accountId}&includeOpen=true&limit=1000`
          : `/api/trades?accountId=${accountId}&includeOpen=true&limit=1000`;

        const response = await apiRequest(url);
        const trades = response.data?.trades || response.data?.value || (Array.isArray(response.data) ? response.data : []);

        return Array.isArray(trades) ? trades.filter(t => {
          const tSymbol = (t.symbol || t.originalSymbol || t.original_symbol || '').trim();
          const info = parseOptionSymbol(tSymbol);
          const isStock = !info.isOption;

          // Match symbol if provided
          if (searchSymbol && tSymbol !== searchSymbol) return false;

          // Check both camelCase and snake_case for DB compatibility
          const isClosed = t.closing_date || t.closingDate ||
            (t.net_pl !== undefined && t.net_pl !== null && parseFloat(t.net_pl) !== 0) ||
            (t.netPL !== undefined && t.netPL !== null && parseFloat(t.netPL) !== 0) ||
            (t.realized_pl !== undefined && t.realized_pl !== null && parseFloat(t.realized_pl) !== 0) ||
            (t.realizedPL !== undefined && t.realizedPL !== null && parseFloat(t.realizedPL) !== 0);

          return isStock && !isClosed;
        }) : [];
      };

      let openStockTrades = await searchForTrade(symbol);

      // If not found by exact symbol, try searching all open trades for this account
      // This helps if the symbol is mapped differently in the DB (e.g. "FAS " or "FAS.OLD")
      if (openStockTrades.length === 0) {
        console.log(`No exact match for ${symbol}, searching all open trades...`);
        const allOpen = await searchForTrade(null);
        openStockTrades = allOpen.filter(t => {
          const tSym = (t.symbol || t.originalSymbol || t.original_symbol || '').trim();
          return tSym === symbol || tSym.startsWith(symbol) || symbol.startsWith(tSym);
        });
      }

      if (openStockTrades.length > 0) {
        // Sort by quantity descending to prefer larger trades if no exact match found
        openStockTrades.sort((a, b) => Math.abs(b.quantity) - Math.abs(a.quantity));

        // Find the best match by quantity
        const targetQty = Math.abs(position.quantity);
        const bestMatch = openStockTrades.find(t => Math.abs(t.quantity) === targetQty) || openStockTrades[0];

        setSelectedTradeForAssignment(bestMatch);
        // Default to the position quantity, not the trade quantity, 
        // but the validation will still check against the trade.
        // Actually, if we pick a trade, we should probably default to the position quantity 
        // if it's <= trade quantity, otherwise default to trade quantity.
        const defaultQty = Math.min(targetQty, Math.abs(bestMatch.quantity));
        setAssignQuantity(defaultQty.toString());
      }
    } catch (error) {
      console.error('Error finding trade for assignment:', error);
      // We don't block the dialog anymore if trade lookup fails
    }
  };

  const handleSubmitAssign = async () => {
    const qty = parseFloat(assignQuantity);
    if (isNaN(qty) || qty <= 0) {
      setAssignmentError("Please enter a valid positive quantity.");
      return;
    }

    if (selectedTradeForAssignment && qty > Math.abs(selectedTradeForAssignment.quantity)) {
      setAssignmentError("Quantity cannot exceed the original trade quantity.");
      return;
    }

    try {
      const isCash = assignFlagType === 'cash';
      if (selectedTradeForAssignment) {
        // Split and assign existing trade
        await apiRequest(`/api/trades/${selectedTradeForAssignment.id}/assign`, {
          method: 'POST',
          body: JSON.stringify({ quantity: qty, isCash })
        });
      } else if (selectedPositionForAssignment) {
        // Manual assignment by symbol/account (when no trade found in DB)
        const symbol = (selectedPositionForAssignment.symbol || '').trim();
        const accountId = selectedPositionForAssignment.accountId || selectedPositionForAssignment.account_id;

        // Calculate proportional cost basis if quantity is different
        const fullQty = Math.abs(selectedPositionForAssignment.quantity || 1);
        const fullCost = parseFloat(selectedPositionForAssignment.cost_basis || 0);
        const proportionalCost = (fullCost / fullQty) * qty;

        await apiRequest(`/api/trades/manual-assign`, {
          method: 'POST',
          body: JSON.stringify({
            symbol,
            accountId,
            quantity: selectedPositionForAssignment.quantity < 0 ? -qty : qty,
            cost: Math.abs(proportionalCost),
            isCash
          })
        });
      }

      setAssignDialogOpen(false);
      setRefreshKey(prev => prev + 1); // Trigger data refresh
      setAssignmentError(null);
      // Refresh positions to update the UI blocks
      fetchOpenPositions(false);
    } catch (error) {
      console.error('Error assigning trade:', error);
      const errorMsg = error.response?.data?.details ||
        error.response?.data?.error ||
        error.message ||
        "Failed to mark as assigned.";
      setAssignmentError(errorMsg);
    }
  };

  // Remove the assigned flag from a position so it moves back to Equity.
  const handleUnassign = async (position) => {
    try {
      await apiRequest('/api/trades/unassign', {
        method: 'POST',
        body: JSON.stringify({
          symbol: (position.symbol || '').trim(),
          accountId: position.accountId || position.account_id
        })
      });
      setRefreshKey(prev => prev + 1);
      fetchOpenPositions(false);
    } catch (error) {
      console.error('Error unassigning position:', error);
      setAssignmentError(error.response?.data?.error || 'Failed to unassign position.');
    }
  };

  // ==================== Roll/Close Dialog Handlers ====================


  function addDaysToDate(dateStr, days) {
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  }

  function addWeekToDate(dateStr) {
    return addDaysToDate(dateStr, 7);
  }

  function yymmddToISO(yymmdd) {
    if (!yymmdd || yymmdd.length !== 6) return '';
    const yy = yymmdd.slice(0, 2);
    const mm = yymmdd.slice(2, 4);
    const dd = yymmdd.slice(4, 6);
    return `20${yy}-${mm}-${dd}`;
  }

  // Plaid-connected accounts are read-only — trading actions are unavailable
  const isNonTradingAccount = (accountId) => {
    if (accountId == null) return false;
    const acc = accounts.find(a => String(a.id) === String(accountId));
    return String(acc?.apiType || acc?.api_type || '').toLowerCase() === 'plaid';
  };

  // Build legs for a position or spread group
  // For single option: position has optionInfo
  // For spread: group has legs[] array
  const handleOpenRollCloseDialog = async (positionOrGroup, mode) => {
    if (isNonTradingAccount(positionOrGroup.accountId || positionOrGroup.account_id)) {
      setAssignmentError('Trading is not available for Plaid-connected accounts (read-only).');
      return;
    }
    let underlying = '';
    let accountId = null;
    let accountName = '';
    let absQty = 1;
    const closeLegs = [];

    // Detect equity position (no optionInfo or optionInfo.isOption is false)
    const isEquityPosition = !positionOrGroup.isSpread && (!positionOrGroup.optionInfo || !positionOrGroup.optionInfo.isOption);

    if (isEquityPosition && (mode === 'add' || mode === 'close')) {
      // Equity add/close: simple buy or sell shares
      const position = positionOrGroup;
      underlying = position.underlying || position.symbol || '';
      accountId = position.accountId || position.account_id;
      accountName = position.accountName || '';
      absQty = Math.abs(position.quantity);
      const isLong = position.quantity > 0;

      if (mode === 'add') {
        // Add = buy more shares in same direction
        closeLegs.push({
          assetType: 'EQUITY',
          symbol: underlying,
          instruction: isLong ? 'BUY' : 'SELL_SHORT',
          isClose: false,
          isNew: true,
          enabled: true,
        });
      } else {
        // Close = sell (if long) or buy to cover (if short)
        closeLegs.push({
          assetType: 'EQUITY',
          symbol: underlying,
          instruction: isLong ? 'SELL' : 'BUY_TO_COVER',
          isClose: true,
          isNew: false,
          enabled: true,
        });
      }

      setRollCloseDialog({
        open: true,
        mode,
        underlying,
        accountId,
        accountName,
        legs: closeLegs,
        orderType: 'LIMIT',
        duration: 'DAY',
        price: '',
        quantity: absQty,
        multiTargetEnabled: false,
        exitTargets: [
          { qty: '', price: '', enabled: true },
          { qty: '', price: '', enabled: true },
          { qty: '', price: '', enabled: true },
          { qty: '', price: '', enabled: true },
        ],
        loading: false,
        error: null,
        result: null,
        orderId: null,
        orderStatus: null,
        isModifyMode: false,
      });
      setRollCloseQuotes({});
      setRollCloseAutoPrice(true);
      return;
    }

    if (isEquityPosition && mode === 'sell_cc') {
      // Sell covered call: open a new short call against existing long stock
      const position = positionOrGroup;
      underlying = position.underlying || position.symbol || '';
      accountId = position.accountId || position.account_id;
      accountName = position.accountName || '';
      absQty = Math.abs(position.quantity);

      // Default to the nearest weekly expiration (from the option chain),
      // ~10% OTM strike. Falls back to 30 DTE when no chain is available.
      const fallbackExp = new Date();
      fallbackExp.setDate(fallbackExp.getDate() + 30);
      const expStr = (await fetchNearestWeeklyExpiration(underlying))
        || fallbackExp.toISOString().split('T')[0];
      const defaultStrike = position.underlyingPrice
        ? Math.ceil(position.underlyingPrice * 1.10)
        : '';

      closeLegs.push({
        assetType: 'OPTION',
        expiration: expStr,
        strike: String(defaultStrike),
        putCall: 'C',
        instruction: 'SELL_TO_OPEN',
        isClose: false,
        isNew: true,
        enabled: true,
      });

      setRollCloseDialog({
        open: true,
        mode: 'add',
        underlying,
        accountId,
        accountName,
        legs: closeLegs,
        orderType: 'LIMIT',
        duration: 'DAY',
        price: '',
        quantity: Math.min(absQty, 1),
        multiTargetEnabled: false,
        exitTargets: [
          { qty: '', price: '', enabled: true },
          { qty: '', price: '', enabled: true },
          { qty: '', price: '', enabled: true },
          { qty: '', price: '', enabled: true },
        ],
        loading: false,
        error: null,
        result: null,
        orderId: null,
        orderStatus: null,
        isModifyMode: false,
      });
      setRollCloseQuotes({});
      setRollCloseAutoPrice(true);
      return;
    }

    if (positionOrGroup.isSpread) {
      // Spread: group.legs array
      underlying = positionOrGroup.underlying || '';
      accountId = positionOrGroup.accountId;
      accountName = positionOrGroup.accountName || '';
      absQty = Math.abs(positionOrGroup.legs[0]?.quantity || 1);

      positionOrGroup.legs.forEach(leg => {
        const info = leg.optionInfo;
        if (!info || !info.isOption) return;
        const isLong = leg.quantity > 0;
        closeLegs.push({
          expiration: yymmddToISO(info.expiration),
          strike: String(info.strike || ''),
          putCall: info.type || 'C',
          instruction: isLong ? 'SELL_TO_CLOSE' : 'BUY_TO_CLOSE',
          isClose: true,
          isNew: false,
          enabled: true,
          isFuturesOption: info.isFuturesOption || false,
          quoteSymbol: info.quoteSymbol || null,
          orderSymbol: info.orderSymbol || null,
          contractSymbol: leg.symbol || null,
        });
      });
    } else {
      // Single option position
      const position = positionOrGroup;
      const info = position.optionInfo;
      if (!info || !info.isOption) return;
      underlying = info.underlying || '';
      accountId = position.accountId || position.account_id;
      accountName = position.accountName || '';
      absQty = Math.abs(position.quantity);
      const isLong = position.quantity > 0;

      closeLegs.push({
        expiration: yymmddToISO(info.expiration),
        strike: String(info.strike || ''),
        putCall: info.type || 'C',
        instruction: isLong ? 'SELL_TO_CLOSE' : 'BUY_TO_CLOSE',
        isClose: true,
        isNew: false,
        enabled: true,
        isFuturesOption: info.isFuturesOption || false,
        quoteSymbol: info.quoteSymbol || null,
        orderSymbol: info.orderSymbol || null,
        contractSymbol: position.symbol || null,
      });
    }

    // For roll: add new open legs with same strikes/types, +1 week expiry
    // For add: add new open legs with same strikes/types, same expiry
    const newLegs = [];
    if (mode === 'roll') {
      closeLegs.forEach(closeLeg => {
        const isBuyClose = closeLeg.instruction === 'BUY_TO_CLOSE';
        newLegs.push({
          expiration: addWeekToDate(closeLeg.expiration),
          strike: closeLeg.strike,
          putCall: closeLeg.putCall,
          instruction: isBuyClose ? 'SELL_TO_OPEN' : 'BUY_TO_OPEN',
          isClose: false,
          isNew: true,
          enabled: true,
        });
      });
    } else if (mode === 'add') {
      closeLegs.forEach(closeLeg => {
        const isBuyClose = closeLeg.instruction === 'BUY_TO_CLOSE';
        newLegs.push({
          expiration: closeLeg.expiration,
          strike: closeLeg.strike,
          putCall: closeLeg.putCall,
          instruction: isBuyClose ? 'SELL_TO_OPEN' : 'BUY_TO_OPEN',
          isClose: false,
          isNew: true,
          enabled: true,
        });
      });
    }

    // In 'add' mode, only show the new open legs (no close legs)
    const dialogLegs = mode === 'add' ? newLegs : [...closeLegs, ...newLegs];

    setRollCloseDialog({
      open: true,
      mode,
      underlying,
      accountId,
      accountName,
      legs: dialogLegs,
      orderType: 'LIMIT',
      duration: 'DAY',
      price: '',
      quantity: absQty,
      multiTargetEnabled: false,
      exitTargets: [
        { qty: '', price: '', enabled: true },
        { qty: '', price: '', enabled: true },
        { qty: '', price: '', enabled: true },
        { qty: '', price: '', enabled: true },
      ],
      loading: false,
      error: null,
      result: null,
      orderId: null,
      orderStatus: null,
      isModifyMode: false,
    });
    setRollCloseQuotes({});
    setRollCloseAutoPrice(true);
  };

  const handleCloseRollCloseDialog = () => {
    setRollCloseDialog(prev => ({ ...prev, open: false, loading: false, error: null, result: null, orderId: null, orderStatus: null, isModifyMode: false }));
    if (rollCloseQuoteIntervalRef.current) {
      clearInterval(rollCloseQuoteIntervalRef.current);
      rollCloseQuoteIntervalRef.current = null;
    }
    if (rollCloseStatusIntervalRef.current) {
      clearInterval(rollCloseStatusIntervalRef.current);
      rollCloseStatusIntervalRef.current = null;
    }
  };

  const handleAddRollCloseLeg = async () => {
    const fallbackExp = new Date();
    fallbackExp.setDate(fallbackExp.getDate() + 30);
    const expirationStr = (await fetchNearestWeeklyExpiration(rollCloseDialog.underlying))
      || fallbackExp.toISOString().slice(0, 10);
    setRollCloseDialog(prev => ({
      ...prev,
      legs: [
        ...prev.legs,
        {
          expiration: expirationStr,
          strike: '',
          putCall: 'C',
          instruction: 'BUY_TO_OPEN',
          isClose: false,
          isNew: true,
          enabled: false,
        }
      ]
    }));
  };

  // ==================== Price Alert Handlers ====================

  const buildCloseData = (positionOrGroup) => {
    let underlying = '';
    let accountId = null;
    let absQty = 1;
    const closeLegs = [];

    if (positionOrGroup.isSpread) {
      underlying = positionOrGroup.underlying || '';
      accountId = positionOrGroup.accountId;
      absQty = Math.abs(positionOrGroup.legs[0]?.quantity || 1);
      positionOrGroup.legs.forEach(leg => {
        const info = leg.optionInfo;
        if (!info || !info.isOption) return;
        const isLong = leg.quantity > 0;
        closeLegs.push({
          expiration: yymmddToISO(info.expiration),
          strike: String(info.strike || ''),
          putCall: info.type || 'C',
          instruction: isLong ? 'SELL_TO_CLOSE' : 'BUY_TO_CLOSE',
        });
      });
    } else {
      const position = positionOrGroup;
      const info = position.optionInfo;
      if (!info || !info.isOption) {
        // Stock position — no close dialog applicable
        return null;
      }
      underlying = info.underlying || '';
      accountId = position.accountId || position.account_id;
      absQty = Math.abs(position.quantity);
      const isLong = position.quantity > 0;
      closeLegs.push({
        expiration: yymmddToISO(info.expiration),
        strike: String(info.strike || ''),
        putCall: info.type || 'C',
        instruction: isLong ? 'SELL_TO_CLOSE' : 'BUY_TO_CLOSE',
      });
    }

    return { underlying, accountId, legs: closeLegs, quantity: absQty };
  };

  const handleOpenPriceAlertDialog = (positionOrGroup) => {
    const optionPrice = positionOrGroup.isSpread
      ? positionOrGroup.legs?.[0]?.currentPrice || (positionOrGroup.legs?.[0]?.marketValue || 0) / (Math.abs(positionOrGroup.legs?.[0]?.quantity || 1) * 100)
      : positionOrGroup.currentPrice;
    const underlyingPrice = positionOrGroup.underlyingPrice || positionOrGroup.underlyingQuote?.last || null;
    setPriceAlertDialog({
      open: true,
      position: positionOrGroup,
      targetPrice: optionPrice ? String(Math.round(optionPrice * 100) / 100) : '',
      condition: 'above',
      alertType: 'option',
      optionPrice: optionPrice || null,
      underlyingPrice: underlyingPrice,
      loading: false,
      error: null,
    });
  };

  const handleClosePriceAlertDialog = () => {
    setPriceAlertDialog(prev => ({ ...prev, open: false, loading: false, error: null }));
  };

  const handleCreatePriceAlert = async () => {
    const { position, targetPrice, condition, alertType } = priceAlertDialog;
    if (!targetPrice || isNaN(parseFloat(targetPrice))) {
      setPriceAlertDialog(prev => ({ ...prev, error: 'Enter a valid target price' }));
      return;
    }

    const closeData = buildCloseData(position);
    if (!closeData) {
      setPriceAlertDialog(prev => ({ ...prev, error: 'Price alerts on stock positions are not supported yet' }));
      return;
    }

    // Build the OCC symbol for the position (first leg for spreads)
    let symbol = '';
    if (position.isSpread) {
      const firstLeg = position.legs[0];
      const info = firstLeg.optionInfo;
      if (info) {
        symbol = buildOCCSymbol(closeData.underlying, yymmddToISO(info.expiration), info.type, parseFloat(info.strike));
      }
    } else {
      const info = position.optionInfo;
      if (info) {
        symbol = buildOCCSymbol(closeData.underlying, yymmddToISO(info.expiration), info.type, parseFloat(info.strike));
      }
    }

    if (!symbol) {
      setPriceAlertDialog(prev => ({ ...prev, error: 'Could not build symbol for alert' }));
      return;
    }

    const positionLabel = position.isSpread
      ? `${closeData.underlying} ${position.spreadType || ''} ${closeData.legs.map(l => l.strike).join('/')}`
      : `${closeData.underlying} ${closeData.legs[0]?.putCall === 'C' ? 'Call' : 'Put'} ${closeData.legs[0]?.strike}`;

    setPriceAlertDialog(prev => ({ ...prev, loading: true, error: null }));
    try {
      await apiRequest('/api/price-alerts', {
        method: 'POST',
        body: JSON.stringify({
          symbol,
          underlying: closeData.underlying,
          positionLabel,
          condition,
          targetPrice: parseFloat(targetPrice),
          alertType,
          closeData,
          accountId: closeData.accountId,
        }),
      });
      setPriceAlertDialog(prev => ({ ...prev, open: false, loading: false }));
      // Refresh alerts list
      fetchActivePriceAlerts();
    } catch (err) {
      setPriceAlertDialog(prev => ({ ...prev, loading: false, error: err.message || 'Failed to create alert' }));
    }
  };

  const handleDeletePriceAlert = async (alertId) => {
    try {
      await apiRequest(`/api/price-alerts/${alertId}`, { method: 'DELETE' });
      fetchActivePriceAlerts();
    } catch (err) {
      console.error('[PriceAlert] Delete failed:', err);
    }
  };

  const fetchActivePriceAlerts = useCallback(async () => {
    try {
      const resp = await apiRequest('/api/price-alerts');
      const alerts = Array.isArray(resp.data) ? resp.data : [];
      setActivePriceAlerts(alerts.filter(a => a.is_active && !a.triggered_at));
    } catch (err) {
      // Silent fail — alerts are optional
    }
  }, []);

  // Fetch active alerts on mount and poll for triggered alerts
  useEffect(() => {
    fetchActivePriceAlerts();
    // Check alerts every 30 seconds
    priceAlertCheckRef.current = setInterval(async () => {
      try {
        await apiRequest('/api/price-alerts/check', { method: 'POST' });
        fetchActivePriceAlerts();
      } catch (err) {
        // Silent fail
      }
    }, 30000);
    return () => {
      if (priceAlertCheckRef.current) {
        clearInterval(priceAlertCheckRef.current);
        priceAlertCheckRef.current = null;
      }
    };
  }, [fetchActivePriceAlerts]);

  // Check if a position/group has an active price alert
  const hasActivePriceAlert = (positionOrGroup) => {
    if (!positionOrGroup) return false;
    let underlying = '';
    let strikes = [];
    if (positionOrGroup.isSpread) {
      underlying = positionOrGroup.underlying || '';
      strikes = positionOrGroup.legs?.map(l => String(l.optionInfo?.strike || '')) || [];
    } else {
      underlying = positionOrGroup.optionInfo?.underlying || '';
      strikes = [String(positionOrGroup.optionInfo?.strike || '')];
    }
    return activePriceAlerts.some(a =>
      a.underlying === underlying &&
      a.is_active &&
      strikes.some(s => a.position_label?.includes(s))
    );
  };

  // Poll order status after placement
  const fetchWorkingCloseOrdersRef = useRef(null);
  const pollRollCloseOrderStatus = useCallback(async (orderId, accountId) => {
    if (!orderId || !accountId) return;
    // Paper mode: no live order to poll
    if (accountId === 'paper' || paperMode) return;
    try {
      const accountInfo = accountsRef.current.find(a => String(a.id) === String(accountId));
      const apiType = String(accountInfo?.apiType || accountInfo?.api_type || '').toLowerCase();
      const isRobinhoodMcp = apiType === 'robinhood-mcp';
      const isRobinhoodDirect = apiType === 'robinhood' || apiType === 'robinhood-api';

      let status = null;
      if (isRobinhoodDirect) {
        // The open-orders endpoint only returns working orders — if the order
        // is absent it reached a terminal state, resolved from order history.
        const resp = await apiRequest(`/api/robinhood-api/orders/${accountId}`);
        const found = (resp.data?.orders || []).find(o => String(o.orderId || o.id) === String(orderId));
        if (found) {
          status = String(found.status || '').toUpperCase();
        } else {
          const h = await apiRequest(`/api/robinhood-api/order-history/${accountId}?refresh=1`);
          const ho = (h.data?.orders || []).find(o =>
            String(o.orderId) === String(orderId) || String(o.raw?.id || '') === String(orderId));
          status = ho ? String(ho.status || '').toUpperCase() : null;
        }
      } else if (isRobinhoodMcp) {
        const resp = await apiRequest(`/api/mcp/robinhood/orders?accountId=${accountId}&refresh=1`);
        const o = (resp.data?.orders || []).find(x => String(x.orderId || x.id) === String(orderId));
        status = o ? String(o.status || o.state || '').toUpperCase() : null;
      } else {
        const response = await apiRequest(`/api/schwab-api/orders/${orderId}?accountId=${accountId}`);
        status = String(response.data?.order?.status || '').toUpperCase();
      }
      if (!status) return;

      setRollCloseDialog(prev => {
        // If order is no longer working, stop polling
        if (status && !WORKING_ORDER_STATUSES.includes(status)) {
          if (rollCloseStatusIntervalRef.current) {
            clearInterval(rollCloseStatusIntervalRef.current);
            rollCloseStatusIntervalRef.current = null;
          }
          // If rejected/cancelled, clear result success and show error
          if (['REJECTED', 'CANCELLED', 'EXPIRED', 'REPLACED'].includes(status)) {
            return {
              ...prev,
              orderStatus: status,
              result: prev.result ? { ...prev.result, success: false } : null,
              error: status === 'CANCELLED' ? 'Order was cancelled' : `Order ${status.toLowerCase()}`,
            };
          }
        }
        return { ...prev, orderStatus: status };
      });
      // Refresh working orders and positions when status changes
      if (status && !WORKING_ORDER_STATUSES.includes(status)) {
        if (fetchWorkingCloseOrdersRef.current) fetchWorkingCloseOrdersRef.current();
        setTimeout(() => fetchOpenPositions(false), 1000);
        if (fetchRecentOrdersRef.current) fetchRecentOrdersRef.current(true);
      }
    } catch (err) {
      // Silent fail on status poll errors
    }
  }, [paperMode]);

  // Symbol a leg should be quoted under. The order leg may carry a broker
  // display string (e.g. Robinhood "FAS 10/16/26 $155 Put") or an AM-settled
  // contract ("SPX  …") — quotes go to Schwab, which only resolves OSI
  // symbols, so non-OSI contract symbols get rebuilt as OCC from the parsed
  // parts while genuine OSI symbols (incl. SPX vs SPXW distinction) are kept.
  const rollCloseLegQuoteKey = (leg, underlying) => {
    if (leg.assetType === 'EQUITY') return underlying;
    if (leg.isFuturesOption && leg.quoteSymbol) return leg.quoteSymbol;
    if (leg.isClose && leg.contractSymbol) {
      const cs = String(leg.contractSymbol);
      if (/^[A-Z.]+\s*\d{6}[CP]\d{8}$/i.test(cs)) return cs;
      if (underlying && leg.expiration && leg.strike) {
        try {
          return buildOCCSymbol(underlying, leg.expiration, leg.putCall, parseFloat(leg.strike));
        } catch { /* fall through */ }
      }
      return cs;
    }
    return underlying ? buildOCCSymbol(underlying, leg.expiration, leg.putCall, parseFloat(leg.strike)) : '';
  };

  // Build OCC symbols for all legs (for quote fetching and order placement)
  const buildRollCloseLegSymbols = useCallback(() => {
    const dlg = rollCloseDialog;
    if (!dlg.underlying || dlg.legs.length === 0) return [];
    return dlg.legs.filter(leg => leg.enabled !== false).map(leg => {
      // Equity leg: use underlying symbol directly, no OCC formatting
      if (leg.assetType === 'EQUITY') {
        return { symbol: dlg.underlying, instruction: leg.instruction, quantity: parseInt(dlg.quantity) || 1, assetType: 'EQUITY' };
      }
      // Futures-option legs carry their real contract symbols — an OCC
      // rebuild would produce a symbol Schwab can't quote or accept.
      // Orders submit the raw instrument symbol ("/MS4EV26_P7600:XCME",
      // assetType OPTION per Schwab's request schema); quotes use the
      // stripped uniform symbol ("./MS4EV26P7600").
      if (leg.isFuturesOption && (leg.orderSymbol || leg.quoteSymbol)) {
        return {
          symbol: leg.orderSymbol || leg.quoteSymbol,
          quoteSymbol: leg.quoteSymbol || leg.orderSymbol,
          instruction: leg.instruction,
          quantity: parseInt(dlg.quantity) || 1,
          assetType: 'OPTION'
        };
      }
      // Close legs carry the position's actual contract symbol — e.g. an
      // AM-settled SPX monthly must close via `SPX …`, not the rebuilt SPXW
      // symbol (which would open an opposite position in a different contract).
      if (leg.isClose && leg.contractSymbol) {
        return {
          symbol: leg.contractSymbol,
          quoteSymbol: rollCloseLegQuoteKey(leg, dlg.underlying),
          instruction: leg.instruction,
          quantity: parseInt(dlg.quantity) || 1,
          assetType: 'OPTION'
        };
      }
      const sym = buildOCCSymbol(dlg.underlying, leg.expiration, leg.putCall, parseFloat(leg.strike));
      return { symbol: sym, instruction: leg.instruction, quantity: parseInt(dlg.quantity) || 1, assetType: 'OPTION' };
    });
  }, [rollCloseDialog]);

  // Apply an edit to a dialog leg. Editing identity fields (strike,
  // expiration, putCall) must invalidate stamped contract symbols —
  // buildRollCloseLegSymbols prefers leg.contractSymbol over the OCC rebuild
  // (needed for AM-settled contracts), so a stale contractSymbol would
  // silently ignore the user's edit and submit the original contract.
  const patchRollCloseLeg = (idx, patch) => {
    setRollCloseDialog(prev => {
      const legs = [...prev.legs];
      const identityChanged =
        patch.strike !== undefined || patch.expiration !== undefined || patch.putCall !== undefined;
      legs[idx] = {
        ...legs[idx],
        ...patch,
        ...(identityChanged
          ? { contractSymbol: undefined, orderSymbol: undefined, quoteSymbol: undefined }
          : {}),
      };
      return { ...prev, legs };
    });
  };

  // Calculate net mid from quotes
  const calculateRollCloseNetMid = useCallback((builtLegs, quotes) => {
    if (!builtLegs || !quotes) return null;
    let netMid = 0;
    let allFound = true;
    for (const leg of builtLegs) {
      const symKey = (leg.quoteSymbol || leg.symbol).replace(/\s+/g, '');
      const q = quotes[symKey];
      if (!q || (!q.bid && !q.ask)) {
        allFound = false;
        continue;
      }
      const isBuy = leg.instruction === 'BUY_TO_OPEN' || leg.instruction === 'BUY_TO_CLOSE';
      const roundedMid = roundSPXTo5Cents(q.mid, isBuy, q.underlyingSymbol || leg.symbol);
      netMid += isBuy ? roundedMid : -roundedMid;
    }
    if (!allFound) return null;
    return netMid;
  }, []);

  // Fetch quotes for roll/close dialog legs
  const fetchRollCloseQuotes = useCallback(async () => {
    const dlg = rollCloseDialog;
    if (!dlg.open || (dlg.result && !dlg.isModifyMode)) return;
    const builtLegs = buildRollCloseLegSymbols();
    if (builtLegs.length === 0) {
      setRollCloseQuotes({});
      return;
    }
    // Always use Schwab market data for quotes when available (same as TradeDialog)
    let quoteAccountId = dlg.accountId;
    const schwabAcc = accountsRef.current.find(
      a => a.isActive && (a.apiType || a.api_type || '').toLowerCase() === 'schwab'
    );
    if (schwabAcc) {
      quoteAccountId = schwabAcc.id;
    } else if (dlg.accountId === 'paper' || paperMode) {
      // No Schwab account available; fall back to position avg price
      const quoteMap = {};
      builtLegs.forEach(leg => {
        const qKey = leg.quoteSymbol || leg.symbol;
        const pos = openPositionsRef.current.find(p => p.symbol === leg.symbol || p.symbol === qKey);
        const avgPrice = pos?.averagePrice || 0;
        quoteMap[qKey] = { bid: avgPrice, ask: avgPrice, mid: avgPrice, last: avgPrice };
      });
      setRollCloseQuotes(quoteMap);
      return;
    }
    // Quote keys prefer quoteSymbol — futures options quote under the
    // stripped uniform symbol while their order leg carries the raw symbol.
    const symbols = builtLegs.map(l => l.quoteSymbol || l.symbol);
    // Include the underlying ticker so the dialog can show its bid/ask/mid
    if (dlg.underlying && !symbols.includes(dlg.underlying)) {
      symbols.push(dlg.underlying);
    }
    try {
      // Determine quote account type to use the correct quote endpoint
      const accountInfo = accountsRef.current.find(a => String(a.id) === String(quoteAccountId));
      const accountApiType = String(accountInfo?.apiType || accountInfo?.api_type || '').toLowerCase();

      let quoteMap = {};

      if (accountApiType === 'schwab' || quoteAccountId !== dlg.accountId) {
        // Schwab: use dedicated Schwab quote endpoint (returns a map keyed by symbol)
        const response = await apiRequest('/api/schwab-api/quotes', {
          method: 'POST',
          body: JSON.stringify({ accountId: quoteAccountId, symbols }),
        });
        quoteMap = response.data?.quotes || {};
      } else {
        // Tradier and other account types: use generic account quote endpoint
        const response = await apiRequest(`/api/accounts/${dlg.accountId}/quotes`, {
          method: 'POST',
          body: JSON.stringify({ symbols }),
        });
        const qData = response.data?.quotes?.quote || response.data?.quotes?.quotes?.quote || [];
        const arr = Array.isArray(qData) ? qData : [qData];
        arr.forEach(q => {
          if (q && q.symbol) {
            const symKey = String(q.symbol).replace(/\s+/g, '');
            const bid = parseFloat(q.bid) || 0;
            const ask = parseFloat(q.ask) || 0;
            const mid = bid && ask ? (bid + ask) / 2 : (bid || ask || 0);
            quoteMap[symKey] = { bid, ask, mid, last: parseFloat(q.last) || 0 };
          }
        });
      }
      setRollCloseQuotes(quoteMap);

      // Auto-fill price from net mid
      if (rollCloseAutoPrice && (dlg.orderType === 'LIMIT' || dlg.orderType === 'NET_DEBIT' || dlg.orderType === 'NET_CREDIT')) {
        const netMid = calculateRollCloseNetMid(builtLegs, quoteMap);
        if (netMid !== null) {
          const rounded = Math.round(Math.abs(netMid) * 100) / 100;
          setRollCloseDialog(prev => ({ ...prev, price: String(rounded) }));
        }
      }
    } catch (err) {
      // Silent fail on quote errors
    }
  }, [rollCloseDialog, buildRollCloseLegSymbols, calculateRollCloseNetMid, rollCloseAutoPrice, paperMode]);

  // Poll quotes every 2 seconds while dialog is open
  useEffect(() => {
    if (rollCloseDialog.open && (!rollCloseDialog.result || rollCloseDialog.isModifyMode)) {
      fetchRollCloseQuotes();
      rollCloseQuoteIntervalRef.current = setInterval(fetchRollCloseQuotes, 2000);
      return () => {
        if (rollCloseQuoteIntervalRef.current) {
          clearInterval(rollCloseQuoteIntervalRef.current);
          rollCloseQuoteIntervalRef.current = null;
        }
      };
    }
  }, [rollCloseDialog.open, rollCloseDialog.result, fetchRollCloseQuotes]);

  const handleRollClosePlaceOrder = async () => {
    const dlg = rollCloseDialog;
    if (!dlg.accountId) {
      setRollCloseDialog(prev => ({ ...prev, error: 'Missing account ID' }));
      return;
    }

    const builtLegs = buildRollCloseLegSymbols();
    if (builtLegs.length === 0) {
      setRollCloseDialog(prev => ({ ...prev, error: 'No valid legs' }));
      return;
    }

    setRollCloseDialog(prev => ({ ...prev, loading: true, error: null, result: null }));

    // Paper mode: route to paper trading close endpoint instead of live broker
    if (dlg.accountId === 'paper' || paperMode) {
      try {
        const closeSymbol = dlg.underlying || builtLegs[0]?.symbol || '';
        const response = await apiRequest('/api/paper-trading/close-position', {
          method: 'POST',
          body: JSON.stringify({
            symbol: closeSymbol,
            quantity: builtLegs[0]?.quantity ? Math.abs(builtLegs[0].quantity) : undefined,
            limitPrice: dlg.orderType === 'LIMIT' ? parseFloat(dlg.price) : undefined,
          }),
        });

        if (response.data?.success) {
          setRollCloseDialog(prev => ({
            ...prev,
            loading: false,
            result: {
              success: true,
              orderId: `paper-${Date.now()}`,
              message: `Paper position closed for ${closeSymbol}`,
              orderType: dlg.orderType,
              price: dlg.price,
            },
          }));
          setTimeout(() => fetchOpenPositions(false), 1000);
        } else {
          setRollCloseDialog(prev => ({
            ...prev,
            loading: false,
            error: `Paper close failed: ${response.data?.error || 'Unknown error'}`,
          }));
        }
      } catch (err) {
        setRollCloseDialog(prev => ({
          ...prev,
          loading: false,
          error: `Paper close failed: ${err.message || 'Unknown error'}`,
        }));
      }
      return;
    }

    // Determine account type to route to the correct order endpoint
    const accountInfo = accountsRef.current.find(a => String(a.id) === String(dlg.accountId));
    const accountApiType = String(accountInfo?.apiType || accountInfo?.api_type || '').toLowerCase();
    const isRobinhoodMcp = accountApiType === 'robinhood-mcp';
    const isRobinhoodDirect = accountApiType === 'robinhood' || accountApiType === 'robinhood-api';
    const placeOrderUrl = isRobinhoodMcp
      ? '/api/mcp/robinhood/orders/place'
      : isRobinhoodDirect
        ? `/api/robinhood-api/orders/place/${dlg.accountId}`
        : '/api/schwab-api/orders/place';

    try {
      let effectiveOrderType = dlg.orderType;
      let effectivePrice = (dlg.orderType === 'LIMIT' || dlg.orderType === 'NET_DEBIT' || dlg.orderType === 'NET_CREDIT') ? parseFloat(dlg.price) : undefined;

      // For multi-leg orders, always auto-determine NET_DEBIT vs NET_CREDIT from net mid
      if (builtLegs.length > 1) {
        const netMid = calculateRollCloseNetMid(builtLegs, rollCloseQuotes);
        if (netMid !== null) {
          effectiveOrderType = netMid >= 0 ? 'NET_DEBIT' : 'NET_CREDIT';
          if (rollCloseAutoPrice || effectivePrice === undefined || isNaN(effectivePrice)) {
            effectivePrice = Math.abs(Math.round(netMid * 100) / 100);
          }
        } else {
          // No quotes available — fall back to user's explicit selection or NET_DEBIT
          if (dlg.orderType !== 'NET_CREDIT') {
            effectiveOrderType = 'NET_DEBIT';
          } else {
            effectiveOrderType = 'NET_CREDIT';
          }
        }
      }

      // Multi-target mode: place separate orders for each enabled target
      if (dlg.multiTargetEnabled) {
        const enabledTargets = (dlg.exitTargets || []).filter(t => t.enabled && t.qty && t.price);
        if (enabledTargets.length === 0) {
          setRollCloseDialog(prev => ({ ...prev, loading: false, error: 'No valid exit targets enabled' }));
          return;
        }

        const results = [];
        const errors = [];
        for (const target of enabledTargets) {
          const targetQty = parseInt(target.qty) || 0;
          if (targetQty <= 0) continue;
          const targetLegs = builtLegs.map(l => ({ ...l, quantity: targetQty }));
          const targetBody = {
            accountId: dlg.accountId,
            legs: targetLegs,
            orderType: effectiveOrderType,
            price: parseFloat(target.price),
            session: 'NORMAL',
            duration: dlg.duration || 'DAY',
          };
          try {
            const resp = await apiRequest(placeOrderUrl, {
              method: 'POST',
              body: JSON.stringify(targetBody),
            });
            if (resp.data?.success) {
              results.push({ orderId: resp.data.orderId, qty: targetQty, price: target.price });
            } else {
              const detail = resp.data?.message || resp.data?.error || resp.data?.details || 'Unknown error';
              errors.push(`Target ${targetQty}@$${target.price}: ${detail}`);
            }
          } catch (targetErr) {
            errors.push(`Target ${targetQty}@$${target.price}: ${targetErr.message || 'Request failed'}`);
          }
        }

        if (results.length > 0) {
          setRollCloseDialog(prev => ({
            ...prev,
            loading: false,
            orderId: results[0].orderId,
            orderStatus: 'WORKING',
            isModifyMode: true,
            result: {
              success: true,
              orderId: results.map(r => r.orderId).join(', '),
              message: `${results.length} order(s) placed${errors.length > 0 ? `, ${errors.length} failed` : ''}`,
              orderType: effectiveOrderType,
              multiTarget: true,
              orderIds: results.map(r => r.orderId),
            },
          }));
          // Start polling order status for first order
          if (rollCloseStatusIntervalRef.current) clearInterval(rollCloseStatusIntervalRef.current);
          rollCloseStatusIntervalRef.current = setInterval(() => pollRollCloseOrderStatus(results[0].orderId, dlg.accountId), 3000);
          fetchWorkingCloseOrders();
          setTimeout(() => fetchWorkingCloseOrders(), 3000);
          setTimeout(() => fetchOpenPositions(false), 2000);
          if (fetchRecentOrdersRef.current) fetchRecentOrdersRef.current(true);
        } else {
          setRollCloseDialog(prev => ({
            ...prev,
            loading: false,
            error: `All orders failed: ${errors.join('; ')}`,
          }));
        }
        return;
      }

      const body = {
        accountId: dlg.accountId,
        legs: builtLegs,
        orderType: effectiveOrderType,
        price: effectivePrice,
        session: 'NORMAL',
        duration: dlg.duration || 'DAY',
      };

      const response = await apiRequest(placeOrderUrl, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (response.data?.success) {
        const orderId = response.data.orderId;
        setRollCloseDialog(prev => ({
          ...prev,
          loading: false,
          orderId,
          orderStatus: 'WORKING',
          isModifyMode: true,
          result: {
            success: true,
            orderId,
            message: 'Order placed successfully',
            orderType: effectiveOrderType,
            price: effectivePrice,
          },
        }));
        // Start polling order status
        if (rollCloseStatusIntervalRef.current) clearInterval(rollCloseStatusIntervalRef.current);
        rollCloseStatusIntervalRef.current = setInterval(() => pollRollCloseOrderStatus(orderId, dlg.accountId), 3000);
        // Refresh working orders — immediate + delayed retry (Schwab may take 1-3s to show WORKING)
        fetchWorkingCloseOrders();
        setTimeout(() => fetchWorkingCloseOrders(), 3000);
        setTimeout(() => fetchOpenPositions(false), 2000);
        // Refresh recent orders for New Trades section
        if (fetchRecentOrdersRef.current) fetchRecentOrdersRef.current(true);
      } else {
        // Capture all possible error fields from Schwab
        const detail = response.data?.message || response.data?.error || response.data?.details ||
          response.data?.errors?.[0]?.message || 'Unknown error';
        const status = response.data?.status ? ` (HTTP ${response.data.status})` : '';
        setRollCloseDialog(prev => ({
          ...prev,
          loading: false,
          error: `Order rejected: ${detail}${status}`,
        }));
      }
    } catch (err) {
      const detail = err.response?.data?.error || err.response?.data?.message ||
        err.response?.data?.details || err.response?.data?.errors?.[0]?.message ||
        err.message || 'Unknown error';
      const status = err.response?.status ? ` (HTTP ${err.response.status})` : '';
      setRollCloseDialog(prev => ({
        ...prev,
        loading: false,
        error: `Order rejected: ${detail}${status}`,
      }));
    }
  };

  // Modify existing roll/close order (or place a new one if the old one is no longer WORKING)
  const handleRollCloseModifyOrder = async () => {
    const dlg = rollCloseDialog;
    if (!dlg.accountId || (!dlg.result?.orderId && !dlg.orderId)) return;

    // Paper mode: no live order to modify, just re-place the close
    if (dlg.accountId === 'paper' || paperMode) {
      handleRollClosePlaceOrder();
      return;
    }

    const builtLegs = buildRollCloseLegSymbols();
    if (builtLegs.length === 0) return;

    setRollCloseDialog(prev => ({ ...prev, loading: true, error: null }));

    // Determine account type to route to the correct order endpoint
    const accountInfo = accountsRef.current.find(a => String(a.id) === String(dlg.accountId));
    const accountApiType = String(accountInfo?.apiType || accountInfo?.api_type || '').toLowerCase();
    const isRobinhoodMcp = accountApiType === 'robinhood-mcp';
    const isRobinhoodDirect = accountApiType === 'robinhood' || accountApiType === 'robinhood-api';
    const placeOrderUrl = isRobinhoodMcp
      ? '/api/mcp/robinhood/orders/place'
      : isRobinhoodDirect
        ? `/api/robinhood-api/orders/place/${dlg.accountId}`
        : '/api/schwab-api/orders/place';

    try {
      let effectiveOrderType = dlg.orderType;
      let effectivePrice = (dlg.orderType === 'LIMIT' || dlg.orderType === 'NET_DEBIT' || dlg.orderType === 'NET_CREDIT') ? parseFloat(dlg.price) : undefined;

      if (builtLegs.length > 1) {
        const netMid = calculateRollCloseNetMid(builtLegs, rollCloseQuotes);
        if (netMid !== null) {
          effectiveOrderType = netMid >= 0 ? 'NET_DEBIT' : 'NET_CREDIT';
          if (rollCloseAutoPrice || effectivePrice === undefined || isNaN(effectivePrice)) {
            effectivePrice = Math.abs(Math.round(netMid * 100) / 100);
          }
        }
      }

      const body = {
        accountId: dlg.accountId,
        legs: builtLegs,
        orderType: effectiveOrderType,
        price: effectivePrice,
        session: 'NORMAL',
        duration: dlg.duration || 'DAY',
      };

      const modifyOrderId = dlg.result?.orderId || dlg.orderId;
      // If the order is still working, modify it. Otherwise, place a new order.
      // For Robinhood anything not definitively terminal is treated as
      // cancellable — an unrecognized open state must not skip the cancel step
      // or the original stays live and the replacement duplicates the order.
      const orderStatusNorm = String(dlg.orderStatus || '').toUpperCase();
      const isTerminal = TERMINAL_ORDER_STATUSES.includes(orderStatusNorm);
      const canModify = WORKING_ORDER_STATUSES.includes(orderStatusNorm) ||
        ((isRobinhoodMcp || isRobinhoodDirect) && !isTerminal);

      if (canModify && !isRobinhoodMcp && !isRobinhoodDirect) {
        // Schwab: modify the existing working order in-place
        const response = await apiRequest(`/api/schwab-api/orders/${modifyOrderId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });

        if (response.data?.success) {
          setRollCloseDialog(prev => ({
            ...prev,
            loading: false,
            orderStatus: 'WORKING',
            result: {
              ...prev.result,
              orderId: modifyOrderId,
              success: true,
              message: 'Order modified successfully',
              orderType: effectiveOrderType,
              price: effectivePrice,
            },
            error: null,
          }));
          fetchWorkingCloseOrders();
          setTimeout(() => fetchOpenPositions(false), 2000);
          if (fetchRecentOrdersRef.current) fetchRecentOrdersRef.current(true);
        } else {
          const detail = response.data?.message || response.data?.error || response.data?.details || 'Unknown error';
          setRollCloseDialog(prev => ({ ...prev, loading: false, error: `Modify failed: ${detail}` }));
        }
      } else if (canModify && (isRobinhoodMcp || isRobinhoodDirect)) {
        // Robinhood has no modify endpoint — cancel the old order, then place a new one
        const cancelUrl = isRobinhoodMcp
          ? `/api/mcp/robinhood/orders/${modifyOrderId}/cancel`
          : `/api/robinhood-api/orders/${dlg.accountId}/cancel/${modifyOrderId}`;
        const cancelBody = {
          accountId: dlg.accountId,
          assetType: body.legs?.some(l => l.assetType === 'OPTION') ? 'OPTION' : 'EQUITY',
        };
        const cancelResponse = await apiRequest(cancelUrl, {
          method: 'POST',
          body: JSON.stringify(cancelBody),
        });
        // A failed cancel leaves the original order working — placing the
        // replacement anyway creates duplicate fills (e.g. extra spreads).
        if (!cancelResponse.data?.success) {
          const detail = cancelResponse.data?.error || cancelResponse.data?.message || 'Cancel request failed';
          setRollCloseDialog(prev => ({
            ...prev,
            loading: false,
            error: `Modify aborted: could not cancel the original order (${detail}). No new order was placed.`,
          }));
          return;
        }
        const response = await apiRequest(placeOrderUrl, {
          method: 'POST',
          body: JSON.stringify(body),
        });

        if (response.data?.success) {
          const newOrderId = response.data.orderId;
          setRollCloseDialog(prev => ({
            ...prev,
            loading: false,
            orderId: newOrderId,
            orderStatus: 'WORKING',
            isModifyMode: true,
            result: {
              success: true,
              orderId: newOrderId,
              message: 'New order placed successfully',
              orderType: effectiveOrderType,
              price: effectivePrice,
            },
            error: null,
          }));
          // Start polling order status
          if (rollCloseStatusIntervalRef.current) clearInterval(rollCloseStatusIntervalRef.current);
          rollCloseStatusIntervalRef.current = setInterval(() => pollRollCloseOrderStatus(newOrderId, dlg.accountId), 3000);
          fetchWorkingCloseOrders();
          setTimeout(() => fetchWorkingCloseOrders(), 3000);
          setTimeout(() => fetchOpenPositions(false), 2000);
          if (fetchRecentOrdersRef.current) fetchRecentOrdersRef.current(true);
        } else {
          const detail = response.data?.message || response.data?.error || response.data?.details || 'Unknown error';
          setRollCloseDialog(prev => ({ ...prev, loading: false, error: `Order failed: ${detail}` }));
        }
      } else {
        // Order is no longer WORKING — place a new order with the current parameters
        const response = await apiRequest(placeOrderUrl, {
          method: 'POST',
          body: JSON.stringify(body),
        });

        if (response.data?.success) {
          const newOrderId = response.data.orderId;
          setRollCloseDialog(prev => ({
            ...prev,
            loading: false,
            orderId: newOrderId,
            orderStatus: 'WORKING',
            isModifyMode: true,
            result: {
              success: true,
              orderId: newOrderId,
              message: 'Order placed successfully',
              orderType: effectiveOrderType,
              price: effectivePrice,
            },
            error: null,
          }));
          if (rollCloseStatusIntervalRef.current) clearInterval(rollCloseStatusIntervalRef.current);
          rollCloseStatusIntervalRef.current = setInterval(() => pollRollCloseOrderStatus(newOrderId, dlg.accountId), 3000);
          fetchWorkingCloseOrders();
          setTimeout(() => fetchWorkingCloseOrders(), 3000);
          setTimeout(() => fetchOpenPositions(false), 2000);
          if (fetchRecentOrdersRef.current) fetchRecentOrdersRef.current(true);
        } else {
          const detail = response.data?.message || response.data?.error || response.data?.details || 'Unknown error';
          setRollCloseDialog(prev => ({ ...prev, loading: false, error: `Order failed: ${detail}` }));
        }
      }
    } catch (err) {
      const detail = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown error';
      setRollCloseDialog(prev => ({ ...prev, loading: false, error: `Modify failed: ${detail}` }));
    }
  };

  // Cancel existing roll/close order (from dialog)
  const handleRollCloseCancelOrder = async () => {
    const dlg = rollCloseDialog;
    if (!dlg.accountId || !dlg.orderId) return;

    // Paper mode: no live order to cancel, just close dialog
    if (dlg.accountId === 'paper' || paperMode) {
      setRollCloseDialog(prev => ({ ...prev, loading: false, open: false, orderId: null, result: null }));
      return;
    }

    setRollCloseDialog(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Determine if this is a Robinhood account to route to the correct cancel endpoint
      const accountInfo = accountsRef.current.find(a => String(a.id) === String(dlg.accountId));
      const accountApiType = String(accountInfo?.apiType || accountInfo?.api_type || '').toLowerCase();

      // Try to find the order so we can pass assetType for the direct Robinhood API
      const order = [...recentOrdersRef.current, ...workingCloseOrders].find(o =>
        String(o.orderId) === String(dlg.orderId) &&
        String(o._accountId || o.accountId) === String(dlg.accountId)
      );
      const assetType = order?.assetType || (order?.raw?.assetType) || '';

      let response;
      if (accountApiType === 'robinhood-mcp') {
        response = await apiRequest(`/api/mcp/robinhood/orders/${dlg.orderId}/cancel`, {
          method: 'POST',
          body: JSON.stringify({ accountId: dlg.accountId }),
        });
      } else if (accountApiType === 'robinhood' || accountApiType === 'robinhood-api') {
        response = await apiRequest(`/api/robinhood-api/orders/${dlg.accountId}/cancel/${dlg.orderId}`, {
          method: 'POST',
          body: JSON.stringify({ accountId: dlg.accountId, assetType }),
        });
      } else {
        response = await apiRequest(`/api/schwab-api/orders/${dlg.orderId}`, {
          method: 'DELETE',
          body: JSON.stringify({ accountId: dlg.accountId }),
        });
      }

      if (response.data?.success || response.data?.message?.includes('success')) {
        // Stop status polling
        if (rollCloseStatusIntervalRef.current) {
          clearInterval(rollCloseStatusIntervalRef.current);
          rollCloseStatusIntervalRef.current = null;
        }
        setRollCloseDialog(prev => ({
          ...prev,
          loading: false,
          orderStatus: 'CANCELLED',
          result: prev.result ? { ...prev.result, success: false } : null,
          error: 'Order was cancelled',
        }));
        fetchWorkingCloseOrders();
        setTimeout(() => fetchOpenPositions(false), 2000);
        if (fetchRecentOrdersRef.current) fetchRecentOrdersRef.current(true);
      } else {
        const detail = response.data?.message || response.data?.error || 'Unknown error';
        setRollCloseDialog(prev => ({ ...prev, loading: false, error: `Cancel failed: ${detail}` }));
      }
    } catch (err) {
      const detail = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown error';
      setRollCloseDialog(prev => ({ ...prev, loading: false, error: `Cancel failed: ${detail}` }));
    }
  };

  // Cancel order from position row (with confirmation)
  const handleCancelOrderFromRow = async () => {
    const { orderId, accountId } = cancelOrderConfirm;
    if (!orderId || !accountId) return;

    try {
      // Determine if this is a Robinhood account to route to the correct cancel endpoint
      const accountInfo = accountsRef.current.find(a => String(a.id) === String(accountId));
      const accountApiType = String(accountInfo?.apiType || accountInfo?.api_type || '').toLowerCase();

      // Try to find the order so we can pass assetType for the direct Robinhood API
      const order = [...recentOrdersRef.current, ...workingCloseOrders].find(o =>
        String(o.orderId) === String(orderId) &&
        String(o._accountId || o.accountId) === String(accountId)
      );
      const assetType = order?.assetType || (order?.raw?.assetType) || '';

      let response;
      if (accountApiType === 'robinhood-mcp') {
        response = await apiRequest(`/api/mcp/robinhood/orders/${orderId}/cancel`, {
          method: 'POST',
          body: JSON.stringify({ accountId }),
        });
      } else if (accountApiType === 'robinhood' || accountApiType === 'robinhood-api') {
        response = await apiRequest(`/api/robinhood-api/orders/${accountId}/cancel/${orderId}`, {
          method: 'POST',
          body: JSON.stringify({ accountId, assetType }),
        });
      } else {
        response = await apiRequest(`/api/schwab-api/orders/${orderId}`, {
          method: 'DELETE',
          body: JSON.stringify({ accountId }),
        });
      }

      if (response.data?.success || response.data?.message?.includes('success')) {
        setCancelOrderConfirm({ open: false, orderId: null, accountId: null, positionLabel: '' });
        fetchWorkingCloseOrders();
        setTimeout(() => fetchOpenPositions(false), 1000);
        setTimeout(() => fetchRecentOrdersRef.current && fetchRecentOrdersRef.current(true), 1000);
      } else {
        const detail = response.data?.message || response.data?.error || 'Unknown error';
        setCancelOrderConfirm(prev => ({ ...prev, open: false, error: detail }));
      }
    } catch (err) {
      const detail = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown error';
      setCancelOrderConfirm(prev => ({ ...prev, open: false, error: detail }));
    }
  };

  // Reopen dialog for modifying an existing working order
  const handleModifyOrderFromRow = (positionOrGroup, mode) => {
    // Find the working order for this position
    let workingOrder = null;
    let underlying = '';
    let accountId = null;
    let accountName = '';

    if (positionOrGroup.isSpread) {
      underlying = positionOrGroup.underlying || '';
      accountId = positionOrGroup.accountId;
      accountName = positionOrGroup.accountName || '';
      // Find first working order matching any leg
      for (const leg of positionOrGroup.legs) {
        const info = getClosingOrderInfo(leg.accountId, leg.symbol);
        if (info.hasClosing && info.workingOrders.length > 0) {
          workingOrder = info.workingOrders[0];
          break;
        }
      }
    } else {
      const position = positionOrGroup;
      underlying = position.optionInfo?.underlying || '';
      accountId = position.accountId || position.account_id;
      accountName = position.accountName || '';
      const info = getClosingOrderInfo(position.accountId, position.symbol);
      if (info.hasClosing && info.workingOrders.length > 0) {
        workingOrder = info.workingOrders[0];
      }
    }

    if (!workingOrder) return;

    // Rebuild legs from the original position/group
    const closeLegs = [];
    if (positionOrGroup.isSpread) {
      positionOrGroup.legs.forEach(leg => {
        const info = leg.optionInfo;
        if (!info || !info.isOption) return;
        const isLong = leg.quantity > 0;
        closeLegs.push({
          expiration: yymmddToISO(info.expiration),
          strike: String(info.strike || ''),
          putCall: info.type || 'C',
          instruction: isLong ? 'SELL_TO_CLOSE' : 'BUY_TO_CLOSE',
          isClose: true,
          isNew: false,
          enabled: true,
          isFuturesOption: info.isFuturesOption || false,
          quoteSymbol: info.quoteSymbol || null,
          orderSymbol: info.orderSymbol || null,
          contractSymbol: leg.symbol || null,
        });
      });
    } else {
      const position = positionOrGroup;
      const info = position.optionInfo;
      if (!info || !info.isOption) return;
      const isLong = position.quantity > 0;
      closeLegs.push({
        expiration: yymmddToISO(info.expiration),
        strike: String(info.strike || ''),
        putCall: info.type || 'C',
        instruction: isLong ? 'SELL_TO_CLOSE' : 'BUY_TO_CLOSE',
        isClose: true,
        isNew: false,
        enabled: true,
        isFuturesOption: info.isFuturesOption || false,
        quoteSymbol: info.quoteSymbol || null,
        orderSymbol: info.orderSymbol || null,
        contractSymbol: position.symbol || null,
      });
    }

    const newLegs = [];
    if (mode === 'roll') {
      closeLegs.forEach(closeLeg => {
        const isBuyClose = closeLeg.instruction === 'BUY_TO_CLOSE';
        newLegs.push({
          expiration: addWeekToDate(closeLeg.expiration),
          strike: closeLeg.strike,
          putCall: closeLeg.putCall,
          instruction: isBuyClose ? 'SELL_TO_OPEN' : 'BUY_TO_OPEN',
          isClose: false,
          isNew: true,
          enabled: true,
        });
      });
    }

    setRollCloseDialog({
      open: true,
      mode,
      underlying,
      accountId,
      accountName,
      legs: [...closeLegs, ...newLegs],
      orderType: 'LIMIT',
      price: String(workingOrder.price || ''),
      quantity: Math.abs(workingOrder.quantity || 1),
      multiTargetEnabled: false,
      exitTargets: [
        { qty: '', price: '', enabled: true },
        { qty: '', price: '', enabled: true },
        { qty: '', price: '', enabled: true },
        { qty: '', price: '', enabled: true },
      ],
      loading: false,
      error: null,
      result: null,
      orderId: workingOrder.orderId,
      orderStatus: 'WORKING',
      isModifyMode: true,
    });
    setRollCloseQuotes({});
    setRollCloseAutoPrice(false); // Manual mode when modifying
  };

  // Modify a working opening or exit order from the New Trades section
  const handleModifyNewTradeOrder = (order) => {
    const orderLegs = order.orderLegCollection || [];
    if (orderLegs.length === 0) return;

    const isExitOrder = !!order._isExitOrder;

    // Extract underlying from first option leg
    let ul = '';
    for (const leg of orderLegs) {
      const sym = leg.instrument?.symbol || '';
      const info = parseOptionSymbol(sym);
      if (info.isOption) { ul = info.underlying; break; }
      ul = sym;
    }

    // Build legs for the Roll/Close dialog
    // Exit orders have closing instructions (BUY_TO_CLOSE / SELL_TO_CLOSE)
    const legs = orderLegs.map(l => {
      const sym = l.instrument?.symbol || '';
      const info = parseOptionSymbol(sym);
      const isClose = isExitOrder || l.instruction === 'BUY_TO_CLOSE' || l.instruction === 'SELL_TO_CLOSE';
      if (info.isOption) {
        return {
          expiration: yymmddToISO(info.expiration),
          strike: String(info.strike || ''),
          putCall: info.type || 'C',
          instruction: l.instruction || (isClose ? 'BUY_TO_CLOSE' : 'BUY_TO_OPEN'),
          isClose,
          isNew: true,
          enabled: true,
        };
      }
      return {
        assetType: 'EQUITY',
        symbol: sym,
        expiration: '',
        strike: '',
        putCall: 'C',
        instruction: l.instruction || (isClose ? 'SELL' : 'BUY'),
        isClose,
        isNew: true,
        enabled: true,
      };
    });

    setRollCloseDialog({
      open: true,
      mode: isExitOrder ? 'close' : 'new',
      underlying: ul,
      accountId: order._accountId,
      accountName: order._accountName || '',
      legs,
      orderType: order.orderType || 'LIMIT',
      price: String(order.price || ''),
      quantity: Math.abs(orderLegs[0]?.quantity || 1),
      multiTargetEnabled: false,
      exitTargets: [
        { qty: '', price: '', enabled: true },
        { qty: '', price: '', enabled: true },
        { qty: '', price: '', enabled: true },
        { qty: '', price: '', enabled: true },
      ],
      loading: false,
      error: null,
      result: null,
      orderId: order.orderId,
      orderStatus: String(order.status || 'WORKING').toUpperCase(),
      isModifyMode: true,
      isExitOrder,
    });
    setRollCloseQuotes({});
    setRollCloseAutoPrice(false);
  };


  // Open-trade tags keyed by account|symbol — live broker positions carry no
  // trade ids, so their tags are looked up from the open trades behind them.
  const openTradeTagsByKey = useMemo(() => {
    const map = new Map();
    const addKey = (key, tags) => {
      if (!key || !Array.isArray(tags) || tags.length === 0) return;
      if (!map.has(key)) map.set(key, new Set());
      tags.forEach(t => t && map.get(key).add(t));
    };
    allTradesForTags.forEach(t => {
      if (t.closingDate || t.closing_date) return;
      if (t._hideOpeningLeg || t.isMatched === true || t.is_matched === true) return;
      const acc = t.accountId || t.account_id;
      if (!acc) return;
      addKey(`${acc}|${contractTagKey(t.originalSymbol || t.original_symbol)}`, t.tags);
      addKey(`${acc}|${contractTagKey(t.symbol)}`, t.tags);
    });
    return map;
  }, [allTradesForTags]);

  // Tags for one entity via the open-trade lookup (legs handled by getAllTags).
  const lookupPositionTags = (e) => {
    if (!e) return [];
    const acc = e.accountId || e.account_id;
    const out = new Set();
    [e.originalSymbol, e.original_symbol, e.symbol, e.position?.symbol, e.position?.original_symbol, e.position?.originalSymbol].forEach(sym => {
      const s = openTradeTagsByKey.get(`${acc}|${contractTagKey(sym)}`);
      if (s) s.forEach(t => out.add(t));
    });
    return [...out];
  };

  // Full tag set for display/filtering: entity's own tags + nested trades/legs
  // + live-position lookup against open trades.
  const getAllTags = (entity) => collectEntityTags(entity, lookupPositionTags);

  // Displayed tags are suppressed to PeakBot only; filtering/editing still sees
  // the full set via getAllTags.
  const getVisibleTags = (entity) => getAllTags(entity).filter(t => /peakbot/i.test(String(t)));

  // Distinct leg strikes for an open spread group, ascending — "7665/7670/7755/7760"
  const groupLegStrikeSuffix = (group) => {
    const seen = new Set();
    (group?.legs || []).forEach(leg => {
      const s = leg?.optionInfo?.strike ?? parseFloat(leg?.strike_price ?? leg?.strike);
      if (Number.isFinite(s) && s > 0) seen.add(s);
    });
    return [...seen].sort((a, b) => a - b).map(s => (s % 1 === 0 ? String(s) : s.toFixed(2))).join('/');
  };

  // Tag filter: include = union match (any selected tag present); exclude =
  // reject when any excluded tag is present. A tag in both lists excludes.
  const matchesTagFilter = (entity) =>
    entityMatchesTagFilter(entity, tagFilter, tagFilterExclude, lookupPositionTags);

  // Every tag currently present on open positions or closed trades — the
  // option list for the tag filter dropdown.
  const allTags = useMemo(() => {
    const s = new Set();
    groupedPositions.forEach(g => getAllTags(g).forEach(t => s.add(t)));
    symbolData.forEach(r => getEntityTags(r).forEach(t => s.add(t)));
    return [...s].sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedPositions, symbolData, openTradeTagsByKey]);

  // Underlyings that have BOTH an open stock position and an open option
  // position — backs the 'options-stocks' Position Type filter value.
  const symbolsWithStockAndOption = useMemo(() => {
    const withStock = new Set();
    const withOption = new Set();
    (groupedPositions || []).forEach(g => {
      const sym = String(g.underlying || g.symbol || g.position?.symbol || '').toUpperCase();
      if (!sym) return;
      if (g.groupType === 'option') withOption.add(sym);
      else withStock.add(sym);
    });
    return new Set([...withOption].filter(s => withStock.has(s)));
  }, [groupedPositions]);

  // Comprehensive filter function for positions
  const applyFilters = (positions) => {
    const { include: incTerms, exclude: excTerms } = parseSymbolFilter(symbolFilter);
    return positions.filter(position => {
      // Symbol filter (include / exclude)
      if (incTerms.length > 0 || excTerms.length > 0) {
        const sym = (position.underlying || position.symbol || '').toLowerCase();
        if (excTerms.some(t => sym.includes(t))) return false;
        if (incTerms.length > 0 && !incTerms.some(t => sym.includes(t))) return false;
      }

      // Tag filter (union match)
      if (!matchesTagFilter(position)) return false;

      // Open date range filter
      if (openDateStart || openDateEnd) {
        const openDate = (position.openDate || position.tradeDate || '').slice(0, 10);
        if (openDate) {
          if (openDateStart && openDate < openDateStart) return false;
          if (openDateEnd && openDate > openDateEnd) return false;
        }
      }

      // Position type filter - handle multi-select
      if (positionFilter && !positionFilter.includes('all')) {
        const isOption = position.groupType === 'option';
        const isStock = position.groupType === 'equity' || position.groupType === 'etf' || position.groupType === 'stock' || position.groupType === 'cash';
        const isDividend = position.positionType === 'dividend' || position.type === 'dividend' ||
          (position.tradeType || '').toLowerCase() === 'dividend' ||
          (position.trade_type || '').toLowerCase() === 'dividend';

        const matchesOption = positionFilter.includes('options') && isOption;
        const matchesStock = positionFilter.includes('stocks') && isStock;
        const matchesDividend = positionFilter.includes('dividends') && isDividend;
        const matchesBoth = positionFilter.includes('options-stocks') &&
          symbolsWithStockAndOption.has(String(position.underlying || position.symbol || position.position?.symbol || '').toUpperCase());

        if (!matchesOption && !matchesStock && !matchesDividend && !matchesBoth) return false;
      }

      // Strategy filter
      if (strategyFilter && strategyFilter !== 'all') {
        const spreadType = position.spreadType || '';
        const isEquity = position.groupType === 'equity' || position.groupType === 'stock' || position.groupType === 'cash';
        const isSingleOption = position.groupType === 'option' && !spreadType;

        if (strategyFilter === 'Equity') {
          if (!isEquity) return false;
        } else if (strategyFilter === 'Futures Option') {
          if (!position.isFuturesOption && !/^futures/i.test(position.strategyType || '')) return false;
        } else if (strategyFilter === 'Dividend') {
          if (position.positionType !== 'dividend' && position.type !== 'dividend' &&
              (position.tradeType || '').toLowerCase() !== 'dividend') return false;
        } else if (['Short Call', 'Long Call', 'Short Put', 'Long Put'].includes(strategyFilter)) {
          if (!isSingleOption) return false;
          // Match by strategyType if available, otherwise by spreadType
          const stratType = position.strategyType || spreadType || '';
          if (!stratType.includes(strategyFilter)) return false;
        } else {
          // Check for specific strategy match
          if (!spreadType.includes(strategyFilter)) return false;
        }
      }

      return true;
    });
  };

  // Resolve the current group-selection keys to concrete legs / manual groups.
  // Keys: `leg:<accountId>:<symbol>` (individual option row),
  //       `spread:<group.id>` (auto spread row -> all of its legs),
  //       `mgroup:<groupId>` (persisted manual group -> for ungroup).
  const getGroupSelectionInfo = () => {
    const legs = [];
    const manualGroupIds = [];
    if (selectedGroupLegs.size > 0) {
      const rows = applyFilters(groupedPositions);
      rows.forEach(g => {
        if (g.groupType !== 'option') return;
        if (g.isManualGroup) {
          if (selectedGroupLegs.has(`mgroup:${g.groupId}`)) manualGroupIds.push(g.groupId);
          return;
        }
        if (g.isSpread) {
          if (selectedGroupLegs.has(`spread:${g.id}`)) {
            (g.legs || []).forEach(l => legs.push({
              symbol: String(l.symbol || '').trim(),
              quantity: l.quantity,
              accountId: String(l.accountId || g.accountId || ''),
              expiration: g.expiration || ''
            }));
          }
          return;
        }
        const pos = g.position;
        if (pos && pos.optionInfo?.isOption) {
          const acc = String(g.accountId || pos.accountId || pos.account_id || '');
          const key = `leg:${acc}:${String(pos.symbol || '').trim()}`;
          if (selectedGroupLegs.has(key)) {
            legs.push({
              symbol: String(pos.symbol).trim(),
              quantity: pos.quantity,
              accountId: acc,
              expiration: pos.optionInfo?.expiration || ''
            });
          }
        }
      });
    }
    const accountIds = [...new Set(legs.map(l => l.accountId))];
    const expirations = [...new Set(legs.map(l => String(l.expiration || '')))];
    const canCreateGroup = manualGroupIds.length === 0 && legs.length >= 2 && legs.length <= 4 && accountIds.length === 1 && expirations.length === 1;
    const canUngroup = manualGroupIds.length === 1 && legs.length === 0;
    // Lock selections to the first selected leg's account + expiration so
    // incompatible rows can't be added to the group.
    const lockedAccountId = legs.length > 0 ? legs[0].accountId : null;
    const lockedExpiration = legs.length > 0 ? String(legs[0].expiration || '') : null;
    let hint = 'Select 2–4 option legs in the same account & expiration, or select one grouped position to ungroup.';
    if (manualGroupIds.length > 0) {
      hint = legs.length > 0
        ? 'A grouped position can only be ungrouped — deselect it to group legs.'
        : 'Selected position is grouped — click Ungroup to release its legs.';
    } else if (legs.length > 0) {
      if (accountIds.length > 1) hint = 'Selected legs span multiple accounts — groups must stay within one account.';
      else if (expirations.length > 1) hint = 'Selected legs have different expirations — all legs must share one expiration.';
      else if (legs.length > 4) hint = 'Maximum 4 legs — deselect some legs.';
      else if (legs.length === 1) hint = 'Select at least one more leg from the same account & expiration.';
      else hint = 'Ready — click Create Group to auto-detect the strategy.';
    }
    return { legs, manualGroupIds, canCreateGroup, canUngroup, lockedAccountId, lockedExpiration, hasManualSelection: manualGroupIds.length > 0, hint };
  };

  const toggleGroupSelectionKey = (key) => {
    setSelectedGroupLegs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCreateGroupFromSelection = async () => {
    const { legs, canCreateGroup } = getGroupSelectionInfo();
    if (!canCreateGroup) return;
    const accountId = legs[0].accountId;
    try {
      const resp = await apiRequest('/api/trade-groups', {
        method: 'POST',
        body: JSON.stringify({ accountId, legs: legs.map(l => ({ symbol: l.symbol, quantity: l.quantity })) })
      });
      const newGroup = resp.data?.group;
      if (newGroup) setTradeGroups(prev => [...prev, newGroup]);
      setSelectedGroupLegs(new Set());
      setGroupSelectMode(false);
    } catch (err) {
      console.error('Failed to create group:', err);
      alert(err?.response?.data?.error || 'Failed to create group');
    }
  };

  const handleUngroupFromSelection = async () => {
    const { manualGroupIds, canUngroup } = getGroupSelectionInfo();
    if (!canUngroup) return;
    const groupId = manualGroupIds[0];
    try {
      await apiRequest(`/api/trade-groups/${groupId}`, { method: 'DELETE' });
      setTradeGroups(prev => prev.filter(g => String(g.id) !== String(groupId)));
      setSelectedGroupLegs(new Set());
      setGroupSelectMode(false);
    } catch (err) {
      console.error('Failed to ungroup position:', err);
      alert('Failed to ungroup position');
    }
  };

  // Check if a strategy type matches the current strategyFilter
  const strategyMatchesFilter = (strategyType) => strategyTypeMatchesFilter(strategyType, strategyFilter);

  // Filter function for closed positions (pivot rows) — delegates to the
  // shared implementation so Dashboard produces identical filtered totals.
  const applyClosedPositionFilters = (rows) => filterClosedPositionRows(rows, {
    symbolFilter,
    strategyFilter,
    openDateStart,
    openDateEnd,
    positionFilter,
    tagFilter,
    tagFilterExclude,
    groupBy: closedGroupBy,
    tagLookup: lookupPositionTags,
    bothSymbols: symbolsWithStockAndOption,
  });


  // Group positions by expiration date for options and stocks separately
  const groupPositionsByExpiration = async (positions, byUnderlying = false, manualGroups = []) => {
    if (!positions || positions.length === 0) return [];

    // Normalize underlying symbols for quotes: convert spaces to dots for class tickers (e.g., "BRK B" -> "BRK.B")
    const formatUnderlyingForQuotes = (u) => {
      if (!u) return u;
      const trimmed = u.trim();
      const m = trimmed.match(/^([A-Z]+)\s+([A-Z])$/);
      if (m) return `${m[1]}.${m[2]}`;
      return trimmed;
    };

    // Add option info to each position — keep optionInfo already attached by the
    // broker-specific mappers (Robinhood legs carry real contract metadata that
    // parseOptionSymbol can't recover from the descriptive display symbol).
    const positionsWithOptions = positions.map(pos => ({
      ...pos,
      optionInfo: pos.optionInfo || parseOptionSymbol(pos.symbol),
      // Use trade_type as fallback for option detection, but preserve isOption if already set
      isOption: pos.isOption || parseOptionSymbol(pos.symbol).isOption ||
        (pos.trade_type || '').toLowerCase() === 'option' ||
        (pos.tradeType || '').toLowerCase() === 'option' ||
        (pos.transaction_type || '').toLowerCase() === 'option'
    }));

    // Separate options and stocks
    let optionPositions = positionsWithOptions.filter(pos => pos.isOption);
    let stockPositions = positionsWithOptions.filter(pos => !pos.isOption);

    // Apply manual trade groups: remove legs that are part of a persisted group so
    // they are rendered as a single strategy row instead of individual positions.
    const manualGroupRows = [];
    const consumedByManualGroup = new Set();
    // Quantity-proportional fields — splitting a position across groups scales
    // these so each leg's P&L reflects its own contract count.
    const QTY_PROPORTIONAL = ['cost_basis', 'marketValue', 'market_value', 'dayPL', 'unrealizedPL', 'unrealized_pl'];
    const scaleQtyFields = (target, src, take) => {
      const srcQty = Math.abs(parseFloat(src.quantity) || 0);
      const ratio = srcQty > 0 ? Math.min(1, Math.abs(take) / srcQty) : 0;
      QTY_PROPORTIONAL.forEach(f => {
        if (typeof target[f] === 'number') target[f] = src[f] * ratio;
      });
      return target;
    };
    if (manualGroups && manualGroups.length > 0) {
      manualGroups.forEach(group => {
        const accountId = String(group.accountId || group.account_id || '');
        const legs = group.legs || [];
        if (!accountId || legs.length === 0) return;

        const legBySym = new Map(legs.map(l => [String(l.symbol || '').trim(), l]));
        const pairs = [];
        optionPositions.forEach(pos => {
          const posAcc = String(pos.accountId || pos.account_id || '');
          const legDef = legBySym.get(String(pos.symbol || '').trim());
          if (posAcc === accountId && legDef && !pairs.some(p => p.legDef === legDef)) {
            pairs.push({ pos, legDef });
          }
        });
        // Only apply if all legs are currently open
        if (pairs.length !== legs.length) return;

        // The order ids shared by the group's fully-owned legs identify which
        // opening order this group represents — needed to carve the right lot
        // out of a contract shared with another strategy (e.g. an IC and a
        // PCS both long the same put).
        const groupOrderIds = new Set();
        pairs.forEach(({ pos, legDef }) => {
          const want = Math.abs(parseFloat(legDef.quantity) || 0);
          const have = Math.abs(parseFloat(pos.quantity) || 0);
          if (!want || have <= want + 1e-9) {
            (pos.orderLots || []).forEach(l => l.orderId && groupOrderIds.add(l.orderId));
          }
        });

        const matched = [];
        pairs.forEach(({ pos, legDef }) => {
          const want = parseFloat(legDef.quantity) || 0;
          const have = parseFloat(pos.quantity) || 0;
          if (want && have && Math.sign(want) === Math.sign(have) && Math.abs(have) > Math.abs(want) + 1e-9) {
            // Shared contract: only the group's share joins the group. The
            // remainder stays in normal grouping with the lot belonging to
            // this group's order carved out, so the other strategy (PCS etc.)
            // keeps its own leg.
            const carvedIds = [];
            let toRemove = Math.abs(want);
            let carvedCost = 0, remCost = 0;
            // When every covering lot carries its fill cost and the lots fully
            // cover the position, cost is attributed per-fill — the broker's
            // averagePrice can reflect only settled lots, so proportional
            // scaling would hand this leg the other strategy's premium.
            const lots = pos.orderLots || [];
            const lotsCosted = lots.length > 0 && lots.every(l => Number.isFinite(l.cost));
            const lotsCover = lotsCosted && Math.abs(lots.reduce((s, l) => s + Math.abs(l.qty), 0) - Math.abs(have)) < 1e-6;
            const ordered = [...lots].sort((a, b) =>
              (groupOrderIds.has(b.orderId) ? 1 : 0) - (groupOrderIds.has(a.orderId) ? 1 : 0));
            const remLots = [];
            for (const l of ordered) {
              const lotCosted = Number.isFinite(l.cost) && Math.abs(l.qty) > 0;
              if (toRemove > 1e-9 && Math.sign(l.qty) === Math.sign(want)) {
                const take = Math.min(Math.abs(l.qty), toRemove);
                toRemove -= take;
                if (l.orderId != null) carvedIds.push(String(l.orderId));
                if (lotCosted) carvedCost += Math.abs(l.cost) * (take / Math.abs(l.qty));
                const rem = Math.abs(l.qty) - take;
                if (rem > 1e-9) {
                  remLots.push({ ...l, qty: Math.sign(l.qty) * rem });
                  if (lotCosted) remCost += Math.abs(l.cost) * (rem / Math.abs(l.qty));
                }
              } else {
                remLots.push(l);
                if (lotCosted) remCost += Math.abs(l.cost);
              }
            }
            const legCopy = scaleQtyFields({ ...pos, quantity: want, _src: pos._src || pos }, pos, want);
            if (carvedIds.length) legCopy._orderIds = carvedIds;
            // The leg carries its own fill's date, not the contract's earliest
            const carvedLot = ordered.find(l => carvedIds.includes(String(l.orderId)) && Math.sign(l.qty) === Math.sign(want));
            if (carvedLot?.tradeDate) legCopy.openDate = carvedLot.tradeDate;
            // Always scale the remainder's proportional fields; only
            // cost_basis is overridden by the per-fill attribution.
            scaleQtyFields(pos, pos, have - want);
            if (lotsCover && carvedCost > 0) {
              legCopy.cost_basis = Math.sign(want) < 0 ? -carvedCost : carvedCost;
              pos.cost_basis = Math.sign(have) < 0 ? -remCost : remCost;
            }
            matched.push(legCopy);
            pos.quantity = have - want;
            pos.orderLots = remLots.length ? remLots : null;
          } else {
            consumedByManualGroup.add(pos);
            matched.push(pos);
          }
        });
        manualGroupRows.push({ group, matched });
      });
    }

    // Remove manually grouped legs from the positions that feed normal grouping.
    optionPositions = optionPositions.filter(pos => !consumedByManualGroup.has(pos));

    // Create a map of stock holdings for covered call detection per account
    const stockHoldings = {};
    stockPositions.forEach(pos => {
      const accountId = pos.account_id;
      if (!stockHoldings[accountId]) {
        stockHoldings[accountId] = {};
      }
      const normSymbol = formatUnderlyingForQuotes(pos.symbol);
      stockHoldings[accountId][normSymbol] = (stockHoldings[accountId][normSymbol] || 0) + (parseFloat(pos.quantity) || 0);
    });

    // Get unique underlying symbols for quote fetching (both option underlyings and stock positions)
    const optionUnderlyings = [...new Set(optionPositions.map(pos => pos.optionInfo.underlying).filter(Boolean))];
    const stockSymbols = [...new Set(stockPositions.map(pos => pos.symbol).filter(Boolean))];
    const underlyingSymbolsOriginal = [...new Set([...optionUnderlyings, ...stockSymbols])];
    let underlyingQuotes = {};

    // Fetch underlying stock quotes if we have option positions
    if (underlyingSymbolsOriginal.length > 0) {
      try {
        const list = Array.isArray(accountsRef.current) ? accountsRef.current : [];
        const schwabAccount = list.find(acc => String(acc.apiType || acc.api_type).toLowerCase() === 'schwab');

        if (schwabAccount?.id) {
          const quotesResponse = await apiRequest('/api/schwab-api/quotes', {
            method: 'POST',
            body: JSON.stringify({
              accountId: schwabAccount.id,
              symbols: underlyingSymbolsOriginal,
            }),
          });
          const quoteMap = quotesResponse.data?.quotes || {};

          underlyingSymbolsOriginal.forEach(orig => {
            const key = String(orig).replace(/\s+/g, '').toUpperCase();
            const quote = quoteMap[key] || quoteMap[orig] || Object.entries(quoteMap).find(([symbol]) => (
              String(symbol).replace(/\s+/g, '').toUpperCase() === key
            ))?.[1];

            if (quote) {
              underlyingQuotes[orig] = quote;
            }
          });
        }
      } catch (error) {
        console.error('Error fetching underlying quotes:', error);
      }
    }

    // Fallback: If no quote from API, try to find current price from stock positions
    stockPositions.forEach(pos => {
      const symbol = pos.symbol;
      if (symbol && !underlyingQuotes[symbol] && pos.currentPrice) {
        underlyingQuotes[symbol] = { last: pos.currentPrice };
      }
    });

    // Fallback: If underlying quote still missing (e.g. SPX index), extract underlyingPrice
    // from option positions — Schwab returns it in option quote responses
    optionPositions.forEach(pos => {
      const underlying = pos.optionInfo?.underlying;
      if (underlying && !underlyingQuotes[underlying] && pos.underlyingPrice) {
        underlyingQuotes[underlying] = { last: pos.underlyingPrice, change: 0, change_percentage: 0 };
      }
    });

    // Fetch earnings dates for underlying symbols from the earnings calendar
    const earningsDates = {};
    const uniqueUnderlyingSymbols = [...new Set(underlyingSymbolsOriginal)];
    if (uniqueUnderlyingSymbols.length > 0) {
      try {
        const symbolsParam = uniqueUnderlyingSymbols.map(encodeURIComponent).join(',');
        const response = await apiRequest(`/api/earnings?symbols=${symbolsParam}`);
        const earnings = response.data?.earnings || {};
        Object.keys(earnings).forEach((symbol) => {
          if (earnings[symbol]) {
            earningsDates[symbol] = earnings[symbol];
          }
        });
      } catch (error) {
        console.warn('Failed to fetch earnings dates:', error);
      }
    }

    const result = [];

    // Group options by expiration date
    const optionsByExpiration = {};
    optionPositions.forEach(pos => {
      const expKey = pos.optionInfo.expiration || 'Unknown';
      if (!optionsByExpiration[expKey]) {
        optionsByExpiration[expKey] = [];
      }
      optionsByExpiration[expKey].push(pos);
    });

    // Add option groups sorted by expiration date
    const sortedExpirations = Object.keys(optionsByExpiration).sort();
    sortedExpirations.forEach(expiration => {
      const positions = optionsByExpiration[expiration];

      // Group by underlying AND account within each expiration
      const underlyingGroups = {};
      positions.forEach(pos => {
        const underlying = pos.optionInfo.underlying;
        const accountId = pos.accountId || pos.account_id;
        const groupKey = `${underlying}_${accountId}`;
        if (!underlyingGroups[groupKey]) {
          underlyingGroups[groupKey] = [];
        }
        underlyingGroups[groupKey].push(pos);
      });

      // Process each underlying group for spreads.
      // Sub-group by openDate so that spreads opened on different days (but
      // same underlying/expiration) are detected individually instead of being
      // lumped together as a "Multi-Leg Position".
      Object.entries(underlyingGroups).forEach(([groupKey, underlyingPositions]) => {
        const [underlying, accountId] = groupKey.split('_');

        // Bucket by the opening broker order when the DB has it: two spreads
        // opened the same day on the same chain (e.g. two iron condors sharing
        // a leg) stay separate groups instead of being fused. A position whose
        // lots exactly cover its quantity is split into one pseudo-leg per
        // order; legs without order info fall back to open-date grouping.
        const buckets = new Map();
        const pushToBucket = (key, dateKey, p) => {
          if (!buckets.has(key)) buckets.set(key, { dateKey, positions: [] });
          buckets.get(key).positions.push(p);
        };
        const splitLeg = (pos, take, lot) => {
          const leg = scaleQtyFields({ ...pos, quantity: take, _src: pos }, pos, take);
          // The broker order this slice came from — shown in the Order # column
          // so legs grouped under a wrong order are visible at a glance.
          if (lot?.orderId) leg._orderIds = [String(lot.orderId)];
          // Show the lot's own fill date, not the contract's earliest open
          if (lot?.tradeDate) leg.openDate = lot.tradeDate;
          // Prefer the fill's own cost when known — two orders can share a
          // contract at different prices, so a pro-rated slice misattributes
          // premium. Trade `cost` is unsigned; sign it by leg direction.
          if (lot && Number.isFinite(lot.cost) && Math.abs(lot.qty) > 0) {
            leg.cost_basis = Math.sign(take) < 0
              ? -Math.abs(lot.cost) * (Math.abs(take) / Math.abs(lot.qty))
              : Math.abs(lot.cost) * (Math.abs(take) / Math.abs(lot.qty));
          }
          return leg;
        };
        underlyingPositions.forEach(pos => {
          const posQty = parseFloat(pos.quantity) || 0;
          const lots = (pos.orderLots || []).filter(l => l.orderId && Math.abs(l.qty) > 0);
          const lotSum = lots.reduce((s, l) => s + l.qty, 0);
          const toNodate = (p) => {
            const d = p.openDate || p.tradeDate || 'unknown';
            pushToBucket(`nodate:${d}`, d, p);
          };
          if (lots.length === 0) { toNodate(pos); return; }
          if (Math.abs(lotSum - posQty) < 1e-6) {
            lots.forEach(l => pushToBucket(`order:${l.orderId}`, null, splitLeg(pos, l.qty, l)));
            return;
          }
          // Lots exceed or fall short of the broker quantity (partial closes
          // not yet matched in the DB). Allocate the position across lots
          // newest-first — FIFO close matching retires the oldest fills — and
          // leave any uncovered remainder to date grouping.
          if (Math.sign(lotSum) !== Math.sign(posQty)) { toNodate(pos); return; }
          let remaining = posQty;
          [...lots].sort((a, b) => String(b.tradeDate || '').localeCompare(String(a.tradeDate || '')))
            .forEach(l => {
              const take = Math.sign(posQty) * Math.min(Math.abs(remaining), Math.abs(l.qty));
              if (Math.abs(take) < 1e-6) return;
              pushToBucket(`order:${l.orderId}`, null, splitLeg(pos, take, l));
              remaining -= take;
            });
          if (Math.abs(remaining) > 1e-6) toNodate(splitLeg(pos, remaining));
        });

        // A contract filled by multiple orders lands in the same bucket as
        // several pseudo-legs (one per lot). Merge them back into a single leg
        // so the group renders one row per contract and spread detection sees
        // distinct strikes rather than duplicated rows faking a wider pattern
        // (e.g. 2×short put + 2×short call reading as an "Iron Condor").
        const mergeContractLegs = (positions) => {
          const byContract = new Map();
          positions.forEach(p => {
            const key = `${p.accountId || p.account_id || ''}|${String(p.symbol || '').trim()}`;
            const cur = byContract.get(key);
            if (!cur) { byContract.set(key, { ...p, _parts: [p] }); return; }
            cur.quantity = (parseFloat(cur.quantity) || 0) + (parseFloat(p.quantity) || 0);
            QTY_PROPORTIONAL.forEach(f => {
              if (typeof p[f] === 'number') cur[f] = (typeof cur[f] === 'number' ? cur[f] : 0) + p[f];
            });
            cur._parts.push(p);
          });
          return [...byContract.values()];
        };
        // Track which positions have been consumed into a spread group.
        const consumedPositions = new Set();
        const consumeLeg = (leg) => (leg._parts || [leg]).forEach(p => consumedPositions.add(p));
        let spreadIndex = 0;

        buckets.forEach((bucket) => {
          const datePositions = mergeContractLegs(bucket.positions);
          const dateKey = bucket.dateKey !== null ? bucket.dateKey
            : (datePositions[0]?.openDate || datePositions[0]?.tradeDate || 'unknown');
          if (datePositions.length >= 2) {
            // First try the standard detectSpreadType for known multi-leg patterns
            // (iron condor, straddle, strangle, single vertical spread)
            const spread = detectSpreadType(datePositions);
            if (spread) {
              // Mark original position objects as consumed BEFORE enriching
              // (the .map() below creates new objects that won't match)
              spread.legs.forEach(consumeLeg);

              const metrics = calculateSpreadMetrics(spread);

              // Enrich spread legs with strike distance
              spread.legs = spread.legs.map(leg => {
                const strike = leg.optionInfo?.strike || 0;
                let strikeDistance = null;
                let strikeDistancePct = null;
                let isOTM = false;

                const underlyingPrice = underlyingQuotes[underlying]?.last;

                if (leg.optionInfo?.isOption && strike > 0 && underlyingPrice) {
                  if (leg.optionInfo.type === 'P') {
                    strikeDistance = underlyingPrice - strike;
                    isOTM = strikeDistance > 0;
                  } else {
                    strikeDistance = strike - underlyingPrice;
                    isOTM = strikeDistance > 0;
                  }
                  strikeDistancePct = (Math.abs(strikeDistance) / underlyingPrice) * 100;
                }

                return {
                  ...leg,
                  strikeDistance,
                  strikeDistancePct,
                  isOTM
                };
              });

              result.push({
                id: `spread_${groupKey}_${expiration}_${spreadIndex++}`,
                type: 'spread',
                openDate: dateKey !== 'unknown' ? dateKey : (datePositions[0]?.openDate || datePositions[0]?.tradeDate || null),
                spreadType: spread.type,
                symbol: underlying,
                underlying: underlying,
                accountId: accountId,
                accountName: datePositions[0]?.accountName || 'Unknown',
                expiration: expiration,
                legs: spread.legs,
                metrics,
                isSpread: true,
                groupType: 'option',
                isFuturesOption: spread.legs.some(l => l.optionInfo?.isFuturesOption),
                expirationGroup: expiration,
                underlyingPrice: underlyingQuotes[underlying]?.last || null,
                underlyingQuote: underlyingQuotes[underlying] || null,
                earningsDate: earningsDates[underlying] || null,
                daysToEarnings: getDaysToEarnings(earningsDates[underlying])
              });
            } else {
              // detectSpreadType returned null — try to pair multiple 2-leg
              // vertical spreads (e.g. two separate put credit spreads opened
              // same day with same underlying/expiration).
              const calls = datePositions.filter(p => p.optionInfo?.type === 'C').sort((a, b) => a.optionInfo.strike - b.optionInfo.strike);
              const puts = datePositions.filter(p => p.optionInfo?.type === 'P').sort((a, b) => a.optionInfo.strike - b.optionInfo.strike);

              // Only attempt pairing if all positions are same type (all calls or all puts)
              // and we have equal longs and shorts
              if (calls.length > 0 && puts.length === 0) {
                const longs = calls.filter(p => p.quantity > 0);
                const shorts = calls.filter(p => p.quantity < 0);
                if (longs.length === shorts.length && longs.length >= 1) {
                  // Pair each long with the next available short (sorted by strike)
                  for (let i = 0; i < longs.length; i++) {
                    const pair = [longs[i], shorts[i]];
                    const pairSpread = detectSpreadType(pair);
                    if (pairSpread) {
                      pairSpread.legs.forEach(consumeLeg);
                      const metrics = calculateSpreadMetrics(pairSpread);
                      pairSpread.legs = pairSpread.legs.map(leg => {
                        const strike = leg.optionInfo?.strike || 0;
                        let strikeDistance = null, strikeDistancePct = null, isOTM = false;
                        const ulPrice = underlyingQuotes[underlying]?.last;
                        if (leg.optionInfo?.isOption && strike > 0 && ulPrice) {
                          if (leg.optionInfo.type === 'P') { strikeDistance = ulPrice - strike; isOTM = strikeDistance > 0; }
                          else { strikeDistance = strike - ulPrice; isOTM = strikeDistance > 0; }
                          strikeDistancePct = (Math.abs(strikeDistance) / ulPrice) * 100;
                        }
                        return { ...leg, strikeDistance, strikeDistancePct, isOTM };
                      });
                      result.push({
                        id: `spread_${groupKey}_${expiration}_${spreadIndex++}`,
                        type: 'spread',
                        openDate: dateKey !== 'unknown' ? dateKey : (datePositions[0]?.openDate || datePositions[0]?.tradeDate || null),
                        spreadType: pairSpread.type,
                        symbol: underlying,
                        underlying: underlying,
                        accountId: accountId,
                        accountName: datePositions[0]?.accountName || 'Unknown',
                        expiration: expiration,
                        legs: pairSpread.legs,
                        metrics,
                        isSpread: true,
                        groupType: 'option',
                        isFuturesOption: pairSpread.legs.some(l => l.optionInfo?.isFuturesOption),
                        expirationGroup: expiration,
                        underlyingPrice: underlyingQuotes[underlying]?.last || null,
                        underlyingQuote: underlyingQuotes[underlying] || null,
                        earningsDate: earningsDates[underlying] || null,
                        daysToEarnings: getDaysToEarnings(earningsDates[underlying])
                      });
                    }
                  }
                }
              } else if (puts.length > 0 && calls.length === 0) {
                const longs = puts.filter(p => p.quantity > 0);
                const shorts = puts.filter(p => p.quantity < 0);
                if (longs.length === shorts.length && longs.length >= 1) {
                  // Pair each long with the next available short (sorted by strike)
                  for (let i = 0; i < longs.length; i++) {
                    const pair = [longs[i], shorts[i]];
                    const pairSpread = detectSpreadType(pair);
                    if (pairSpread) {
                      pairSpread.legs.forEach(consumeLeg);
                      const metrics = calculateSpreadMetrics(pairSpread);
                      pairSpread.legs = pairSpread.legs.map(leg => {
                        const strike = leg.optionInfo?.strike || 0;
                        let strikeDistance = null, strikeDistancePct = null, isOTM = false;
                        const ulPrice = underlyingQuotes[underlying]?.last;
                        if (leg.optionInfo?.isOption && strike > 0 && ulPrice) {
                          if (leg.optionInfo.type === 'P') { strikeDistance = ulPrice - strike; isOTM = strikeDistance > 0; }
                          else { strikeDistance = strike - ulPrice; isOTM = strikeDistance > 0; }
                          strikeDistancePct = (Math.abs(strikeDistance) / ulPrice) * 100;
                        }
                        return { ...leg, strikeDistance, strikeDistancePct, isOTM };
                      });
                      result.push({
                        id: `spread_${groupKey}_${expiration}_${spreadIndex++}`,
                        type: 'spread',
                        openDate: dateKey !== 'unknown' ? dateKey : (datePositions[0]?.openDate || datePositions[0]?.tradeDate || null),
                        spreadType: pairSpread.type,
                        symbol: underlying,
                        underlying: underlying,
                        accountId: accountId,
                        accountName: datePositions[0]?.accountName || 'Unknown',
                        expiration: expiration,
                        legs: pairSpread.legs,
                        metrics,
                        isSpread: true,
                        groupType: 'option',
                        isFuturesOption: pairSpread.legs.some(l => l.optionInfo?.isFuturesOption),
                        expirationGroup: expiration,
                        underlyingPrice: underlyingQuotes[underlying]?.last || null,
                        underlyingQuote: underlyingQuotes[underlying] || null,
                        earningsDate: earningsDates[underlying] || null,
                        daysToEarnings: getDaysToEarnings(earningsDates[underlying])
                      });
                    }
                  }
                }
              }
            }
          }
        });

        // Collect remaining (non-consumed) positions for individual display.
        // Split legs carry `_src` back to the broker position — merge
        // unconsumed lots so a partially grouped position shows its residual
        // quantity rather than vanishing or double-counting.
        const remainingByPos = new Map();
        buckets.forEach(bucket => bucket.positions.forEach(p => {
          if (consumedPositions.has(p)) return;
          const src = p._src || p;
          if (!remainingByPos.has(src)) remainingByPos.set(src, { ...src, quantity: 0 });
          remainingByPos.get(src).quantity += parseFloat(p.quantity) || 0;
        }));
        const remainingPositions = underlyingPositions
          .filter(pos => remainingByPos.has(pos))
          .map(pos => remainingByPos.get(pos));

        // Add individual option positions
        remainingPositions.forEach((pos, idx) => {
          let bpEffect = 0;
          const isShort = pos.quantity < 0;
          const absQty = Math.abs(pos.quantity);
          const strike = pos.optionInfo?.strike || 0;
          // Futures options use exchange-set (SPAN) margin — strike×100 is an
          // equity-option model and wildly overstates buying power for /MES etc.
          const isFuturesOption = pos.isFuturesOption || pos.optionInfo?.isFuturesOption;

          if (pos.optionInfo && isShort && strike > 0 && !isFuturesOption) {
            if (pos.optionInfo.type === 'P') {
              bpEffect = strike * absQty * 100;
            } else if (pos.optionInfo.type === 'C') {
              const underlyingSymbol = pos.optionInfo.underlying;
              const normUnderlying = formatUnderlyingForQuotes(underlyingSymbol);
              const accountId = pos.account_id;

              let currentStock = 0;
              if (stockHoldings[accountId] && stockHoldings[accountId][normUnderlying]) {
                currentStock = stockHoldings[accountId][normUnderlying];
              }

              const sharesNeeded = absQty * 100;

              if (currentStock >= sharesNeeded) {
                bpEffect = 0;
                if (stockHoldings[accountId]) {
                  stockHoldings[accountId][normUnderlying] -= sharesNeeded;
                }
              } else {
                const coveredShares = Math.max(0, currentStock);
                const coveredContracts = Math.floor(coveredShares / 100);
                const uncoveredContracts = absQty - coveredContracts;

                bpEffect = strike * uncoveredContracts * 100;

                if (stockHoldings[accountId]) {
                  stockHoldings[accountId][normUnderlying] = Math.max(0, currentStock - (coveredContracts * 100));
                }
              }
            }
          }

          // Calculate Strike Distance
          let strikeDistance = null;
          let strikeDistancePct = null;
          let isOTM = false;

          const underlyingPrice = underlyingQuotes[underlying]?.last;
          if (pos.optionInfo?.isOption && strike > 0 && underlyingPrice) {
            if (pos.optionInfo.type === 'P') {
              strikeDistance = underlyingPrice - strike;
              isOTM = strikeDistance > 0;
            } else {
              strikeDistance = strike - underlyingPrice;
              isOTM = strikeDistance > 0;
            }
            strikeDistancePct = (Math.abs(strikeDistance) / underlyingPrice) * 100;
          }

          const optDir = pos.quantity >= 0 ? 'Long' : 'Short';
          const optType = pos.optionInfo.type === 'C' ? 'Call' : 'Put';

          result.push({
            id: `option_${groupKey}_${pos.symbol}_${idx}`,
            type: 'individual',
            openDate: pos.openDate || pos.tradeDate || null,
            symbol: pos.symbol,
            underlying: pos.optionInfo.underlying,
            accountId: pos.accountId || pos.account_id,
            accountName: pos.accountName || 'Unknown',
            position: pos,
            isSpread: false,
            groupType: 'option',
            isFuturesOption: pos.optionInfo?.isFuturesOption || false,
            strategyType: pos.optionInfo?.isFuturesOption
              ? `Futures ${optDir} ${optType}`
              : `${optDir} ${optType}`,
            expirationGroup: expiration,
            underlyingPrice: underlyingPrice || null,
            underlyingQuote: underlyingQuotes[underlying] || null,
            bpEffect,
            strikeDistance,
            strikeDistancePct,
            isOTM,
            earningsDate: earningsDates[underlying] || null,
            daysToEarnings: getDaysToEarnings(earningsDates[underlying])
          });
        });
      });
    });

    // Add stock positions at the end
    stockPositions.forEach((pos, idx) => {
      const isAssigned =
        pos.isAssigned === true || pos.isAssigned === 'true' || pos.isAssigned === 'True' ||
        pos.is_assigned === true || pos.is_assigned === 'true' || pos.is_assigned === 'True' ||
        pos.transactionType === 'MANUAL_ASSIGN' || pos.transaction_type === 'MANUAL_ASSIGN' ||
        pos.transactionType === 'ASSIGNMENT' || pos.transaction_type === 'ASSIGNMENT' ||
        false;

      const isCash =
        pos.isCash === true || pos.isCash === 'true' || pos.isCash === 'True' ||
        pos.is_cash === true || pos.is_cash === 'true' || pos.is_cash === 'True' ||
        pos.isManualCash === true ||
        false;

      const instrument = pos.instrument || {};
      const assetType = (instrument.assetType || '').toUpperCase();
      const assetSubType = (instrument.assetSubType || instrument.asset_sub_type || '').toUpperCase();
      const description = (instrument.description || instrument.desc || '').toUpperCase();
      const isETF =
        assetSubType === 'EXCHANGE_TRADED_FUND' ||
        assetSubType === 'ETF' ||
        assetType === 'EXCHANGE_TRADED_FUND' ||
        description.includes('ETF') ||
        description.includes('EXCHANGE TRADED FUND') ||
        description.includes('INDEX FUND');
      const isEquity = assetType === 'EQUITY';

      if (isAssigned) console.log(`Position ${pos.symbol} in account ${pos.accountId} is assigned! Setting group to Assigned Stocks.`);

      let groupType = 'equity';
      let expirationGroup = 'Equity Positions';
      if (isCash) {
        groupType = 'cash';
        expirationGroup = 'Cash Positions';
      } else if (isAssigned) {
        groupType = 'stock';
        expirationGroup = 'Assigned Stocks';
      } else if (isETF) {
        groupType = 'etf';
        expirationGroup = 'ETF Position';
      }

      result.push({
        id: `stock_${pos.symbol}_${idx}`,
        type: 'individual',
        openDate: pos.openDate || pos.tradeDate || null,
        symbol: pos.symbol,
        underlying: pos.symbol,
        accountId: pos.accountId || pos.account_id,
        accountName: pos.accountName || 'Unknown',
        position: pos,
        isSpread: false,
        groupType,
        expirationGroup,
        underlyingPrice: pos.currentPrice || null,
        underlyingQuote: underlyingQuotes[pos.symbol] || null,
        bpEffect: 0,
        isAssigned,
        isCash,
        earningsDate: earningsDates[pos.symbol] || null,
        daysToEarnings: getDaysToEarnings(earningsDates[pos.symbol])
      });
    });

    // Add manual groups as spread-like rows
    manualGroupRows.forEach(({ group, matched }) => {
      const first = matched[0];
      const accountId = String(group.accountId || group.account_id || '');
      const legs = (group.legs || []).map(legDef => {
        const pos = matched.find(p => String(p.symbol || '').trim() === String(legDef.symbol || '').trim());
        const optionInfo = pos ? pos.optionInfo : parseOptionSymbol(legDef.symbol || '');
        const strike = optionInfo?.strike || legDef.strike || 0;
        const type = optionInfo?.type || legDef.type;
        let strikeDistance = null, strikeDistancePct = null, isOTM = false;
        const ulPrice = underlyingQuotes[group.underlying || first?.underlying]?.last;
        if (optionInfo?.isOption && strike > 0 && ulPrice) {
          if (type === 'P') { strikeDistance = ulPrice - strike; isOTM = strikeDistance > 0; }
          else { strikeDistance = strike - ulPrice; isOTM = strikeDistance > 0; }
          strikeDistancePct = (Math.abs(strikeDistance) / ulPrice) * 100;
        }
        return {
          ...(pos || {}),
          optionInfo,
          strikeDistance,
          strikeDistancePct,
          isOTM,
          groupId: group.id,
          groupStrategy: group.strategy
        };
      });

      const metrics = calculateSpreadMetrics({ type: group.strategy, legs });
      result.push({
        id: `manual_${group.id}`,
        type: 'spread',
        openDate: first?.openDate || first?.tradeDate || null,
        spreadType: group.strategy,
        symbol: first?.optionInfo?.underlying || group.legs?.[0]?.underlying || '',
        underlying: first?.optionInfo?.underlying || group.legs?.[0]?.underlying || '',
        accountId,
        accountName: first?.accountName || 'Unknown',
        expiration: first?.optionInfo?.expiration || group.legs?.[0]?.expiration || 'Unknown',
        legs,
        metrics,
        isSpread: true,
        isManualGroup: true,
        groupId: group.id,
        groupType: 'option',
        expirationGroup: first?.optionInfo?.expiration || group.legs?.[0]?.expiration || 'Unknown',
        underlyingPrice: underlyingQuotes[first?.optionInfo?.underlying || group.legs?.[0]?.underlying]?.last || null,
        underlyingQuote: underlyingQuotes[first?.optionInfo?.underlying || group.legs?.[0]?.underlying] || null,
        earningsDate: earningsDates[first?.optionInfo?.underlying || group.legs?.[0]?.underlying] || null,
        daysToEarnings: getDaysToEarnings(earningsDates[first?.optionInfo?.underlying || group.legs?.[0]?.underlying])
      });
    });

    if (byUnderlying) {
      // Stock, ETF, and assigned rows join their option legs under the symbol
      // group; cash positions stay in their own pinned section.
      return result.map(group => (group.groupType === 'cash' ? group : { ...group, expirationGroup: group.underlying }));
    }

    return result;
  };

  // Helper functions for expandable rows
  const toggleSpreadExpansion = (spreadId) => {
    const newExpanded = new Set(expandedSpreads);
    if (newExpanded.has(spreadId)) {
      newExpanded.delete(spreadId);
    } else {
      newExpanded.add(spreadId);
    }
    setExpandedSpreads(newExpanded);
  };

  const isSpreadExpanded = (spreadId) => {
    return expandedSpreads.has(spreadId);
  };

  // Fetch account summaries (cash balances)
  useEffect(() => {
    const fetchSummaries = async () => {
      if (!accountsLoaded) return;
      setSummariesLoading(true);
      try {
        if (paperMode) {
          // Paper mode: fetch paper trading summary for cash balance
          const response = await apiRequest('/api/paper-trading/summary');
          const summary = response.data || {};
          const paperCash = (summary.accountBalance != null && Number.isFinite(Number(summary.accountBalance)))
            ? Number(summary.accountBalance)
            : (summary.cashBalance != null && Number.isFinite(Number(summary.cashBalance)))
              ? Number(summary.cashBalance)
              : 100000;
          setAccountSummaries([{
            accountName: 'Paper Trading',
            accountId: 'paper',
            cash: paperCash,
            error: null,
          }]);
        } else {
          const url = `/api/accounts/summary${!selectedAccount.includes('all') ? `?accountIds=${selectedAccount.join(',')}` : ''}`;
          const response = await apiRequest(url);
          // Only investment accounts carry positions/cash on this page —
          // exclude bank and credit-card balances from the summary.
          setAccountSummaries((response.data.accounts || [])
            .filter(s => investmentIds.has(String(s.accountId ?? s.account_id ?? s.id))));
        }
      } catch (error) {
        console.error('Error fetching account summaries:', error);
      } finally {
        setSummariesLoading(false);
      }
    };
    fetchSummaries();
  }, [accountsLoaded, selectedAccount, refreshKey, paperMode]);

  // Fetch accounts on component mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await apiRequest('/api/accounts?include_inactive=true');
        const accountsData = response.data.value || response.data;
        setAccounts(accountsData);
        accountsRef.current = accountsData;
        setAccountsLoaded(true);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };
    fetchAccounts();
  }, []);

  // Fetch trade groups once accounts are loaded
  useEffect(() => {
    const fetchTradeGroups = async () => {
      if (!accountsLoaded) return;
      try {
        const response = await apiRequest('/api/trade-groups');
        setTradeGroups(response.data?.groups || []);
      } catch (err) {
        console.error('Error fetching trade groups:', err);
        setTradeGroups([]);
      }
    };
    fetchTradeGroups();
  }, [accountsLoaded, refreshKey]);

  // Process open positions data when it changes
  useEffect(() => {
    const processPositions = async () => {
      // Filter positions based on selectedAccount (live mode also restricts
      // to investment accounts so Plaid bank data can't leak in)
      const basePositions = paperMode
        ? openPositions
        : openPositions.filter(p => investmentIds.has(String(p.accountId || p.account_id || '')));
      const filteredPositions = selectedAccount.includes('all')
        ? basePositions
        : basePositions.filter(p => {
          const accId = String(p.accountId || p.account_id || '');
          const selectedAccountIds = selectedAccount.map(id => String(id));
          return selectedAccountIds.includes(accId);
        });

      // Add a core cash-balance row for each selected account so cash shows up
      // alongside other positions and the account name is visible (logos can be duplicated).
      const cashPositions = (accountSummaries || [])
        .filter(s => s.cash != null && Number.isFinite(Number(s.cash)) && Number(s.cash) !== 0)
        .filter(s => paperMode || investmentIds.has(String(s.accountId)))
        .filter(s => selectedAccount.includes('all') || selectedAccount.map(id => String(id)).includes(String(s.accountId)))
        .map(s => ({
          symbol: 'Cash',
          quantity: 1,
          currentPrice: Number(s.cash),
          marketValue: Number(s.cash),
          averagePrice: Number(s.cash),
          cost_basis: Number(s.cash),
          change: 0,
          changePercent: 0,
          dayPL: 0,
          accountName: s.accountName || accountsRef.current.find(a => String(a.id) === String(s.accountId))?.name || 'Unknown',
          accountId: s.accountId,
          isOption: false,
          isCash: true,
          isBrokerCash: true,
        }));

      const combinedPositions = [...filteredPositions, ...cashPositions].filter(p =>
        p.isCash || p.isOption || (
          Math.abs(p.quantity || 0) > 0 &&
          ((p.cost_basis || 0) !== 0 || (p.marketValue || 0) !== 0)
        )
      );

      if (combinedPositions.length > 0) {
        const processedPositions = combinedPositions.map(position => {
          // Always compute Market Value based on current price
          const isOption = position.isOption || position.symbol.includes('_') || /[A-Z]+\d{6}[CP]\d+/.test(position.symbol);
          // Futures options carry their own contract multiplier (e.g. /MES = 5);
          // the equity-option default of 100 is only a fallback.
          const multiplier = position.contractMultiplier || position.optionInfo?.multiplier || (isOption ? 100 : 1);
          const quantity = parseFloat(position.quantity || 0);
          const unitPrice = position.last_price || position.currentPrice || 0;
          // For stocks and single legs, market value should be positive magnitude
          const marketValue = Math.abs(quantity) * unitPrice * multiplier;
          // Preserve provided averagePrice if present; else derive from cost basis when possible
          const averagePrice = position.averagePrice !== undefined
            ? position.averagePrice
            : (Math.abs(quantity) > 0
              ? Math.abs(position.cost_basis || 0) / (Math.abs(quantity) * multiplier)
              : 0);
          // Daily P/L from price change (per share/contract * quantity)
          const change = parseFloat(position.change || 0);
          const dayPL = change * quantity * multiplier;
          return {
            ...position,
            isOption,
            marketValue,
            averagePrice,
            dayPL
          };
        });

        const grouped = await groupPositionsByExpiration(processedPositions, groupOpenByUnderlying, tradeGroups);
        setGroupedPositions(grouped);

        const cashGroupTotal = grouped
          .filter(g => g.expirationGroup === 'Cash Positions')
          .reduce((sum, g) => {
            const mv = g.isSpread
              ? (g.metrics?.totalMarketValue || 0)
              : (g.position?.marketValue || 0);
            return sum + Number(mv);
          }, 0);
        setTotalCash(cashGroupTotal);
      } else {
        setGroupedPositions([]);
        setTotalCash(0);
      }
    };

    processPositions();
  }, [openPositions, selectedAccount, groupOpenByUnderlying, accountSummaries, tradeGroups]);

  // Fetch open positions definition (outside useEffect for reuse)
  const fetchOpenPositions = useCallback(async (silent = false) => {
    if (!accountsLoaded) {
      return;
    }

    // Paper mode: fetch from paper trading API and skip live broker positions
    if (paperMode) {
      if (!silent) setPositionsLoading(true);
      try {
        const response = await apiRequest('/api/paper-trading/positions');
        const paperPositions = response.data?.data || response.data || [];

        const transformed = paperPositions.map(pos => {
          const isOption = (pos.asset_type || pos.assetType) !== 'EQUITY';
          const avgPrice = parseFloat(pos.avg_fill_price || pos.avgFillPrice || 0);
          const currentPrice = parseFloat(pos.current_price || pos.currentPrice || avgPrice);
          const qty = parseInt(pos.quantity || 0);
          const mult = isOption ? 100 : 1;
          const costBasis = avgPrice * Math.abs(qty) * mult;
          const marketValue = parseFloat(pos.market_value || pos.marketValue || 0) || (currentPrice * Math.abs(qty) * mult);
          const unrealizedPL = parseFloat(pos.unrealized_pl || pos.unrealizedPl || 0);
          const unrealizedPLPct = parseFloat(pos.unrealized_pl_percent || pos.unrealizedPlPercent || 0);
          const instruction = pos.instruction || '';
          const isShort = instruction.includes('SELL') || instruction.includes('SHORT');
          const signedQty = isShort ? -Math.abs(qty) : Math.abs(qty);

          return {
            id: pos.id,
            symbol: pos.symbol,
            underlying: pos.underlying,
            quantity: signedQty,
            tags: Array.isArray(pos.tags) ? pos.tags : [],
            currentPrice,
            marketValue,
            change: unrealizedPL,
            changePercent: unrealizedPLPct,
            accountName: 'Paper Trading',
            accountId: 'paper',
            averagePrice: avgPrice,
            cost_basis: isShort ? -costBasis : costBasis,
            isOption,
            isPaperTrade: true,
            openDate: pos.opened_at || pos.openedAt || null,
            expiration: pos.expiration || null,
            strike: pos.strike || null,
            putCall: pos.put_call || pos.putCall || null,
            instruction,
            isAutomated: !!(pos.apitokenid || pos.apiTokenId),
            takeProfitPct: pos.take_profit_pct != null ? parseFloat(pos.take_profit_pct) : null,
            stopLossPct: pos.stop_loss_pct != null ? parseFloat(pos.stop_loss_pct) : null,
            takeProfitTiers: pos.take_profit_tiers || pos.takeProfitTiers || null,
            stopLossTiers: pos.stop_loss_tiers || pos.stopLossTiers || null,
            exitsTaken: parseInt(pos.exits_taken || pos.exitsTaken || 0),
          };
        });

        setOpenPositions(transformed);
        openPositionsRef.current = transformed;
        setCachedOpenPositions(transformed);
      } catch (error) {
        console.error('Error fetching paper open positions:', error);
        setOpenPositions([]);
        openPositionsRef.current = [];
      } finally {
        if (!silent) setPositionsLoading(false);
      }
      return;
    }

    // Try to load from cache first for instant display — then fetch fresh
    // below anyway; the cache only covers the first paint and may carry
    // stale order-lot attribution.
    const cached = getCachedOpenPositions();
    const hasCache = !!(cached && cached.length > 0);
    if (hasCache && !silent) {
      setOpenPositions(cached);
      openPositionsRef.current = cached;
    }

    if (!silent && !hasCache) setPositionsLoading(true);
    try {
      const now = Date.now();

      // Determine which accounts are due for refresh based on their configured interval.
      // Non-silent calls (initial load or manual refresh) always fetch all accounts.
      const accountsToFetch = [];
      const skippedAccountIds = new Set();
      for (const account of accountsRef.current) {
        if (!account.isActive) continue;
        if (silent) {
          const intervalSec = Number(account.positionUpdateInterval || account.position_update_interval);
          const intervalMs = (Number.isFinite(intervalSec) && intervalSec > 0 ? intervalSec : 30) * 1000;
          const lastFetch = lastFetchTimesRef.current[account.id] || 0;
          if (now - lastFetch < intervalMs) {
            skippedAccountIds.add(String(account.id));
            continue;
          }
        }
        accountsToFetch.push(account);
      }

      // Preserve positions from accounts that are skipped this refresh cycle.
      let allPositions = openPositionsRef.current.filter(p => skippedAccountIds.has(String(p.accountId)));

      for (const account of accountsToFetch) {
        try {
          // Skip disabled accounts and non-investment accounts (bank/credit
          // rows can't hold positions)
          if (!account.isActive) continue;
          if (!investmentIds.has(String(account.id))) continue;

          // For manual accounts, fetch open positions from database instead of broker API
          const accountApiType = account.apiType || account.api_type;
          if (String(accountApiType).toLowerCase() === 'manual') {
            try {
              const dbPositionsResponse = await apiRequest(`/api/trades/positions/open?accountId=${account.id}`);
              const responseData = dbPositionsResponse.data || {};
              const dbPositions = Array.isArray(responseData.openPositions)
                ? responseData.openPositions
                : (Array.isArray(responseData) ? responseData : []);
              // Transform database positions to match broker API format
              let transformedPositions = dbPositions.map(pos => {
                // DB positions use totalQuantity/avgPrice, not quantity/averagePrice
                const rawQty = parseFloat(pos.totalQuantity ?? pos.quantity ?? 0);
                // Determine if this is an option by checking originalSymbol in trades array
                const firstTrade = pos.trades?.[0] || {};
                const originalSymbol = firstTrade.originalSymbol || pos.original_symbol || pos.originalSymbol || '';
                const rawData = firstTrade.rawData || pos.raw_data || pos.rawData || null;
                const optionInfo = parseOptionSymbol(originalSymbol || pos.symbol || '');
                const isOption = optionInfo.isOption;
                // For options, use the OCC/descriptive symbol so parseOptionSymbol can parse it downstream
                const displaySymbol = isOption ? originalSymbol : (pos.symbol || pos.underlying || '');
                // Determine sign from transaction type (STO/SELL_TO_OPEN = short)
                const tt = String(firstTrade.transactionType || firstTrade.tradeType || pos.transaction_type || pos.trade_type || '').toUpperCase();
                const isShort = tt === 'STO' || tt === 'SELL_TO_OPEN' || tt === 'SELL' || tt === 'SHORT';
                const signedQty = isShort ? -Math.abs(rawQty) : Math.abs(rawQty);
                const avgPrice = parseFloat(pos.avgPrice ?? pos.averagePrice ?? pos.price ?? pos.average_price ?? 0);
                const totalCost = parseFloat(pos.totalCost ?? pos.cost ?? 0);
                const costBasis = totalCost || (avgPrice * Math.abs(rawQty) * (isOption ? 100 : 1));
                const holdingPrice = parseFloat(rawData?.price || 0);
                const rawAssetClass = (rawData?.asset_class || '').toUpperCase();
                const rawDescription = (rawData?.description || '').toUpperCase();
                const isETFClass =
                  rawAssetClass.includes('ETF') ||
                  rawAssetClass.includes('EXCHANGE') ||
                  rawDescription.includes('ETF') ||
                  rawDescription.includes('EXCHANGE TRADED FUND');
                const instrument = rawData
                  ? {
                      description: rawData.description,
                      assetType: isETFClass ? 'ETF' : (rawAssetClass.includes('CASH') ? 'CASH' : 'EQUITY'),
                      assetSubType: '',
                    }
                  : (pos.instrument || {});
                return {
                  ...pos,
                  symbol: displaySymbol,
                  underlying: isOption ? optionInfo.underlying : displaySymbol,
                  quantity: signedQty,
                  averagePrice: avgPrice,
                  cost_basis: isShort ? -costBasis : costBasis,
                  marketValue: pos.marketValue || pos.market_value || 0,
                  currentPrice: holdingPrice,
                  rawData,
                  instrument,
                  accountId: account.id,
                  account: account.name,
                  accountName: account.name,
                  apiType: 'manual',
                  isOption,
                  openDate: pos.openDate || pos.earliestTradeDate || null,
                  expiration: optionInfo.expiration || null,
                  strike: optionInfo.strike || null,
                  putCall: optionInfo.type || null,
                  instruction: tt,
                };
              });

              // Fetch Schwab live quotes for manual positions if a Schwab account is linked
              const schwabAccount = accountsRef.current.find(a => a.isActive && (a.apiType || a.api_type || '').toLowerCase() === 'schwab');
              if (schwabAccount && transformedPositions.length > 0) {
                const quoteSymbols = [...new Set(transformedPositions.map(p => p.symbol).filter(Boolean))];
                try {
                  const quoteResponse = await apiRequest('/api/schwab-api/quotes', {
                    method: 'POST',
                    body: JSON.stringify({ accountId: schwabAccount.id, symbols: quoteSymbols }),
                  });
                  const quotes = quoteResponse.data?.quotes || {};
                  transformedPositions = transformedPositions.map(pos => {
                    const q = quotes[pos.symbol] || quotes[pos.underlying] || {};
                    const last = parseFloat(q.last) || parseFloat(q.lastPrice) || parseFloat(q.mark) || 0;
                    const bid = parseFloat(q.bid) || 0;
                    const ask = parseFloat(q.ask) || 0;
                    const currentPrice = last || (bid && ask ? (bid + ask) / 2 : (bid || ask || pos.currentPrice || 0));
                    const marketValue = currentPrice * Math.abs(pos.quantity) * (pos.isOption ? 100 : 1);
                    const prevClose = parseFloat(q.prevClose) || parseFloat(q.close) || 0;
                    const change = (last && prevClose) ? currentPrice - prevClose : 0;
                    const changePercent = (last && prevClose) ? (change / prevClose) * 100 : 0;
                    return { ...pos, currentPrice, marketValue, change, changePercent };
                  });
                } catch (quoteErr) {
                  console.warn('[StockSummary] Failed to fetch Schwab quotes for manual account:', quoteErr.message);
                }
              }

              allPositions.push(...transformedPositions);
            } catch (dbError) {
              console.warn(`Failed to fetch database positions for manual account ${account.id}:`, dbError);
            }
            continue;
          }

          let positionsResponse;
          let positions = [];

          if (accountApiType === 'schwab') {
            positionsResponse = await apiRequest(`/api/schwab-api/positions/${account.id}`);
            if (positionsResponse.data.positions?.securitiesAccount?.positions) {
              positions = positionsResponse.data.positions.securitiesAccount.positions;
            }
          } else if (String(accountApiType).toLowerCase() === 'robinhood-mcp') {
            // Use MCP for robinhood-mcp
            positionsResponse = await apiRequest(`/api/mcp/positions?accountId=${account.id}`);
            if (positionsResponse.data.positions) {
              positions = positionsResponse.data.positions;

              // Separate option and stock positions for quote fetching
              const optionPositions = positions.filter(pos => pos.isOption || pos.instrumentId);
              const stockPositions = positions.filter(pos => !(pos.isOption || pos.instrumentId));

              // Fetch option quotes
              if (optionPositions.length > 0) {
                const instrumentIds = [...new Set(optionPositions.map(pos => pos.instrumentId).filter(Boolean))];
                try {
                  const quotesResponse = await apiRequest(`/api/mcp/option-quotes?instrumentIds=${instrumentIds.join(',')}`);

                  const quotes = quotesResponse.data.quotes || [];
                  const quotesMap = {};
                  quotes.forEach(quote => {
                    if (quote && quote.instrument_id) {
                      quotesMap[quote.instrument_id] = quote;
                    }
                  });

                  // Update option positions with current prices
                  positions = positions.map(position => {
                    if (position.instrumentId && quotesMap[position.instrumentId]) {
                      const quote = quotesMap[position.instrumentId];
                      const currentPrice = parseFloat(quote.mark_price || position.currentPrice || 0);
                      const marketValue = Math.abs(position.quantity) * currentPrice * 100;
                      const rawChange = parseFloat(quote.change_in_price);
                      const prevClose = parseFloat(quote.previous_close || quote.previousClose || quote.prev_close || 0);
                      const change = !isNaN(rawChange) && rawChange !== 0 ? rawChange : (prevClose ? currentPrice - prevClose : 0);
                      const rawChangePct = parseFloat(quote.percent_change);
                      const changePercent = !isNaN(rawChangePct) && rawChangePct !== 0 ? rawChangePct : (prevClose ? (change / prevClose) * 100 : 0);
                      return {
                        ...position,
                        currentPrice,
                        marketValue,
                        change,
                        changePercent
                      };
                    }
                    return position;
                  });
                } catch (quoteError) {
                  console.warn('[StockSummary] Failed to fetch MCP option quotes:', quoteError);
                }
              }

              // Fetch stock quotes
              if (stockPositions.length > 0) {
                const stockSymbols = [...new Set(stockPositions.map(pos => pos.symbol).filter(Boolean))];
                try {
                  const quotesResponse = await apiRequest(`/api/mcp/equity-quotes?symbols=${stockSymbols.join(',')}`);

                  const quotes = quotesResponse.data.quotes || [];
                  const quotesMap = {};
                  quotes.forEach(quote => {
                    if (quote && quote.symbol) {
                      quotesMap[quote.symbol] = quote;
                    }
                  });

                  // Update stock positions with current prices
                  positions = positions.map(position => {
                    if (!position.isOption && !position.instrumentId && quotesMap[position.symbol]) {
                      const quote = quotesMap[position.symbol];
                      const currentPrice = parseFloat(quote.last_trade_price || quote.last_price || quote.mark_price || position.currentPrice || 0);
                      const marketValue = Math.abs(position.quantity) * currentPrice;
                      const rawChange = parseFloat(quote.change_in_price);
                      const prevClose = parseFloat(quote.previous_close || quote.previousClose || quote.prev_close || quote.close || 0);
                      const change = !isNaN(rawChange) && rawChange !== 0 ? rawChange : (prevClose ? currentPrice - prevClose : 0);
                      const rawChangePct = parseFloat(quote.percent_change);
                      const changePercent = !isNaN(rawChangePct) && rawChangePct !== 0 ? rawChangePct : (prevClose ? (change / prevClose) * 100 : 0);
                      return {
                        ...position,
                        currentPrice,
                        marketValue,
                        change,
                        changePercent
                      };
                    }
                    return position;
                  });
                } catch (quoteError) {
                  console.warn('[StockSummary] Failed to fetch MCP equity quotes:', quoteError);
                }
              }

              // Process Robinhood MCP positions with updated prices
              const processedPositions = positions.map(position => {
                return {
                  ...position,
                  accountName: account.name,
                  accountId: String(account.id),
                };
              }).filter(p => p.quantity !== 0 && Math.abs(p.quantity) > 0.01);
              allPositions.push(...processedPositions);
            }
          } else if (String(accountApiType).toLowerCase() === 'tradier-mcp') {
            // Use MCP for tradier-mcp
            positionsResponse = await apiRequest(`/api/mcp/tradier/positions?accountId=${account.id}`);
            if (positionsResponse.data.positions) {
              positions = positionsResponse.data.positions;
            }

            // Fetch option quotes using MCP get_option_quotes for options
            const optionPositions = positions.filter(pos => pos.isOption || pos.instrumentId);
            const stockPositions = positions.filter(pos => !(pos.isOption || pos.instrumentId));

            if (optionPositions.length > 0) {
              const instrumentIds = [...new Set(optionPositions.map(pos => pos.instrumentId).filter(Boolean))];

              if (instrumentIds.length > 0) {
                try {
                  const quotesResponse = await apiRequest(`/api/mcp/option-quotes?instrumentIds=${instrumentIds.join(',')}`);

                  const quotes = quotesResponse.data.quotes || [];
                  const quotesMap = {};
                  quotes.forEach(quote => {
                    if (quote && quote.instrument_id) {
                      quotesMap[quote.instrument_id] = quote;
                    }
                  });

                  // Update option positions with current prices from MCP quotes
                  positions = positions.map(position => {
                    if (position.instrumentId && quotesMap[position.instrumentId]) {
                      const quote = quotesMap[position.instrumentId];
                      const currentPrice = quote.mark_price || position.currentPrice || 0;
                      const marketValue = Math.abs(position.quantity) * currentPrice * 100;

                      return {
                        ...position,
                        currentPrice,
                        marketValue
                      };
                    }
                    return position;
                  });
                } catch (quoteError) {
                  console.warn('[StockSummary] Failed to fetch MCP option quotes:', quoteError);
                }
              }
            }

            // For stocks, use MCP get_equity_quotes
            if (stockPositions.length > 0) {
              const stockSymbols = [...new Set(stockPositions.map(pos => pos.symbol).filter(Boolean))];

              if (stockSymbols.length > 0) {
                try {
                  const quotesResponse = await apiRequest(`/api/mcp/equity-quotes?symbols=${stockSymbols.join(',')}`);

                  const quotes = quotesResponse.data.quotes || [];
                  const quotesMap = {};
                  quotes.forEach(quote => {
                    if (quote && quote.symbol) {
                      quotesMap[quote.symbol] = quote;
                    }
                  });

                  // Update stock positions with current prices from MCP quotes
                  positions = positions.map(position => {
                    if (!position.isOption && !position.instrumentId) {
                      const quote = quotesMap[position.symbol];
                      const currentPrice = quote?.last_trade_price || quote?.last_price || quote?.mark_price || position.currentPrice || 0;
                      const marketValue = position.quantity * currentPrice;

                      return {
                        ...position,
                        currentPrice,
                        marketValue
                      };
                    }
                    return position;
                  });
                } catch (quoteError) {
                  console.warn('[StockSummary] Failed to fetch MCP equity quotes:', quoteError);
                }
              }
            }
          } else if (String(accountApiType).toLowerCase() === 'plaid') {
            positionsResponse = await apiRequest(`/api/plaid-api/positions/${account.id}`);
            positions = positionsResponse.data?.positions || [];
          } else if (['robinhood', 'robinhood-api'].includes(String(accountApiType).toLowerCase())) {
            // Use old CSV import for robinhood
            positionsResponse = await apiRequest(`/api/robinhood-api/positions/${account.id}`);
            const rhData = positionsResponse.data;
            if (rhData?.success && Array.isArray(rhData.results)) {
              // Robinhood returns results[].equityPositions and results[].optionPositions
              // Tag each with isOption so downstream grouping can separate them
              rhData.results.forEach(r => {
                if (Array.isArray(r.equityPositions)) {
                  positions.push(...r.equityPositions.map(p => ({ ...p, isOption: false })));
                }
                if (Array.isArray(r.optionPositions)) {
                  positions.push(...r.optionPositions.map(p => ({ ...p, isOption: true })));
                }
              });
            } else if (rhData?.success && Array.isArray(rhData.positions)) {
              // Fallback: python service may return flat positions array
              positions = rhData.positions;
            }
          } else {
            positionsResponse = await apiRequest(`/api/accounts/${account.id}/positions`);
            if (positionsResponse.data.positions?.positions?.position) {
              const positionData = positionsResponse.data.positions.positions.position;
              positions = Array.isArray(positionData) ? positionData : [positionData];
            }
          }

          if (positions.length > 0) {
            let positionsWithQuotes;

            if (accountApiType === 'schwab') {
              const positionSymbols = [...new Set(positions.map(position => position.instrument?.symbol).filter(Boolean))];
              let schwabQuoteMap = {};

              if (positionSymbols.length > 0) {
                try {
                  const quoteResponse = await apiRequest('/api/schwab-api/quotes', {
                    method: 'POST',
                    body: JSON.stringify({ accountId: account.id, symbols: positionSymbols }),
                  });
                  schwabQuoteMap = quoteResponse.data?.quotes || {};
                } catch (quoteError) {
                  console.warn(`[StockSummary] Failed to fetch Schwab position quotes for ${account.name}:`, quoteError.message);
                }
              }

              const findSchwabQuote = (symbol) => {
                const key = String(symbol || '').replace(/\s+/g, '').toUpperCase();
                return schwabQuoteMap[key] || schwabQuoteMap[symbol] || Object.entries(schwabQuoteMap).find(([quoteSymbol]) => (
                  String(quoteSymbol).replace(/\s+/g, '').toUpperCase() === key
                ))?.[1] || null;
              };

              positionsWithQuotes = positions.map(position => {
                const symbol = position.instrument?.symbol || '';
                const longQty = parseFloat(position.longQuantity) || 0;
                const shortQty = parseFloat(position.shortQuantity) || 0;
                const quantity = longQty > 0 ? longQty : -shortQty;
                const isOption = position.instrument?.assetType === 'OPTION';
                const averagePrice = parseFloat(position.averagePrice) || 0;
                const brokerMarketValue = Math.abs(parseFloat(position.marketValue) || 0);
                const quote = findSchwabQuote(symbol);
                const quotePrice = parseFloat(quote?.mid) || parseFloat(quote?.last) || 0;
                const bid = parseFloat(quote?.bid) || 0;
                const ask = parseFloat(quote?.ask) || 0;
                const midFromBidAsk = bid && ask ? (bid + ask) / 2 : (bid || ask || 0);
                const finalQuotePrice = quotePrice || midFromBidAsk;
                const currentPrice = finalQuotePrice || (Math.abs(quantity) > 0
                  ? (isOption ? brokerMarketValue / (Math.abs(quantity) * 100) : brokerMarketValue / Math.abs(quantity))
                  : 0);
                const marketValue = currentPrice * Math.abs(quantity) * (isOption ? 100 : 1);

                const absCost = averagePrice * Math.abs(quantity) * (isOption ? 100 : 1);
                const cost_basis = quantity < 0 ? -absCost : absCost;

                // Fallback to price vs previous close when Schwab doesn't return netChange
                const rawChange = parseFloat(quote?.change);
                const prevClose = parseFloat(quote?.prevclose || quote?.close) || 0;
                const change = !isNaN(rawChange) && rawChange !== 0
                  ? rawChange
                  : (prevClose ? currentPrice - prevClose : 0);
                const rawChangePct = parseFloat(quote?.change_percentage);
                const changePercent = !isNaN(rawChangePct) && rawChangePct !== 0
                  ? rawChangePct
                  : (prevClose ? (change / prevClose) * 100 : 0);

                return {
                  symbol,
                  quantity,
                  currentPrice,
                  marketValue,
                  change,
                  changePercent,
                  accountName: account.name,
                  accountId: String(account.id),
                  instrument: position.instrument,
                  averagePrice,
                  cost_basis,
                  isOption,
                  underlyingPrice: quote?.underlyingPrice != null ? parseFloat(quote.underlyingPrice) : null,
                  underlyingSymbol: quote?.underlyingSymbol || null,
                };
              }).filter(p => p.quantity !== 0 && Math.abs(p.quantity) > 0.01); // Filter out zero quantity and near-zero positions
            } else if (['robinhood', 'robinhood-api'].includes(String(accountApiType).toLowerCase())) {
              positionsWithQuotes = [];
              positions.forEach(position => {
                // Option positions from Robinhood's /options/aggregate_positions/ endpoint
                // already include strike_price/expiration_date/option_type directly on each
                // leg (ISO date format), and a real underlying `symbol` (e.g. "GPN") — they
                // do NOT need instrument-URL resolution. Build a parseable descriptive symbol
                // (e.g. "GPN 09/18/26 $90 Call") from each leg so parseOptionSymbol()
                // downstream correctly classifies it as an option with real expiration/strike.
                const legs = Array.isArray(position.legs) ? position.legs : [];
                if (legs.length > 0) {
                  // Emit one position per leg so detectSpreadType can group multi-leg positions
                  // REST positions carry the raw ticker in chain_symbol; the python
                  // driver puts it in `underlying` (its `symbol` is the formatted
                  // display string — using it would double the date/strike suffix).
                  // Some sources may still hand us a descriptive label — strip an
                  // existing option suffix so we never print it twice.
                  let underlying = position.chain_symbol || position.underlying || position.symbol || '';
                  const _uInfo = parseOptionSymbol(underlying);
                  if (_uInfo.isOption) underlying = _uInfo.underlying;
                  const clearingCostBasis = parseFloat(position.clearing_cost_basis || 0);
                  const aggregateQty = Math.abs(parseFloat(position.quantity || position.intraday_quantity || 0));
                  const positionAvg = parseFloat(position.average_open_price ?? position.averagePrice ?? 0);

                  // REST aggregate legs carry ratio_quantity = leg ratio within the
                  // spread (1 for a standard leg); contract count is ratio × the
                  // aggregate spread quantity. The python fallback instead puts the
                  // signed contract count on leg.quantity — prefer it when present.
                  const legContractQty = (leg) => (leg.quantity != null && leg.quantity !== '')
                    ? Math.abs(parseFloat(leg.quantity))
                    : Math.abs(parseFloat(leg.ratio_quantity ?? 1) || 1) * aggregateQty;

                  // Per-leg avg prices may not always be present; if they are, cost basis sums to total cost.
                  const hasRealPerLegAvg = legs.every(leg => leg.average_open_price != null || leg.average_price != null);
                  const totalCostBasisFromLegs = legs.reduce((sum, leg) => {
                    const legAvg = parseFloat(leg.average_open_price ?? leg.average_price ?? positionAvg ?? 0);
                    return sum + (legAvg * legContractQty(leg) * 100);
                  }, 0);

                  legs.forEach((leg, legIdx) => {
                    const exp = leg.expiration_date || ''; // 'YYYY-MM-DD'
                    const strike = parseFloat(leg.strike_price || 0);
                    const typ = (leg.option_type || '').toLowerCase();
                    const callput = typ === 'call' ? 'Call' : 'Put';
                    // 'short' (sold/credit) vs 'long' (bought/debit).
                    // Prioritize per-leg position_type — it's authoritative for each leg.
                    const legPositionType = (leg.position_type || '').toLowerCase();
                    const isShort = legPositionType
                      ? legPositionType === 'short'
                      : (position.direction || '').toLowerCase() === 'credit';
                    const qty = legContractQty(leg);
                    let displaySymbol = underlying;
                    if (exp && strike) {
                      const [yyyy, mm, dd] = exp.split('-');
                      const yy = yyyy ? yyyy.slice(-2) : '';
                      displaySymbol = `${displaySymbol} ${mm}/${dd}/${yy} $${strike} ${callput}`;
                    }
                    const signedQty = isShort ? -qty : qty;

                    // Use per-leg entry price/mark if available; otherwise fall back to aggregate values.
                    const perLegAvg = parseFloat(leg.average_open_price ?? leg.average_price ?? positionAvg ?? 0);
                    const perLegMark = parseFloat(leg.mark_price ?? position.mark_price ?? position.currentPrice ?? 0);
                    const costBasisAbs = perLegAvg * qty * 100;
                    let finalCostBasis = costBasisAbs;
                    if (!hasRealPerLegAvg && clearingCostBasis > 0 && totalCostBasisFromLegs > 0) {
                      finalCostBasis = clearingCostBasis * (costBasisAbs / totalCostBasisFromLegs);
                    } else if (!hasRealPerLegAvg && clearingCostBasis > 0 && legs.length === 1) {
                      finalCostBasis = clearingCostBasis;
                    }
                    const marketValueAbs = perLegMark * qty * 100;

                    const expiration6digit = leg.expiration_6digit || (exp ? `${exp.slice(2, 4)}${exp.slice(5, 7)}${exp.slice(8, 10)}` : '');
                    positionsWithQuotes.push({
                      ...position,
                      symbol: displaySymbol,
                      quantity: signedQty,
                      cost_basis: isShort ? -finalCostBasis : finalCostBasis,
                      marketValue: marketValueAbs,
                      averagePrice: perLegAvg,
                      currentPrice: perLegMark,
                      mid: perLegMark,
                      accountName: account.name,
                      accountId: String(account.id),
                      isOption: true,
                      _legIndex: legIdx,
                      optionInfo: {
                        underlying: underlying,
                        type: typ === 'call' ? 'C' : 'P',
                        strike: strike,
                        expiration: expiration6digit,
                        isOption: true,
                      },
                    });
                  });
                  return;
                }
                const quantity = parseFloat(position.quantity || 0);
                const avgBuyPrice = parseFloat(position.average_buy_price || 0);
                // Robinhood raw equity positions don't have a cost_basis field;
                // compute it from average_buy_price * quantity
                let cost_basis = avgBuyPrice * Math.abs(quantity);
                let marketValue = parseFloat(position.marketValue || 0);

                if (quantity < 0 && cost_basis > 0) {
                  cost_basis = -cost_basis;
                }

                // Use live quote data fetched server-side from Robinhood /quotes/
                const lastPrice = parseFloat(position.last_trade_price || 0);
                const prevClose = parseFloat(position.previous_close || position.adjusted_previous_close || 0);

                if (lastPrice > 0) {
                  marketValue = Math.abs(quantity) * lastPrice;
                }
                const change = prevClose > 0 ? lastPrice - prevClose : 0;
                const changePercent = prevClose > 0 ? (change / prevClose) * 100 : 0;
                const averagePrice = avgBuyPrice;

                if (quantity === 0) return;

                positionsWithQuotes.push({
                  ...position,
                  quantity,
                  cost_basis,
                  marketValue: Math.abs(marketValue),
                  currentPrice: lastPrice,
                  change,
                  changePercent,
                  averagePrice,
                  accountName: account.name,
                  accountId: String(account.id),
                });
              });
            } else if (String(accountApiType).toLowerCase() === 'plaid') {
              // Plaid holdings arrive already normalized with daily-snapshot
              // prices (institution_price/value) — no live quote enrichment.
              positionsWithQuotes = positions
                .filter(p => Math.abs(p.quantity || 0) > 0)
                .map(p => ({
                  ...p,
                  accountName: account.name,
                  accountId: String(account.id),
                  isOption: false,
                }));
            } else if (String(accountApiType).toLowerCase() === 'tradier-mcp') {
              // Fetch quotes for all positions using Tradier REST API

              const allSymbols = [...new Set(positions.map(pos => pos.symbol).filter(Boolean))];

              if (allSymbols.length > 0) {
                try {
                  const quotesResponse = await apiRequest(`/api/accounts/${account.id}/quotes`, {
                    method: 'POST',
                    body: JSON.stringify({ symbols: allSymbols })
                  });

                  const quotes = quotesResponse.data?.quotes?.quotes?.quote || [];
                  const quotesArray = Array.isArray(quotes) ? quotes : [quotes];
                  const quotesMap = {};
                  quotesArray.forEach(quote => {
                    if (quote && quote.symbol) {
                      quotesMap[quote.symbol] = quote;
                    }
                  });

                  // Update positions with current prices
                  positions = positions.map(position => {
                    const quote = quotesMap[position.symbol];
                    if (quote) {
                      const bid = parseFloat(quote.bid) || 0;
                      const ask = parseFloat(quote.ask) || 0;
                      const mid = bid && ask ? (bid + ask) / 2 : (bid || ask || 0);
                      const currentPrice = mid || parseFloat(quote.last) || 0;
                      const isOption = position.isOption || /[A-Z]+\d{6}[CP]\d+/.test(position.symbol);
                      const marketValue = Math.abs(position.quantity) * currentPrice * (isOption ? 100 : 1);
                      const rawChange = parseFloat(quote.change);
                      const prevClose = parseFloat(quote.prevclose || quote.previous_close || quote.previousClose || quote.close || 0);
                      const change = !isNaN(rawChange) && rawChange !== 0 ? rawChange : (prevClose ? currentPrice - prevClose : 0);
                      const rawChangePct = parseFloat(quote.change_percentage);
                      const changePercent = !isNaN(rawChangePct) && rawChangePct !== 0 ? rawChangePct : (prevClose ? (change / prevClose) * 100 : 0);

                      return {
                        ...position,
                        currentPrice,
                        marketValue,
                        change,
                        changePercent
                      };
                    }
                    return position;
                  });
                } catch (quoteError) {
                  console.warn('[StockSummary] Failed to fetch Tradier quotes:', quoteError);
                }
              }

              positionsWithQuotes = positions.map(position => {
                return {
                  ...position,
                  accountName: account.name,
                  accountId: String(account.id),
                };
              }).filter(p => p.quantity !== 0 && Math.abs(p.quantity) > 0.01);
            } else {
              const symbols = positions.map(pos => pos.symbol).join(',');
              const quotesResponse = await apiRequest(`/api/accounts/${account.id}/quotes`, {
                method: 'POST',
                body: JSON.stringify({ symbols })
              });

              const quotesData = quotesResponse.data.quotes?.quotes?.quote || [];
              const quotesArray = Array.isArray(quotesData) ? quotesData : [quotesData];
              const quotes = {};
              quotesArray.forEach(quote => {
                if (quote && quote.symbol) {
                  quotes[quote.symbol] = quote;
                }
              });

              positionsWithQuotes = positions.map(position => {
                const q = quotes[position.symbol];
                const bid = parseFloat(q?.bid) || 0;
                const ask = parseFloat(q?.ask) || 0;
                const mid = bid && ask ? (bid + ask) / 2 : (bid || ask || 0);
                const currentPrice = mid || parseFloat(q?.last) || 0;
                const isOption = position.symbol.includes('_') || /[A-Z]+\d{6}[CP]\d+/.test(position.symbol);
                const marketValue = Math.abs(position.quantity) * currentPrice * (isOption ? 100 : 1);
                const averagePrice = parseFloat(position.averagePrice || position.average_price || position.price || 0);
                const costBasis = parseFloat(position.cost_basis || position.costBasis || position.cost || 0) || (averagePrice * Math.abs(position.quantity) * (isOption ? 100 : 1));
                const rawChange = parseFloat(q?.change);
                const prevClose = parseFloat(q?.prevclose || q?.previous_close || q?.previousClose || q?.close || 0);
                const change = !isNaN(rawChange) && rawChange !== 0 ? rawChange : (prevClose ? currentPrice - prevClose : 0);
                const rawChangePct = parseFloat(q?.change_percentage);
                const changePercent = !isNaN(rawChangePct) && rawChangePct !== 0 ? rawChangePct : (prevClose ? (change / prevClose) * 100 : 0);

                return {
                  ...position,
                  accountName: account.name,
                  accountId: String(account.id),
                  currentPrice,
                  change,
                  changePercent,
                  marketValue,
                  averagePrice,
                  cost_basis: costBasis,
                  isOption
                };
              }).filter(p => p.quantity !== 0); // Filter out zero quantity positions
            }

            allPositions.push(...positionsWithQuotes);
          }
          lastFetchTimesRef.current[account.id] = Date.now();
        } catch (error) {
          console.error(`Error fetching positions for account ${account.name}:`, error);
        }
      }

      // Filter out expired options from open positions
      try {
        const filtered = (allPositions || []).filter(p => {
          const raw = String(p.symbol || '').trim();
          const info = parseOptionSymbol(raw);
          if (!info.isOption || !info.expiration) return true;
          const exp = info.expiration;
          if (/^\d{6}$/.test(exp)) {
            const year = 2000 + parseInt(exp.substring(0, 2));
            const month = parseInt(exp.substring(2, 4)) - 1;
            const day = parseInt(exp.substring(4, 6));
            const expirationDate = new Date(year, month, day);
            expirationDate.setHours(23, 59, 59, 999);
            return expirationDate >= new Date();
          }
          return true;
        });
        allPositions.length = 0;
        allPositions.push(...filtered);
      } catch (filterErr) {
        console.warn('Expired-option filtering skipped:', filterErr?.message || filterErr);
      }

      // Reconcile with database trades to identify assigned positions
      // Also fetch manually assigned stocks that may not be in broker positions
      try {
        // Fetch all explicitly assigned trades so we don't drop rows just because
        // they fall outside the 5000 most-recent-trades window.
        const assignedResponse = await apiRequest('/api/trades?isAssigned=true&limit=100000');
        const allAssignedTrades = assignedResponse.data?.trades || assignedResponse.data?.value || (Array.isArray(assignedResponse.data) ? assignedResponse.data : []);

        // Fetch cash-flagged trades (cash-secured positions)
        const cashResponse = await apiRequest('/api/trades?isCash=true&limit=100000');
        const allCashTrades = cashResponse.data?.trades || cashResponse.data?.value || (Array.isArray(cashResponse.data) ? cashResponse.data : []);

        // Fetch a general batch for open-date backfill and unmatched opening trades.
        const tradesResponse = await apiRequest('/api/trades?limit=5000');
        const dbTrades = tradesResponse.data?.trades || tradesResponse.data?.value || (Array.isArray(tradesResponse.data) ? tradesResponse.data : []);

        const isTradeAssigned = (t) => {
          return t.isAssigned === true || t.isAssigned === 'true' || t.isAssigned === 'True' ||
            t.is_assigned === true || t.is_assigned === 'true' || t.is_assigned === 'True' ||
            t.transactionType === 'MANUAL_ASSIGN' || t.transaction_type === 'MANUAL_ASSIGN' ||
            t.transactionType === 'ASSIGNMENT' || t.transaction_type === 'ASSIGNMENT';
        };

        const isTradeCash = (t) => {
          return t.isCash === true || t.isCash === 'true' || t.isCash === 'True' ||
            t.is_cash === true || t.is_cash === 'true' || t.is_cash === 'True' ||
            t.transactionType === 'MANUAL_CASH' || t.transaction_type === 'MANUAL_CASH';
        };

        const isOpenTrade = (t) => {
          return !(t.closingDate || t.closing_date);
        };

        const isOpeningTrade = (t) => {
          const tt = String(t.tradeType || t.trade_type || '').toUpperCase();
          const pe = String(t.positionEffect || t.position_effect || '').toUpperCase();
          return pe.includes('SELL TO OPEN') || pe.includes('BUY TO OPEN') ||
            tt === 'BUY' || tt === 'SELL' || tt === 'BTO' || tt === 'STO';
        };

        // Stock rows created by option assignment never get a closing_date, so
        // lots whose delivered shares were later sold still read as "open".
        // FIFO-allocate all stock sells against stock buys per symbol+account to
        // find how many shares of each flagged lot are actually still held.
        // The trades endpoint caps at 1000 rows per request — page through so
        // sells outside the recent-trades window aren't missed.
        const hasOpenFlagged = [...allAssignedTrades, ...allCashTrades]
          .some(t => (isTradeAssigned(t) || isTradeCash(t)) && isOpenTrade(t));
        const coverTrades = [];
        if (hasOpenFlagged) {
          for (let off = 0; ; off += 1000) {
            const r = await apiRequest(`/api/trades?limit=1000&offset=${off}`);
            const batch = r.data?.trades || r.data?.value || (Array.isArray(r.data) ? r.data : []);
            coverTrades.push(...batch);
            if (batch.length < 1000 || off >= 50000) break;
          }
        }
        const allTradesById = new Map();
        [...coverTrades, ...dbTrades, ...allAssignedTrades, ...allCashTrades].forEach(t => { if (t && t.id != null) allTradesById.set(t.id, t); });
        const stockQtyOf = (t) => parseFloat(t.quantity || 0) || 0;
        const stockDate = (t) => String(t.tradeDate || t.trade_date || '');
        const stockSymKey = (t) => {
          const sym = String(t.symbol || t.originalSymbol || t.original_symbol || '').trim().toUpperCase();
          if (!sym || parseOptionSymbol(sym).isOption) return null;
          return `${t.accountId || t.account_id || ''}|${sym}`;
        };
        const buysByKey = new Map(), sellsByKey = new Map(), netByKey = new Map();
        allTradesById.forEach(t => {
          const key = stockSymKey(t);
          if (!key) return;
          const q = stockQtyOf(t);
          netByKey.set(key, (netByKey.get(key) || 0) + q);
          if (q > 0) (buysByKey.get(key) || buysByKey.set(key, []).get(key)).push(t);
          else if (q < 0) (sellsByKey.get(key) || sellsByKey.set(key, []).get(key)).push(t);
        });
        const remainingById = new Map();
        buysByKey.forEach((buys, key) => {
          buys.sort((a, b) => stockDate(a).localeCompare(stockDate(b)));
          const remaining = buys.map(t => Math.abs(stockQtyOf(t)));
          (sellsByKey.get(key) || []).sort((a, b) => stockDate(a).localeCompare(stockDate(b))).forEach(s => {
            let left = Math.abs(stockQtyOf(s));
            for (let i = 0; i < buys.length && left > 0; i++) {
              if (remaining[i] <= 0 || stockDate(buys[i]) > stockDate(s)) continue;
              const take = Math.min(remaining[i], left);
              remaining[i] -= take; left -= take;
            }
          });
          buys.forEach((t, i) => remainingById.set(t.id, remaining[i]));
        });
        // Sells can go unmatched in the FIFO pass when the bought shares were
        // never recorded here (transfers-in, pre-history) — so also cap each
        // flagged lot by the symbol's net holdings, newest lots first (FIFO
        // leaves the most recent lots standing after a partial sell-down).
        const flaggedRemainingById = new Map();
        const flaggedBuysByKey = new Map();
        [...allAssignedTrades, ...allCashTrades].forEach(t => {
          if (!(isTradeAssigned(t) || isTradeCash(t)) || !isOpenTrade(t) || stockQtyOf(t) <= 0) return;
          const key = stockSymKey(t);
          if (!key) return;
          (flaggedBuysByKey.get(key) || flaggedBuysByKey.set(key, []).get(key)).push(t);
        });
        flaggedBuysByKey.forEach((list, key) => {
          let netLeft = Math.max(0, netByKey.get(key) || 0);
          list.sort((a, b) => stockDate(b).localeCompare(stockDate(a)));
          list.forEach(t => {
            const shown = Math.min(remainingById.get(t.id) ?? Math.abs(stockQtyOf(t)), netLeft);
            netLeft -= shown;
            flaggedRemainingById.set(t.id, shown);
          });
        });
        // Shares of a flagged buy still held. Sell-side deliveries (qty<0) are
        // never held positions; rows outside the map had no covering sells.
        const flaggedRemaining = (t) => {
          if (stockQtyOf(t) <= 0) return 0;
          const rem = flaggedRemainingById.get(t.id);
          return rem === undefined ? Math.abs(stockQtyOf(t)) : rem;
        };

        // Identify all assigned trades from DB that are still open AND held
        const manualAssignTrades = allAssignedTrades.filter(t => {
          if (!isTradeAssigned(t) || !isOpenTrade(t)) return false;
          return flaggedRemaining(t) > 0;
        });

        // Identify all cash-flagged trades from DB that are still open AND held
        const manualCashTrades = allCashTrades.filter(t => {
          if (!isTradeCash(t) || !isOpenTrade(t)) return false;
          return flaggedRemaining(t) > 0;
        });

        // Identify all open trades from DB that are not matched
        // This catches STO/BTO trades without corresponding closes
        const openDbTrades = dbTrades.filter(t => {
          // Only include unmatched opening trades
          // Treat undefined as unmatched for backward compatibility with old trades
          const isMatched = t.isMatched === true || t.is_matched === true;
          if (isMatched) {
            return false;
          }
          if (isTradeAssigned(t)) {
            return false; // Already handled
          }

          // Don't filter closed trades - display DB values as-is
          // Removed filtering of closing_date and realized_pl

          // Filter out expired options
          const sym = String(t.symbol || t.originalSymbol || t.original_symbol || '').trim();
          const optInfo = parseOptionSymbol(sym);
          if (optInfo.isOption && optInfo.expiration) {
            const exp = optInfo.expiration;
            if (/^\d{6}$/.test(exp)) {
              const year = 2000 + parseInt(exp.substring(0, 2));
              const month = parseInt(exp.substring(2, 4)) - 1;
              const day = parseInt(exp.substring(4, 6));
              const expirationDate = new Date(year, month, day);
              expirationDate.setHours(23, 59, 59, 999);
              if (expirationDate < new Date()) {
                return false;
              }
            }
          } else if (sym.includes('Call') || sym.includes('Put') || sym.includes('/')) {
            // Symbol might be in human-readable format, try to extract date
            const dateMatch = sym.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
            if (dateMatch) {
              const month = parseInt(dateMatch[1]) - 1;
              const day = parseInt(dateMatch[2]);
              const year = parseInt(dateMatch[3]);
              const fullYear = year < 100 ? 2000 + year : year;
              const expirationDate = new Date(fullYear, month, day);
              expirationDate.setHours(23, 59, 59, 999);
              if (expirationDate < new Date()) {
                return false;
              }
            }
          }

          return true;
        });

        // Create a set of broker position keys (symbol + accountId) to avoid double counting
        const brokerPositionKeys = new Set(allPositions.map(p => {
          const sym = (p.symbol || '').trim().toUpperCase();
          const accId = String(p.accountId || p.account_id || '');
          return `${sym}_${accId}`;
        }));

        // Add manual assigned positions that aren't in broker positions
        manualAssignTrades.forEach(t => {
          const sym = (t.symbol || t.originalSymbol || t.original_symbol || '').trim();
          const symUpper = sym.toUpperCase();
          const tAccId = String(t.accountId || t.account_id || '');
          const posKey = `${symUpper}_${tAccId}`;

          // Add assigned positions as long as they are open and not already shown in
          // the broker/manual position feed. The isOpenTrade() check above already
          // excludes closed trades; if an assigned row is still open, we surface it
          // for any account type so deliberately flagged assignments are not hidden.
          const account = accountsRef.current.find(a => String(a.id) === tAccId);
          if (!account) return;

          // Skip if this symbol + account is already in broker positions, or the
          // account was skipped this cycle (its positions are already preserved).
          if (brokerPositionKeys.has(posKey) || skippedAccountIds.has(tAccId)) return;

          // Add as a position from manual assignment — only the shares still
          // held (assigned lots partially sold show the remaining quantity)
          const origQty = Math.abs(parseFloat(t.quantity || 0)) || 1;
          const qty = flaggedRemaining(t);
          const costBasis = Math.abs(parseFloat(t.cost || t.cost_basis || 0)) * (qty / origQty);
          const avgPrice = qty > 0 ? costBasis / qty : 0;

          allPositions.push({
            symbol: sym,
            quantity: qty,
            currentPrice: avgPrice, // Use cost as estimate since no market price
            marketValue: costBasis,
            change: 0,
            changePercent: 0,
            accountName: account?.name || 'Manual Account',
            accountId: tAccId,
            averagePrice: avgPrice,
            cost_basis: parseFloat(t.cost || t.cost_basis || 0) || costBasis,
            isOption: false,
            isAssigned: true,
            isManualAssign: true,
            transactionType: t.transactionType || t.transaction_type
          });
        });

        // Add manual cash positions that aren't in broker positions
        manualCashTrades.forEach(t => {
          const sym = (t.symbol || t.originalSymbol || t.original_symbol || '').trim();
          const symUpper = sym.toUpperCase();
          const tAccId = String(t.accountId || t.account_id || '');
          const posKey = `${symUpper}_${tAccId}`;
          const account = accountsRef.current.find(a => String(a.id) === tAccId);
          if (!account || brokerPositionKeys.has(posKey) || skippedAccountIds.has(tAccId)) return;
          const origQty = Math.abs(parseFloat(t.quantity || 0)) || 1;
          const qty = flaggedRemaining(t);
          const costBasis = Math.abs(parseFloat(t.cost || t.cost_basis || 0)) * (qty / origQty);
          const avgPrice = qty > 0 ? costBasis / qty : 0;
          allPositions.push({
            symbol: sym,
            quantity: qty,
            currentPrice: avgPrice,
            marketValue: costBasis,
            change: 0,
            changePercent: 0,
            accountName: account?.name || 'Manual Account',
            accountId: tAccId,
            averagePrice: avgPrice,
            cost_basis: parseFloat(t.cost || t.cost_basis || 0) || costBasis,
            isOption: false,
            isCash: true,
            isManualCash: true,
            transactionType: t.transactionType || t.transaction_type
          });
        });

        // Futures-option helper: Schwab option instruments on futures underlyings
        // (/MES, /ES, ...) carry symbols like "/MS4EV26_P7600:XCME" and an
        // underlyingSymbol like ".MS4EV26:XCME". The positions API never returns
        // them, so their imported fills are the only source for open positions.
        // optionInfo comes from the instrument payload (expirationDate, putCall,
        // strikePrice) because the futures month-code symbol carries no expiry date.
        const futuresOptionInfo = (t) => {
          const items = (t.rawData || t.raw_data || {}).transferItems || [];
          const hit = items.find(i => {
            const inst = i.instrument || {};
            if (inst.assetType !== 'OPTION') return false;
            const s = String(inst.symbol || '');
            const u = String(inst.underlyingSymbol || '');
            return s.startsWith('/') || u.startsWith('.');
          });
          if (!hit) return null;
          const inst = hit.instrument;
          const exp = String(inst.expirationDate || '').slice(0, 10); // YYYY-MM-DD
          if (!exp) return null;
          const expiration = `${exp.slice(2, 4)}${exp.slice(5, 7)}${exp.slice(8, 10)}`;
          const expDate = new Date(`${exp}T23:59:59`);
          const raw = String(inst.symbol || inst.underlyingSymbol || '');
          const root = raw.replace(/^\/+/, '').replace(/_.*$/, '').replace(/\..*$/, '').replace(/:.*$/, '');
          // Futures options carry their own premium multiplier (e.g. /MES = 5).
          // Treating them as equity options (×100) inflates value/P&L 20×.
          const multiplier = parseFloat(inst.optionPremiumMultiplier) || 1;
          return {
            isOption: true,
            isFuturesOption: true,
            multiplier,
            underlying: `/${root}`,
            expiration,
            expDate,
            type: String(inst.putCall || '').toUpperCase() === 'CALL' ? 'C' : 'P',
            strike: parseFloat(inst.strikePrice) || 0,
            // Fallback mark when the quote endpoint has nothing fresher.
            closingPrice: parseFloat(inst.closingPrice) || 0,
            // Quote endpoint DOES serve futures options as the uniform symbol
            // minus the exchange suffix, e.g. "./MS4EV26P7600".
            quoteSymbol: String(inst.uniformSymbol || '').replace(/:.*$/, ''),
            // Order entry validates against the full uniform symbol WITH
            // exchange suffix, e.g. "./MS4EV26P7600:XCME" — both the stripped
            // quote form and the raw "/MS4EV26_P7600:XCME" are rejected as
            // "Invalid option symbol".
            orderSymbol: inst.uniformSymbol || inst.symbol || null,
            originalSymbol: inst.symbol || t.originalSymbol || t.symbol
          };
        };
        // Net open quantity per futures-option contract so closed/expired fills
        // don't linger as phantom positions. Recent-window fills only — a close
        // older than the loaded trades could leave a stale leg.
        const futuresNetQty = new Map();
        // Key on the raw contract symbol — open and close fills carry the same
        // original_symbol, and positionContractKey would merge calls/puts on the
        // same futures root into one key.
        const futuresContractKeyOf = (t) =>
          `${t.accountId || t.account_id}|${String(t.originalSymbol || t.original_symbol || t.symbol || '').trim().toUpperCase()}`;
        dbTrades.forEach(t => {
          if (!futuresOptionInfo(t)) return;
          const k = futuresContractKeyOf(t);
          futuresNetQty.set(k, (futuresNetQty.get(k) || 0) + (parseFloat(t.quantity) || 0));
        });
        const emittedFuturesContracts = new Set();

        // Add open trades from DB that aren't in broker positions (STO without close, etc.)
        // IMPORTANT: DB-derived positions only apply to MANUAL accounts. Schwab/Tradier
        // (and other API brokers) are fully sourced from the broker API, so we must never
        // surface raw DB trades as open positions for them. Manual accounts are already
        // reconciled by the backend (/api/trades/positions/open) and pushed above, so we
        // skip them here to avoid double counting. This prevents phantom stock positions
        // from appearing for API accounts when only option positions exist.
        // Exception: futures options are never returned by broker positions APIs, so
        // their open fills are surfaced here (netted against closes) or they'd be
        // invisible entirely.
        openDbTrades.forEach(t => {
          const sym = (t.symbol || t.originalSymbol || t.original_symbol || '').trim();
          const symUpper = sym.toUpperCase();
          const tAccId = String(t.accountId || t.account_id || '');
          const posKey = `${symUpper}_${tAccId}`;

          const account = accountsRef.current.find(a => String(a.id) === tAccId);
          const isManual = account && String(account.apiType || account.api_type).toLowerCase() === 'manual';

          // Accounts skipped this refresh keep their preserved positions — pushing
          // a DB-derived row for them again would render a duplicate.
          if (skippedAccountIds.has(tAccId)) return;

          // Only manual accounts may surface DB-derived positions. For manual accounts the
          // backend reconciliation already supplied positions above, so nothing to add here.
          if (!isManual) {
            const futOpt = futuresOptionInfo(t);
            if (!futOpt) return;
            const fk = futuresContractKeyOf(t);
            if (emittedFuturesContracts.has(fk)) return;
            const netQty = futuresNetQty.get(fk) || 0;
            if (!netQty || futOpt.expDate < new Date()) return;
            emittedFuturesContracts.add(fk);
            const origQty = Math.abs(parseFloat(t.quantity || 0)) || 1;
            const costBasis = Math.abs(parseFloat(t.cost || t.cost_basis || 0));
            // `cost` is total dollars per contract (points × optionPremiumMultiplier).
            // Price fields must stay in option points (7.3) so downstream valuation
            // applies the contract multiplier — not the dollar total.
            const mult = futOpt.multiplier || 1;
            const pointPrice = parseFloat(t.price) || (origQty > 0 && mult > 0 ? costBasis / (origQty * mult) : 0);
            // No live quote exists for futures options — Schwab's own
            // closingPrice on the instrument is the best available mark.
            const markPrice = futOpt.closingPrice > 0 ? futOpt.closingPrice : pointPrice;
            allPositions.push({
              symbol: t.originalSymbol || t.original_symbol || t.symbol,
              quantity: netQty,
              currentPrice: markPrice,
              marketValue: Math.abs(netQty) * markPrice * mult,
              change: 0,
              changePercent: 0,
              accountName: account?.name || 'Database Trade',
              accountId: tAccId,
              averagePrice: pointPrice,
              cost_basis: netQty < 0 ? -costBasis * Math.abs(netQty) / origQty : costBasis * Math.abs(netQty) / origQty,
              contractMultiplier: mult,
              isOption: true,
              optionInfo: futOpt,
              isDbTrade: true,
              isFuturesOption: true,
              tradeType: t.tradeType || t.trade_type,
              transactionType: t.transactionType || t.transaction_type
            });
            return;
          }

          // Skip if this symbol + account is already in broker/manual positions
          if (brokerPositionKeys.has(posKey)) {
            return;
          }

          // Add as a position from DB open trade
          const costBasis = Math.abs(parseFloat(t.cost || t.cost_basis || 0));
          const proceeds = Math.abs(parseFloat(t.proceeds || 0));
          const qty = parseFloat(t.quantity || 0);
          const avgPrice = qty > 0 ? costBasis / qty : 0;

          // For options, parse the symbol to get option info
          const optInfo = parseOptionSymbol(sym);

          const newPosition = {
            symbol: sym,
            quantity: qty,
            currentPrice: avgPrice, // Use cost/proceeds as estimate since no market price
            marketValue: costBasis || proceeds,
            change: 0,
            changePercent: 0,
            accountName: account?.name || 'Database Trade',
            accountId: tAccId,
            averagePrice: avgPrice,
            cost_basis: costBasis,
            isOption: optInfo.isOption,
            optionInfo: optInfo.isOption ? optInfo : null,
            isDbTrade: true,
            tradeType: t.tradeType || t.trade_type,
            transactionType: t.transactionType || t.transaction_type
          };

          allPositions.push(newPosition);
        });

        // Build a map of earliest opening trade date per contract (and per
        // account), so every open position can display its trade open date.
        // This covers broker (Schwab/Tradier) positions too, since the broker
        // positions API does not return an open date.
        const openDateByKey = new Map();
        const recordOpenDate = (key, dateStr) => {
          if (!key || !dateStr) return;
          const existing = openDateByKey.get(key);
          if (!existing || new Date(dateStr) < new Date(existing)) {
            openDateByKey.set(key, dateStr);
          }
        };
        // Same earliest-fill tracking, but for the full timestamp — the Open
        // Date column shows the fill time when the position opened today.
        const openTsByKey = new Map();
        const recordOpenTs = (key, tsStr) => {
          if (!key || !tsStr) return;
          const existing = openTsByKey.get(key);
          if (!existing || new Date(tsStr) < new Date(existing)) {
            openTsByKey.set(key, tsStr);
          }
        };
        // Positions opened before the recent-trades window (or beyond the
        // per-request row cap) still need their open date — fetch ALL open
        // trades (closing_date IS NULL), paged, instead of relying on dbTrades.
        const allOpenTrades = [];
        for (let off = 0; ; off += 1000) {
          const r = await apiRequest(`/api/trades?openOnly=true&limit=1000&offset=${off}`);
          const batch = r.data?.trades || r.data?.value || (Array.isArray(r.data) ? r.data : []);
          allOpenTrades.push(...batch);
          if (batch.length < 1000 || off >= 50000) break;
        }
        // Signed open quantity per broker order, per contract. Lets the
        // positions view attribute each open contract to the order(s) that
        // opened it, so two strategies sharing legs stay separate groups.
        const orderLotsByKey = new Map();
        const signedOpenQty = (t) => {
          const raw = parseFloat(t.quantity || 0) || 0;
          if (raw < 0) return raw;
          const tt = String(t.tradeType || t.trade_type || t.action || '').toUpperCase().replace(/_/g, ' ');
          const pe = String(t.positionEffect || t.position_effect || '').toUpperCase().replace(/_/g, ' ');
          if (tt.includes('SELL') || tt.startsWith('STO') || pe.includes('SELL')) return -Math.abs(raw);
          return Math.abs(raw);
        };

        const allTradesForOpenDates = [...allOpenTrades, ...dbTrades, ...allAssignedTrades];
        // The three arrays overlap — the same open trade appears in both the
        // paged openOnly fetch and the recent-trades window. Dedupe by id so
        // order lots aren't double-counted (double lots break the coverage
        // check and collapse every spread into individual legs).
        const seenTradeIds = new Set();
        const openLotsRawByKey = new Map();
        const closedQtyByKey = new Map();
        const closedQtyByOrder = new Map();
        const closeGroupsByOrder = new Map();
        let lotSeq = 0;
        allTradesForOpenDates.forEach(t => {
          const tid = t.id ?? `${t.orderId || t.order_id}|${t.originalSymbol || t.original_symbol || t.symbol}|${t.quantity}`;
          if (seenTradeIds.has(tid)) return;
          seenTradeIds.add(tid);
          const symRaw = (t.originalSymbol || t.original_symbol || t.symbol || '').trim();
          if (!symRaw) return;
          const accId = String(t.accountId || t.account_id || '');
          const contractKey = positionContractKey(symRaw);
          const tt = String(t.tradeType || t.trade_type || t.action || '').toUpperCase().replace(/_/g, ' ');
          const pe = String(t.positionEffect || t.position_effect || '').toUpperCase().replace(/_/g, ' ');
          const isClosingFill = tt.includes('CLOSE') || pe.includes('CLOSE');
          // Closing fills retire the OLDEST open lot (broker FIFO). Without this
          // a contract opened by two orders and partially closed attributes the
          // surviving leg to the wrong order, splitting its spread into solo legs.
          if (accId && isClosingFill) {
            const k = `${accId}|${contractKey}`;
            const c = -signedOpenQty(t);
            closedQtyByKey.set(k, (closedQtyByKey.get(k) || 0) + c);
            const closeOrderId = t.orderId || t.order_id;
            if (closeOrderId) {
              if (!closedQtyByOrder.has(k)) closedQtyByOrder.set(k, new Map());
              const m = closedQtyByOrder.get(k);
              const coid = String(closeOrderId);
              m.set(coid, (m.get(coid) || 0) + c);
            }
            // Group close fills by the REAL closing order id (raw transaction's
            // orderId) — the stored order_id is the opening order's stamp.
            const realCloseOrder = String(t.rawData?.orderId || t.raw_data?.orderId || closeOrderId || '');
            if (realCloseOrder) {
              if (!closeGroupsByOrder.has(realCloseOrder)) closeGroupsByOrder.set(realCloseOrder, []);
              closeGroupsByOrder.get(realCloseOrder).push({ key: k, qty: c });
            }
            return;
          }
          // Earliest non-closed trade for a contract is its open date. We avoid
          // the stricter isOpeningTrade() here because it misses option opens
          // stored as buy_to_open/OPENING, which would drop their open dates.
          if (!isOpenTrade(t)) return;
          const dateStr = t.tradeDate || t.trade_date;
          if (dateStr) {
            if (accId) recordOpenDate(`${accId}|${contractKey}`, dateStr);
            recordOpenDate(`GLOBAL|${contractKey}`, dateStr);
          }
          const tsStr = t.tradeTimestamp || t.trade_timestamp;
          if (tsStr) {
            if (accId) recordOpenTs(`${accId}|${contractKey}`, tsStr);
            recordOpenTs(`GLOBAL|${contractKey}`, tsStr);
          }
          // Order lots are account-scoped only — a global fallback could merge
          // fills from another account holding the same contract.
          const orderId = t.orderId || t.order_id;
          if (accId && orderId) {
            if (!openLotsRawByKey.has(`${accId}|${contractKey}`)) openLotsRawByKey.set(`${accId}|${contractKey}`, []);
            openLotsRawByKey.get(`${accId}|${contractKey}`).push({ orderId: String(orderId), qty: signedOpenQty(t), tradeDate: dateStr || '', cost: parseFloat(t.cost ?? t.cost_basis ?? NaN), seq: lotSeq++ });
          }
        });
        // A multi-leg closing order unwinds ONE strategy. Its "home" opening
        // order is the order that uniquely owns one of its legs — legs shared
        // across orders (e.g. two condors both long the same put) don't vote.
        // Resolving the home first lets a shared leg retire against the order
        // whose companion leg actually closed, keeping the other order's
        // spread intact.
        const closeOrderHome = new Map();
        closeGroupsByOrder.forEach((legs, oid) => {
          const votes = new Map();
          legs.forEach(({ key }) => {
            const owners = new Set((openLotsRawByKey.get(key) || []).map(l => l.orderId));
            if (owners.size === 1) {
              const owner = [...owners][0];
              votes.set(owner, (votes.get(owner) || 0) + 1);
            }
          });
          let best = null, bestN = 0;
          votes.forEach((n, o) => { if (n > bestN) { best = o; bestN = n; } });
          if (best) closeOrderHome.set(oid, best);
        });
        // Per-contract ordered retirement preference: closing-order homes
        // first, then the stamped order id on the fill.
        const prefByContract = new Map();
        closeGroupsByOrder.forEach((legs, oid) => {
          const home = closeOrderHome.get(oid);
          if (!home) return;
          legs.forEach(({ key, qty }) => {
            if (!prefByContract.has(key)) prefByContract.set(key, []);
            prefByContract.get(key).push({ orderId: home, qty });
          });
        });

        openLotsRawByKey.forEach((lots, key) => {
          // FIFO retirement: same-day fills order by numeric order id (Schwab
          // order ids increase over time), falling back to insertion order.
          lots.sort((a, b) => {
            const d = String(a.tradeDate).localeCompare(String(b.tradeDate));
            if (d !== 0) return d;
            const on = Number(a.orderId) - Number(b.orderId);
            return Number.isFinite(on) && on !== 0 ? on : a.seq - b.seq;
          });
          let closed = closedQtyByKey.get(key) || 0;
          // Preferred lot order: the closing order's home first, then the
          // order id stamped on the close fill (Schwab attributes closes to
          // the opening order via positionId). Remainder falls back to FIFO.
          const prefs = [...(prefByContract.get(key) || [])];
          (closedQtyByOrder.get(key) || new Map()).forEach((q, o) => prefs.push({ orderId: o, qty: q }));
          const rem = lots.map(l => Math.abs(l.qty));
          for (const p of prefs) {
            if (p.qty === 0 || Math.sign(p.qty) !== Math.sign(closed)) continue;
            let need = Math.abs(p.qty);
            for (let i = 0; i < lots.length && need > 1e-9; i++) {
              const lot = lots[i];
              if (lot.orderId !== p.orderId || Math.sign(lot.qty) !== Math.sign(p.qty)) continue;
              const take = Math.min(rem[i], need, Math.abs(closed));
              rem[i] -= take;
              need -= take;
              closed = Math.sign(closed) * (Math.abs(closed) - take);
            }
          }
          const surviving = [];
          for (let i = 0; i < lots.length; i++) {
            const lot = lots[i];
            let r = rem[i];
            if (closed !== 0 && Math.sign(lot.qty) === Math.sign(closed)) {
              const retire = Math.min(r, Math.abs(closed));
              r -= retire;
              closed = Math.sign(closed) * (Math.abs(closed) - retire);
            }
            if (r > 1e-9) surviving.push({ ...lot, qty: Math.sign(lot.qty) * r });
          }
          if (surviving.length) orderLotsByKey.set(key, surviving);
        });

        // Order-history supplement: fills opened since the last transaction
        // sync aren't in the trades table yet, but the (poller-kept) order
        // cache already has them. Derive opening lots from FILLED orders'
        // legs so two strategies sharing a strike today still bucket by
        // order id instead of falling back to same-date merging.
        try {
          const schwabAccIds = [...new Set(allPositions.map(p => String(p.accountId || p.account_id || '')).filter(Boolean))]
            .filter(id => (accountsRef.current || []).some(a => String(a.id) === id
              && String(a.apiType || a.api_type || '').toLowerCase() === 'schwab'));
          const histLotsByKey = new Map();
          for (const accId of schwabAccIds) {
            let orders;
            try {
              const r = await apiRequest(`/api/schwab-api/orders?accountId=${accId}`);
              orders = r.data?.orders || [];
            } catch (e) {
              console.warn(`[StockSummary] order-history fetch failed for account ${accId}:`, e.message);
              continue;
            }
            for (const o of orders) {
              if (String(o.status || '').toUpperCase() !== 'FILLED') continue;
              for (const leg of o.orderLegCollection || []) {
                const instr = String(leg.instruction || '').toUpperCase();
                if (!instr.endsWith('_OPEN')) continue; // opening fills only
                const sym = leg.instrument?.symbol;
                const qty = parseFloat(leg.quantity) || 0;
                if (!sym || !qty) continue;
                const key = `${accId}|${positionContractKey(sym)}`;
                if (o.enteredTime) recordOpenTs(key, o.enteredTime);
                const orderId = String(o.orderId);
                // Skip orders whose fills already landed in the trades table
                if ((orderLotsByKey.get(key) || []).some(l => l.orderId === orderId)) continue;
                if (!histLotsByKey.has(key)) histLotsByKey.set(key, new Map());
                const m = histLotsByKey.get(key);
                const cur = m.get(orderId) || { orderId, qty: 0, tradeDate: String(o.enteredTime || '').slice(0, 10), cost: NaN, seq: lotSeq++ };
                cur.qty += instr.startsWith('SELL') ? -Math.abs(qty) : Math.abs(qty);
                m.set(orderId, cur);
              }
            }
          }
          histLotsByKey.forEach((m, key) => {
            const existing = orderLotsByKey.get(key) || [];
            orderLotsByKey.set(key, [...existing, ...m.values()]);
          });
        } catch (e) {
          console.warn('[StockSummary] order-history lot supplement failed:', e.message);
        }

        const assignedMap = new Map();

        allAssignedTrades.forEach(t => {
          if (isTradeAssigned(t) && isOpenTrade(t) && flaggedRemaining(t) > 0) {
            const accId = String(t.accountId || t.account_id || '');
            const symRaw = (t.symbol || t.originalSymbol || t.original_symbol || '').trim();
            if (!symRaw) return;

            const candidates = [];
            const symUpper = symRaw.toUpperCase();
            candidates.push(symUpper);

            if (symUpper.includes(' ')) {
              candidates.push(symUpper.replace(/\s+/g, '.'));
            }
            if (symUpper.includes('.')) {
              candidates.push(symUpper.replace(/\./g, ' '));
            }

            const optInfo = parseOptionSymbol(symRaw);
            if (optInfo && optInfo.isOption && optInfo.underlying) {
              const undUpper = optInfo.underlying.toUpperCase();
              candidates.push(undUpper);
              if (undUpper.includes(' ')) candidates.push(undUpper.replace(/\s+/g, '.'));
              if (undUpper.includes('.')) candidates.push(undUpper.replace(/\./g, ' '));
            }

            candidates.forEach(cs => {
              if (accId) {
                assignedMap.set(`${accId}_${cs}`, true);
              }
              assignedMap.set(`GLOBAL_${cs}`, true);
            });
          }
        });

        const cashMap = new Map();

        allCashTrades.forEach(t => {
          if (isTradeCash(t) && isOpenTrade(t) && flaggedRemaining(t) > 0) {
            const accId = String(t.accountId || t.account_id || '');
            const symRaw = (t.symbol || t.originalSymbol || t.original_symbol || '').trim();
            if (!symRaw) return;

            const candidates = [];
            const symUpper = symRaw.toUpperCase();
            candidates.push(symUpper);

            if (symUpper.includes(' ')) {
              candidates.push(symUpper.replace(/\s+/g, '.'));
            }
            if (symUpper.includes('.')) {
              candidates.push(symUpper.replace(/\./g, ' '));
            }

            const optInfo = parseOptionSymbol(symRaw);
            if (optInfo && optInfo.isOption && optInfo.underlying) {
              const undUpper = optInfo.underlying.toUpperCase();
              candidates.push(undUpper);
              if (undUpper.includes(' ')) candidates.push(undUpper.replace(/\s+/g, '.'));
              if (undUpper.includes('.')) candidates.push(undUpper.replace(/\./g, ' '));
            }

            candidates.forEach(cs => {
              if (accId) {
                cashMap.set(`${accId}_${cs}`, true);
              }
              cashMap.set(`GLOBAL_${cs}`, true);
            });
          }
        });

        allPositions = allPositions.map(p => {
          const sym = (p.symbol || '').trim().toUpperCase();
          const accId = String(p.accountId || p.account_id || '');

          const matchKeys = [];
          if (accId) matchKeys.push(`${accId}_${sym}`);
          matchKeys.push(`GLOBAL_${sym}`);

          if (sym.includes(' ')) matchKeys.push(`GLOBAL_${sym.replace(/\s+/g, '.')}`);
          if (sym.includes('.')) matchKeys.push(`GLOBAL_${sym.replace(/\./g, ' ')}`);
          if (accId && sym.includes(' ')) matchKeys.push(`${accId}_${sym.replace(/\s+/g, '.')}`);
          if (accId && sym.includes('.')) matchKeys.push(`${accId}_${sym.replace(/\./g, ' ')}`);

          // Attach the trade open date. Prefer a broker-provided date
          // (e.g. Tradier `date_acquired`), then fall back to the earliest
          // opening trade for this contract derived from stored trades.
          const contractKey = positionContractKey(p.originalSymbol || p.symbol);
          const openDate = p.openDate
            || p.date_acquired
            || p.tradeDate
            || openDateByKey.get(`${accId}|${contractKey}`)
            || openDateByKey.get(`GLOBAL|${contractKey}`)
            || null;
          const openTs = openTsByKey.get(`${accId}|${contractKey}`)
            || openTsByKey.get(`GLOBAL|${contractKey}`)
            || (p.openDate && /\d{1,2}:\d{2}/.test(String(p.openDate)) ? p.openDate : null)
            || null;
          const orderLots = orderLotsByKey.get(`${accId}|${contractKey}`) || null;

          // Only account-scoped assigned matches; avoid cross-account false positives
          const assignedKeys = accId ? matchKeys.filter(k => k.startsWith(`${accId}_`)) : [];
          const isMatch = p.isAssigned || assignedKeys.some(key => assignedMap.has(key));
          const isCashMatch = p.isCash || matchKeys.some(key => cashMap.has(key));

          if (isMatch || isCashMatch) {
            return { ...p, isAssigned: isMatch || p.isAssigned, isCash: isCashMatch || p.isCash, openDate, openTs, orderLots };
          }
          return { ...p, openDate, openTs, orderLots };
        });

        // Live marks for synthesized futures-option positions: the positions
        // API omits them but the quotes endpoint does serve them under the
        // uniform symbol minus exchange suffix ("./MS4EV26P7600").
        try {
          const futQuotesNeeded = allPositions.filter(p => p.isFuturesOption && p.optionInfo?.quoteSymbol);
          if (futQuotesNeeded.length > 0) {
            const schwabAccount = (accountsRef.current || []).find(acc => String(acc.apiType || acc.api_type).toLowerCase() === 'schwab');
            if (schwabAccount?.id) {
              const symbols = [...new Set(futQuotesNeeded.map(p => p.optionInfo.quoteSymbol))];
              const qResp = await apiRequest('/api/schwab-api/quotes', {
                method: 'POST',
                body: JSON.stringify({ accountId: schwabAccount.id, symbols }),
              });
              const quoteMap = qResp.data?.quotes || {};
              allPositions = allPositions.map(p => {
                if (!p.isFuturesOption || !p.optionInfo?.quoteSymbol) return p;
                // Endpoint normalizes quotes to {bid, ask, mid, last, mark, ...}
                const q = quoteMap[p.optionInfo.quoteSymbol] || {};
                // For shorts the liquidation mark is the ask side; mark (when
                // present) then mid then last is the priority chain.
                const mark = parseFloat(q.mark) || parseFloat(q.mid) || parseFloat(q.last) || 0;
                if (!mark) return p;
                const mult = p.contractMultiplier || p.optionInfo?.multiplier || 1;
                const qty = Math.abs(parseFloat(p.quantity) || 0);
                const signedQty = parseFloat(p.quantity) || 0;
                const markChange = parseFloat(q.markChange ?? q.change) || 0;
                return {
                  ...p,
                  currentPrice: mark,
                  marketValue: qty * mark * mult,
                  change: markChange * signedQty * mult,
                  changePercent: parseFloat(q.change_percentage) || 0,
                };
              });
            }
          }
        } catch (futQuoteErr) {
          console.warn('Futures-option quote refresh skipped:', futQuoteErr?.message || futQuoteErr);
        }
      } catch (dbError) {
        console.warn('Reconciliation with database trades failed:', dbError);
      }

      setOpenPositions(allPositions);
      openPositionsRef.current = allPositions;

      // Save to cache for next load
      setCachedOpenPositions(allPositions);

    } catch (error) {
      console.error('Error fetching open positions:', error);
      setOpenPositions([]);
      openPositionsRef.current = [];
    } finally {
      if (!silent) setPositionsLoading(false);
    }
  }, [selectedAccount, accountsLoaded, refreshKey, paperMode]);

  // Initial fetch of open positions
  useEffect(() => {
    if (accountsLoaded || !selectedAccount.includes('all')) {
      fetchOpenPositions(false);
    }
  }, [fetchOpenPositions, accountsLoaded, selectedAccount, refreshKey, paperMode]);

  // Auto-refresh open positions during market hours using the shortest configured account interval.
  useEffect(() => {
    if (!accountsLoaded && selectedAccount.includes('all')) return;
    const activeAccounts = accountsRef.current.filter(a => a.isActive);
    if (activeAccounts.length === 0) return;

    const intervalMs = Math.min(
      ...activeAccounts.map(a => {
        const sec = Number(a.positionUpdateInterval || a.position_update_interval);
        return (Number.isFinite(sec) && sec > 0 ? sec : 30) * 1000;
      })
    );

    const interval = setInterval(() => {
      if (isMarketOpen()) {
        fetchOpenPositions(true);
      }
    }, intervalMs);
    return () => clearInterval(interval);
  }, [fetchOpenPositions, accountsLoaded, selectedAccount]);

  // Fetch pending (recently-closed, not-yet-settled) positions. Isolated from
  // the open/closed position logic so it cannot affect existing functionality.
  // Resolve a position group to its chain(s): fills link via chain_id/chainId
  // directly or via their trade id through the fillChain map.
  const groupChainPL = (group) => {
    const ids = new Set();
    const norm = s => String(s || '').replace(/\s+/g, '').toUpperCase();
    const walk = (o, depth) => {
      if (!o || depth > 3) return;
      const cid = o.chain_id || o.chainId
        || chainPLMap.fillChain?.[o.id]
        || chainPLMap.symbolChain?.[norm(o.original_symbol || o.symbol)]
        || chainPLMap.symbolChain?.[norm(o.contractSymbol)];
      if (cid) ids.add(cid);
      (o.trades || []).forEach(t => walk(t, depth + 1));
      (o.fills || []).forEach(t => walk(t, depth + 1));
      (o.legs || []).forEach(l => walk(l, depth + 1));
    };
    walk(group, 0);
    if (!ids.size) return null;
    let v = 0, found = false;
    ids.forEach(id => { const pl = chainPLMap.pl?.[id]; if (pl != null) { v += pl; found = true; } });
    return found ? v : null;
  };

  useEffect(() => {
    apiRequest('/api/trade-chains/summary').then(r => {
      setChainPLMap({ pl: r.data?.pl || {}, fillChain: r.data?.fillChain || {}, symbolChain: r.data?.symbolChain || {} });
    }).catch(() => {});
  }, []);

  // Fetch the anchor close for each equity symbol in view — anchored to the
  // selected duration's start date (periods without a start = all-time P/L).
  const DUR_FROM = (() => {
    if (timePeriod === 'custom') return customStartDate || '1970-01-01';
    if (timePeriod === 'month' || timePeriod === 'current_month') {
      const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    }
    return getRangeForPeriod(timePeriod)?.startDate || '1970-01-01';
  })();
  const DUR_LABEL = {
    '30d': '30D', '60d': '60D', '90d': '90D', '180d': '180D',
    'ytd': 'YTD', 'month': 'MTD', 'current_month': 'MTD',
    'previous_month': 'PrevMo', 'custom': 'Dur', 'all': 'All',
  }[timePeriod] || 'Dur';
  // Anchors are per-from-date — drop stale ones when the period changes.
  useEffect(() => { setDurPrices({}); }, [DUR_FROM]);
  useEffect(() => {
    const syms = [...new Set((groupedPositions || [])
      .flatMap(g => g.isSpread ? (g.legs || []) : [g.position])
      .filter(p => p && p.symbol && !p.optionInfo?.isOption)
      .map(p => String(p.symbol).trim().toUpperCase()))]
      .filter(s => !(s in durPrices));
    if (!syms.length) return;
    apiRequest(`/api/stock-duration-pl/prices?from=${DUR_FROM}&symbols=${syms.join(',')}`)
      .then(r => {
        const body = r.data?.prices ? r.data : (r.prices ? r : {});
        setDurPrices(prev => ({ ...prev, ...(body.prices || {}) }));
      })
      .catch(() => {});
  }, [groupedPositions, DUR_FROM]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open P/L (Stocks) card: duration total by default, click toggles all-time
  const [openStocksDurMode, setOpenStocksDurMode] = useState(true);

  // Bid/ask width defense for roll/close orders — wide spreads get negligent
  // fills (the FAS roll). Detect the worst enabled leg; >15% requires ack.
  const [rollCloseWideAck, setRollCloseWideAck] = useState(false);
  useEffect(() => { if (!rollCloseDialog.open) setRollCloseWideAck(false); }, [rollCloseDialog.open]);
  const rollCloseWidthWarn = () => {
    let worst = null;
    (rollCloseDialog.legs || []).forEach(leg => {
      if (leg.enabled === false) return;
      const key = rollCloseLegQuoteKey(leg, rollCloseDialog.underlying).replace(/\s+/g, '');
      const q = rollCloseQuotes[key];
      if (!q || !(q.bid > 0) || !(q.ask > 0)) return;
      const pct = (q.ask - q.bid) / ((q.bid + q.ask) / 2);
      if (!worst || pct > worst.pct) worst = { pct, sym: key };
    });
    return worst && worst.pct > 0.15 ? worst : null;
  };
  const wideWarn = rollCloseWidthWarn();

  // Duration P/L for a stock position: lots held at the anchor price
  // gain (mark - anchor); lots bought during the period gain (mark - lot cost).
  const stockDurationPL = (position) => {
    if (!position || position.optionInfo?.isOption) return null;
    const anchor = durPrices[String(position.symbol || '').trim().toUpperCase()];
    const qty = Math.abs(position.quantity ?? position.longQuantity ?? position.settledLongQuantity ?? 0);
    if (!qty) return null;
    const mark = position.currentPrice ?? position.mark
      ?? (position.marketValue != null ? position.marketValue / qty : null)
      ?? position.averagePrice;
    if (mark == null) return null;
    // FIFO open lots from the position's fills when available
    const fills = (position.trades || position.fills || [])
      .filter(t => typeof t.quantity === 'number' && t.quantity !== 0)
      .slice()
      .sort((a, b) => String(a.trade_date || a.tradeDate || '').localeCompare(String(b.trade_date || b.tradeDate || '')));
    if (fills.length) {
      const buys = fills.filter(f => f.quantity > 0).map(f => ({ q: f.quantity, px: f.price, d: String(f.trade_date || f.tradeDate || '').slice(0, 10) }));
      let sellQ = fills.filter(f => f.quantity < 0).reduce((s, f) => s + Math.abs(f.quantity), 0);
      const lots = [];
      for (const b of buys) {
        const take = Math.min(b.q, sellQ); sellQ -= take;
        if (b.q - take > 0) lots.push({ q: b.q - take, px: b.price, d: b.d });
      }
      if (lots.length) {
        let pl = 0;
        for (const lot of lots) {
          pl += lot.q * (lot.d < DUR_FROM ? (mark - (anchor ?? lot.px)) : (mark - lot.px));
        }
        return pl;
      }
    }
    // Fallback: single-lot heuristic on the position's open date
    if (anchor == null) return null;
    const d = String(position.openDate || '').slice(0, 10);
    return d && d < DUR_FROM ? qty * (mark - anchor) : qty * (mark - (position.averagePrice || mark));
  };

  const fetchPendingPositions = useCallback(async (force = false) => {
    if (paperMode) { setPendingPositions([]); return; }
    try {
      const acct = selectedAccount.includes('all') ? 'all' : selectedAccount.join(',');
      const resp = await apiRequest(`/api/pending-positions?accountId=${acct}${force ? '&force=true' : ''}`);
      const list = (resp && resp.data && Array.isArray(resp.data.pending)) ? resp.data.pending : [];
      setPendingPositions(list);
    } catch (e) {
      console.warn('Failed to fetch pending positions:', e?.message || e);
    }
  }, [selectedAccount, paperMode]);

  // Fetch working (unfilled) closing orders to flag open positions with closing orders in flight.
  const fetchWorkingCloseOrders = useCallback(async () => {
    if (paperMode) { setWorkingCloseOrders([]); return; }
    try {
      // Schwab + direct Robinhood REST (robinhood / robinhood-api) support
      // working-order lookups. robinhood-mcp stays excluded — it's throttled
      // and fetched separately in fetchRecentOrders.
      const workingOrderTypes = ['schwab', 'robinhood', 'robinhood-api'];
      const eligibleAccounts = selectedAccount.includes('all')
        ? accountsRef.current.filter(a => a.isActive && workingOrderTypes.includes((a.apiType || '').toLowerCase())).map(a => String(a.id))
        : selectedAccount.filter(id => {
            const acc = accountsRef.current.find(a => String(a.id) === String(id));
            return acc && acc.isActive && workingOrderTypes.includes((acc.apiType || '').toLowerCase());
          });
      if (eligibleAccounts.length === 0) {
        setWorkingCloseOrders([]);
        return;
      }
      const acct = eligibleAccounts.join(',');
      const resp = await apiRequest(`/api/working-orders?accountId=${acct}`);
      const list = (resp && resp.data && Array.isArray(resp.data.workingOrders)) ? resp.data.workingOrders : [];
      setWorkingCloseOrders(list);
    } catch (e) {
      console.warn('Failed to fetch working orders:', e?.message || e);
    }
  }, [selectedAccount, paperMode]);

  // Wire ref so pollRollCloseOrderStatus can call it without forward-reference
  fetchWorkingCloseOrdersRef.current = fetchWorkingCloseOrders;

  // Fetch recent orders (working + filled today) for "New Trades" section
  const fetchRecentOrders = useCallback(async (force = false) => {
    if (!accountsLoaded || paperMode) { setRecentOrders([]); return; }
    setRecentOrdersLoading(true);
    try {
      const brokerTypes = ['schwab', 'robinhood', 'robinhood-api', 'robinhood-mcp'];
      const selectedAccountStrs = selectedAccount.map(String);
      const accountsToFetch = selectedAccountStrs.includes('all')
        ? accountsRef.current.filter(a => a.isActive && brokerTypes.includes((a.apiType || '').toLowerCase()))
        : accountsRef.current.filter(a => a.isActive && selectedAccountStrs.includes(String(a.id)) && brokerTypes.includes((a.apiType || '').toLowerCase()));

      const allOrders = [];
      const now = Date.now();
      const robinhoodMcpThrottleMs = 5 * 60 * 1000; // 5 minutes — MCP has a daily/rate API limit
      for (const account of accountsToFetch) {
        const apiType = (account.apiType || '').toLowerCase();
        const isRobinhoodMcp = apiType === 'robinhood-mcp';
        const isRobinhoodDirect = apiType === 'robinhood' || apiType === 'robinhood-api';
        const isRobinhood = isRobinhoodMcp || isRobinhoodDirect;
        try {
          if (isRobinhood) {
            // Robinhood MCP is throttled (rate-limited API); Robinhood direct-API
            // (robin_stocks) has no such limit and can refresh every cycle, like Schwab.
            if (isRobinhoodMcp) {
              const lastFetchForAccount = lastRobinhoodFetchRef.current.get(String(account.id)) || 0;
              if (!force && now - lastFetchForAccount < robinhoodMcpThrottleMs) {
                // Preserve existing MCP orders from current state instead of dropping them
                const existingRh = recentOrdersRef.current.filter(o => {
                  const acctId = String(o._accountId || '');
                  return acctId === String(account.id);
                });
                allOrders.push(...existingRh);
                continue;
              }
              lastRobinhoodFetchRef.current.set(String(account.id), now);
            }
            let rhOrders;
            if (isRobinhoodDirect) {
              // Fetch from Robinhood direct-API (robin_stocks Python client) — no throttle
              const resp = await apiRequest(`/api/robinhood-api/orders/${account.id}`);
              rhOrders = resp.data?.orders || [];
            } else {
              // Fetch from Robinhood MCP
              const resp = await apiRequest(`/api/mcp/robinhood/orders?accountId=${account.id}`);
              rhOrders = resp.data?.orders || [];
            }
            // Normalize Robinhood orders to Schwab-like structure for the UI
            const normalizedRh = rhOrders.map(o => {
              // Direct-API orders have legs inside raw; MCP orders have legs at top level
              const rhLegs = o.legs || o.raw?.legs || [];
              const chainSymbol = o.chain_symbol || o.chainSymbol || o.raw?.chain_symbol || '';
              const orderLegCollection = rhLegs.map(l => {
                const side = String(l.side || '').toUpperCase();
                const effect = String(l.position_effect || l.positionEffect || '').toUpperCase();
                let instruction = 'BUY_TO_OPEN';
                if (effect === 'OPEN' && side === 'BUY') instruction = 'BUY_TO_OPEN';
                else if (effect === 'OPEN' && side === 'SELL') instruction = 'SELL_TO_OPEN';
                else if (effect === 'CLOSE' && side === 'BUY') instruction = 'BUY_TO_CLOSE';
                else if (effect === 'CLOSE' && side === 'SELL') instruction = 'SELL_TO_CLOSE';

                // Build OCC-format symbol (YYMMDD + C/P + 8-digit strike)
                const expDateRaw = l.expirationDate || l.expiration_date || '';
                const yy = expDateRaw.slice(2, 4);
                const mm = expDateRaw.slice(5, 7);
                const dd = expDateRaw.slice(8, 10);
                const expDate = `${yy}${mm}${dd}`;
                const putCall = (l.optionType || l.option_type || '').toLowerCase() === 'put' ? 'P' : 'C';
                const strikeNum = parseFloat(l.strikePrice || l.strike_price || 0);
                const strikeStr = String(Math.round(strikeNum * 1000)).padStart(8, '0');
                const symbol = `${chainSymbol}${expDate}${putCall}${strikeStr}`;

                return {
                  instrument: {
                    symbol,
                    assetType: 'OPTION',
                    underlyingSymbol: chainSymbol,
                  },
                  instruction,
                  quantity: Number(l.quantity || l.ratio_quantity || o.quantity || 0),
                  positionEffect: l.position_effect || l.positionEffect || '',
                };
              });
              // Equity orders have no legs — synthesize one from the top-level
              // side so the Working Trades classifier can see them.
              if (!orderLegCollection.length) {
                orderLegCollection.push({
                  instrument: { symbol: o.symbol || '', assetType: o.assetType || 'EQUITY' },
                  instruction: String(o.side || '').toUpperCase() === 'SELL' ? 'SELL' : 'BUY',
                  quantity: Number(o.quantity || 0),
                  positionEffect: o.positionEffect || '',
                });
              }
              return {
                ...o,
                orderId: o.orderId || o.id,
                orderLegCollection,
                enteredDateTime: o.enteredTime || o.created_at || '',
                orderType: o.type || o.orderType || '',
                price: o.price,
              };
            });
            // Robinhood statuses: live, pending, confirmed, unconfirmed, queued, filled, cancelled, rejected, expired
            // Only show working/active orders in the Working Trades section — filled orders are tracked elsewhere
            const workingRh = normalizedRh.filter(o => ['LIVE', 'PENDING', 'CONFIRMED', 'UNCONFIRMED', 'QUEUED'].includes(o.status));
            workingRh.forEach(o => {
              o._accountName = account.name;
              o._accountId = String(account.id);
            });
            allOrders.push(...workingRh);
          } else {
            // Fetch Schwab working orders (WORKING + PENDING_ACTIVATION)
            const workingResp = await apiRequest(`/api/schwab-api/orders?accountId=${account.id}&status=WORKING`);
            const working = workingResp.data?.orders || [];
            const pendingResp = await apiRequest(`/api/schwab-api/orders?accountId=${account.id}&status=PENDING_ACTIVATION`);
            const pending = pendingResp.data?.orders || [];
            // Tag with account info
            [...working, ...pending].forEach(o => {
              o._accountName = account.name;
              o._accountId = String(account.id);
            });
            allOrders.push(...working, ...pending);
          }
        } catch (err) {
          console.warn('[fetchRecentOrders] account', account.id, apiType, 'fetch failed:', err?.response?.data || err?.message || err);
        }
      }
      // Sort: working first, then by entered time desc
      const workingStatuses = ['WORKING', 'PENDING_ACTIVATION', 'LIVE', 'PENDING', 'CONFIRMED', 'UNCONFIRMED', 'QUEUED'];
      allOrders.sort((a, b) => {
        const aWorking = workingStatuses.includes(a.status);
        const bWorking = workingStatuses.includes(b.status);
        if (aWorking && !bWorking) return -1;
        if (!aWorking && bWorking) return 1;
        const aTime = a.enteredDateTime || a.enteredDate || a.enteredTime || '';
        const bTime = b.enteredDateTime || b.enteredDate || b.enteredTime || '';
        return bTime.localeCompare(aTime);
      });
      // Show opening trades + their OTO child exit orders + standalone working closing orders
      const expandedOrders = [];
      const parentOrderIds = new Set(); // track parent order IDs to link standalone children
      allOrders.forEach(o => {
        // Support both Schwab (orderLegCollection) and Robinhood (legs) order structures
        const legs = o.orderLegCollection || o.legs || [];
        const getInstruction = (l) => {
          if (l.instruction) return String(l.instruction).toUpperCase();
          // Map Robinhood side/position_effect to instruction
          const side = String(l.side || '').toUpperCase();
          const effect = String(l.position_effect || l.positionEffect || '').toUpperCase();
          if (effect === 'OPEN' && side === 'BUY') return 'BUY_TO_OPEN';
          if (effect === 'OPEN' && side === 'SELL') return 'SELL_TO_OPEN';
          if (effect === 'CLOSE' && side === 'BUY') return 'BUY_TO_CLOSE';
          if (effect === 'CLOSE' && side === 'SELL') return 'SELL_TO_CLOSE';
          return '';
        };
        const isOpening = legs.some(l => {
          const instr = getInstruction(l);
          return instr === 'BUY_TO_OPEN' || instr === 'SELL_TO_OPEN' || instr === 'BUY';
        });
        const isClosing = legs.some(l => {
          const instr = getInstruction(l);
          return instr === 'BUY_TO_CLOSE' || instr === 'SELL_TO_CLOSE' || instr === 'SELL' || instr === 'BUY_TO_COVER';
        });
        if (isOpening) {
          expandedOrders.push(o);
          // Extract child exit orders from TRIGGER strategies
          const children = o.childOrderStrategies || [];
          children.forEach((child, childIdx) => {
            const childLegs = child.orderLegCollection || [];
            const childIsClosing = childLegs.some(l =>
              l.instruction === 'BUY_TO_CLOSE' || l.instruction === 'SELL_TO_CLOSE'
            );
            if (childIsClosing) {
              expandedOrders.push({
                ...child,
                // Nested children may not have status/orderId — inherit from parent
                status: child.status || 'PENDING_ACTIVATION',
                orderId: child.orderId || o.orderId,
                enteredDateTime: child.enteredDateTime || o.enteredDateTime,
                enteredDate: child.enteredDate || o.enteredDate,
                _accountName: o._accountName,
                _accountId: o._accountId,
                _parentOrderId: o.orderId,
                _isExitOrder: true,
                _exitIndex: childIdx,
              });
            }
          });
        } else if (isClosing) {
          // Standalone closing order (parent already filled, child activated as separate order)
          expandedOrders.push({
            ...o,
            _isExitOrder: true,
            _exitIndex: -1, // standalone, not nested
          });
        }
      });
      setRecentOrders(expandedOrders);
    } catch (err) {
      console.warn('Failed to fetch recent orders:', err?.message || err);
    } finally {
      setRecentOrdersLoading(false);
    }
  }, [selectedAccount, accountsLoaded, paperMode]);

  // Fetch quotes for all option legs in recent orders via /api/market-quotes,
  // which prefers the streaming feed (Schwab LEVELONE_OPTIONS / Tradier
  // events) and falls back to REST — no per-account lookup needed here.
  const fetchNewTradeQuotes = useCallback(async () => {
    const orders = recentOrdersRef.current;
    if (!orders || orders.length === 0) return;
    const allSymbols = new Set();
    orders.forEach(order => {
      (order.orderLegCollection || []).forEach(leg => {
        const sym = leg.instrument?.symbol || '';
        if (sym) allSymbols.add(sym);
      });
    });
    if (allSymbols.size === 0) return;
    try {
      const resp = await apiRequest(`/api/market-quotes?symbols=${[...allSymbols].map(encodeURIComponent).join(',')}`);
      const quoteMap = resp.data?.quotes || {};
      const out = {};
      allSymbols.forEach(raw => {
        const symKey = raw.replace(/\s+/g, '');
        const q = quoteMap[raw.trim().toUpperCase()] || {};
        out[symKey] = {
          bid: q.bid ?? 0,
          ask: q.ask ?? 0,
          mid: (q.bid != null && q.ask != null) ? (q.bid + q.ask) / 2 : (q.last ?? 0),
          last: q.last ?? 0,
        };
      });
      setNewTradeQuotes(out);
    } catch (err) {
      // non-critical
    }
  }, []);

  // Wire ref for manual refresh
  const fetchRecentOrdersRef = useRef(fetchRecentOrders);
  fetchRecentOrdersRef.current = fetchRecentOrders;

  // Track recent orders in a ref so fetchNewTradeQuotes can access latest without re-creating callback
  const recentOrdersRef = useRef([]);
  recentOrdersRef.current = recentOrders;

  // Initial fetch + auto-refresh on the SAME cadence as the open-position scan
  // (every 30s for Schwab, every 5min for Robinhood MCP due to daily API limits).
  const lastRobinhoodFetchRef = useRef(new Map());
  useEffect(() => {
    fetchPendingPositions(false);
    fetchWorkingCloseOrders();
    fetchRecentOrders();
    const interval = setInterval(() => {
      fetchPendingPositions(false);
      fetchWorkingCloseOrders();
      fetchRecentOrders();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchPendingPositions, fetchWorkingCloseOrders, fetchRecentOrders]);

  // Poll quotes for new trades every 30s via Schwab API (independent of Robinhood order throttle)
  useEffect(() => {
    fetchNewTradeQuotes();
    const quoteInterval = setInterval(fetchNewTradeQuotes, 3000);
    return () => clearInterval(quoteInterval);
  }, [fetchNewTradeQuotes, recentOrders]);

  // Fetch closed trades and build symbol x period pivot
  useEffect(() => {
    const fetchData = async () => {
      // Try to load from cache first for instant display
      const cached = getCachedData();
      if (cached) {
        setSymbolData(cached.symbolData || []);
        setClosedTotalsRow(cached.closedTotalsRow || null);
        setClosedPeriodColumns(cached.closedPeriodColumns || []);
        setLoading(false);
      }

      // Always fetch fresh data in background
      try {
        const tradesBaseUrl = paperMode ? '/api/paper-trading/trades-formatted' : '/api/trades';
        let tradesUrl = tradesBaseUrl;
        const tradesParams = new URLSearchParams();

        if (timePeriod === 'custom' && customStartDate && customEndDate) {
          tradesParams.append('startDate', customStartDate);
          tradesParams.append('endDate', customEndDate);
        } else {
          const range = getRangeForPeriod(timePeriod);
          if (range) {
            tradesParams.append('startDate', range.startDate);
            tradesParams.append('endDate', range.endDate);
          }
        }

        // Include open/unclosed trades (closing_date is null) so manual Pershing SELL rows
        // with no closing_date are still available for client-side FIFO reconciliation.
        tradesParams.append('includeOpen', 'true');

        // Always fetch all trades, filter in UI based on selectedAccount

        if (tradesParams.toString()) {
          tradesUrl += `?${tradesParams.toString()}`;
        }

        const fetchAllTrades = async (baseUrl, chunkSize = 500) => {
          const all = [];
          const seen = new Set();
          let offset = 0;
          let total = null;
          while (true) {
            const sep = baseUrl.includes('?') ? '&' : '?';
            const url = `${baseUrl}${sep}limit=${chunkSize}&offset=${offset}`;
            const resp = await apiRequest(url);
            const data = resp.data || {};
            const batch = data.trades || data || [];
            if (Array.isArray(batch)) {
              batch.forEach(t => {
                if (t && t.id != null) {
                  if (seen.has(t.id)) return;
                  seen.add(t.id);
                }
                all.push(t);
              });
            }
            const reportedTotal = typeof data.total === 'number' ? data.total : null;
            if (reportedTotal !== null) {
              total = reportedTotal;
            }
            if ((total !== null && all.length >= total) || (!Array.isArray(batch) || batch.length < chunkSize)) {
              break;
            }
            offset += chunkSize;
          }
          return all;
        };

        const trades = await fetchAllTrades(tradesUrl, 500);

        const reconciledTrades = reconcileTradesForDisplay(trades);
        setAllTradesForTags(reconciledTrades);

        // Filter trades by account first
        let accountFilteredTrades = reconciledTrades;
        if (!selectedAccount.includes('all')) {
          accountFilteredTrades = reconciledTrades.filter(trade => {
            const accId = String(trade.accountId || trade.account_id || '');
            const selectedAccountIds = selectedAccount.map(id => String(id));
            return selectedAccountIds.includes(accId);
          });
        }

        // Position type filter is applied in applyClosedPositionFilters for reactive filtering
        // Compute the selected time range first so we can filter unclosed trades client-side.
        let rangeStart;
        let rangeEnd;
        if (timePeriod === 'custom' && customStartDate && customEndDate) {
          rangeStart = new Date(customStartDate + 'T00:00:00Z');
          rangeEnd = new Date(customEndDate + 'T23:59:59Z');
        } else {
          const range = getRangeForPeriod(timePeriod);
          if (range) {
            rangeStart = new Date(range.startDate + 'T00:00:00Z');
            rangeEnd = new Date(range.endDate + 'T23:59:59Z');
          } else {
            const now = new Date();
            rangeStart = new Date(now);
            rangeStart.setMonth(rangeStart.getMonth() - 1);
            rangeEnd = new Date(now);
          }
        }

        // The server returns unclosed trades (closing_date IS NULL) when includeOpen=true,
        // so enforce the date range client-side using closingDate || tradeDate.
        // Bank cash-flow rows (Plaid depository income) are not positions.
        let pivotInput = accountFilteredTrades.filter(trade => {
          if (isBankCashFlowRow(trade)) return false;
          const td = trade.closingDate || trade.tradeDate;
          if (!td) return false;
          const d = new Date(td);
          if (isNaN(d.getTime())) return false;
          if (rangeStart && d < rangeStart) return false;
          if (rangeEnd && d > rangeEnd) return false;
          return true;
        });

        // Exclude current open positions from closed-position display. After
        // reconcileTradesForDisplay, an opening trade still open has no closingDate,
        // no closing side, and is not a dividend/close type. Closing SELL rows and
        // option closes are still included.
        pivotInput = pivotInput.filter(trade => {
          if (trade.closingDate) return true;
          if (trade._hideOpeningLeg) return false;
          if (trade._closedSide || trade._isClosing) return true;
          const tt = String(trade.tradeType || trade.trade_type || trade.transactionType || trade.transaction_type || '').toUpperCase();
          const ttNorm = tt.replace(/_/g, ' ');
          if (tt === 'DIVIDEND' || tt === 'DIV') return true;
          if (tt === 'SELL' || tt === 'STC' || tt === 'BTC' || ttNorm.includes('TO CLOSE') || tt === 'OEXP' || tt === 'OASGN' || tt === 'ASSIGNMENT' || tt === 'EXPIRE') return true;
          return false;
        });

        const getPeriodKey = (trade) => {
          const td = trade.closingDate || trade.tradeDate;
          if (closedGroupBy === 'week') {
            const date = new Date(td);
            const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const dayNum = d.getUTCDay() || 7;
            d.setUTCDate(d.getUTCDate() + 4 - dayNum);
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
            const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
            return `${d.getUTCFullYear()}-${String(weekNo).padStart(2, '0')}`;
          }
          if (closedGroupBy === 'quarter') {
            const date = new Date(td);
            const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const q = Math.floor(d.getUTCMonth() / 3) + 1;
            return `${d.getUTCFullYear()}-Q${q}`;
          }
          if (closedGroupBy === 'month') return (td || '').slice(0, 7);
          if (closedGroupBy === 'year') return (td || '').slice(0, 4);
          return (td || '').slice(0, 10);
        };

        const getPeriodKeyFromDate = (dateObj) => {
          const y = dateObj.getUTCFullYear();
          const m = dateObj.getUTCMonth();
          const d = dateObj.getUTCDate();
          const base = new Date(Date.UTC(y, m, d));
          if (closedGroupBy === 'week') {
            const dayNum = base.getUTCDay() || 7;
            const isoThursday = new Date(base);
            isoThursday.setUTCDate(base.getUTCDate() + 4 - dayNum);
            const yearStart = new Date(Date.UTC(isoThursday.getUTCFullYear(), 0, 1));
            const weekNo = Math.ceil(((isoThursday - yearStart) / 86400000 + 1) / 7);
            return `${isoThursday.getUTCFullYear()}-${String(weekNo).padStart(2, '0')}`;
          }
          if (closedGroupBy === 'quarter') {
            const q = Math.floor(base.getUTCMonth() / 3) + 1;
            return `${base.getUTCFullYear()}-Q${q}`;
          }
          if (closedGroupBy === 'month') {
            return `${base.getUTCFullYear()}-${String(base.getUTCMonth() + 1).padStart(2, '0')}`;
          }
          if (closedGroupBy === 'year') {
            return `${base.getUTCFullYear()}`;
          }
          return base.toISOString().slice(0, 10);
        };

        const colSet = new Set();
        const pivotMap = new Map();

        const getUnderlyingSymbol = (trade) => {
          const raw = String(trade.originalSymbol || trade.symbol || '').trim();
          const info = parseOptionSymbol(raw);
          if (info?.isOption) {
            return info.underlying || '';
          }
          const normalized = raw.replace(/_/g, ' ').trim();
          const tokens = normalized.split(/\s+/).filter(Boolean);
          if (tokens.length >= 2 && /^[A-Z]$/.test(tokens[1])) {
            return `${tokens[0]} ${tokens[1]}`;
          }
          return tokens[0] || normalized || raw;
        };

        const isOptionExpired = (trade) => {
          const info = parseOptionSymbol(trade.originalSymbol || trade.symbol || '');
          if (!info.isOption || !info.expiration) return false;
          const exp = info.expiration;
          if (/^\d{6}$/.test(exp)) {
            const year = 2000 + parseInt(exp.substring(0, 2));
            const month = parseInt(exp.substring(2, 4)) - 1;
            const day = parseInt(exp.substring(4, 6));
            const expirationDate = new Date(year, month, day);
            expirationDate.setHours(23, 59, 59, 999);
            return expirationDate < new Date();
          }
          return false;
        };

        const isClosingTrade = (trade) => {
          const tt = (String(trade.tradeType || '')).toUpperCase().trim();
          if (!tt) return false;
          if (tt.includes('TO CLOSE') || tt.includes('STC') || tt.includes('BTC') || tt.includes('CLOSE')) {
            return true;
          }
          if (tt.includes('TO OPEN') || tt.includes('BTO') || tt.includes('STO')) {
            return false;
          }
          if (tt.startsWith('SELL')) return true;
          if (tt.includes('EXP')) return true;
          return false;
        };

        const getRealizedValueForPivot = (trade) => {
          // For dividend-only rows, realizedPL should not contribute (the dividend
          // field carries the value separately). Only use realizedPL for actual
          // closing trades (sells, option closes, etc.).
          const tt = String(trade.tradeType || trade.trade_type || '').toUpperCase();
          const isDividend = tt === 'DIVIDEND' || tt === 'DIV';
          if (isDividend) return 0;

          return getClosedTradeNetPL(trade);
        };

        const getDividendValue = (trade) => {
          const div = trade.dividend || trade.dividend_amount || 0;
          return parseFloat(div) || 0;
        };

        // First pass: collect all trades per symbol from pivotInput (after position filtering)
        // This ensures the trades match the filtered totals
        const symbolTradesMap = new Map();
        const tradeIdSet = new Set(); // Track trade IDs to prevent duplicates
        pivotInput.forEach(trade => {
          const symbol = getUnderlyingSymbol(trade);
          if (!symbol) return;
          // Skip AGG (synthesized aggregate) trades as they're not actual user trades
          if (trade.tradeType === 'AGG') return;
          // Use trade ID or combination of fields to deduplicate
          const tradeId = trade.id || trade.external_id || `${trade.symbol}_${trade.tradeDate}_${trade.quantity}_${trade.price}`;
          if (tradeIdSet.has(tradeId)) return;
          tradeIdSet.add(tradeId);

          if (!symbolTradesMap.has(symbol)) {
            symbolTradesMap.set(symbol, []);
          }
          symbolTradesMap.get(symbol).push(trade);
        });

        // Build pivot with P/L calculations - include all trades for closed positions
        // Use same deduplication to prevent double-counting
        const pivotTradeIdSet = new Set();
        pivotInput.forEach(trade => {
          // Skip AGG trades and deduplicate
          if (trade.tradeType === 'AGG') return;
          const tradeId = trade.id || trade.external_id || `${trade.symbol}_${trade.tradeDate}_${trade.quantity}_${trade.price}`;
          if (pivotTradeIdSet.has(tradeId)) return;
          pivotTradeIdSet.add(tradeId);

          const periodKey = getPeriodKey(trade);
          if (!periodKey) return;

          const symbol = getUnderlyingSymbol(trade);
          if (!symbol) return;
          const realized = getRealizedValueForPivot(trade);
          const dividend = getDividendValue(trade);

          colSet.add(periodKey);
          if (!pivotMap.has(symbol)) {
            pivotMap.set(symbol, {
              symbol,
              periodTotals: {},
              periodClosed: {},
              periodDiv: {},
              totalPL: 0,
              totalDividendOnly: 0,
              openPL: 0,
              trades: [] // Will be populated after pivot construction
            });
          }
          const row = pivotMap.get(symbol);
          row.periodTotals[periodKey] = (row.periodTotals[periodKey] || 0) + realized + dividend;
          row.periodClosed[periodKey] = (row.periodClosed[periodKey] || 0) + realized;
          row.periodDiv[periodKey] = (row.periodDiv[periodKey] || 0) + dividend;
          row.totalPL += (realized + dividend);
          row.totalDividendOnly += dividend;
        });

        // Attach all trades to each pivot row after pivot calculation
        pivotMap.forEach((row, symbol) => {
          row.trades = symbolTradesMap.get(symbol) || [];
        });

        const columnsChrono = [];
        if (rangeStart && rangeEnd && rangeStart <= rangeEnd) {
          const cursor = new Date(Date.UTC(rangeStart.getUTCFullYear(), rangeStart.getUTCMonth(), rangeStart.getUTCDate()));
          const end = new Date(Date.UTC(rangeEnd.getUTCFullYear(), rangeEnd.getUTCMonth(), rangeEnd.getUTCDate()));
          let lastKey = null;
          while (cursor <= end) {
            const key = getPeriodKeyFromDate(cursor);
            if (key && key !== lastKey) {
              columnsChrono.push(key);
              lastKey = key;
            }
            cursor.setUTCDate(cursor.getUTCDate() + 1);
          }
        }
        Array.from(colSet.values()).forEach(k => {
          if (!columnsChrono.includes(k)) columnsChrono.push(k);
        });
        const finalColumns = columnsChrono.reverse();
        setClosedPeriodColumns(finalColumns);
        const rows = Array.from(pivotMap.values());

        const reconcileWithStockPerformance = async (symbolRows) => {
          const baseParams = new URLSearchParams();
          if (timePeriod === 'custom' && customStartDate && customEndDate) {
            baseParams.append('startDate', customStartDate);
            baseParams.append('endDate', customEndDate);
          } else {
            const range = getRangeForPeriod(timePeriod);
            if (range) {
              baseParams.append('startDate', range.startDate);
              baseParams.append('endDate', range.endDate);
            }
          }
          if (!selectedAccount.includes('all')) {
            baseParams.append('accountId', selectedAccount.join(','));
          }

          const candidates = symbolRows
            .filter(r => typeof r.totalPL === 'number' && Math.abs(r.totalPL) > 0.01)
            .map(r => r.symbol)
            .filter(Boolean);

          const uniqueSymbols = Array.from(new Set(candidates));
          if (uniqueSymbols.length === 0) return symbolRows;

          const perfResults = await Promise.all(uniqueSymbols.map(async (sym) => {
            try {
              const url = `/api/stocks/${encodeURIComponent(sym)}/performance${baseParams.toString() ? `?${baseParams.toString()}` : ''}`;
              const resp = await apiRequest(url);
              return { symbol: sym, data: resp.data && resp.data.performance };
            } catch (e) {
              console.error('Error fetching stock performance for summary reconciliation:', sym, e);
              return { symbol: sym, data: null };
            }
          }));

          const perfMap = new Map();
          perfResults.forEach(({ symbol, data }) => {
            perfMap.set(symbol, data);
          });

          symbolRows.forEach(row => {
            const perf = perfMap.get(row.symbol);
            if (!perf) return;
            const serverRealized = parseFloat(perf.realizedPL);
            if (isNaN(serverRealized) || Math.abs(serverRealized) < 0.01) return;
            const clientPL = row.totalPL || 0;
            if (clientPL === 0) {
              row.totalPL = serverRealized;
              const currentTotal = Object.values(row.periodTotals || {}).reduce((s, v) => s + (v || 0), 0);
              if (Math.abs(currentTotal) > 0.01) {
                const factor = serverRealized / currentTotal;
                Object.keys(row.periodTotals || {}).forEach(k => {
                  row.periodTotals[k] = (row.periodTotals[k] || 0) * factor;
                });
                Object.keys(row.periodClosed || {}).forEach(k => {
                  row.periodClosed[k] = (row.periodClosed[k] || 0) * factor;
                });
                Object.keys(row.periodDiv || {}).forEach(k => {
                  row.periodDiv[k] = (row.periodDiv[k] || 0) * factor;
                });
              }
              return;
            }
            const signsDiffer = clientPL * serverRealized < 0;
            const similarMag = Math.abs(Math.abs(clientPL) - Math.abs(serverRealized)) < 1.0;
            if (signsDiffer && similarMag) {
              const currentTotal = Object.values(row.periodTotals || {}).reduce((s, v) => s + (v || 0), 0);
              row.totalPL = serverRealized;
              if (Math.abs(currentTotal) > 0.01) {
                const factor = serverRealized / currentTotal;
                Object.keys(row.periodTotals || {}).forEach(k => {
                  row.periodTotals[k] = (row.periodTotals[k] || 0) * factor;
                });
                Object.keys(row.periodClosed || {}).forEach(k => {
                  row.periodClosed[k] = (row.periodClosed[k] || 0) * factor;
                });
                Object.keys(row.periodDiv || {}).forEach(k => {
                  row.periodDiv[k] = (row.periodDiv[k] || 0) * factor;
                });
              }
            }
          });

          return symbolRows;
        };

        const reconciledRows = rows;
        setSymbolData(reconciledRows);

        const totalsRow = { symbol: 'Total', periodTotals: {}, periodClosed: {}, periodDiv: {}, totalPL: 0, totalDividendOnly: 0 };
        reconciledRows.forEach((row) => {
          totalsRow.totalPL += row.totalPL;
          totalsRow.totalDividendOnly += (row.totalDividendOnly || 0);
          finalColumns.forEach((col) => {
            totalsRow.periodTotals[col] = (totalsRow.periodTotals[col] || 0) + (row.periodTotals?.[col] || 0);
            totalsRow.periodClosed[col] = (totalsRow.periodClosed[col] || 0) + (row.periodClosed?.[col] || 0);
            totalsRow.periodDiv[col] = (totalsRow.periodDiv[col] || 0) + (row.periodDiv?.[col] || 0);
          });
        });

        setClosedTotalsRow(totalsRow);

        // Save to cache for next load
        setCachedData({
          symbolData: reconciledRows,
          closedTotalsRow: totalsRow,
          closedPeriodColumns: finalColumns
        });

      } catch (error) {
        console.error('Error fetching stock summary data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timePeriod, customStartDate, customEndDate, selectedAccount, closedGroupBy, refreshKey, paperMode]);

  const handleTimePeriodChange = (event) => {
    setTimePeriod(event.target.value);
  };

  // Open the cost-basis/realized-P/L editor for a closed trade row
  const openPlEdit = (trade) => {
    if (!trade || !trade.id) return;
    setPlEdit({
      trade,
      cost: trade.cost ?? '',
      proceeds: trade.proceeds ?? '',
      realizedPL: trade.realizedPL ?? trade.realized_pl ?? '',
      closingDate: (trade.closingDate || '').slice(0, 10),
      saving: false,
      error: null,
    });
  };

  const savePlEdit = async () => {
    if (!plEdit) return;
    setPlEdit(p => ({ ...p, saving: true, error: null }));
    try {
      await apiRequest(`/api/trades/${plEdit.trade.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          cost: parseFloat(plEdit.cost),
          proceeds: parseFloat(plEdit.proceeds),
          realizedPL: parseFloat(plEdit.realizedPL),
          ...(plEdit.closingDate ? { closingDate: plEdit.closingDate } : {}),
        }),
      });
      setPlEdit(null);
      setRefreshKey(k => k + 1);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to update trade';
      setPlEdit(p => ({ ...p, saving: false, error: msg }));
    }
  };

  const handleAccountChange = (event) => {
    const {
      target: { value },
    } = event;

    const newValue = typeof value === 'string' ? value.split(',') : value;

    // Group header selection (sentinel values) - toggle all accounts in the group
    const groupSentinel = newValue.find(v => v === 'group:taxable' || v === 'group:non-taxable');
    if (groupSentinel) {
      handleAccountCategorySelect(groupSentinel === 'group:taxable' ? 'taxable' : 'non-taxable');
      return;
    }

    if (newValue.includes('all') && !draftAccount.includes('all')) {
      setDraftAccount(['all']);
      return;
    }

    if (draftAccount.includes('all') && newValue.length > 1) {
      setDraftAccount(newValue.filter(v => v !== 'all'));
      return;
    }

    if (newValue.length === 0) {
      setDraftAccount(['all']);
      return;
    }

    setDraftAccount(newValue);
  };

  const applyAccountSelection = () => {
    setSelectedAccount(draftAccount);
  };

  const { privacyMask } = usePrivacyMask();

  // Format currency
  const formatCurrency = (value) => {
    if (privacyMask) return '••••••';
    const n = Number(value);
    if (!Number.isFinite(n)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(n);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  // Get color for P/L values
  const getPLColor = (value) => {
    if (value > 0) return 'success';
    if (value < 0) return 'error';
    return 'default';
  };

  // Derive why a closed position group closed from its closing trades' recorded
  // reason (paper trades carry reason: STOP_LOSS/TAKE_PROFIT/EXPIRED/MANUAL/etc).
  // Returns up to two { label, color, title } chips so mixed exits (e.g. partial
  // target then stop-loss) still show both.
  const CLOSE_REASON_INFO = {
    STOP_LOSS: { label: 'Stop-loss', color: 'error', title: 'Closed by stop-loss tier' },
    TAKE_PROFIT: { label: 'Target', color: 'success', title: 'Closed by take-profit tier' },
    EXPIRED: { label: 'Expired', color: 'warning', title: 'Settled at expiration intrinsic value — stop/target cannot fire once the contract stops quoting' },
    AUTO_EXIT: { label: 'Auto-exit', color: 'info', title: 'Closed by the auto-exit monitor' },
    MANUAL: { label: 'Manual', color: 'default', title: 'Closed manually' },
    WEBHOOK_CLOSE: { label: 'Alert', color: 'default', title: 'Closed by an alert signal' },
  };
  const getCloseReasons = (strat) => {
    const closing = (strat?.trades || [])
      .filter(t => String(t.positionEffect || t.position_effect || '').toUpperCase() === 'CLOSING' && t.reason);
    const byReason = new Map();
    for (const t of closing) {
      const e = byReason.get(t.reason) || { pl: 0, basis: 0 };
      const proceeds = parseFloat(t.proceeds) || 0;
      const pl = parseFloat(t.realizedPL ?? t.netPL ?? t.realized_pl) || 0;
      const isCover = String(t.action || t.instruction || '').toUpperCase().startsWith('BUY');
      e.pl += pl;
      e.basis += isCover ? proceeds + pl : proceeds - pl;
      byReason.set(t.reason, e);
    }
    return [...byReason.keys()].slice(0, 2).map(r => {
      const info = CLOSE_REASON_INFO[r] || { label: r, color: 'default', title: r };
      const { pl, basis } = byReason.get(r);
      const pct = basis > 0 ? ` ${pl >= 0 ? '+' : ''}${((pl / basis) * 100).toFixed(0)}%` : '';
      return { ...info, label: `${info.label}${pct}` };
    });
  };

  // Render period header label
  const formatPeriodHeader = (key) => {
    if (closedGroupBy === 'week' && typeof key === 'string') {
      const parts = key.split('-');
      if (parts.length === 2) {
        const year = parseInt(parts[0], 10);
        const week = parseInt(parts[1], 10);
        if (!isNaN(year) && !isNaN(week)) {
          const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
          const dow = simple.getUTCDay();
          const isoMonday = new Date(simple);
          const diff = (dow <= 4 ? dow - 1 : dow - 8);
          isoMonday.setUTCDate(simple.getUTCDate() - diff);
          const saturday = new Date(isoMonday);
          saturday.setUTCDate(isoMonday.getUTCDate() + 5);
          return saturday.toISOString().slice(0, 10);
        }
      }
    }
    return key;
  };

  // Render a period cell split into closed P/L and dividends (rows/caches
  // without the split maps fall back to the combined total).
  const renderPeriodPLCell = (row, col) => {
    const total = row?.periodTotals?.[col] || 0;
    const div = row?.periodDiv?.[col] || 0;
    const closed = row?.periodClosed?.[col] ?? (total - div);
    if (Math.abs(div) < 0.005) return formatCurrency(total);
    return (
      <Box sx={{ lineHeight: 1.25 }}>
        <Box>{formatCurrency(closed)}</Box>
        <Box sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>{formatCurrency(div)} div</Box>
      </Box>
    );
  };

  // Handle row click to navigate to stock analysis
  const handleRowClick = (symbol) => {
    navigate(`/stock-analysis/${symbol}`);
  };

  // Handle sorting for closed positions
  const handleClosedPositionsSort = (field) => {
    const isAsc = closedPositionsSort.field === field && closedPositionsSort.direction === 'asc';
    setClosedPositionsSort({ field, direction: isAsc ? 'desc' : 'asc' });
  };

  // Build a TradeDialog alert to repeat a strategy's original trade
  const buildRetradeAlert = (strat) => {
    const rawAccountId = strat.trades?.[0]?.accountId || strat.trades?.[0]?.account_id;
    const trades = strat.trades || [];

    // Helper to extract the original instruction from trade type/position effect fields
    const getOriginalInstruction = (t) => {
      const tt = String(t.tradeType || t.trade_type || '').toUpperCase().replace(/\s+/g, '_');
      const pe = String(t.positionEffect || t.position_effect || '').toUpperCase().replace(/\s+/g, '_');
      const raw = tt || pe;

      // Map common aliases to standard Schwab instruction strings
      const map = {
        BTO: 'BUY_TO_OPEN',
        BUY_TO_OPEN: 'BUY_TO_OPEN',
        STO: 'SELL_TO_OPEN',
        SELL_TO_OPEN: 'SELL_TO_OPEN',
        BTC: 'BUY_TO_CLOSE',
        BUY_TO_CLOSE: 'BUY_TO_CLOSE',
        STC: 'SELL_TO_CLOSE',
        SELL_TO_CLOSE: 'SELL_TO_CLOSE',
        BUY: 'BUY',
        BUY_TO_COVER: 'BUY_TO_COVER',
        SELL: 'SELL',
        SELL_SHORT: 'SELL_SHORT',
      };

      // Try direct exact match first
      if (map[raw]) return map[raw];
      // Try common shorthand fallbacks
      if (raw.includes('BUY') && raw.includes('OPEN')) return 'BUY_TO_OPEN';
      if (raw.includes('SELL') && raw.includes('OPEN')) return 'SELL_TO_OPEN';
      if (raw.includes('BUY') && (raw.includes('CLOSE') || raw.includes('COVER'))) return 'BUY_TO_CLOSE';
      if (raw.includes('SELL') && (raw.includes('CLOSE') || raw.includes('CLOSING'))) return 'SELL_TO_CLOSE';
      if (raw.includes('BUY_TO_COVER')) return 'BUY_TO_COVER';
      if (raw === 'SELL' || raw.includes('SELL_SHORT')) return 'SELL_SHORT';
      if (raw === 'BUY') return 'BUY';

      // Fallback to BUY_TO_OPEN if no instruction can be determined
      return 'BUY_TO_OPEN';
    };

    const isEquity = trades.every(t => {
      const info = parseOptionSymbol(t.originalSymbol || t.symbol || '');
      return !info.isOption;
    });

    if (isEquity) {
      const t = trades[0];
      const instruction = getOriginalInstruction(t);
      const isSell = instruction.includes('SELL') || instruction === 'SELL_SHORT';
      const shortInstr = isSell ? 'SELL_SHORT' : 'BUY';
      return {
        isTradeAlert: true,
        assetType: 'EQUITY',
        symbol: (t.symbol || t.originalSymbol || strat.underlying || '').toUpperCase(),
        underlying: (strat.underlying || '').toUpperCase(),
        instruction: shortInstr,
        action: shortInstr,
        quantity: Math.abs(parseInt(t.quantity) || 1),
        price: null,
        orderType: 'LIMIT',
        duration: 'DAY',
        tradeAccountId: rawAccountId,
      };
    }

    const legs = trades.map(t => {
      const raw = t.originalSymbol || t.symbol || '';
      const info = parseOptionSymbol(raw);
      const q = Math.abs(parseFloat(t.quantity) || 0) || 1;
      const instruction = getOriginalInstruction(t);

      if (info.isOption) {
        return {
          instruction,
          putCall: (info.type === 'C' ? 'CALL' : 'PUT'),
          side: info.type,
          strike: info.strike,
          expiration: info.expiration,
          quantity: q,
        };
      }
      return {
        instruction,
        quantity: q,
        symbol: (t.symbol || t.originalSymbol || '').toUpperCase(),
      };
    }).filter(Boolean);

    return {
      isTradeAlert: true,
      assetType: 'OPTION',
      symbol: (strat.underlying || '').toUpperCase(),
      underlying: (strat.underlying || '').toUpperCase(),
      strategy: 'Custom',
      strategyType: 'custom',
      quantity: 1,
      price: null,
      orderType: 'LIMIT',
      duration: 'DAY',
      tradeAccountId: rawAccountId,
      legs,
    };
  };

  const handleOpenRetrade = (strat) => {
    const alert = buildRetradeAlert(strat);
    setRetradeAlert(alert);
    setRetradeOpen(true);
  };

  // Export closed positions to CSV
  const exportClosedPositionsToCSV = async () => {
    try {
      // Fetch underlying transactions for current time range and filters
      const { startDate, endDate } = getRangeForPeriod(timePeriod);
      const timeRange = getRangeForPeriod(timePeriod);

      const exportBaseUrl = paperMode ? '/api/paper-trading/trades-formatted' : '/api/trades';
      const response = await apiRequest(`${exportBaseUrl}?startDate=${startDate}&endDate=${endDate}`);

      if (!response.data || !response.data.trades) {
        console.error('No trade data found for export');
        return;
      }

      // Get all trades and filter by position type
      let filteredTrades = response.data.trades;
      if (positionFilter && !positionFilter.includes('all')) {
        filteredTrades = filteredTrades.filter(trade => {
          const raw = trade.originalSymbol || trade.symbol || '';
          const info = parseOptionSymbol(raw);
          const isOption = info && info.isOption;
          const isStock = !isOption;
          const isDividend = trade.positionType === 'dividend' || trade.type === 'dividend' ||
            (trade.tradeType || '').toLowerCase() === 'dividend' ||
            (trade.trade_type || '').toLowerCase() === 'dividend' ||
            (trade.realizedPL && Math.abs(parseFloat(trade.realizedPL)) < 0.01 && trade.dividend && Math.abs(parseFloat(trade.dividend)) > 0.01);

          const matchesOption = positionFilter.includes('options') && isOption;
          const matchesStock = positionFilter.includes('stocks') && isStock && !isDividend;
          const matchesDividend = positionFilter.includes('dividends') && isDividend;
          const matchesBoth = positionFilter.includes('options-stocks') &&
            symbolsWithStockAndOption.has(String(info?.underlying || raw).toUpperCase());

          return matchesOption || matchesStock || matchesDividend || matchesBoth;
        });
      }

      // Convert to CSV with transaction details
      const headers = [
        'Date', 'Symbol', 'Type', 'Action', 'Quantity',
        'Price', 'Proceeds', 'Cost', 'Fees',
        'Realized P/L', 'Dividend', 'Net P/L'
      ];

      const csvContent = [
        headers.join(','),
        ...filteredTrades.map(trade => {
          const action = trade.action || trade.transactionType || '';
          const isBuy = action.includes('BUY') || action.includes('BTO');

          return [
            `"${trade.tradeDate || ''}"`,
            `"${trade.symbol || ''}"`,
            `"${trade.positionType || ''}"`,
            `"${action}"`,
            `"${trade.quantity || ''}"`,
            `"${trade.price || ''}"`,
            `"${isBuy ? (trade.proceeds || '') : (trade.cost || '')}"`,
            `"${isBuy ? (trade.cost || '') : (trade.proceeds || '')}"`,
            `"${trade.commission || ''}"`,
            `"${trade.realizedPL || ''}"`,
            `"${trade.dividend || ''}"`,
            `"${trade.netPL || ''}"`
          ].join(',');
        })
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  // Apply the page symbol and time-period filters to pending positions.
  const { include: pendingInc, exclude: pendingExc } = parseSymbolFilter(symbolFilter);
  const hasPendingPositionFilter = positionFilter && !positionFilter.includes('all');
  const filteredPendingPositions = pendingPositions.filter((p) => {
    // Pending positions are always recently-closed options/stocks (never dividends),
    // so exclude them entirely unless the position filter includes their type.
    if (hasPendingPositionFilter) {
      const raw = p.originalSymbol || p.symbol || '';
      const info = parseOptionSymbol(raw);
      const isOption = info && info.isOption;
      const matchesOption = positionFilter.includes('options') && isOption;
      const matchesStock = positionFilter.includes('stocks') && !isOption;
      const matchesBoth = positionFilter.includes('options-stocks') &&
        symbolsWithStockAndOption.has(String(info?.underlying || raw).toUpperCase());
      if (!matchesOption && !matchesStock && !matchesBoth) return false;
    }

    if (pendingInc.length > 0 || pendingExc.length > 0) {
      const sym = (p.symbol || p.originalSymbol || '').toLowerCase();
      if (pendingExc.some(t => sym.includes(t))) return false;
      if (pendingInc.length > 0 && !pendingInc.some(t => sym.includes(t))) return false;
    }

    const pendingDate = p.filledTime ? String(p.filledTime).slice(0, 10) : '';
    if (timePeriod === 'last_year') return false;
    if (timePeriod === 'custom') {
      if (customStartDate && pendingDate && pendingDate < customStartDate) return false;
      if (customEndDate && pendingDate && pendingDate > customEndDate) return false;
    } else {
      const range = getRangeForPeriod(timePeriod);
      if (range && pendingDate && (pendingDate < range.startDate || pendingDate > range.endDate)) return false;
    }

    return true;
  });

  // Short label for a strategy type (PCS, IC, Equity, ...) used as a chip.
  const abbreviateStrategy = (type) => {
    if (!type) return '';
    return type
      .replace('Put Credit Spread', 'PCS')
      .replace('Put Debit Spread', 'PDS')
      .replace('Call Credit Spread', 'CCS')
      .replace('Call Debit Spread', 'CDS')
      .replace('Iron Condor', 'IC')
      .replace('Long Straddle', 'Straddle')
      .replace('Short Straddle', 'Straddle')
      .replace('Long Strangle', 'Strangle')
      .replace('Short Strangle', 'Strangle');
  };

  // Compact label for an individual pending leg (e.g. "S 145P", or "AAPL" for stock).
  const pendingLegLabel = (leg) => {
    if (!leg.optionInfo?.isOption) return leg.symbol;
    const dir = leg.quantity >= 0 ? 'L' : 'S';
    const t = leg.optionInfo.type || '';
    const strike = leg.optionInfo.strike;
    return `${dir} ${strike != null ? strike : ''}${t}`;
  };

  // Group flat pending closing legs into recognizable strategies (PCS, Iron
  // Condor, Equity, single options). Reuses detectSpreadType + parseOptionSymbol.
  // Pending rows carry positive quantities, so the original position side is
  // reconstructed from the closing tradeType (buy_to_close => was short).
  const pendingStrategyGroups = (() => {
    const withInfo = filteredPendingPositions.map((p) => {
      const optionInfo = parseOptionSymbol(p.originalSymbol || p.symbol);
      const tt = String(p.tradeType || '').toLowerCase();
      const sign = tt.includes('buy') ? -1 : 1; // closing a short => was short
      return {
        ...p,
        optionInfo,
        isOption: optionInfo.isOption || p.isOption,
        quantity: Math.abs(Number(p.quantity) || 0) * sign,
        absQuantity: Math.abs(Number(p.quantity) || 0)
      };
    });

    const optionLegs = withInfo.filter((p) => p.isOption && p.optionInfo?.isOption);
    const stockLegs = withInfo.filter((p) => !(p.isOption && p.optionInfo?.isOption));

    const groups = [];

    // Options: group by underlying + account + expiration, then detect strategy.
    const byKey = {};
    optionLegs.forEach((p) => {
      const underlying = p.optionInfo.underlying || p.symbol;
      const exp = p.optionInfo.expiration || 'Unknown';
      const key = `${underlying}|${p.accountId}|${exp}`;
      (byKey[key] = byKey[key] || []).push(p);
    });

    Object.entries(byKey).forEach(([key, legs]) => {
      const [underlying, , exp] = key.split('|');
      const spread = legs.length >= 2 ? detectSpreadType(legs) : null;
      if (spread && legs.length >= 2) {
        groups.push({
          id: `pstrat_${key}`,
          strategyType: spread.type,
          isFuturesOption: spread.legs.some(l => l.optionInfo?.isFuturesOption),
          legs: spread.legs,
          underlying,
          expiration: exp,
          accountName: legs[0].accountName,
          accountId: legs[0].accountId
        });
      } else if (legs.length >= 4) {
        // Try pairing multiple 2-leg spreads (e.g. two separate put credit spreads
        // closed same day with same underlying/expiration)
        const calls = legs.filter(p => p.optionInfo?.type === 'C').sort((a, b) => a.optionInfo.strike - b.optionInfo.strike);
        const puts = legs.filter(p => p.optionInfo?.type === 'P').sort((a, b) => a.optionInfo.strike - b.optionInfo.strike);
        let paired = false;
        if (calls.length > 0 && puts.length === 0) {
          const longs = calls.filter(p => p.quantity > 0);
          const shorts = calls.filter(p => p.quantity < 0);
          if (longs.length === shorts.length && longs.length >= 1) {
            for (let i = 0; i < longs.length; i++) {
              const pairSpread = detectSpreadType([longs[i], shorts[i]]);
              if (pairSpread) {
                paired = true;
                groups.push({
                  id: `pstrat_${key}_${i}`,
                  strategyType: pairSpread.type,
                  isFuturesOption: pairSpread.legs.some(l => l.optionInfo?.isFuturesOption),
                  legs: pairSpread.legs,
                  underlying,
                  expiration: exp,
                  accountName: legs[0].accountName,
                  accountId: legs[0].accountId
                });
              }
            }
          }
        } else if (puts.length > 0 && calls.length === 0) {
          const longs = puts.filter(p => p.quantity > 0);
          const shorts = puts.filter(p => p.quantity < 0);
          if (longs.length === shorts.length && longs.length >= 1) {
            for (let i = 0; i < longs.length; i++) {
              const pairSpread = detectSpreadType([longs[i], shorts[i]]);
              if (pairSpread) {
                paired = true;
                groups.push({
                  id: `pstrat_${key}_${i}`,
                  strategyType: pairSpread.type,
                  isFuturesOption: pairSpread.legs.some(l => l.optionInfo?.isFuturesOption),
                  legs: pairSpread.legs,
                  underlying,
                  expiration: exp,
                  accountName: legs[0].accountName,
                  accountId: legs[0].accountId
                });
              }
            }
          }
        }
        if (!paired) {
          legs.forEach((p) => {
            const dir = p.quantity >= 0 ? 'Long' : 'Short';
            const t = p.optionInfo.type === 'C' ? 'Call' : p.optionInfo.type === 'P' ? 'Put' : 'Option';
            groups.push({
              id: `pstrat_single_${p.id}`,
              strategyType: `${p.optionInfo?.isFuturesOption ? 'Futures ' : ''}${dir} ${t}`,
              isFuturesOption: p.optionInfo?.isFuturesOption || false,
              legs: [p],
              underlying,
              expiration: exp,
              accountName: p.accountName,
              accountId: p.accountId
            });
          });
        }
      } else {
        legs.forEach((p) => {
          const dir = p.quantity >= 0 ? 'Long' : 'Short';
          const t = p.optionInfo.type === 'C' ? 'Call' : p.optionInfo.type === 'P' ? 'Put' : 'Option';
          groups.push({
            id: `pstrat_single_${p.id}`,
            strategyType: `${p.optionInfo?.isFuturesOption ? 'Futures ' : ''}${dir} ${t}`,
            isFuturesOption: p.optionInfo?.isFuturesOption || false,
            legs: [p],
            underlying,
            expiration: exp,
            accountName: p.accountName,
            accountId: p.accountId
          });
        });
      }
    });

    // Stocks: each leg is an Equity position.
    stockLegs.forEach((p) => {
      groups.push({
        id: `pstrat_eq_${p.id}`,
        strategyType: 'Equity',
        legs: [p],
        underlying: p.symbol,
        expiration: null,
        accountName: p.accountName,
        accountId: p.accountId
      });
    });

    // Aggregate per-group totals.
    groups.forEach((g) => {
      g.totalPL = g.legs.reduce((s, l) => s + (Number(l.realizedPL) || 0), 0);
      g.totalProceeds = g.legs.reduce((s, l) => s + (Number(l.proceeds) || 0), 0);
      g.totalCostBasis = g.legs.reduce((s, l) => s + (Number(l.costBasis) || 0), 0);
      g.filledTime = g.legs[0]?.filledTime || null;
    });

    // Sort by estimated P/L magnitude (largest first) for quick scanning.
    groups.sort((a, b) => Math.abs(b.totalPL) - Math.abs(a.totalPL));

    // Apply strategy filter
    if (strategyFilter && strategyFilter !== 'all') {
      return groups.filter((g) => {
        if (strategyFilter === 'Equity') {
          return g.strategyType === 'Equity';
        } else if (strategyFilter === 'Futures Option') {
          return g.isFuturesOption === true || /^futures /i.test(g.strategyType || '');
        } else if (['Short Call', 'Long Call', 'Short Put', 'Long Put'].includes(strategyFilter)) {
          return g.strategyType === strategyFilter;
        } else {
          return g.strategyType.includes(strategyFilter);
        }
      });
    }

    return groups;
  })();

  // Build a lookup of symbols that have closing orders — either filled (pending)
  // or working (unfilled). Used to flag open positions with a closing order in flight.
  // Normalize: uppercase, remove underscores AND whitespace so "SPX_012025C4500"
  // and "SPX 012025C4500" both become "SPX012025C4500".
  const normSym = (s) => (s || '').toUpperCase().replace(/[_\s]+/g, '');

  // Working (unfilled) closing orders — keyed by accountId|normalizedSymbol
  const workingCloseMap = new Map();
  workingCloseOrders.forEach((o) => {
    const key = `${String(o.accountId)}|${normSym(o.originalSymbol || o.symbol)}`;
    if (!workingCloseMap.has(key)) workingCloseMap.set(key, []);
    workingCloseMap.get(key).push(o);
  });

  // Pending (filled, not settled) closing orders
  const pendingCloseKeys = new Set(
    pendingPositions.map(p =>
      `${String(p.accountId)}|${normSym(p.originalSymbol || p.symbol)}`
    )
  );

  // Returns { hasClosing, workingOrders } for a given position
  const getClosingOrderInfo = (accountId, symbol) => {
    const key = `${String(accountId)}|${normSym(symbol)}`;
    const working = workingCloseMap.get(key) || [];
    const pending = pendingCloseKeys.has(key);
    return {
      hasClosing: working.length > 0 || pending,
      workingOrders: working
    };
  };

  // Returns the first working order for a position or spread group
  const getWorkingOrderForPosition = (positionOrGroup) => {
    if (positionOrGroup.isSpread) {
      for (const leg of (positionOrGroup.legs || [])) {
        const info = getClosingOrderInfo(leg.accountId, leg.symbol);
        if (info.hasClosing && info.workingOrders.length > 0) {
          return info.workingOrders[0];
        }
      }
    } else {
      const info = getClosingOrderInfo(positionOrGroup.accountId, positionOrGroup.symbol);
      if (info.hasClosing && info.workingOrders.length > 0) {
        return info.workingOrders[0];
      }
    }
    return null;
  };

  // Filter and sort closed positions (symbol data)
  const filteredSymbolData = applyClosedPositionFilters(symbolData);

  // Recompute totals from filtered data so they reflect active filters
  const filteredTotalsRow = useMemo(() => {
    const totals = { symbol: 'Total', periodTotals: {}, periodClosed: {}, periodDiv: {}, totalPL: 0, totalDividendOnly: 0 };
    filteredSymbolData.forEach(row => {
      totals.totalPL += (row.totalPL || 0);
      totals.totalDividendOnly += (row.totalDividendOnly || 0);
      closedPeriodColumns.forEach(col => {
        totals.periodTotals[col] = (totals.periodTotals[col] || 0) + (row.periodTotals?.[col] || 0);
        totals.periodClosed[col] = (totals.periodClosed[col] || 0) + (row.periodClosed?.[col] || 0);
        totals.periodDiv[col] = (totals.periodDiv[col] || 0) + (row.periodDiv?.[col] || 0);
      });
    });
    return totals;
  }, [filteredSymbolData, closedPeriodColumns]);

  const weeklyGroupedData = getWeeklyGroupedData();
  const monthlyGroupedData = getMonthlyGroupedData();
  const openCloseGroupedData = getOpenCloseGroupedData();
  const sortPeriodRows = (a, b) => {
    // In period modes, default sort by periodKey descending unless explicitly sorting by another field
    const { field, direction } = closedPositionsSort;
    if (field === 'symbol') {
      // Period column sorts chronologically; default direction is descending
      return direction === 'asc'
        ? a.periodKey.localeCompare(b.periodKey)
        : b.periodKey.localeCompare(a.periodKey);
    }

    let aValue, bValue;

    switch (field) {
      case 'totalPL':
        aValue = parseFloat(a.totalPL) || 0;
        bValue = parseFloat(b.totalPL) || 0;
        break;
      case 'closedPL':
        aValue = (parseFloat(a.totalPL) || 0) - (parseFloat(a.totalDividendOnly) || 0);
        bValue = (parseFloat(b.totalPL) || 0) - (parseFloat(b.totalDividendOnly) || 0);
        break;
      case 'totalDividendOnly':
        aValue = parseFloat(a.totalDividendOnly) || 0;
        bValue = parseFloat(b.totalDividendOnly) || 0;
        break;
      default:
        aValue = a[field] || 0;
        bValue = b[field] || 0;
    }

    if (typeof aValue === 'string') {
      return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    return direction === 'asc' ? aValue - bValue : bValue - aValue;
  };
  const sortedWeeklyData = [...weeklyGroupedData].sort(sortPeriodRows);
  const sortedMonthlyData = [...monthlyGroupedData].sort(sortPeriodRows);
  // Week rows and Month-detail rows share the same table; periodKey/periodLabel
  // carry the granularity. 'calendar' mode's detail sub-view uses months.
  const isPeriodMode = groupByMode === 'week' || (groupByMode === 'calendar' && calendarViewMode === 'detail');
  const sortedPeriodData = groupByMode === 'week' ? sortedWeeklyData : sortedMonthlyData;
  // Per-symbol open P/L split into options vs equity — feeds both the
  // closed-positions table cells and its sort fields.
  const openPLBySymbol = new Map();
  groupedPositions.forEach(group => {
    let symbol = group.isSpread ? group.underlying : group.position?.symbol;
    if (symbol && !group.isSpread && group.position) {
      const optInfo = parseOptionSymbol(String(symbol).trim());
      if (optInfo.isOption && optInfo.underlying) symbol = optInfo.underlying;
    }
    if (!symbol) return;
    const key = String(symbol).toLowerCase();
    let bucket = openPLBySymbol.get(key);
    if (!bucket) { bucket = { options: 0, equity: 0 }; openPLBySymbol.set(key, bucket); }
    let uPL;
    if (group.isSpread) {
      uPL = group.metrics?.unrealizedPL || 0;
    } else {
      uPL = positionUnrealizedPL(group.position);
    }
    if (group.groupType === 'option' || group.isSpread) bucket.options += uPL;
    else bucket.equity += uPL;
  });
  const getOpenPL = (symbol, which) => {
    const b = openPLBySymbol.get(String(symbol || '').toLowerCase());
    if (!b) return 0;
    return which === 'options' ? b.options : which === 'equity' ? b.equity : b.options + b.equity;
  };

  const sortedSymbolData = [...filteredSymbolData].sort((a, b) => {
    const { field, direction } = closedPositionsSort;
    let aValue, bValue;

    // Check if field is a period column
    if (closedPeriodColumns.includes(field)) {
      aValue = parseFloat(a.periodTotals?.[field]) || 0;
      bValue = parseFloat(b.periodTotals?.[field]) || 0;
    } else {
      switch (field) {
        case 'symbol':
          aValue = a.symbol || '';
          bValue = b.symbol || '';
          break;
        case 'totalPL':
          aValue = parseFloat(a.totalPL) || 0;
          bValue = parseFloat(b.totalPL) || 0;
          break;
        case 'openPLOptions':
          aValue = getOpenPL(a.symbol, 'options');
          bValue = getOpenPL(b.symbol, 'options');
          break;
        case 'openPLEquity':
          aValue = getOpenPL(a.symbol, 'equity');
          bValue = getOpenPL(b.symbol, 'equity');
          break;
        case 'openPL':
          aValue = getOpenPL(a.symbol);
          bValue = getOpenPL(b.symbol);
          break;
        case 'closedPL':
          aValue = (parseFloat(a.totalPL) || 0) - (parseFloat(a.totalDividendOnly) || 0);
          bValue = (parseFloat(b.totalPL) || 0) - (parseFloat(b.totalDividendOnly) || 0);
          break;
        case 'totalDividendOnly':
          aValue = parseFloat(a.totalDividendOnly) || 0;
          bValue = parseFloat(b.totalDividendOnly) || 0;
          break;
        default:
          aValue = a[field] || 0;
          bValue = b[field] || 0;
      }
    }

    if (typeof aValue === 'string') {
      return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    return direction === 'asc' ? aValue - bValue : bValue - aValue;
  });

  // Professional PDF report of the currently filtered screen data — summary
  // KPIs on top, open/pending positions, then closed groups with transactions.
  const generatePdfReport = () => {
    try {
      const fmtSigned = (v) => {
        const n = Number(v) || 0;
        return `${n < 0 ? '-' : '+'}${formatCurrency(Math.abs(n))}`;
      };
      const fmtExp = (iso) => {
        const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!m) return iso || '';
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      };
      const filteredPositions = applyFilters(groupedPositions);

      // KPI totals — same math as the summary cards above.
      let totalOpenOptionsPL = 0;
      let totalOpenStocksPL = 0;
      let totalOpenBP = 0;
      filteredPositions.forEach(group => {
        if (group.groupType === 'option') {
          if (group.isSpread) {
            totalOpenOptionsPL += group.metrics?.unrealizedPL || 0;
            totalOpenBP += group.metrics?.bpEffect || 0;
          } else {
            totalOpenOptionsPL += positionUnrealizedPL(group.position);
            totalOpenBP += group.bpEffect || 0;
          }
        } else if (group.groupType === 'equity' || group.groupType === 'etf' || group.groupType === 'stock' || group.groupType === 'cash') {
          totalOpenStocksPL += positionUnrealizedPL(group.position);
        }
      });
      const totalClosedPL = filteredSymbolData.reduce((sum, row) => sum + (row.totalPL || 0), 0);
      const totalDividendPL = filteredSymbolData.reduce((sum, row) => sum + (row.totalDividendOnly || 0), 0);
      const totalPendingPL = filteredPendingPositions.reduce((sum, p) => sum + (Number(p.realizedPL) || 0), 0);
      const totalClosedTradePL = totalClosedPL - totalDividendPL + totalPendingPL;

      // Open positions — single table, expiration as a column.
      const OPEN_HEAD = ['Account', 'Position', 'Type', 'Expiration', 'Qty', 'Market Value', 'P/L', 'Day P/L'];
      const openRows = filteredPositions.map(g => {
        const isSpread = g.isSpread;
        const acct = isSpread ? (g.legs?.[0]?.accountName || '') : (g.position?.accountName || '');
        const label = isSpread
          ? formatSpreadLabel(g)
          : (g.position?.optionInfo?.isOption ? formatOptionLabel(g.position) : (g.position?.symbol || ''));
        const type = isSpread
          ? `${g.isFuturesOption ? 'Futures ' : ''}${g.type || g.spreadType || ''}`
          : (g.position?.optionInfo?.isFuturesOption
            ? 'Futures Option'
            : (g.position?.optionInfo?.isOption ? 'Option' : (g.groupType || 'Equity')));
        const expiration = fmtExp(isSpread
          ? (g.expiration || '')
          : (g.position?.optionInfo?.expiration || ''));
        const qty = isSpread
          ? Math.max(...(g.legs || []).map(l => Math.abs(l.quantity || 0)), 0)
          : Math.abs(g.position?.quantity || 0);
        const mv = Math.abs(isSpread ? (g.metrics?.totalMarketValue || 0) : (g.position?.marketValue || 0));
        const pl = isSpread ? (g.metrics?.unrealizedPL || 0) : positionUnrealizedPL(g.position);
        const day = isSpread ? (g.metrics?.dayPL || 0) : (Number(g.position?.change) || 0);
        return [acct, label, type, expiration, String(qty), formatCurrency(mv), fmtSigned(pl), fmtSigned(day)];
      });
      const openSections = openRows.length
        ? [{ head: OPEN_HEAD, rows: openRows }]
        : [];

      // Recently closed, awaiting settlement.
      const pendingRows = (pendingStrategyGroups || []).map(g => [
        g.accountName || '',
        `${g.underlying || ''} ${fmtExp(g.expiration)}`.trim(),
        g.isFuturesOption && !/^futures/i.test(g.strategyType || '')
          ? `Futures ${g.strategyType}`
          : (g.strategyType || ''),
        (g.legs || []).map(l => `${l.quantity > 0 ? '+' : ''}${l.quantity} ${formatOptionLabel(l)}`).join('  +  '),
        formatCurrency(g.totalProceeds || 0),
        formatCurrency(g.totalCostBasis || 0),
        fmtSigned(g.totalPL),
        g.filledTime || '',
      ]);

      // Closed positions — one detail block per strategy group with its fills.
      const CLOSED_HEAD = ['Date/Time', 'Contract', 'Type', 'Qty', 'Price', 'Realized P/L'];
      const closedGroups = [];
      sortedSymbolData.forEach(row => {
        groupTradesByStrategy(row.trades || []).forEach(s => {
          closedGroups.push({
            heading: `${s.displaySymbol || row.symbol} — ${s.strategyType}`,
            meta: `Open ${fmtExp(s.openDate) || '—'} → Close ${fmtExp(s.closeDate) || '—'}   ·   Qty ${s.quantity || '—'}   ·   P/L ${fmtSigned(s.totalPL)}`,
            head: CLOSED_HEAD,
            rows: (s.trades || []).map(t => [
              t.tradeTimestamp || t.trade_timestamp
                ? new Date(t.tradeTimestamp || t.trade_timestamp).toLocaleString()
                : (t.tradeDate || ''),
              t.originalSymbol || t.symbol || '',
              String(t.tradeType || t.trade_type || ''),
              String(t.quantity ?? ''),
              t.price != null ? formatCurrency(t.price) : '',
              fmtSigned(getClosedTradeNetPL(t)),
            ]),
            closeTs: s.closeDate || '',
          });
        });
      });
      closedGroups.sort((a, b) => b.closeTs.localeCompare(a.closeTs));

      generatePositionsReport({
        title: 'Portfolio Position Report',
        generatedAt: new Date().toLocaleString(),
        kpis: [
          { label: 'Open P/L (Options)', value: fmtSigned(totalOpenOptionsPL) },
          { label: 'Open P/L (Stocks)', value: fmtSigned(totalOpenStocksPL) },
          { label: 'Total BP Effect', value: formatCurrency(totalOpenBP) },
          { label: 'Closed Trade P/L', value: fmtSigned(totalClosedTradePL) },
          { label: 'Total Dividends', value: fmtSigned(totalDividendPL) },
        ],
        openSections,
        pending: pendingRows.length
          ? { head: ['Account', 'Symbol/Spread', 'Strategy', 'Legs', 'Proceeds', 'Cost Basis', 'Est. P/L', 'Filled'], rows: pendingRows }
          : null,
        closedGroups,
        closedTotal: `Total Closed P/L (filtered): ${fmtSigned(totalClosedTradePL)}`,
        plColumns: { 'P/L': true, 'Day P/L': true, 'Realized P/L': true, 'Est. P/L': true },
        filename: `position-report-${new Date().toISOString().slice(0, 10)}.pdf`,
      });
    } catch (error) {
      console.error('Error generating PDF report:', error);
    }
  };

  return (
    <Box sx={{ '& .MuiContainer-root': { padding: isMobile ? '0' : undefined } }}>
      {/* Summary totals — matches Dashboard stat card style */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 1 : 2, mb: isMobile ? 2 : 4 }}>
        {(() => {
          const filteredPositions = applyFilters(groupedPositions);
          let totalOpenOptionsPL = 0;
          let totalOpenStocksPL = 0;
          let totalOpenStocksDurPL = 0;
          let totalOpenStocksDurHas = false;
          let totalOpenBP = 0;

          filteredPositions.forEach(group => {
            if (group.groupType === 'option') {
              if (group.isSpread) {
                totalOpenOptionsPL += group.metrics?.unrealizedPL || 0;
                totalOpenBP += group.metrics?.bpEffect || 0;
              } else {
                totalOpenOptionsPL += positionUnrealizedPL(group.position);
                totalOpenBP += group.bpEffect || 0;
              }
            } else if (group.groupType === 'equity' || group.groupType === 'etf' || group.groupType === 'stock' || group.groupType === 'cash') {
              totalOpenStocksPL += positionUnrealizedPL(group.position);
              const dpl = stockDurationPL(group.position);
              if (dpl != null) { totalOpenStocksDurPL += dpl; totalOpenStocksDurHas = true; }
            }
          });
          // Calculate totals from filtered data instead of global totals
          const totalClosedPL = filteredSymbolData.reduce((sum, row) => sum + (row.totalPL || 0), 0);
          const totalDividendPL = filteredSymbolData.reduce((sum, row) => sum + (row.totalDividendOnly || 0), 0);
          const totalTradePL = totalClosedPL - totalDividendPL;
          // Include pending estimated P/L in the closed trade summary, matching Dashboard.
          const totalPendingPL = filteredPendingPositions.reduce((sum, p) => sum + (Number(p.realizedPL) || 0), 0);
          const totalClosedTradePL = totalTradePL + totalPendingPL;

          const getNumberColor = (val) => {
            const tone = getPLColor(val);
            if (tone === 'success') return 'success.main';
            if (tone === 'error') return 'error.main';
            return 'text.primary';
          };

          return (
            <>
              <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
                <Card>
                  <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
                    <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                      Open P/L (Options)
                    </Typography>
                    <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold', color: getNumberColor(totalOpenOptionsPL) }}>
                      {formatCurrency(totalOpenOptionsPL)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
                <Card sx={{ cursor: 'pointer' }} onClick={() => setOpenStocksDurMode(m => !m)}>
                  <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
                    <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }} title="Click to toggle duration / all-time">
                      Open P/L (Stocks){totalOpenStocksDurHas ? (openStocksDurMode ? ` — ${DUR_LABEL}` : ' — All-time') : ''}
                    </Typography>
                    <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold', color: getNumberColor(openStocksDurMode && totalOpenStocksDurHas ? totalOpenStocksDurPL : totalOpenStocksPL) }}>
                      {formatCurrency(openStocksDurMode && totalOpenStocksDurHas ? totalOpenStocksDurPL : totalOpenStocksPL)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
                <Card>
                  <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
                    <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                      Total BP Effect
                    </Typography>
                    <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold' }} color="primary">
                      {formatCurrency(totalOpenBP)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
                <Card>
                  <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
                    <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                      Closed Trade P/L
                    </Typography>
                    <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold', color: getNumberColor(totalClosedTradePL) }}>
                      {formatCurrency(totalClosedTradePL)}
                    </Typography>
                    {Math.abs(totalPendingPL) > 0.005 && (
                      <Typography variant="caption" color="warning.main" display="block" sx={{ fontSize: '0.6rem' }}>
                        incl. {formatCurrency(totalPendingPL)} pending
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
                <Card>
                  <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
                    <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                      Total Dividends
                    </Typography>
                    <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {formatCurrency(totalDividendPL)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
                <Card>
                  <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
                    <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                      {paperMode ? 'Account Balance' : 'Total Cash'}
                    </Typography>
                    <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                      {formatCurrency(totalCash)}
                    </Typography>
                    {accountSummaries.some(s => s.error) && (
                      <Typography variant="caption" color="error" display="block" sx={{ fontSize: '0.6rem' }}>
                        * Errors loading some accounts
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </>
          );
        })()}
      </Box>

      {/* Assignment Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)}>
        <DialogTitle>{assignFlagType === 'cash' ? 'Mark as Cash Position' : 'Mark as Assigned'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the quantity to flag.
          </Typography>

          <ToggleButtonGroup
            value={assignFlagType}
            exclusive
            onChange={(e, val) => { if (val) setAssignFlagType(val); }}
            size="small"
            sx={{ mb: 2 }}
            fullWidth
          >
            <ToggleButton value="assigned">Assigned</ToggleButton>
            <ToggleButton value="cash">Cash Position</ToggleButton>
          </ToggleButtonGroup>

          {selectedTradeForAssignment ? (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                MATCHED DATABASE TRADE:
              </Typography>
              <Typography variant="body2">
                {selectedTradeForAssignment.symbol} - {Math.abs(selectedTradeForAssignment.quantity)} shares
                (Opened: {new Date(selectedTradeForAssignment.opening_date || selectedTradeForAssignment.openingDate).toLocaleDateString()})
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={() => setSelectedTradeForAssignment(null)}
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Not the right trade? Mark manually instead
              </Button>
            </Box>
          ) : (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'info.soft', borderRadius: 1, border: '1px dashed', borderColor: 'info.main' }}>
              <Typography variant="body2" color="info.main">
                No exact matching trade found in database. This will be marked as a <strong>Manual Assignment</strong>.
              </Typography>
            </Box>
          )}

          <TextField
            autoFocus
            margin="dense"
            label="Quantity"
            type="number"
            fullWidth
            variant="outlined"
            value={assignQuantity}
            onChange={(e) => setAssignQuantity(e.target.value)}
            helperText={selectedTradeForAssignment ? `Max: ${Math.abs(selectedTradeForAssignment.quantity)} (from matched trade)` : `Full position: ${selectedPositionForAssignment ? Math.abs(selectedPositionForAssignment.quantity) : ''}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} variant="contained" color="inherit">Cancel</Button>
          <Button onClick={handleSubmitAssign} variant="contained" color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Save filter preset dialog */}
      <Dialog open={presetDialog.open} onClose={() => setPresetDialog({ open: false, name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Save Filter Preset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Preset Name"
            fullWidth
            variant="outlined"
            value={presetDialog.name}
            onChange={(e) => setPresetDialog(p => ({ ...p, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset(); }}
            helperText={savedFilters[presetDialog.name.trim()] ? 'A preset with this name exists and will be overwritten' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresetDialog({ open: false, name: '' })} variant="contained" color="inherit">Cancel</Button>
          <Button onClick={handleSavePreset} variant="contained" color="primary" disabled={!presetDialog.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!assignmentError} autoHideDuration={6000} onClose={() => setAssignDialogOpen(false)}>
        <Alert onClose={() => setAssignmentError(null)} severity="error" sx={{ width: '100%' }}>
          {assignmentError}
        </Alert>
      </Snackbar>

      {/* Comprehensive Filter Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => setShowFilters(prev => !prev)}
          endIcon={showFilters ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        >
          {showFilters ? 'Hide Filters' : 'More Filters'}
        </Button>
        <TextField
          variant="outlined"
          size="small"
          sx={{ minWidth: 150 }}
          label="Symbol"
          value={symbolFilter}
          onChange={(e) => setSymbolFilter(e.target.value)}
          placeholder="e.g. AAPL, -TSLA"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Strategy</InputLabel>
          <Select
            value={strategyFilter}
            onChange={(e) => setStrategyFilter(e.target.value)}
            label="Strategy"
            renderValue={(v) => (STRATEGY_OPTIONS.find(o => o.value === v) || {}).label || v || 'All Strategies'}
          >
            {STRATEGY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Saved Filters</InputLabel>
          <Select
            value={selectedPreset}
            label="Saved Filters"
            onChange={(e) => handleSelectPreset(e.target.value)}
          >
            {Object.keys(savedFilters).length === 0 && (
              <MenuItem value="" disabled><em>None saved</em></MenuItem>
            )}
            {Object.keys(savedFilters).sort().map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Save current filters as preset">
          <IconButton size="small" onClick={() => setPresetDialog({ open: true, name: selectedPreset || '' })}>
            <SaveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete selected preset">
          <span>
            <IconButton
              size="small"
              disabled={!selectedPreset || !savedFilters[selectedPreset]}
              onClick={handleDeletePreset}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FilterAltOffIcon />}
          onClick={resetFilters}
        >
          Reset
        </Button>
        {(openDateStart || openDateEnd) && (
          <Typography variant="caption" color="text.secondary">
            Open date: {openDateStart || '…'} to {openDateEnd || '…'}
          </Typography>
        )}
      </Box>
      <Collapse in={showFilters}>
      <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 3 } }}>
        {/* Row 1: Main Filters */}
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
            <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Account</InputLabel>
              <Select
                multiple
                value={draftAccount}
                onChange={handleAccountChange}
                input={<OutlinedInput label="Account" />}
                renderValue={() => paperMode ? 'Paper Trading' : (() => {
                  const selected = draftAccount;
                  if (selected.includes('all')) return 'All Accounts';
                  if (selected.length === 1) {
                    const account = accounts.find(a => a.id === selected[0]);
                    return account ? account.name : selected[0];
                  }
                  return `${selected.length} Accounts`;
                })()}
              >
                {paperMode ? (
                  <MenuItem value="all" dense>
                    <Checkbox size="small" checked />
                    <ListItemText primary="Paper Trading" />
                  </MenuItem>
                ) : ([
                <MenuItem key="all" value="all" dense>
                  <Checkbox size="small" checked={draftAccount.includes('all')} />
                  <ListItemText primary="All Accounts" />
                </MenuItem>,

                ...(taxableAccounts.length > 0 ? [
                  <MenuItem
                    key="taxable-header"
                    value="group:taxable"
                    dense
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.05)',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={taxableAccounts.length > 0 && taxableAccounts.every(a => draftAccount.includes(a.id))}
                      indeterminate={taxableAccounts.some(a => draftAccount.includes(a.id)) && !taxableAccounts.every(a => draftAccount.includes(a.id))}
                    />
                    <ListItemText primary="Taxable Accounts" primaryTypographyProps={{ fontWeight: 600 }} />
                  </MenuItem>,
                  ...taxableAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id} dense>
                      <Checkbox size="small" checked={draftAccount.indexOf(account.id) > -1} />
                      <ListItemText primary={account.name} />
                    </MenuItem>
                  ))
                ] : []),

                ...(nonTaxableAccounts.length > 0 ? [
                  <MenuItem
                    key="non-taxable-header"
                    value="group:non-taxable"
                    dense
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.05)',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={nonTaxableAccounts.length > 0 && nonTaxableAccounts.every(a => draftAccount.includes(a.id))}
                      indeterminate={nonTaxableAccounts.some(a => draftAccount.includes(a.id)) && !nonTaxableAccounts.every(a => draftAccount.includes(a.id))}
                    />
                    <ListItemText primary="Non-Taxable Accounts" primaryTypographyProps={{ fontWeight: 600 }} />
                  </MenuItem>,
                  ...nonTaxableAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id} dense>
                      <Checkbox size="small" checked={draftAccount.indexOf(account.id) > -1} />
                      <ListItemText primary={account.name} />
                    </MenuItem>
                  ))
                ] : []),

                ...displayAccounts.filter(a => !a.accountType).map((account) => (
                  <MenuItem key={account.id} value={account.id} dense>
                    <Checkbox size="small" checked={draftAccount.indexOf(account.id) > -1} />
                    <ListItemText primary={account.name} />
                  </MenuItem>
                ))
                ])}
              </Select>
            </FormControl>
            <Button
              onClick={applyAccountSelection}
              variant="contained"
              size="small"
              disabled={JSON.stringify(draftAccount.sort()) === JSON.stringify(selectedAccount.sort())}
              sx={{ minWidth: '40px', px: 2 }}
            >
              OK
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={handleTimePeriodChange}
              label="Time Period"
            >
              <MenuItem value="current_month">Current Month</MenuItem>
              <MenuItem value="previous_month">Previous Month</MenuItem>
              <MenuItem value="30d">30 Days</MenuItem>
              <MenuItem value="60d">60 Days</MenuItem>
              <MenuItem value="90d">90 Days</MenuItem>
              <MenuItem value="180d">180 Days</MenuItem>
              <MenuItem value="ytd">Year to Date</MenuItem>
              <MenuItem value="last_year">Last Year</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 165 }}>
            <InputLabel>Closed Columns</InputLabel>
            <Select
              value={closedGroupBy}
              onChange={(e) => setClosedGroupBy(e.target.value)}
              label="Closed Columns"
            >
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
              <MenuItem value="quarter">Quarter</MenuItem>
              <MenuItem value="year">Year</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Position Type</InputLabel>
            <Select
              multiple
              value={positionFilter}
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes('all') && !positionFilter.includes('all')) {
                  setPositionFilter(['all']);
                  return;
                }
                if (positionFilter.includes('all') && value.length > 1) {
                  setPositionFilter(value.filter(v => v !== 'all'));
                  return;
                }
                if (value.length === 0) {
                  setPositionFilter(['all']);
                  return;
                }
                setPositionFilter(value);
              }}
              label="Position Type"
              renderValue={(selected) => {
                if (selected.includes('all')) return 'All Positions';
                return selected.map(v => {
                  if (v === 'options') return 'Options';
                  if (v === 'stocks') return 'Stocks';
                  if (v === 'options-stocks') return 'Option+Stock';
                  if (v === 'dividends') return 'Dividends';
                  return v;
                }).join(', ');
              }}
            >
              <MenuItem value="all">
                <Checkbox checked={positionFilter.includes('all')} />
                <ListItemText primary="All Positions" />
              </MenuItem>
              <MenuItem value="options">
                <Checkbox checked={positionFilter.indexOf('options') > -1} />
                <ListItemText primary="Options Only" />
              </MenuItem>
              <MenuItem value="stocks">
                <Checkbox checked={positionFilter.indexOf('stocks') > -1} />
                <ListItemText primary="Stocks Only" />
              </MenuItem>
              <MenuItem value="options-stocks">
                <Checkbox checked={positionFilter.indexOf('options-stocks') > -1} />
                <ListItemText primary="Option and Stock" secondary="Symbols holding both" />
              </MenuItem>
              <MenuItem value="dividends">
                <Checkbox checked={positionFilter.indexOf('dividends') > -1} />
                <ListItemText primary="Dividends" />
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>


        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Open Grouping</InputLabel>
            <Select
              value={groupOpenByUnderlying ? 'underlying' : 'expiration'}
              onChange={(e) => setGroupOpenByUnderlying(e.target.value === 'underlying')}
              label="Open Grouping"
            >
              <MenuItem value="underlying">By Underlying</MenuItem>
              <MenuItem value="expiration">By Expiration</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 90 }}>
            <InputLabel>Tags</InputLabel>
            <Select
              multiple
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              label="Tags"
              renderValue={(selected) => selected.join(', ')}
            >
              {allTags.length === 0 && (
                <MenuItem disabled value=""><em>No tags</em></MenuItem>
              )}
              {allTags.map(tag => (
                <MenuItem key={tag} value={tag} disabled={tagFilterExclude.includes(tag)}>
                  <Checkbox checked={tagFilter.includes(tag)} />
                  <ListItemText primary={tag} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Exclude Tags</InputLabel>
            <Select
              multiple
              value={tagFilterExclude}
              onChange={(e) => setTagFilterExclude(e.target.value)}
              label="Exclude Tags"
              renderValue={(selected) => selected.join(', ')}
            >
              {allTags.length === 0 && (
                <MenuItem disabled value=""><em>No tags</em></MenuItem>
              )}
              {allTags.map(tag => (
                <MenuItem key={tag} value={tag} disabled={tagFilter.includes(tag)}>
                  <Checkbox checked={tagFilterExclude.includes(tag)} />
                  <ListItemText primary={tag} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Row 2: Additional Filters (shown when custom date range or advanced filters) */}
        {timePeriod === 'custom' && (
          <>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                sx={{ minWidth: 140 }}
                label="Start Date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                sx={{ minWidth: 140 }}
                label="End Date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 140 }}>
            <InputLabel shrink>Open Start</InputLabel>
            <OutlinedInput
              type="date"
              label="Open Start"
              notched
              value={openDateStart}
              onChange={(e) => setOpenDateStart(e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 140 }}>
            <InputLabel shrink>Open End</InputLabel>
            <OutlinedInput
              type="date"
              label="Open End"
              notched
              value={openDateEnd}
              onChange={(e) => setOpenDateEnd(e.target.value)}
            />
          </FormControl>
        </Grid>


      </Grid>
      </Collapse>

      {/* Working Trades Section — working + recently filled opening orders */}
      {recentOrders.length > 0 && (
        <Box sx={{ mb: isMobile ? 1 : 2, mx: isMobile ? '-8px' : 0, width: isMobile ? 'calc(100% + 16px)' : 'auto' }}>
        {!isMobile ? (
        <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '&.Mui-expanded': { minHeight: 40 }, '& .MuiAccordionSummary-content': { my: 0.5, '&.Mui-expanded': { my: 0.5 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6" component="h2">
                      Working Trades
                    </Typography>
                    <Chip label={`${recentOrders.length} orders`} size="small" sx={{ ml: 2 }} />
                  </Box>
                  {recentOrdersLoading && <CircularProgress size={16} sx={{ mr: 2 }} />}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="40px"></TableCell>
                        {(selectedAccount.includes('all') || selectedAccount.length > 1) && (
                          <TableCell><strong>Account</strong></TableCell>
                        )}
                        <TableCell><strong>Symbol/Strategy</strong></TableCell>
                        <TableCell align="right"><strong>Type</strong></TableCell>
                        <TableCell align="right"><strong>Qty</strong></TableCell>
                        <TableCell align="right"><strong>Price</strong></TableCell>
                        <TableCell align="right"><strong>Status</strong></TableCell>
                        <TableCell align="right"><strong>Time</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const allRows = [];
                        recentOrders.forEach((order, orderIdx) => {
                          const orderLegs = order.orderLegCollection || [];
                          const isWorking = ['WORKING', 'PENDING_ACTIVATION', 'LIVE', 'PENDING', 'CONFIRMED', 'UNCONFIRMED', 'QUEUED'].includes(order.status);
                          const orderId = order.orderId || '-';
                          const enteredTime = order.enteredDateTime || order.enteredDate || '';
                          const timeStr = enteredTime ? new Date(enteredTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-';
                          const orderKey = `newtrade_${orderIdx}`;
                          const isOrderExpanded = expandedNewTrades.has(orderKey);
                          const isExitOrder = !!order._isExitOrder;

                          // Extract underlying from first option leg
                          let ul = '';
                          let singleLegInfo = null;
                          for (const leg of orderLegs) {
                            const sym = leg.instrument?.symbol || '';
                            const info = parseOptionSymbol(sym);
                            if (info.isOption) { ul = info.underlying; singleLegInfo = info; break; }
                            ul = sym;
                          }

                          const strategyType = detectStrategyFromOrderLegs(orderLegs);
                          const totalQty = orderLegs[0]?.quantity || '-';

                          const legStrikeSuffix = orderLegs.length > 1 ? [...new Set(orderLegs
                            .map(l => parseOptionSymbol(l.instrument?.symbol || ''))
                            .filter(i => i.isOption && i.strike != null)
                            .map(i => i.strike))].sort((a, b) => a - b)
                            .map(s => (s % 1 === 0 ? String(s) : s.toFixed(2))).join('/') : '';

                          // For single-leg options, show strike and expiry alongside strategy
                          let displayLabel = `${ul} ${strategyType}`;
                          if (orderLegs.length === 1 && singleLegInfo) {
                            const expStr = singleLegInfo.expiration || '';
                            const expLabel = expStr ? 
                              `${expStr.slice(2, 4)}/${expStr.slice(4, 6)}/${expStr.slice(0, 2)}` : '';
                            displayLabel = `${ul} ${singleLegInfo.strike} ${singleLegInfo.type === 'C' ? 'Call' : 'Put'} ${expLabel}`;
                          }

                          // Calculate net mid from quotes
                          let netMid = null;
                          if (orderLegs.length > 0) {
                            let found = true;
                            let mid = 0;
                            orderLegs.forEach(leg => {
                              const sym = leg.instrument?.symbol || '';
                              const symKey = sym.replace(/\s+/g, '');
                              const q = newTradeQuotes[symKey];
                              if (!q || (!q.bid && !q.ask)) { found = false; return; }
                              const isBuy = leg.instruction === 'BUY_TO_OPEN' || leg.instruction === 'BUY_TO_CLOSE';
                              const rawMid = q.mid || ((q.bid + q.ask) / 2);
                              const roundedMid = roundSPXTo5Cents(rawMid, isBuy, ul || q.underlyingSymbol || sym);
                              mid += isBuy ? roundedMid : -roundedMid;
                            });
                            if (found) netMid = mid;
                          }

                          allRows.push(
                            <TableRow key={orderKey} hover sx={{
                              backgroundColor: isExitOrder ? 'rgba(25, 118, 210, 0.06)' : rowAltBg,
                              borderLeft: isExitOrder ? '3px solid #1976d2' : 'none',
                            }}>
                              <TableCell>
                                {orderLegs.length > 1 && (
                                  <IconButton size="small" onClick={() => {
                                    const newExpanded = new Set(expandedNewTrades);
                                    if (isOrderExpanded) newExpanded.delete(orderKey);
                                    else newExpanded.add(orderKey);
                                    setExpandedNewTrades(newExpanded);
                                  }}>
                                    {isOrderExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  </IconButton>
                                )}
                              </TableCell>
                              {(selectedAccount.includes('all') || selectedAccount.length > 1) && (
                                <TableCell>
                                  <AccountNameWithLogo accountName={order._accountName} accountId={order._accountId} accounts={accounts} />
                                </TableCell>
                              )}
                              <TableCell>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {isExitOrder && (
                                      <Chip label={order._exitIndex >= 0 ? `Exit ${order._exitIndex + 1}` : 'Exit'} size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                                    )}
                                    <Typography variant="body2" fontWeight="bold">
                                      {legStrikeSuffix ? (<>{ul}{' '}<Box component="span" sx={{ fontWeight: 'bold', fontSize: '1em', color: 'text.secondary' }}>{legStrikeSuffix}</Box>{' '}{strategyType}</>) : displayLabel}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    ID: {orderId} · {order.orderType || '-'}
                                    {isExitOrder && order._parentOrderId && ` · Parent: ${order._parentOrderId}`}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={strategyType}
                                  size="small"
                                  variant="contained"
                                  color="default"
                                />
                              </TableCell>
                              <TableCell align="right">{totalQty}</TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                  <Typography variant="body2">
                                    {order.price || (order.orderType === 'MARKET' ? 'MKT' : '-')}
                                  </Typography>
                                  {netMid !== null && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      Mid: {Math.abs(netMid).toFixed(2)}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={order.status || '-'}
                                  size="small"
                                  color={isWorking ? 'warning' : order.status === 'FILLED' ? 'success' : order.status === 'CANCELLED' || order.status === 'REJECTED' ? 'error' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="right">{timeStr}</TableCell>
                              <TableCell align="center">
                                {isWorking && (
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      onClick={() => handleModifyNewTradeOrder(order)}
                                      sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 50 }}
                                    >
                                      Modify
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="error"
                                      onClick={() => setCancelOrderConfirm({
                                        open: true,
                                        orderId: orderId,
                                        accountId: order._accountId,
                                        positionLabel: `${displayLabel}${isExitOrder ? ' Exit' : ''}`,
                                      })}
                                      sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 50 }}
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          );

                          // Render individual leg rows when expanded
                          if (orderLegs.length > 1 && isOrderExpanded) {
                            orderLegs.forEach((leg, legIdx) => {
                              const instr = leg.instrument || {};
                              const sym = instr.symbol || '';
                              const info = parseOptionSymbol(sym);
                              const side = (leg.instruction || '').replace(/_/g, ' ');
                              const legLabel = info.isOption
                                ? `${info.underlying} ${info.expiration}${info.type} ${info.strike}`
                                : sym;
                              const symKey = sym.replace(/\s+/g, '');
                              const q = newTradeQuotes[symKey];
                              const legMid = q && (q.bid || q.ask) ? ((q.bid + q.ask) / 2).toFixed(2) : null;

                              allRows.push(
                                <TableRow key={`${orderKey}-leg-${legIdx}`} sx={{ backgroundColor: rowSubBg }}>
                                  <TableCell width="40px" />
                                  {(selectedAccount.includes('all') || selectedAccount.length > 1) && (
                                    <TableCell sx={{ fontSize: '0.75rem' }} />
                                  )}
                                  <TableCell sx={{ fontSize: '0.75rem', pl: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                        {side}
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                        {legLabel}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                    <Chip
                                      label={info.isOption ? `${info.isFuturesOption ? 'Fut. ' : ''}${info.type === 'C' ? 'Call' : 'Put'}` : 'Stock'}
                                      size="small"
                                      variant="contained"
                                      color={info.isOption ? (info.type === 'C' ? 'success' : 'error') : 'default'}
                                      sx={{ height: 18, fontSize: '0.65rem' }}
                                    />
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{leg.quantity || '-'}</TableCell>
                                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                        {legMid || '-'}
                                      </Typography>
                                      {q && q.bid && q.ask && (
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                          B:{q.bid} A:{q.ask}
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>-</TableCell>
                                  <TableCell align="right" sx={{ fontSize: '0.75rem' }}>-</TableCell>
                                  <TableCell align="center" />
                                </TableRow>
                              );
                            });
                          }
                        });

                        return allRows;
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
        ) : (
        <Accordion defaultExpanded sx={{ borderRadius: 0, boxShadow: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" component="h2" fontWeight="bold">
                  Working Trades
                </Typography>
                <Chip label={`${recentOrders.length}`} size="small" sx={{ ml: 1 }} />
              </Box>
              {recentOrdersLoading && <CircularProgress size={16} sx={{ mr: 2 }} />}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <TableContainer component="div" sx={{ mt: 1, width: '100%', overflowX: 'hidden' }}>
              <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Symbol</strong></TableCell>
                    <TableCell align="right"><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const allRows = [];
                    recentOrders.forEach((order, orderIdx) => {
                      const orderLegs = order.orderLegCollection || [];
                      const isWorking = ['WORKING', 'PENDING_ACTIVATION', 'LIVE', 'PENDING', 'CONFIRMED', 'UNCONFIRMED', 'QUEUED'].includes(order.status);
                      const orderId = order.orderId || '-';
                      const orderKey = `newtrade_${orderIdx}`;
                      const isOrderExpanded = expandedNewTrades.has(orderKey);
                      const isExitOrder = !!order._isExitOrder;

                      let ul = '';
                      let singleLegInfo = null;
                      for (const leg of orderLegs) {
                        const sym = leg.instrument?.symbol || '';
                        const info = parseOptionSymbol(sym);
                        if (info.isOption) { ul = info.underlying; singleLegInfo = info; break; }
                        ul = sym;
                      }
                      const strategyType = detectStrategyFromOrderLegs(orderLegs);

                      const legStrikeSuffix = orderLegs.length > 1 ? [...new Set(orderLegs
                        .map(l => parseOptionSymbol(l.instrument?.symbol || ''))
                        .filter(i => i.isOption && i.strike != null)
                        .map(i => i.strike))].sort((a, b) => a - b)
                        .map(s => (s % 1 === 0 ? String(s) : s.toFixed(2))).join('/') : '';

                      let displayLabel = `${ul} ${strategyType}`;
                      if (orderLegs.length === 1 && singleLegInfo) {
                        const expStr = singleLegInfo.expiration || '';
                        const expLabel = expStr ? 
                          `${expStr.slice(2, 4)}/${expStr.slice(4, 6)}/${expStr.slice(0, 2)}` : '';
                        displayLabel = `${ul} ${singleLegInfo.strike} ${singleLegInfo.type === 'C' ? 'Call' : 'Put'} ${expLabel}`;
                      }

                      allRows.push(
                        <TableRow key={orderKey} hover sx={{
                          backgroundColor: isExitOrder ? 'rgba(25, 118, 210, 0.06)' : rowAltBg,
                          borderLeft: isExitOrder ? '3px solid #1976d2' : 'none',
                          cursor: orderLegs.length > 1 ? 'pointer' : 'default',
                        }} onClick={orderLegs.length > 1 ? () => {
                          const newExpanded = new Set(expandedNewTrades);
                          if (isOrderExpanded) newExpanded.delete(orderKey);
                          else newExpanded.add(orderKey);
                          setExpandedNewTrades(newExpanded);
                        } : undefined}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                              {orderLegs.length > 1 && (isOrderExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />)}
                              <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {isExitOrder && (
                                    <Chip label={order._exitIndex >= 0 ? `E${order._exitIndex + 1}` : 'Exit'} size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem' }} />
                                  )}
                                  <Typography variant="body2" fontWeight="bold" noWrap>{legStrikeSuffix ? (<>{ul}{' '}<Box component="span" sx={{ fontWeight: 'bold', fontSize: '1em', color: 'text.secondary' }}>{legStrikeSuffix}</Box>{' '}{strategyType}</>) : displayLabel}</Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }} noWrap>
                                  {order._accountName || ''}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={order.status || '-'}
                              size="small"
                              color={isWorking ? 'warning' : order.status === 'FILLED' ? 'success' : order.status === 'CANCELLED' || order.status === 'REJECTED' ? 'error' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {isWorking && (
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Button size="small" variant="contained" color="primary" onClick={(e) => { e.stopPropagation(); handleModifyNewTradeOrder(order); }} sx={{ fontSize: '0.65rem', py: 0.25, minWidth: 40 }}>Mod</Button>
                                <Button size="small" variant="contained" color="error" onClick={(e) => { e.stopPropagation(); setCancelOrderConfirm({ open: true, orderId, accountId: order._accountId, positionLabel: `${displayLabel}${isExitOrder ? ' Exit' : ''}` }); }} sx={{ fontSize: '0.65rem', py: 0.25, minWidth: 40 }}>X</Button>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );

                      if (orderLegs.length > 1 && isOrderExpanded) {
                        orderLegs.forEach((leg, legIdx) => {
                          const instr = leg.instrument || {};
                          const sym = instr.symbol || '';
                          const info = parseOptionSymbol(sym);
                          const side = (leg.instruction || '').replace(/_/g, ' ');
                          const legLabel = info.isOption
                            ? `${info.expiration}${info.type} ${info.strike}`
                            : sym;

                          allRows.push(
                            <TableRow key={`${orderKey}-leg-${legIdx}`} sx={{ backgroundColor: rowSubBg }}>
                              <TableCell sx={{ pl: 3 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }} noWrap>
                                  {side} {legLabel} ×{leg.quantity || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem' }}>-</TableCell>
                              <TableCell align="center" />
                            </TableRow>
                          );
                        });
                      }
                    });
                    return allRows;
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
        )}
        </Box>
      )}

      {/* Open Positions Section with Accordion */}
      <Box sx={{
        mb: isMobile ? 1 : 3,
        mx: isMobile ? '-8px' : 0,
        width: isMobile ? 'calc(100% + 16px)' : 'auto'
      }}>
        <Accordion
          defaultExpanded
          sx={isMobile ? {
            borderRadius: 0,
            boxShadow: 'none',
            '&:before': { display: 'none' }
          } : undefined}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '&.Mui-expanded': { minHeight: 40 }, '& .MuiAccordionSummary-content': { my: 0.5, '&.Mui-expanded': { my: 0.5 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} component="h2">
                  Open Positions {selectedAccount.includes('all') ? '(All Accounts)' : ''}
                    {(() => {
                      const filtered = applyFilters(groupedPositions);
                      const filteredCount = filtered.length;
                      return (
                        <>
                          {filteredCount > 0 && (
                            <Chip
                              label={`${filteredCount} positions`}
                              size="small"
                              sx={{ ml: 2 }}
                            />
                          )}
                        </>
                      );
                    })()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefreshOpenPositions();
                    }}
                  >
                    Refresh
                  </Button>
                  <Tooltip title={showDetailCols ? 'Hide additional columns' : 'Show additional columns'}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setShowDetailCols(prev => !prev); }}
                    >
                      {showDetailCols ? 'Compact' : 'Detailed'}
                    </Button>
                  </Tooltip>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setGroupSelectMode(prev => !prev);
                      setSelectedGroupLegs(new Set());
                    }}
                  >
                    {groupSelectMode ? 'Done' : 'Group'}
                  </Button>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {groupSelectMode && (() => {
                const sel = getGroupSelectionInfo();
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, mt: 1, flexWrap: 'wrap', bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {sel.hint}
                    </Typography>
                    <Chip label={`${sel.legs.length} leg${sel.legs.length === 1 ? '' : 's'} selected`} size="small" />
                    {sel.manualGroupIds.length > 0 && (
                      <Chip label={`${sel.manualGroupIds.length} group${sel.manualGroupIds.length === 1 ? '' : 's'} selected`} size="small" color="warning" />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" variant="contained" disabled={!sel.canCreateGroup} onClick={handleCreateGroupFromSelection}>
                      Create Group
                    </Button>
                    <Button size="small" variant="outlined" color="warning" disabled={!sel.canUngroup} onClick={handleUngroupFromSelection}>
                      Ungroup
                    </Button>
                  </Box>
                );
              })()}
              {positionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : groupedPositions.length > 0 ? (
                <TableContainer component={isMobile ? 'div' : Paper} sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table
                    size="small"
                    sx={{
                      // Mobile compact cells wrap to a second line; detailed mode
                      // keeps nowrap and relies on horizontal scroll instead.
                      '& .MuiTableCell-root': { py: 0.5, px: isMobile ? 0.3 : 0.75, whiteSpace: isMobile && !showDetailCols ? 'normal' : 'nowrap' },
                      ...(isMobile ? (showDetailCols ? { minWidth: 980, tableLayout: 'auto' } : { width: '100%', tableLayout: 'fixed' }) : {}),
                    }}
                  >
                    {(!isMobile || showDetailCols) && (
                    <TableHead>
                      <TableRow>
                        {!isMobile && <TableCell width="40px"></TableCell>}
                        {(selectedAccount.includes('all') || selectedAccount.length > 1) && (
                          <TableCell>
                            <TableSortLabel
                              active={openPositionsSort.field === 'account'}
                              direction={openPositionsSort.direction}
                              onClick={() => handleOpenPositionsSort('account')}
                            >
                              <strong>Account</strong>
                            </TableSortLabel>
                          </TableCell>
                        )}
                        <TableCell>
                          <TableSortLabel
                            active={openPositionsSort.field === 'symbol'}
                            direction={openPositionsSort.direction}
                            onClick={() => handleOpenPositionsSort('symbol')}
                          >
                            <strong>Symbol/Spread</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={openPositionsSort.field === 'type'}
                            direction={openPositionsSort.direction}
                            onClick={() => handleOpenPositionsSort('type')}
                          >
                            <strong>Type</strong>
                          </TableSortLabel>
                        </TableCell>
                        {!isMobile && (<TableCell align="right">
                          <TableSortLabel
                            active={openPositionsSort.field === 'quantity'}
                            direction={openPositionsSort.direction}
                            onClick={() => handleOpenPositionsSort('quantity')}
                          >
                            <strong>Quantity</strong>
                          </TableSortLabel>
                        </TableCell>)}
                        {showDetailCols && (<TableCell align="right">
                          <TableSortLabel
                            active={openPositionsSort.field === 'avgCost'}
                            direction={openPositionsSort.direction}
                            onClick={() => handleOpenPositionsSort('avgCost')}
                          >
                            <strong>Avg Cost</strong>
                          </TableSortLabel>
                        </TableCell>)}
                        {showDetailCols && (<TableCell align="right">
                          <TableSortLabel
                            active={openPositionsSort.field === 'bpEffect'}
                            direction={openPositionsSort.direction}
                            onClick={() => handleOpenPositionsSort('bpEffect')}
                          >
                            <strong>BP Effect</strong>
                          </TableSortLabel>
                        </TableCell>)}
                        {(!isMobile || showDetailCols) && (<TableCell align="right">
                          <TableSortLabel
                            active={openPositionsSort.field === 'marketValue'}
                            direction={openPositionsSort.direction}
                            onClick={() => handleOpenPositionsSort('marketValue')}
                          >
                            <strong>Market Value</strong>
                          </TableSortLabel>
                        </TableCell>)}
                        <TableCell align="right">
                          <TableSortLabel
                            active={openPositionsSort.field === 'unrealizedPL'}
                            direction={openPositionsSort.direction}
                            onClick={() => handleOpenPositionsSort('unrealizedPL')}
                          >
                            <strong>P/L</strong>
                          </TableSortLabel>
                        </TableCell>
                        {(!isMobile || showDetailCols) && (<TableCell align="right">
                          <TableSortLabel
                            active={openPositionsSort.field === 'plChange'}
                            direction={openPositionsSort.direction}
                            onClick={() => handleOpenPositionsSort('plChange')}
                          >
                            <strong>P/L Chg</strong>
                          </TableSortLabel>
                        </TableCell>)}
                        {showDetailCols && (<TableCell align="right"><strong>Chain P/L</strong></TableCell>)}
                        {showDetailCols && (<TableCell align="right"><Tooltip title="Open P/L since the selected duration's start: lots predating it gain from the anchor close; later buys gain from their cost"><strong>{DUR_LABEL} P/L</strong></Tooltip></TableCell>)}
                        {showDetailCols && (<TableCell align="right">
                          <TableSortLabel
                            active={openPositionsSort.field === 'openDate'}
                            direction={openPositionsSort.direction}
                            onClick={() => handleOpenPositionsSort('openDate')}
                          >
                            <strong>Open Date</strong>
                          </TableSortLabel>
                        </TableCell>)}
                        {showDetailCols && (<TableCell align="right"><strong>Order #</strong></TableCell>)}
                        {(!isMobile || showDetailCols) && (<TableCell align="center"><strong>Actions</strong></TableCell>)}
                      </TableRow>
                    </TableHead>
                    )}
                    <TableBody>
                      {(() => {
                        const filteredPositions = applyFilters(groupedPositions);
                        const groupSelInfo = groupSelectMode ? getGroupSelectionInfo() : { lockedAccountId: null, lockedExpiration: null, hasManualSelection: false, manualGroupIds: [] };
                        const groupsByExpiration = { 'Cash Positions': [], 'Assigned Stocks': [], 'Equity Positions': [], 'ETF Position': [] };
                        filteredPositions.forEach(group => {
                          const expGroup = group.expirationGroup;
                          if (!groupsByExpiration[expGroup]) groupsByExpiration[expGroup] = [];
                          groupsByExpiration[expGroup].push(group);
                        });

                        // Filter to only show groups that have actual positions
                        const groupsToShow = Object.keys(groupsByExpiration).filter(key => groupsByExpiration[key].length > 0);

                        // Sort value for a single group row (spread or single position)
                        const getSortVal = (group, field) => {
                          if (group.isSpread) {
                            const m = group.metrics || {};
                            switch (field) {
                              case 'account': return (group.accountName || '').toLowerCase();
                              case 'symbol': return (group.underlying || '').toLowerCase();
                              case 'type': return 'spread';
                              case 'quantity': return Number(m.totalQuantity || 0);
                              case 'avgCost': return Number(m.avgCost || 0);
                              case 'bpEffect': return Number(m.bpEffect || 0);
                              case 'marketValue': return Number(m.totalMarketValue || 0);
                              case 'unrealizedPL': return Number(m.unrealizedPL || 0);
                              case 'plChange': return Number(m.dayPL || 0);
                              case 'openDate': return ((group.legs || []).map(l => l.openDate).filter(Boolean).sort()[0]) || '';
                              default: return (group.underlying || '').toLowerCase();
                            }
                          } else {
                            const p = group.position || {};
                            switch (field) {
                              case 'account': return (p.accountName || '').toLowerCase();
                              case 'symbol': return (p.symbol || '').toLowerCase();
                              case 'type': {
                                if (p.optionInfo?.isOption) return p.optionInfo.type === 'C' ? 'call' : 'put';
                                return 'stock';
                              }
                              case 'quantity': return Number(p.quantity || 0);
                              case 'avgCost': return Number(Math.abs(p.quantity) > 0 ? ((p.cost_basis || 0) / Math.abs(p.quantity)) : (p.averagePrice || 0));
                              case 'bpEffect': return Number(group.bpEffect || 0);
                              case 'marketValue': return Number(p.marketValue || 0);
                              case 'unrealizedPL': {
                                return Number(positionUnrealizedPL(p) || 0);
                              }
                              case 'plChange': return Number(p.dayPL || 0);
                              case 'openDate': return p.openDate || '';
                              default: return (p.symbol || '').toLowerCase();
                            }
                          }
                        };
                        const sortDir = openPositionsSort.direction === 'asc' ? 1 : -1;
                        const cmp = (a, b) => {
                          const va = getSortVal(a, openPositionsSort.field);
                          const vb = getSortVal(b, openPositionsSort.field);
                          if (typeof va === 'number' && typeof vb === 'number') {
                            return sortDir * (va - vb);
                          }
                          return sortDir * String(va).localeCompare(String(vb));
                        };

                        // Aggregate a whole section (symbol group) into one value
                        // so the sections themselves can be ordered by the
                        // selected column — sums for numeric fields, earliest
                        // date, and min string for text columns.
                        const sectionSortVal = (expKey) => {
                          const rows = groupsByExpiration[expKey] || [];
                          const f = openPositionsSort.field;
                          if (f === 'symbol') return String(expKey).toLowerCase();
                          const vals = rows.map(g => getSortVal(g, f));
                          if (f === 'openDate') return vals.filter(Boolean).sort()[0] || '';
                          if (vals.length && typeof vals[0] === 'number') {
                            if (f === 'avgCost') return vals.reduce((s, v) => s + v, 0) / vals.length;
                            return vals.reduce((s, v) => s + v, 0);
                          }
                          return vals.map(v => String(v || '')).sort()[0] || '';
                        };

                        const sortedExpirations = groupsToShow.sort((a, b) => {
                          const order = { 'Cash Positions': 0, 'Assigned Stocks': 1, 'Equity Positions': 2, 'ETF Position': 3 };
                          const aOrder = order[a] != null ? order[a] : 4;
                          const bOrder = order[b] != null ? order[b] : 4;
                          if (aOrder !== bOrder) return aOrder - bOrder;
                          if (groupOpenByUnderlying) {
                            if (aOrder !== 4) return 0; // pinned special sections
                            const va = sectionSortVal(a), vb = sectionSortVal(b);
                            if (typeof va === 'number' && typeof vb === 'number') return sortDir * (va - vb);
                            return sortDir * String(va).localeCompare(String(vb));
                          }
                          return new Date(a) - new Date(b);
                        });
                        const allRows = [];
                        sortedExpirations.forEach(expiration => {
                          let groupsInExpiration = groupsByExpiration[expiration] || [];
                          const isGroupExpanded = expandedGroups.has(expiration);
                          const groupLabel = expiration === 'Equity Positions' ? 'Equity Positions' :
                            expiration === 'ETF Position' ? 'ETF Position' :
                              expiration === 'Assigned Stocks' ? 'Assigned Positions' :
                                expiration === 'Cash Positions' ? 'Cash Positions' :
                                  groupOpenByUnderlying ? expiration : `Options Expiring ${expiration}`;
                          let totalGroupPL = 0;
                          let totalGroupBP = 0;
                          let totalGroupMarketValue = 0;
                          let totalGroupDayPL = 0;
                          groupsInExpiration.forEach(group => {
                            if (group.isSpread) {
                              totalGroupPL += group.metrics?.unrealizedPL || 0;
                              totalGroupBP += group.metrics?.bpEffect || 0;
                              totalGroupMarketValue += group.metrics?.totalMarketValue || 0;
                              totalGroupDayPL += group.metrics?.dayPL || 0;
                            } else {
                              const position = group.position;
                              const unrealizedPL = positionUnrealizedPL(position);
                              totalGroupPL += unrealizedPL;
                              totalGroupBP += group.bpEffect || 0;
                              totalGroupMarketValue += position.marketValue || 0;
                              totalGroupDayPL += position.dayPL || 0;
                            }
                          });
                          allRows.push(
                            <TableRow key={`${expiration}-header`} sx={{ backgroundColor: expHeaderBg, cursor: isMobile ? 'pointer' : 'default' }} onClick={isMobile ? () => {
                              const newExpanded = new Set(expandedGroups);
                              if (isGroupExpanded) newExpanded.delete(expiration);
                              else newExpanded.add(expiration);
                              setExpandedGroups(newExpanded);
                            } : undefined}>
                              {isMobile ? (
                                <TableCell colSpan={(selectedAccount.includes('all') || selectedAccount.length > 1 ? 7 : 6) + (showDetailCols ? 4 : 0)}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                      {isGroupExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                      <Typography variant="subtitle2" fontWeight="bold" noWrap>{groupLabel}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                                      <Typography variant="subtitle2" fontWeight="bold" color={getPLColor(totalGroupPL)}>
                                        {formatCurrency(totalGroupPL)}
                                      </Typography>
                                      <Typography variant="caption" color={getPLColor(totalGroupDayPL)}>
                                        Chg: {formatCurrency(totalGroupDayPL)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                              ) : (
                                <>
                              <TableCell>
                                <IconButton size="small" onClick={() => {
                                  const newExpanded = new Set(expandedGroups);
                                  if (isGroupExpanded) newExpanded.delete(expiration);
                                  else newExpanded.add(expiration);
                                  setExpandedGroups(newExpanded);
                                }}>
                                  {isGroupExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </TableCell>
                              <TableCell colSpan={(selectedAccount.includes('all') || selectedAccount.length > 1) ? 3 : 2}>
                                <Typography variant="subtitle2" fontWeight="bold">{groupLabel}</Typography>
                              </TableCell>
                              <TableCell align="right"><Chip label={`${groupsInExpiration.length} position${groupsInExpiration.length !== 1 ? 's' : ''}`} size="small" variant="contained" color="primary" /></TableCell>
                              {showDetailCols && (<TableCell />)}
                              {showDetailCols && (<TableCell align="right"><Typography variant="subtitle2" fontWeight="bold" color="primary">{formatCurrency(totalGroupBP)}</Typography></TableCell>)}
                              <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">{formatCurrency(totalGroupMarketValue)}</Typography></TableCell>
                              </>
                              )}
                              {!isMobile && (<TableCell align="right">
                                <Typography variant="subtitle2" fontWeight="bold" color={getPLColor(totalGroupPL)}>{formatCurrency(totalGroupPL)}</Typography>
                                <Typography variant="caption" color={getPLColor(totalGroupPL)}>{(() => { const totalCost = groupsInExpiration.reduce((s, g) => s + Math.abs(g.isSpread ? (g.metrics?.netDebit || 0) : (g.position?.cost_basis || 0)), 0); return totalCost > 0 ? `${(totalGroupPL / totalCost * 100).toFixed(1)}%` : ''; })()}</Typography>
                              </TableCell>)}
                              {!isMobile && (<TableCell align="right">
                                <Typography variant="subtitle2" fontWeight="bold" color={getPLColor(totalGroupDayPL)}>{formatCurrency(totalGroupDayPL)}</Typography>
                              </TableCell>)}
                              {!isMobile && showDetailCols && (<TableCell />)}
                              {!isMobile && showDetailCols && (<TableCell />)}
                              {!isMobile && (<TableCell />)}
                            </TableRow>
                          );
                          if (isGroupExpanded) {
                            const sortedGroups = [...groupsInExpiration].sort(cmp);
                            sortedGroups.forEach((group) => {
                              if (group.isSpread) {
                                const isExpanded = isSpreadExpanded(group.id);
                                const spreadMetrics = group.metrics;
                                const unrealizedPL = spreadMetrics.unrealizedPL || 0;
                                const dayPL = spreadMetrics.dayPL || 0;
                                // Shade the spread root row: red if any short leg is ITM, else green
                                const spreadLegs = group.legs || [];
                                const anyShortITM = spreadLegs.some(leg => {
                                  const legIsShort = (leg.quantity || 0) < 0;
                                  return legIsShort && leg.isOTM === false && leg.strikeDistance != null;
                                });
                                const spreadBg = spreadLegs.length > 0 && spreadLegs.some(l => l.strikeDistance != null)
                                  ? (anyShortITM ? unfavorableOptionBg : favorableOptionBg)
                                  : rowAltBg;
                                const selKey = group.isManualGroup ? `mgroup:${group.groupId}` : `spread:${group.id}`;
                                const selDisabled = group.isManualGroup
                                  ? (groupSelInfo.legs?.length > 0 || (groupSelInfo.manualGroupIds.length > 0 && !selectedGroupLegs.has(selKey)))
                                  : (groupSelInfo.hasManualSelection
                                    || (groupSelInfo.lockedAccountId !== null && String(group.accountId || '') !== groupSelInfo.lockedAccountId)
                                    || (groupSelInfo.lockedExpiration !== null && String(group.expiration || '') !== groupSelInfo.lockedExpiration));
                                allRows.push(
                                  <TableRow
                                    key={`${group.id}-summary`}
                                    hover
                                    sx={{ backgroundColor: spreadBg, cursor: isMobile ? 'pointer' : 'default' }}
                                    onClick={isMobile ? () => (groupSelectMode ? (!selDisabled && toggleGroupSelectionKey(selKey)) : toggleSpreadExpansion(group.id)) : undefined}
                                  >
                                    {!isMobile && (
                                    <TableCell>
                                        {groupSelectMode ? (
                                          <Checkbox
                                            size="small"
                                            disabled={selDisabled}
                                            checked={selectedGroupLegs.has(selKey)}
                                            onChange={() => toggleGroupSelectionKey(selKey)}
                                          />
                                        ) : (
                                          <IconButton size="small" onClick={() => toggleSpreadExpansion(group.id)}>
                                            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                          </IconButton>
                                        )}
                                      </TableCell>
                                    )}
                                    {(selectedAccount.includes('all') || selectedAccount.length > 1) && (
                                      <TableCell sx={isMobile ? { fontSize: '0.65rem', width: 52, p: '4px 2px' } : undefined}>
                                        {isMobile ? (
                                          <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.15, whiteSpace: 'normal', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'break-word' }}>{group.accountName}</Typography>
                                        ) : (
                                          <AccountNameWithLogo accountName={group.accountName} accountId={group.accountId} accounts={accounts} />
                                        )}
                                      </TableCell>
                                    )}
                                    <TableCell
                                      onClick={(e) => {
                                        if (isMobile && groupSelectMode) { e.stopPropagation(); if (!selDisabled) toggleGroupSelectionKey(selKey); return; }
                                        setSelectedPosition(group); setPositionDetailsOpen(true); fetchPositionGreeks(group);
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                    >
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {isMobile && groupSelectMode && (
                                            <Checkbox
                                              size="small"
                                              disabled={selDisabled}
                                              checked={selectedGroupLegs.has(selKey)}
                                              onChange={() => toggleGroupSelectionKey(selKey)}
                                              onClick={(e) => e.stopPropagation()}
                                              sx={{ p: 0.25 }}
                                            />
                                          )}
                                          <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'normal' }}>{group.underlying}{group.isSpread && groupLegStrikeSuffix(group) && (
                                            <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1em', color: 'text.secondary', ml: 0.5 }}>{groupLegStrikeSuffix(group)}</Box>
                                          )}</Typography>
                                          {group.daysToEarnings !== null && group.daysToEarnings !== undefined && (
                                            <Avatar
                                              sx={{
                                                width: 24,
                                                height: 24,
                                                fontSize: '0.7rem',
                                                bgcolor: group.daysToEarnings === 0 ? '#9c27b0' : group.daysToEarnings <= 7 ? '#f44336' : group.daysToEarnings <= 21 ? '#ff9800' : '#4caf50',
                                                color: '#fff'
                                              }}
                                              title={group.daysToEarnings === 0 ? `Earnings today (${group.earningsDate})` : `Earnings in ${group.daysToEarnings} day${group.daysToEarnings === 1 ? '' : 's'} (${group.earningsDate})`}
                                            >
                                              {group.daysToEarnings === 0 ? 'E' : group.daysToEarnings}
                                            </Avatar>
                                          )}
                                        </Box>
                                        {group.underlyingPrice && typeof group.underlyingPrice === 'number' && (
                                          <Typography variant="caption" color="text.secondary" sx={isMobile ? { fontSize: '0.65rem', lineHeight: 1.1 } : undefined}>
                                            ${group.underlyingPrice.toFixed(2)}
                                            {!isMobile && group.underlyingQuote?.change !== undefined && group.underlyingQuote?.change !== null && (
                                              <span style={{ color: (group.underlyingQuote.change || 0) >= 0 ? '#4caf50' : '#f44336' }}>
                                                {' '}({group.underlyingQuote.change >= 0 ? '+' : ''}{group.underlyingQuote.change.toFixed(2)}, {(group.underlyingQuote.change_percentage || 0).toFixed(2)}%)
                                              </span>
                                            )}
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                        <Chip
                                          label={renderStrategyLabel(group.spreadType)}
                                          size="small"
                                          variant="contained"
                                          color="default"
                                          sx={{ height: 'auto', maxWidth: '100%', '& .MuiChip-label': { px: isMobile ? 0.5 : 1, overflow: 'visible', whiteSpace: 'normal' } }}
                                        />
                                        {(() => {
                                          const closingLegs = group.legs.filter(leg => getClosingOrderInfo(leg.accountId, leg.symbol).hasClosing);
                                          if (closingLegs.length === 0) return null;
                                          const allWorking = closingLegs.flatMap(leg => getClosingOrderInfo(leg.accountId, leg.symbol).workingOrders);
                                          const tooltipTitle = allWorking.length > 0
                                            ? allWorking.map(o => `${o.instruction} ${o.quantity} ${o.originalSymbol} @ ${o.price || 'MKT'} (${o.status})`).join('\n')
                                            : 'Pending close (filled, awaiting settlement)';
                                          return (
                                            <Tooltip title={tooltipTitle} arrow>
                                              <Chip icon={<PendingActionsIcon />} label={allWorking.length > 0 ? `Closing (${allWorking.length})` : 'Closing'} size="small" sx={{ bgcolor: '#e65100', color: '#fff', height: 20, '& .MuiChip-icon': { fontSize: 14, color: '#fff' } }} />
                                            </Tooltip>
                                          );
                                        })()}
                                      </Box>
                                    </TableCell>
                                    {!isMobile && (<TableCell align="right">{spreadMetrics.totalQuantity}</TableCell>)}
                                    {showDetailCols && (<TableCell align="right">{formatCurrency(spreadMetrics.avgCost)}</TableCell>)}
                                    {showDetailCols && (<TableCell align="right">{formatCurrency(spreadMetrics.bpEffect || 0)}</TableCell>)}
                                    <TableCell align="right" sx={isMobile ? { fontSize: '0.7rem', p: '4px 2px' } : undefined}>{formatCurrency(spreadMetrics.totalMarketValue)}</TableCell>
                                    <TableCell align="right" sx={isMobile ? { p: '4px 2px' } : undefined}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <Chip label={formatCurrency(unrealizedPL)} color={getPLColor(unrealizedPL)} size="small" sx={isMobile ? { height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } } : undefined} />
                                        {!isMobile && spreadMetrics.netDebit !== 0 && (
                                          <Typography variant="caption" color={getPLColor(unrealizedPL)} sx={{ mt: 0.25 }}>
                                            {(unrealizedPL / Math.abs(spreadMetrics.netDebit) * 100).toFixed(1)}%
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right" sx={isMobile ? { fontSize: '0.7rem', p: '4px 2px' } : undefined}>
                                      {isMobile ? (
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem' }} color={getPLColor(dayPL)}>
                                          {dayPL >= 0 ? '+' : ''}{formatCurrency(dayPL)}
                                        </Typography>
                                      ) : (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <Chip label={formatCurrency(dayPL)} color={getPLColor(dayPL)} size="small" />
                                        {(() => {
                                          const priorPL = unrealizedPL - dayPL;
                                          return priorPL !== 0 ? (
                                            <Typography variant="caption" color={getPLColor(dayPL)} sx={{ mt: 0.25 }}>
                                              {(dayPL / Math.abs(priorPL) * 100).toFixed(1)}%
                                            </Typography>
                                          ) : null;
                                        })()}
                                      </Box>
                                      )}
                                    </TableCell>
                                    {showDetailCols && (<TableCell align="right">
                                      {(() => {
                                        const cpl = groupChainPL(group);
                                        return cpl == null ? '-' : (
                                          <Typography variant="body2" color={cpl >= 0 ? 'success.main' : 'error.main'}>{formatCurrency(cpl)}</Typography>
                                        );
                                      })()}
                                    </TableCell>)}
                                    {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>-</TableCell>)}
                                    {showDetailCols && (<TableCell align="right">{formatOpenDate(
                                      (group.legs || []).map(l => l.openDate).filter(Boolean).sort()[0],
                                      (group.legs || []).map(l => l.openTs).filter(Boolean).sort((a, b) => new Date(a) - new Date(b))[0]
                                    ) || '-'}</TableCell>)}
                                    {showDetailCols && (<TableCell align="right">
                                      {(() => {
                                        const ids = [...new Set((group.legs || []).flatMap(l => legOrderIds(l)))];
                                        if (!ids.length) return '-';
                                        if (ids.length === 1) return <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{ids[0]}</Typography>;
                                        return <Tooltip title={ids.join(', ')}><Chip label={`${ids.length} orders`} size="small" color="warning" variant="outlined" /></Tooltip>;
                                      })()}
                                    </TableCell>)}
                                    <TableCell align="center" sx={isMobile ? { p: '4px 2px' } : undefined}>
                                      {isNonTradingAccount(group.accountId) ? (
                                        <Chip label="Read-only" size="small" variant="outlined" />
                                      ) : isMobile ? (
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setRowActionsMenu({ anchor: e.currentTarget, position: group }); }}>
                                          <KeyboardArrowDown fontSize="small" />
                                        </IconButton>
                                      ) : (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          endIcon={<KeyboardArrowDown />}
                                          onClick={(e) => { e.stopPropagation(); setRowActionsMenu({ anchor: e.currentTarget, position: group }); }}
                                          sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 64, textTransform: 'none' }}
                                        >
                                          Actions
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                                // Leg rows render directly in the outer table so every
                                // column lines up under the header (a nested table inside
                                // a colSpan cell can never align).
                                if (isExpanded) {
                                  group.legs.forEach((leg, legIndex) => {
                                    const isShort = leg.quantity < 0;
                                    const legUnrealizedPL = positionUnrealizedPL(leg);
                                    allRows.push(
                                      <TableRow
                                        key={`${group.id}-leg-${legIndex}`}
                                        sx={{
                                          backgroundColor: (leg.strikeDistance !== null && leg.strikeDistance !== undefined)
                                            ? ((isShort === leg.isOTM) ? favorableOptionBg : unfavorableOptionBg)
                                            : rowSubBg,
                                        }}
                                      >
                                        {!isMobile && <TableCell />}
                                        {(selectedAccount.includes('all') || selectedAccount.length > 1) && (
                                          <TableCell sx={{ fontSize: '0.8rem' }}>
                                            {isMobile ? (
                                              <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.15, whiteSpace: 'normal', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'break-word' }}>{leg.accountName}</Typography>
                                            ) : (
                                              <AccountNameWithLogo accountName={leg.accountName} accountId={leg.accountId} accounts={accounts} size={36} />
                                            )}
                                          </TableCell>
                                        )}
                                        <TableCell sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem', pl: isMobile ? 1 : 4, whiteSpace: isMobile ? 'normal' : undefined }}>{formatOptionLabel(leg)}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}><Chip label={leg.optionInfo?.type === 'C' ? 'Call' : 'Put'} size="small" variant="contained" color="default" /></TableCell>
                                        {!isMobile && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>{leg.quantity}</TableCell>)}
                                        {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCurrency(Math.abs(leg.quantity) > 0 ? ((leg.cost_basis || 0) / Math.abs(leg.quantity)) : 0)}</TableCell>)}
                                        {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>-</TableCell>)}
                                        <TableCell align="right" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>{formatCurrency(leg.marketValue)}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                            <Chip label={formatCurrency(legUnrealizedPL)} color={getPLColor(legUnrealizedPL)} size="small" />
                                            {Math.abs(leg.cost_basis || 0) > 0 && (
                                              <Typography variant="caption" color={getPLColor(legUnrealizedPL)} sx={{ fontSize: '0.7rem', mt: 0.25 }}>
                                                {(legUnrealizedPL / Math.abs(leg.cost_basis) * 100).toFixed(1)}%
                                              </Typography>
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}><Chip label={formatCurrency(leg.change || 0)} size="small" sx={isMobile ? { height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } } : undefined} /></TableCell>
                                        {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>-</TableCell>)}
                                        {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>-</TableCell>)}
                                        {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatOpenDate(leg.openDate, leg.openTs) || '-'}</TableCell>)}
                                        {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                          {(() => { const ids = legOrderIds(leg); return ids.length ? ids.join(', ') : '-'; })()}
                                        </TableCell>)}
                                        <TableCell />
                                      </TableRow>
                                    );
                                  });
                                }
                              } else {
                                const position = group.position;
                                const isShort = position.quantity < 0;
                                const unrealizedPL = positionUnrealizedPL(position);
                                const dayPL = position.dayPL || 0;
                                const isAssignedStock = (position.isAssigned || position.is_assigned) && !position.optionInfo?.isOption;
                                const isAssignedExpanded = expandedAssignedPositions.has(group.id);
                                const legAcc = String(group.accountId || position.accountId || position.account_id || '');
                                const legSelKey = `leg:${legAcc}:${String(position.symbol || '').trim()}`;
                                const legSelDisabled = !position.optionInfo?.isOption
                                  || groupSelInfo.hasManualSelection
                                  || (groupSelInfo.lockedAccountId !== null && legAcc !== groupSelInfo.lockedAccountId)
                                  || (groupSelInfo.lockedExpiration !== null && String(position.optionInfo?.expiration || '') !== groupSelInfo.lockedExpiration);
                                
                                // Find associated option positions for assigned stock
                                const associatedOptions = isAssignedStock ? filteredPositions.filter(g => 
                                  !g.isSpread && 
                                  g.position && 
                                  g.position.optionInfo?.isOption && 
                                  (g.position.optionInfo?.underlying === position.symbol || g.position.underlying === position.symbol || g.position.symbol === position.symbol)
                                ) : [];
                                
                                // Debug logging
                                if (isAssignedStock) {
                                  console.log('[Assigned Stock Debug]', {
                                    stockSymbol: position.symbol,
                                    stockId: group.id,
                                    isExpanded: isAssignedExpanded,
                                    totalFilteredPositions: filteredPositions.length,
                                    associatedOptionsCount: associatedOptions.length,
                                    allOptionSymbols: filteredPositions.filter(g => g.position && g.position.optionInfo?.isOption).map(g => ({
                                      symbol: g.position.symbol,
                                      underlying: g.position.optionInfo?.underlying || g.position.underlying,
                                      account: g.position.accountId || g.position.account
                                    }))
                                  });
                                }

                                allRows.push(
                                  <TableRow
                                    key={group.id}
                                    hover
                                    sx={{
                                      cursor: isAssignedStock ? 'pointer' : 'default',
                                      backgroundColor: (position.optionInfo?.isOption && group.strikeDistance !== null && group.strikeDistance !== undefined)
                                        ? ((isShort === group.isOTM) ? favorableOptionBg : unfavorableOptionBg)
                                        : undefined,
                                    }}
                                    onClick={isAssignedStock ? () => {
                                      const newExpanded = new Set(expandedAssignedPositions);
                                      if (isAssignedExpanded) newExpanded.delete(group.id);
                                      else newExpanded.add(group.id);
                                      setExpandedAssignedPositions(newExpanded);
                                    } : undefined}
                                  >
                                    {!isMobile && (
                                    <TableCell>
                                      {groupSelectMode ? (
                                        <Checkbox
                                          size="small"
                                          disabled={legSelDisabled}
                                          checked={selectedGroupLegs.has(legSelKey)}
                                          onChange={() => toggleGroupSelectionKey(legSelKey)}
                                        />
                                      ) : isAssignedStock ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <IconButton size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            const newExpanded = new Set(expandedAssignedPositions);
                                            if (isAssignedExpanded) newExpanded.delete(group.id);
                                            else newExpanded.add(group.id);
                                            setExpandedAssignedPositions(newExpanded);
                                          }}>
                                            {isAssignedExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                          </IconButton>
                                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUnassign(position); }} title="Remove from Assigned">
                                            <OutlinedFlagIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      ) : (
                                        !position.optionInfo?.isOption && !position.isAssigned && !position.is_assigned && (
                                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenAssignDialog(position); }} title="Mark as Assigned">
                                            <FlagIcon fontSize="small" />
                                          </IconButton>
                                        )
                                      )}
                                    </TableCell>
                                    )}
                                    {(selectedAccount.includes('all') || selectedAccount.length > 1) && (
                                      <TableCell sx={isMobile ? { fontSize: '0.65rem', width: 52, p: '4px 2px' } : undefined}>
                                        {isMobile ? (
                                          <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.15, whiteSpace: 'normal', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'break-word' }}>{position.accountName}</Typography>
                                        ) : (
                                          <AccountNameWithLogo accountName={position.accountName} accountId={position.accountId || position.account_id} accounts={accounts} />
                                        )}
                                      </TableCell>
                                    )}
                                    <TableCell
                                      onClick={() => {
                                        if (isMobile && groupSelectMode) {
                                          if (!legSelDisabled) toggleGroupSelectionKey(legSelKey);
                                          return;
                                        }
                                        setSelectedPosition({ ...position, accountId: position.accountId || position.account_id, symbol: position.symbol, underlying: group.underlying, underlyingPrice: group.underlyingPrice, underlyingQuote: group.underlyingQuote, openDate: group.openDate || position.openDate || position.tradeDate || null, daysToEarnings: group.daysToEarnings, earningsDate: group.earningsDate }); setPositionDetailsOpen(true); fetchPositionGreeks(position);
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                    >
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {isMobile && groupSelectMode && (
                                            <Checkbox
                                              size="small"
                                              disabled={legSelDisabled}
                                              checked={selectedGroupLegs.has(legSelKey)}
                                              onChange={() => toggleGroupSelectionKey(legSelKey)}
                                              onClick={(e) => e.stopPropagation()}
                                              sx={{ p: 0.25 }}
                                            />
                                          )}
                                          <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'normal' }}>
                                            {position.optionInfo?.isOption ? formatOptionLabel(position) : position.symbol}
                                          </Typography>
                                          {group.daysToEarnings !== null && group.daysToEarnings !== undefined && (
                                            <Avatar
                                              sx={{
                                                width: 24,
                                                height: 24,
                                                fontSize: '0.7rem',
                                                bgcolor: group.daysToEarnings === 0 ? '#9c27b0' : group.daysToEarnings <= 7 ? '#f44336' : group.daysToEarnings <= 21 ? '#ff9800' : '#4caf50',
                                                color: '#fff'
                                              }}
                                              title={group.daysToEarnings === 0 ? `Earnings today (${group.earningsDate})` : `Earnings in ${group.daysToEarnings} day${group.daysToEarnings === 1 ? '' : 's'} (${group.earningsDate})`}
                                            >
                                              {group.daysToEarnings === 0 ? 'E' : group.daysToEarnings}
                                            </Avatar>
                                          )}
                                        </Box>
                                        {group.underlyingPrice && typeof group.underlyingPrice === 'number' && (
                                          <Typography variant="caption" color="text.secondary" sx={isMobile ? { fontSize: '0.65rem', lineHeight: 1.1 } : undefined}>
                                            ${group.underlyingPrice.toFixed(2)}
                                            {!isMobile && group.underlyingQuote?.change !== undefined && group.underlyingQuote?.change !== null && (
                                              <span style={{ color: (group.underlyingQuote.change || 0) >= 0 ? '#4caf50' : '#f44336' }}>
                                                {' '}({group.underlyingQuote.change >= 0 ? '+' : ''}{group.underlyingQuote.change.toFixed(2)}, {(group.underlyingQuote.change_percentage || 0).toFixed(2)}%)
                                              </span>
                                            )}
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                        <Chip
                                          label={
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1, py: 0.4, fontWeight: 'bold', fontSize: isMobile ? '0.7rem' : undefined }}>
                                              <span>{position.quantity > 0 ? 'Long' : 'Short'}</span>
                                              <span>{position.optionInfo?.isOption ? `${position.optionInfo.isFuturesOption ? 'Fut. ' : ''}${position.optionInfo.type === 'C' ? 'Call' : 'Put'}` : 'Stock'}</span>
                                            </Box>
                                          }
                                          size="small"
                                          variant="contained"
                                          color="default"
                                          sx={{ height: 'auto', maxWidth: '100%', '& .MuiChip-label': { px: isMobile ? 0.5 : 1, overflow: 'visible', whiteSpace: 'normal' } }}
                                        />
                                        {(() => {
                                          const info = getClosingOrderInfo(position.accountId, position.symbol);
                                          if (!info.hasClosing) return null;
                                          const tooltipTitle = info.workingOrders.length > 0
                                            ? info.workingOrders.map(o => `${o.instruction} ${o.quantity} ${o.originalSymbol} @ ${o.price || 'MKT'} (${o.status})`).join('\n')
                                            : 'Pending close (filled, awaiting settlement)';
                                          return (
                                            <Tooltip title={tooltipTitle} arrow>
                                              <Chip icon={<PendingActionsIcon />} label={info.workingOrders.length > 0 ? `Closing (${info.workingOrders.length})` : 'Closing'} size="small" sx={{ bgcolor: '#e65100', color: '#fff', height: 20, '& .MuiChip-icon': { fontSize: 14, color: '#fff' } }} />
                                            </Tooltip>
                                          );
                                        })()}
                                      </Box>
                                    </TableCell>
                                    {!isMobile && (<TableCell align="right">{position.quantity}</TableCell>)}
                                    {showDetailCols && (<TableCell align="right">{formatCurrency(Math.abs(position.quantity) > 0 ? ((position.cost_basis || 0) / Math.abs(position.quantity)) : 0)}</TableCell>)}
                                    {showDetailCols && (<TableCell align="right">{formatCurrency(group.bpEffect || 0)}</TableCell>)}
                                    <TableCell align="right" sx={isMobile ? { fontSize: '0.7rem', p: '4px 2px' } : undefined}>{formatCurrency(position.marketValue)}</TableCell>
                                    <TableCell align="right" sx={isMobile ? { p: '4px 2px' } : undefined}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <Chip label={formatCurrency(unrealizedPL)} color={getPLColor(unrealizedPL)} size="small" sx={isMobile ? { height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } } : undefined} />
                                        {!isMobile && Math.abs(position.cost_basis || 0) > 0 && (
                                          <Typography variant="caption" color={getPLColor(unrealizedPL)} sx={{ mt: 0.25 }}>
                                            {(unrealizedPL / Math.abs(position.cost_basis) * 100).toFixed(1)}%
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right" sx={isMobile ? { fontSize: '0.7rem', p: '4px 2px' } : undefined}>
                                      {isMobile ? (
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem' }} color={getPLColor(dayPL)}>
                                          {dayPL >= 0 ? '+' : ''}{formatCurrency(dayPL)}
                                        </Typography>
                                      ) : (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <Chip label={formatCurrency(dayPL)} color={getPLColor(dayPL)} size="small" />
                                        {(() => {
                                          const priorPL = unrealizedPL - dayPL;
                                          return priorPL !== 0 ? (
                                            <Typography variant="caption" color={getPLColor(dayPL)} sx={{ mt: 0.25 }}>
                                              {(dayPL / Math.abs(priorPL) * 100).toFixed(1)}%
                                            </Typography>
                                          ) : null;
                                        })()}
                                      </Box>
                                      )}
                                    </TableCell>
                                    {showDetailCols && (<TableCell align="right">
                                      {(() => {
                                        const cpl = groupChainPL(position);
                                        return cpl == null ? '-' : (
                                          <Typography variant="body2" color={cpl >= 0 ? 'success.main' : 'error.main'}>{formatCurrency(cpl)}</Typography>
                                        );
                                      })()}
                                    </TableCell>)}
                                    {showDetailCols && (<TableCell align="right">
                                      {(() => {
                                        const dpl = stockDurationPL(position);
                                        return dpl == null ? '-' : (
                                          <Typography variant="body2" color={dpl >= 0 ? 'success.main' : 'error.main'}>{formatCurrency(dpl)}</Typography>
                                        );
                                      })()}
                                    </TableCell>)}
                                    {showDetailCols && (<TableCell align="right">{formatOpenDate(position.openDate, position.openTs) || '-'}</TableCell>)}
                                    {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                      {(() => { const ids = legOrderIds(position); return ids.length ? ids.join(', ') : '-'; })()}
                                    </TableCell>)}
                                    <TableCell align="center" sx={isMobile ? { p: '4px 2px' } : undefined}>
                                      {position.optionInfo?.isOption && (
                                        isNonTradingAccount(position.accountId) ? (
                                          <Chip label="Read-only" size="small" variant="outlined" />
                                        ) : isMobile ? (
                                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setRowActionsMenu({ anchor: e.currentTarget, position }); }}>
                                            <KeyboardArrowDown fontSize="small" />
                                          </IconButton>
                                        ) : (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            endIcon={<KeyboardArrowDown />}
                                            onClick={(e) => { e.stopPropagation(); setRowActionsMenu({ anchor: e.currentTarget, position }); }}
                                            sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 64, textTransform: 'none' }}
                                          >
                                            Actions
                                          </Button>
                                        )
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                                
                                // Add associated option positions row when assigned stock is expanded
                                if (isAssignedStock && isAssignedExpanded && associatedOptions.length > 0) {
                                  associatedOptions.forEach((optGroup, idx) => {
                                    const optPos = optGroup.position;
                                    const optUnrealizedPL = positionUnrealizedPL(optPos);
                                    allRows.push(
                                      <TableRow key={`${group.id}-opt-${idx}`} sx={{ backgroundColor: optLegBg }}>
                                        {!isMobile && (
                                          <TableCell>
                                            <Box sx={{ width: '40px' }}></Box>
                                          </TableCell>
                                        )}
                                        {(selectedAccount.includes('all') || selectedAccount.length > 1) && (
                                          <TableCell sx={{ fontSize: '0.8rem', pl: isMobile ? 0 : 4 }}>
                                            {isMobile ? (
                                              <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.15, whiteSpace: 'normal', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'break-word' }}>{optPos.accountName}</Typography>
                                            ) : (
                                              <AccountNameWithLogo accountName={optPos.accountName} accountId={optPos.accountId} accounts={accounts} size={36} />
                                            )}
                                          </TableCell>
                                        )}
                                        <TableCell sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem', pl: isMobile ? 0 : 4, whiteSpace: isMobile ? 'normal' : undefined }}>{formatOptionLabel(optPos)}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                          <Chip label={optPos.optionInfo?.type === 'C' ? 'Call' : 'Put'} size="small" variant="contained" color="default" />
                                        </TableCell>
                                        {!isMobile && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>{optPos.quantity}</TableCell>)}
                                        {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCurrency(Math.abs(optPos.quantity) > 0 ? ((optPos.cost_basis || 0) / Math.abs(optPos.quantity)) : 0)}</TableCell>)}
                                        {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>-</TableCell>)}
                                        <TableCell align="right" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>{formatCurrency(optPos.marketValue)}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                          <Chip label={formatCurrency(optUnrealizedPL)} color={getPLColor(optUnrealizedPL)} size="small" sx={isMobile ? { height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } } : undefined} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}><Chip label={formatCurrency(optPos.change || 0)} size="small" sx={isMobile ? { height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } } : undefined} /></TableCell>
                                        {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatOpenDate(optPos.openDate, optPos.openTs) || '-'}</TableCell>)}
                                        {showDetailCols && (<TableCell align="right" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                          {(() => { const ids = legOrderIds(optPos); return ids.length ? ids.join(', ') : '-'; })()}
                                        </TableCell>)}
                                        <TableCell align="center" sx={{ fontSize: '0.8rem' }}>-</TableCell>
                                      </TableRow>
                                    );
                                  });
                                }
                              }
                            });
                          }
                        });
                        return allRows;
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>

              ) : (
                <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    No open positions found for this account.
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
      </Box>

      {/* Position Details Dialog */}
      <Dialog
        open={positionDetailsOpen}
        onClose={() => { setPositionDetailsOpen(false); setSelectedPosition(null); setEditingPositionTags(false); }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            component="span"
            sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            title="Open Stock Analysis"
            onClick={() => {
              const sym = selectedPosition?.underlying || selectedPosition?.symbol;
              if (sym) {
                setPositionDetailsOpen(false);
                navigate(`/stock-analysis/${encodeURIComponent(sym)}`);
              }
            }}
          >
            {selectedPosition?.underlying || selectedPosition?.symbol}
          </Box>
          {' '}Position Details
        </DialogTitle>
        <DialogContent>
          {selectedPosition && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Basic Info */}
              <Box sx={{ p: 2, bgcolor: isDark ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>Position Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Symbol</Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      title="Open Stock Analysis"
                      onClick={() => {
                        const sym = selectedPosition.underlying || selectedPosition.symbol;
                        if (sym) {
                          setPositionDetailsOpen(false);
                          navigate(`/stock-analysis/${encodeURIComponent(sym)}`);
                        }
                      }}
                    >
                      {selectedPosition.underlying || selectedPosition.symbol}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Type</Typography>
                    <Typography variant="body1">{selectedPosition.spreadType || (selectedPosition.optionInfo?.isOption ? (selectedPosition.quantity > 0 ? 'Long ' : 'Short ') + (selectedPosition.optionInfo.type === 'C' ? 'Call' : 'Put') : (selectedPosition.quantity > 0 ? 'Long Stock' : 'Short Stock'))}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Quantity</Typography>
                    <Typography variant="body1">{selectedPosition.quantity}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Cost Basis</Typography>
                    <Typography variant="body1">${(selectedPosition.isSpread ? (selectedPosition.metrics?.netDebit || 0) : (selectedPosition.cost_basis || 0)).toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Net Liquid</Typography>
                    <Typography variant="body1">${(selectedPosition.isSpread ? (selectedPosition.metrics?.totalMarketValue || 0) : (selectedPosition.market_value || selectedPosition.netLiquidationValue || 0)).toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">P/L</Typography>
                    <Typography variant="body1" sx={{ color: (selectedPosition.isSpread ? (selectedPosition.metrics?.unrealizedPL || 0) : (selectedPosition.dayPL || selectedPosition.unrealizedPL || 0)) >= 0 ? 'success.main' : 'error.main' }}>
                      ${((selectedPosition.isSpread ? (selectedPosition.metrics?.unrealizedPL || 0) : (selectedPosition.dayPL || selectedPosition.unrealizedPL || 0))).toFixed(2)}
                    </Typography>
                  </Grid>
                  {selectedPosition.trade_date && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Open Date</Typography>
                      <Typography variant="body1">{new Date(selectedPosition.trade_date).toLocaleDateString()}</Typography>
                    </Grid>
                  )}
                  {selectedPosition.underlyingPrice && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Underlying Price</Typography>
                      <Typography variant="body1">${selectedPosition.underlyingPrice.toFixed(2)}</Typography>
                    </Grid>
                  )}
                  {selectedPosition.daysToEarnings !== null && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Days to Earnings</Typography>
                      <Typography variant="body1">{selectedPosition.daysToEarnings === 0 ? 'Today' : selectedPosition.daysToEarnings}</Typography>
                    </Grid>
                  )}
                  {selectedPosition.trade_date && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Days in Trade</Typography>
                      <Typography variant="body1">{(() => {
                        const tradeDate = new Date(selectedPosition.trade_date);
                        const today = new Date();
                        today.setHours(0,0,0,0);
                        tradeDate.setHours(0,0,0,0);
                        const diffDays = Math.ceil((today - tradeDate) / (1000 * 60 * 60 * 24));
                        return diffDays;
                      })()}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Tags</Typography>
                    {!editingPositionTags ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {getVisibleTags(selectedPosition).length > 0
                          ? getVisibleTags(selectedPosition).map(t => <Chip key={t} label={t} size="small" />)
                          : <Typography variant="body2" color="text.disabled">—</Typography>}
                        {getTagTargets(selectedPosition).length > 0 && (
                          <Button
                            size="small"
                            sx={{ ml: 0.5, minWidth: 0, textTransform: 'none' }}
                            onClick={() => { setPositionTagDraft(getAllTags(selectedPosition)); setEditingPositionTags(true); }}
                          >
                            Edit
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Autocomplete
                          multiple
                          freeSolo
                          autoSelect
                          size="small"
                          options={allTags}
                          value={positionTagDraft}
                          onChange={(e, newValue) => setPositionTagDraft([...new Set(newValue.map(v => String(v).trim()).filter(Boolean))])}
                          renderTags={(value, getTagProps) => value.map((t, i) => {
                            const { key, ...tagProps } = getTagProps({ index: i });
                            return <Chip key={key} {...tagProps} label={t} size="small" />;
                          })}
                          renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select or type a new tag" />}
                          sx={{ minWidth: 240, flex: 1 }}
                        />
                        <Button size="small" variant="contained" onClick={savePositionTags} disabled={savingPositionTags} sx={{ textTransform: 'none' }}>
                          {savingPositionTags ? 'Saving…' : 'Save'}
                        </Button>
                        <Button size="small" onClick={() => setEditingPositionTags(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>

              {/* Automated Exit Targets (paper trading positions opened by a webhook/API token) */}
              {selectedPosition.isPaperTrade && selectedPosition.isAutomated && (selectedPosition.takeProfitPct != null || selectedPosition.stopLossPct != null || (selectedPosition.takeProfitTiers && selectedPosition.takeProfitTiers.length > 0) || (selectedPosition.stopLossTiers && selectedPosition.stopLossTiers.length > 0)) && (
                <Box sx={{ p: 2, bgcolor: isDark ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>Automated Exit Targets</Typography>
                  <Grid container spacing={2}>
                    {selectedPosition.takeProfitPct != null && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Take Profit</Typography>
                        <Typography variant="body1" color="success.main">+{selectedPosition.takeProfitPct}%</Typography>
                      </Grid>
                    )}
                    {selectedPosition.stopLossPct != null && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Stop Loss</Typography>
                        <Typography variant="body1" color="error.main">-{Math.abs(selectedPosition.stopLossPct)}%</Typography>
                      </Grid>
                    )}
                    {selectedPosition.exitsTaken > 0 && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Exits Taken</Typography>
                        <Typography variant="body1">{selectedPosition.exitsTaken}</Typography>
                      </Grid>
                    )}
                  </Grid>
                  {((selectedPosition.takeProfitTiers && selectedPosition.takeProfitTiers.length > 0) || (selectedPosition.stopLossTiers && selectedPosition.stopLossTiers.length > 0)) && (
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      {selectedPosition.takeProfitTiers && selectedPosition.takeProfitTiers.length > 0 && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Take Profit Tiers</Typography>
                          {selectedPosition.takeProfitTiers.map((tier, i) => {
                            const avgPrice = parseFloat(selectedPosition.averagePrice || 0);
                            const isShort = selectedPosition.quantity < 0;
                            const targetPrice = avgPrice > 0
                              ? (isShort ? avgPrice * (1 - tier.pct / 100) : avgPrice * (1 + tier.pct / 100))
                              : null;
                            return (
                              <Typography key={i} variant="body2">
                                Tier {i + 1}: +{tier.pct}%{targetPrice != null ? ` ($${targetPrice.toFixed(2)})` : ''} → close {tier.qtyPct}%
                              </Typography>
                            );
                          })}
                        </Grid>
                      )}
                      {selectedPosition.stopLossTiers && selectedPosition.stopLossTiers.length > 0 && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Stop Loss Tiers</Typography>
                          {selectedPosition.stopLossTiers.map((tier, i) => {
                            const avgPrice = parseFloat(selectedPosition.averagePrice || 0);
                            const isShort = selectedPosition.quantity < 0;
                            const targetPrice = avgPrice > 0
                              ? (isShort ? avgPrice * (1 - tier.pct / 100) : avgPrice * (1 + tier.pct / 100))
                              : null;
                            return (
                              <Typography key={i} variant="body2">
                                Tier {i + 1}: {tier.pct}%{targetPrice != null ? ` ($${targetPrice.toFixed(2)})` : ''}{tier.qtyPct ? ` → close ${tier.qtyPct}%` : ''}
                              </Typography>
                            );
                          })}
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Box>
              )}

              {/* Option Details */}
              {selectedPosition.optionInfo?.isOption && (
                <Box sx={{ p: 2, bgcolor: isDark ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>Option Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Strike</Typography>
                      <Typography variant="body1">${selectedPosition.optionInfo.strike}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Expiration</Typography>
                      <Typography variant="body1">{selectedPosition.optionInfo.expiration}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Days to Expiry</Typography>
                      <Typography variant="body1">{(() => {
                        if (!selectedPosition.optionInfo.expiration) return 'N/A';
                        // Parse YYMMDD format (e.g., "260807")
                        const expStr = selectedPosition.optionInfo.expiration;
                        if (expStr.length !== 6) return 'N/A';
                        const year = 2000 + parseInt(expStr.substring(0, 2));
                        const month = parseInt(expStr.substring(2, 4)) - 1;
                        const day = parseInt(expStr.substring(4, 6));
                        const exp = new Date(year, month, day);
                        const today = new Date();
                        today.setHours(0,0,0,0);
                        exp.setHours(0,0,0,0);
                        const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
                        return diffDays;
                      })()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Mid Price</Typography>
                      <Typography variant="body1">${((selectedPosition.mid || 0)).toFixed(2)}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Greeks and IV */}
              {(selectedPosition.optionInfo?.isOption || selectedPosition.isSpread) && (
                <Box sx={{ p: 2, bgcolor: isDark ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>Greeks & Implied Volatility</Typography>
                  {loadingGreeks ? (
                    <Typography variant="body2" color="text.secondary">Loading Greeks data...</Typography>
                  ) : selectedPosition.isSpread && selectedPosition.legs && selectedPosition.legs.length > 0 ? (
                    // Multi-leg spread: show Greeks for each leg in tabular format
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Leg</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">IV</TableCell>
                            <TableCell align="right">Δ</TableCell>
                            <TableCell align="right">Γ</TableCell>
                            <TableCell align="right">Θ</TableCell>
                            <TableCell align="right">V</TableCell>
                            <TableCell align="right">Mark</TableCell>
                            <TableCell align="right">DTE</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedPosition.legs.map((leg, legIndex) => {
                            const legGreeksData = legGreeks[legIndex];
                            const legLabel = `${leg.optionInfo?.strike || ''} ${leg.optionInfo?.type === 'C' ? 'C' : 'P'}`;
                            return (
                              <TableRow key={legIndex}>
                                <TableCell>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    {legLabel}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="caption" sx={{ color: leg.quantity > 0 ? 'success.main' : 'error.main' }}>
                                    {leg.quantity}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{legGreeksData ? (legGreeksData.iv || 0).toFixed(2) + '%' : 'N/A'}</TableCell>
                                <TableCell align="right">{legGreeksData ? (legGreeksData.delta || 0).toFixed(4) : 'N/A'}</TableCell>
                                <TableCell align="right">{legGreeksData ? (legGreeksData.gamma || 0).toFixed(4) : 'N/A'}</TableCell>
                                <TableCell align="right">{legGreeksData ? (legGreeksData.theta || 0).toFixed(4) : 'N/A'}</TableCell>
                                <TableCell align="right">{legGreeksData ? (legGreeksData.vega || 0).toFixed(4) : 'N/A'}</TableCell>
                                <TableCell align="right">{legGreeksData ? '$' + (legGreeksData.mark || 0).toFixed(2) : 'N/A'}</TableCell>
                                <TableCell align="right">
                                  {(() => {
                                    if (!leg.optionInfo?.expiration) return 'N/A';
                                    const expStr = leg.optionInfo.expiration;
                                    if (expStr.length !== 6) return 'N/A';
                                    const year = 2000 + parseInt(expStr.substring(0, 2));
                                    const month = parseInt(expStr.substring(2, 4)) - 1;
                                    const day = parseInt(expStr.substring(4, 6));
                                    const exp = new Date(year, month, day);
                                    const today = new Date();
                                    today.setHours(0,0,0,0);
                                    exp.setHours(0,0,0,0);
                                    const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
                                    return diffDays;
                                  })()}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : positionGreeks ? (
                    // Single option: show Greeks
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Implied Volatility</Typography>
                        <Typography variant="body1">{(positionGreeks.iv || 0).toFixed(2)}%</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Delta</Typography>
                        <Typography variant="body1">{(positionGreeks.delta || 0).toFixed(4)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Gamma</Typography>
                        <Typography variant="body1">{(positionGreeks.gamma || 0).toFixed(4)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Theta</Typography>
                        <Typography variant="body1">{(positionGreeks.theta || 0).toFixed(4)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Vega</Typography>
                        <Typography variant="body1">{(positionGreeks.vega || 0).toFixed(4)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Rho</Typography>
                        <Typography variant="body1">{(positionGreeks.rho || 0).toFixed(4)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Mark</Typography>
                        <Typography variant="body1">${(positionGreeks.mark || 0).toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Break Even</Typography>
                        <Typography variant="body1">${(positionGreeks.breakEven || 0).toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">In The Money</Typography>
                        <Typography variant="body1">{positionGreeks.inTheMoney ? 'Yes' : 'No'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Probability of Profit</Typography>
                        <Typography variant="body1">{(() => {
                          // Simple POP calculation based on delta
                          // For long options: POP = delta (for calls) or |delta| (for puts)
                          // For short options: POP = 1 - delta (for calls) or 1 - |delta| (for puts)
                          const delta = positionGreeks.delta || 0;
                          const isLong = selectedPosition.quantity > 0;
                          const isCall = selectedPosition.optionInfo.type === 'C';
                          
                          let pop;
                          if (isLong) {
                            pop = isCall ? delta : Math.abs(delta);
                          } else {
                            pop = isCall ? 1 - delta : 1 - Math.abs(delta);
                          }
                          
                          return Math.max(0, Math.min(100, pop * 100)).toFixed(1) + '%';
                        })()}</Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Greeks data not available</Typography>
                  )}
                </Box>
              )}

              {/* Payoff Diagram + underlying stock chart for spreads and single options */}
              {(selectedPosition.optionInfo?.isOption || selectedPosition.isSpread) && (
                <Box sx={{ p: 2, bgcolor: isDark ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="stretch">
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" gutterBottom>Payoff Diagram</Typography>
                      <PayoffDiagram
                        legs={(selectedPosition.legs || []).some(l => l?.optionInfo?.strike)
                          ? selectedPosition.legs
                          : [selectedPosition]}
                        spreadType={selectedPosition.spreadType || (selectedPosition.quantity > 0 ? 'Long' : 'Short') + ' ' + (selectedPosition.optionInfo?.type === 'C' ? 'Call' : 'Put')}
                        underlyingPrice={selectedPosition.underlyingPrice}
                        underlyingSymbol={selectedPosition.underlying || selectedPosition.optionInfo?.underlying}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" gutterBottom>Underlying Price</Typography>
                      <UnderlyingPriceChart
                        symbol={selectedPosition.underlying || selectedPosition.optionInfo?.underlying || selectedPosition.symbol}
                        currentPrice={selectedPosition.underlyingPrice}
                        openDate={selectedPosition.openDate || selectedPosition.created_at}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Daily P/L Chart */}
              <Box sx={{ p: 2, bgcolor: isDark ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>Daily P/L History</Typography>
                <PositionPLChart
                  accountId={selectedPosition.accountId}
                  symbol={selectedPosition.isSpread
                    ? (selectedPosition.legs || []).map(l => l.symbol).filter(Boolean).join(',')
                    : (selectedPosition.symbol || selectedPosition.optionInfo?.occSymbol)}
                  underlying={selectedPosition.isSpread
                    ? selectedPosition.underlying
                    : selectedPosition.optionInfo?.underlying}
                  livePL={selectedPosition.isSpread
                    ? (selectedPosition.metrics?.unrealizedPL ?? null)
                    : positionUnrealizedPL(selectedPosition)}
                  liveMarketValue={selectedPosition.isSpread
                    ? (selectedPosition.metrics?.totalMarketValue ?? null)
                    : (selectedPosition.marketValue ?? null)}
                  liveCurrentPrice={selectedPosition.isSpread ? null : (selectedPosition.currentPrice ?? null)}
                  liveUnderlyingPrice={selectedPosition.underlyingPrice ?? null}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {(selectedPosition?.optionInfo?.isOption || selectedPosition?.isSpread) && !isNonTradingAccount(selectedPosition?.accountId || selectedPosition?.account_id) && (
            <Box sx={{ display: 'flex', gap: 1, mr: 'auto' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  handleOpenRollCloseDialog(selectedPosition, 'roll');
                  setPositionDetailsOpen(false);
                }}
              >
                Roll
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  handleOpenRollCloseDialog(selectedPosition, 'close');
                  setPositionDetailsOpen(false);
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  handleOpenRollCloseDialog(selectedPosition, 'add');
                  setPositionDetailsOpen(false);
                }}
              >
                Add
              </Button>
            </Box>
          )}
          {selectedPosition && selectedPosition.isManualGroup && selectedPosition.groupId && (
            <Box sx={{ display: 'flex', gap: 1, mr: 'auto' }}>
              <Button
                variant="contained"
                color="warning"
                onClick={async () => {
                  try {
                    await apiRequest(`/api/trade-groups/${selectedPosition.groupId}`, { method: 'DELETE' });
                    setTradeGroups(prev => prev.filter(g => g.id !== selectedPosition.groupId));
                    setPositionDetailsOpen(false);
                    setSelectedPosition(null);
                  } catch (err) {
                    console.error('Failed to ungroup position:', err);
                    alert('Failed to ungroup position');
                  }
                }}
              >
                Ungroup
              </Button>
            </Box>
          )}
          {selectedPosition && !selectedPosition.optionInfo?.isOption && !selectedPosition.isSpread && !selectedPosition.isManualGroup && !isNonTradingAccount(selectedPosition.accountId || selectedPosition.account_id) && (
            <Box sx={{ display: 'flex', gap: 1, mr: 'auto' }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  handleOpenRollCloseDialog(selectedPosition, 'add');
                  setPositionDetailsOpen(false);
                }}
              >
                Add
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  handleOpenRollCloseDialog(selectedPosition, 'close');
                  setPositionDetailsOpen(false);
                }}
              >
                Close
              </Button>
              {selectedPosition.quantity > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleOpenRollCloseDialog(selectedPosition, 'sell_cc');
                    setPositionDetailsOpen(false);
                  }}
                >
                  Sell Covered Call
                </Button>
              )}
            </Box>
          )}
          <Button onClick={() => { setPositionDetailsOpen(false); setSelectedPosition(null); }} variant="contained" color="inherit">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Price Alert Dialog */}
      <Dialog
        open={priceAlertDialog.open}
        onClose={handleClosePriceAlertDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Set Price Alert</DialogTitle>
        <DialogContent>
          {priceAlertDialog.position && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {priceAlertDialog.position.isSpread
                ? `${priceAlertDialog.position.underlying} ${priceAlertDialog.position.spreadType || ''}`
                : `${priceAlertDialog.position.optionInfo?.underlying || ''} ${priceAlertDialog.position.optionInfo?.type === 'C' ? 'Call' : 'Put'} ${priceAlertDialog.position.optionInfo?.strike || ''}`}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mb: 1, mt: 1 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={priceAlertDialog.alertType}
              onChange={(e, val) => {
                if (val) {
                  const price = val === 'option' ? priceAlertDialog.optionPrice : priceAlertDialog.underlyingPrice;
                  setPriceAlertDialog(prev => ({
                    ...prev,
                    alertType: val,
                    targetPrice: price ? String(Math.round(price * 100) / 100) : '',
                  }));
                }
              }}
              sx={{ flex: 1 }}
            >
              <ToggleButton value="option" sx={{ fontSize: '0.75rem' }}>
                Option
                {priceAlertDialog.optionPrice != null && (
                  <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                    (${priceAlertDialog.optionPrice.toFixed(2)})
                  </Typography>
                )}
              </ToggleButton>
              <ToggleButton value="underlying" sx={{ fontSize: '0.75rem' }} disabled={priceAlertDialog.underlyingPrice == null}>
                Underlying
                {priceAlertDialog.underlyingPrice != null && (
                  <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                    (${priceAlertDialog.underlyingPrice.toFixed(2)})
                  </Typography>
                )}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Condition</InputLabel>
              <Select
                value={priceAlertDialog.condition}
                label="Condition"
                onChange={(e) => setPriceAlertDialog(prev => ({ ...prev, condition: e.target.value }))}
              >
                <MenuItem value="above">Above</MenuItem>
                <MenuItem value="below">Below</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Target Price"
              value={priceAlertDialog.targetPrice}
              onChange={(e) => setPriceAlertDialog(prev => ({ ...prev, targetPrice: e.target.value }))}
              type="number"
              inputProps={{ step: 0.01, min: 0 }}
              sx={{ flex: 1 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            You'll receive a Telegram notification when the {priceAlertDialog.alertType === 'underlying' ? 'underlying' : 'option'} price crosses your target. A link to the closing trade dialog will be included.
          </Typography>
          {priceAlertDialog.error && (
            <Alert severity="error" sx={{ mt: 1 }}>{priceAlertDialog.error}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePriceAlertDialog} variant="contained" color="inherit">Cancel</Button>
          <Button
            onClick={handleCreatePriceAlert}
            variant="contained"
            disabled={priceAlertDialog.loading}
          >
            {priceAlertDialog.loading ? <CircularProgress size={20} /> : 'Set Alert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Closed Pending Positions Section (recently closed, awaiting broker settlement) */}
      <Box sx={{ mb: isMobile ? 1 : 3, mx: isMobile ? '-8px' : 0, width: isMobile ? 'calc(100% + 16px)' : 'auto' }}>
      {!isMobile ? (
        <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6" component="h2">
                      Closed Pending Positions
                    </Typography>
                    <Chip
                      label={`${pendingStrategyGroups.length} pending`}
                      size="small"
                      color="warning"
                      sx={{ ml: 1 }}
                    />
                    {(() => {
                      const totalPL = filteredPendingPositions.reduce((s, p) => s + (Number(p.realizedPL) || 0), 0);
                      return (
                        <Chip
                          label={`Est. P/L ${totalPL.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
                          size="small"
                          color={totalPL >= 0 ? 'success' : 'error'}
                          variant="contained"
                          sx={{ ml: 1 }}
                        />
                      );
                    })()}
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchPendingPositions(true);
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1, px: 2, pt: 1 }}>
                  Recently closed via broker orders, awaiting settlement. Estimated realized P/L is computed against open positions and will move to Closed once the settled trade arrives (typically 2-4 days).
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Strategy</TableCell>
                        <TableCell>Legs</TableCell>
                        <TableCell>Exp</TableCell>
                        <TableCell align="right">Proceeds</TableCell>
                        <TableCell align="right">Cost Basis</TableCell>
                        <TableCell align="right">Est. P/L</TableCell>
                        <TableCell>Filled</TableCell>
                        <TableCell>Account</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingStrategyGroups.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            No pending positions match the current filters.
                          </TableCell>
                        </TableRow>
                      ) : pendingStrategyGroups.map((g) => {
                        const pl = Number(g.totalPL) || 0;
                        const fmt = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
                        const abbr = abbreviateStrategy(g.strategyType);
                        const isEquity = g.strategyType === 'Equity';
                        let formattedExp = '-';
                        if (g.expiration && g.expiration !== 'Unknown' && /^\d{6}$/.test(g.expiration)) {
                          formattedExp = `${g.expiration.substring(2, 4)}/${g.expiration.substring(4, 6)}/${g.expiration.substring(0, 2)}`;
                        } else if (g.expiration && g.expiration !== 'Unknown') {
                          formattedExp = g.expiration;
                        }
                        return (
                          <TableRow key={g.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight="bold">{g.underlying}</Typography>
                                <Chip
                                  label={abbr}
                                  size="small"
                                  color={isEquity ? 'info' : (pl >= 0 ? 'success' : 'error')}
                                  variant="contained"
                                  title={g.strategyType}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {g.legs.map((l) => (
                                  <Chip
                                    key={`${l.id}_leg`}
                                    label={`${pendingLegLabel(l)} ×${l.absQuantity}`}
                                    size="small"
                                    variant="contained"
                                  />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>{formattedExp}</TableCell>
                            <TableCell align="right">{fmt(g.totalProceeds)}</TableCell>
                            <TableCell align="right">{fmt(g.totalCostBasis)}</TableCell>
                            <TableCell align="right" sx={{ color: pl >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                              {fmt(pl)}
                            </TableCell>
                            <TableCell>{g.filledTime ? new Date(g.filledTime).toLocaleString() : '-'}</TableCell>
                            <TableCell>
                              <AccountNameWithLogo accountName={g.accountName} accountId={g.accountId} accounts={accounts} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
      ) : (
        <Accordion defaultExpanded sx={{ borderRadius: 0, boxShadow: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" component="h2" fontWeight="bold">
                  Pending
                </Typography>
                <Chip label={`${pendingStrategyGroups.length}`} size="small" color="warning" sx={{ ml: 1 }} />
                {(() => {
                  const totalPL = filteredPendingPositions.reduce((s, p) => s + (Number(p.realizedPL) || 0), 0);
                  return (
                    <Chip
                      label={totalPL.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                      size="small"
                      color={totalPL >= 0 ? 'success' : 'error'}
                      variant="contained"
                      sx={{ ml: 1 }}
                    />
                  );
                })()}
              </Box>
              <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); fetchPendingPositions(true); }}>Refresh</Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <TableContainer component="div" sx={{ mt: 1, width: '100%', overflowX: 'hidden' }}>
              <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Strategy</strong></TableCell>
                    <TableCell align="right"><strong>Est. P/L</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingStrategyGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No pending positions.
                      </TableCell>
                    </TableRow>
                  ) : pendingStrategyGroups.map((g) => {
                    const pl = Number(g.totalPL) || 0;
                    const fmt = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
                    const abbr = abbreviateStrategy(g.strategyType);
                    return (
                      <TableRow key={g.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="bold" noWrap>{g.underlying}</Typography>
                            <Chip label={abbr} size="small" color={pl >= 0 ? 'success' : 'error'} variant="contained" sx={{ height: 18, fontSize: '0.6rem' }} />
                            <AccountNameWithLogo accountName={g.accountName} accountId={g.accountId} accounts={accounts} />
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ color: pl >= 0 ? 'success.main' : 'error.main', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {fmt(pl)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}
      </Box>

      {/* Closed Positions Performance Summary */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{
          mb: isMobile ? 1 : 3,
          mx: isMobile ? '-8px' : 0,
          width: isMobile ? 'calc(100% + 16px)' : 'auto'
        }}>
          <Accordion
            defaultExpanded
            sx={isMobile ? {
              borderRadius: 0,
              boxShadow: 'none',
              '&:before': { display: 'none' }
            } : undefined}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} component="h2">
                    {groupByMode === 'calendar' && calendarViewMode === 'calendar'
                      ? 'Closed Positions (Calendar)'
                      : `Closed Positions (Rows: ${groupByMode === 'week' ? 'Weeks' : groupByMode === 'calendar' ? 'Months' : 'Symbols'})`}
                    {filteredSymbolData.length > 0 && groupByMode === 'symbol' && (
                      <Chip
                        label={`${filteredSymbolData.length} symbols`}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    )}
                  </Typography>
                  <ToggleButtonGroup
                    value={groupByMode}
                    exclusive
                    size="small"
                    onChange={(e, val) => {
                      if (val) {
                        e.stopPropagation();
                        setGroupByMode(val);
                        // Period views read chronologically — default to most recent first
                        if (val === 'week' || val === 'calendar') setClosedPositionsSort({ field: 'symbol', direction: 'desc' });
                      }
                    }}
                  >
                    <ToggleButton value="symbol">Symbol</ToggleButton>
                    <ToggleButton value="week">Week</ToggleButton>
                    <ToggleButton value="calendar">Month</ToggleButton>
                  </ToggleButtonGroup>
                  {groupByMode === 'calendar' && (
                    <ToggleButtonGroup
                      value={calendarViewMode}
                      exclusive
                      size="small"
                      onChange={(e, val) => {
                        if (val) {
                          e.stopPropagation();
                          setCalendarViewMode(val);
                        }
                      }}
                    >
                      <ToggleButton value="calendar">Calendar</ToggleButton>
                      <ToggleButton value="detail">Detail</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefreshClosedPositions();
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={exportClosedPositionsToCSV}
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={generatePdfReport}
                  >
                    PDF Report
                  </Button>
                </Box>
              </Box>
            </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto', display: (groupByMode === 'calendar' && calendarViewMode === 'calendar') ? 'none' : 'block' }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.75, whiteSpace: 'nowrap' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={closedPositionsSort.field === 'symbol'}
                            direction={closedPositionsSort.field === 'symbol' ? closedPositionsSort.direction : 'asc'}
                            onClick={() => handleClosedPositionsSort('symbol')}
                          >
                            <strong>{groupByMode === 'week' ? 'Week' : groupByMode === 'calendar' ? 'Month' : 'Symbol'}</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={closedPositionsSort.field === 'totalPL'}
                            direction={closedPositionsSort.field === 'totalPL' ? closedPositionsSort.direction : 'asc'}
                            onClick={() => handleClosedPositionsSort('totalPL')}
                          >
                            <strong>Total P/L</strong>
                          </TableSortLabel>
                        </TableCell>
                        {groupByMode === 'symbol' && (
                          <>
                            <TableCell align="right">
                              <TableSortLabel
                                active={closedPositionsSort.field === 'openPLOptions'}
                                direction={closedPositionsSort.field === 'openPLOptions' ? closedPositionsSort.direction : 'asc'}
                                onClick={() => handleClosedPositionsSort('openPLOptions')}
                              >
                                <strong>Open P/L (Opt)</strong>
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={closedPositionsSort.field === 'openPLEquity'}
                                direction={closedPositionsSort.field === 'openPLEquity' ? closedPositionsSort.direction : 'asc'}
                                onClick={() => handleClosedPositionsSort('openPLEquity')}
                              >
                                <strong>Open P/L (Equity)</strong>
                              </TableSortLabel>
                            </TableCell>
                          </>
                        )}
                        <TableCell align="right">
                            <TableSortLabel
                              active={closedPositionsSort.field === 'closedPL'}
                              direction={closedPositionsSort.field === 'closedPL' ? closedPositionsSort.direction : 'asc'}
                              onClick={() => handleClosedPositionsSort('closedPL')}
                            >
                              <strong>Closed P/L</strong>
                            </TableSortLabel>
                          </TableCell>
                        {isPeriodMode && (
                          <TableCell align="right">
                            <TableSortLabel
                              active={closedPositionsSort.field === 'cumulativeTotal'}
                              direction={closedPositionsSort.field === 'cumulativeTotal' ? closedPositionsSort.direction : 'asc'}
                              onClick={() => handleClosedPositionsSort('cumulativeTotal')}
                            >
                              <strong>Cum. Closed P/L</strong>
                            </TableSortLabel>
                          </TableCell>
                        )}
                          <TableCell align="right">
                            <TableSortLabel
                              active={closedPositionsSort.field === 'totalDividendOnly'}
                              direction={closedPositionsSort.field === 'totalDividendOnly' ? closedPositionsSort.direction : 'asc'}
                              onClick={() => handleClosedPositionsSort('totalDividendOnly')}
                            >
                              <strong>Total Dividend</strong>
                            </TableSortLabel>
                          </TableCell>
                        {isPeriodMode && (
                          <TableCell align="right">
                            <TableSortLabel
                              active={closedPositionsSort.field === 'cumulativeDividend'}
                              direction={closedPositionsSort.field === 'cumulativeDividend' ? closedPositionsSort.direction : 'asc'}
                              onClick={() => handleClosedPositionsSort('cumulativeDividend')}
                            >
                              <strong>Cum. Dividend</strong>
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {groupByMode === 'symbol' && closedPeriodColumns.map((col) => (
                          <TableCell key={col} align="right">
                            <TableSortLabel
                              active={closedPositionsSort.field === col}
                              direction={closedPositionsSort.field === col ? closedPositionsSort.direction : 'asc'}
                              onClick={() => handleClosedPositionsSort(col)}
                            >
                              <strong>{formatPeriodHeader(col)}</strong>
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTotalsRow && groupByMode === 'symbol' && (
                        <TableRow key="closed-totals-row" sx={{ backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : '#f5f5f5' }}>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            Period Total
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            <Typography
                              variant="caption"
                              color={filteredTotalsRow.totalPL >= 0 ? 'success.main' : 'error.main'}
                              sx={{ fontWeight: 600 }}
                            >
                              {formatCurrency(filteredTotalsRow.totalPL)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            {(() => { const v = filteredSymbolData.reduce((s, r) => s + getOpenPL(r.symbol, 'options'), 0);
                              return <Typography variant="caption" sx={{ fontWeight: 600 }} color={v >= 0 ? 'success.main' : 'error.main'}>{formatCurrency(v)}</Typography>; })()}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            {(() => { const v = filteredSymbolData.reduce((s, r) => s + getOpenPL(r.symbol, 'equity'), 0);
                              return <Typography variant="caption" sx={{ fontWeight: 600 }} color={v >= 0 ? 'success.main' : 'error.main'}>{formatCurrency(v)}</Typography>; })()}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            <Typography
                              variant="caption"
                              color={((filteredTotalsRow.totalPL || 0) - (filteredTotalsRow.totalDividendOnly || 0)) >= 0 ? 'success.main' : 'error.main'}
                              sx={{ fontWeight: 600 }}
                            >
                              {formatCurrency((filteredTotalsRow.totalPL || 0) - (filteredTotalsRow.totalDividendOnly || 0))}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            <Typography
                              variant="caption"
                              color={(filteredTotalsRow.totalDividendOnly || 0) >= 0 ? 'success.main' : 'error.main'}
                              sx={{ fontWeight: 600 }}
                            >
                              {formatCurrency(filteredTotalsRow.totalDividendOnly || 0)}
                            </Typography>
                          </TableCell>
                          {groupByMode === 'symbol' && closedPeriodColumns.map((col) => (
                            <TableCell key={`total-${col}`} align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                              {renderPeriodPLCell(filteredTotalsRow, col)}
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                      {isPeriodMode ? sortedPeriodData.map((weekRow) => {
                        const closedPL = (weekRow.totalPL || 0) - (weekRow.totalDividendOnly || 0);
                        const isExpanded = expandedWeeks.has(weekRow.periodKey);

                        return (
                          <React.Fragment key={weekRow.periodKey}>
                            <TableRow
                              hover
                              sx={{ cursor: 'pointer', backgroundColor: isExpanded ? 'action.hover' : 'inherit' }}
                              onClick={() => toggleWeek(weekRow.periodKey)}
                            >
                              <TableCell component="th" scope="row">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  {weekRow.periodLabel}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={formatCurrency(weekRow.totalPL)}
                                  color={getPLColor(weekRow.totalPL)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(closedPL)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(weekRow.cumulativeTotal || 0)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(weekRow.totalDividendOnly || 0)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(weekRow.cumulativeDividend || 0)}
                              </TableCell>
                            </TableRow>

                            {/* Expanded trades row — flat strategy table with sortable columns */}
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ py: 1, px: 2 }}>
                                  <Box sx={{ ml: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Trades for {groupByMode === 'week' ? 'Week' : 'Month'} {weekRow.periodLabel} ({weekRow.trades.length})
                                    </Typography>
                                    {weekRow.trades.length > 0 ? (() => {
                                      const allStrategies = groupTradesByStrategy(weekRow.trades);
                                      const strategies = strategyFilter && strategyFilter !== 'all'
                                        ? allStrategies.filter(s => strategyMatchesFilter(s.strategyType))
                                        : allStrategies;
                                      // Latest execution timestamp within a strategy — secondary sort key
                                      // so same-date strategies order by trade time.
                                      const strategyCloseTime = (s) => Math.max(0, ...(s.trades || []).map(t => {
                                        const ts = new Date(t.tradeTimestamp || t.trade_timestamp || t.tradeDate || 0).getTime();
                                        return isNaN(ts) ? 0 : ts;
                                      }));
                                      const sorted = [...strategies].sort((a, b) => {
                                        const { field, direction } = strategySort;
                                        const dir = direction === 'asc' ? 1 : -1;
                                        let cmp = 0;
                                        switch (field) {
                                          case 'closeDate': cmp = (a.closeDate || '').localeCompare(b.closeDate || ''); break;
                                          case 'openDate': cmp = (a.openDate || '').localeCompare(b.openDate || ''); break;
                                          case 'symbol': cmp = (a.displaySymbol || '').localeCompare(b.displaySymbol || ''); break;
                                          case 'strategy': cmp = (a.strategyType || '').localeCompare(b.strategyType || ''); break;
                                          case 'quantity': cmp = (a.quantity || 0) - (b.quantity || 0); break;
                                          case 'price': cmp = (a.price || 0) - (b.price || 0); break;
                                          case 'dividend': cmp = (a.totalDividend || 0) - (b.totalDividend || 0); break;
                                          case 'realizedPL': cmp = (a.totalPL || 0) - (b.totalPL || 0); break;
                                          default: cmp = 0;
                                        }
                                        if (cmp === 0) cmp = strategyCloseTime(a) - strategyCloseTime(b);
                                        return dir * cmp;
                                      });
                                      return (
                                        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.25, px: 0.5 } }}>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ width: 32, px: 0.25 }} />
                                              <TableCell sx={{ width: 28, px: 0.25 }} />
                                              <TableCell>
                                                <TableSortLabel active={strategySort.field === 'closeDate'} direction={strategySort.field === 'closeDate' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('closeDate')}>
                                                  <strong>Close Date</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell>
                                                <TableSortLabel active={strategySort.field === 'openDate'} direction={strategySort.field === 'openDate' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('openDate')}>
                                                  <strong>Open Date</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell>
                                                <TableSortLabel active={strategySort.field === 'symbol'} direction={strategySort.field === 'symbol' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('symbol')}>
                                                  <strong>Symbol</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell>
                                                <TableSortLabel active={strategySort.field === 'strategy'} direction={strategySort.field === 'strategy' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('strategy')}>
                                                  <strong>Strategy</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell align="right">
                                                <TableSortLabel active={strategySort.field === 'quantity'} direction={strategySort.field === 'quantity' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('quantity')}>
                                                  <strong>Qty</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell align="right">
                                                <TableSortLabel active={strategySort.field === 'price'} direction={strategySort.field === 'price' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('price')}>
                                                  <strong>Price</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell align="right">
                                                <TableSortLabel active={strategySort.field === 'dividend'} direction={strategySort.field === 'dividend' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('dividend')}>
                                                  <strong>Dividend</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell align="right">
                                                <TableSortLabel active={strategySort.field === 'realizedPL'} direction={strategySort.field === 'realizedPL' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('realizedPL')}>
                                                  <strong>Realized P/L</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell align="right"><strong>Cumulative P/L</strong></TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(() => {
                                              const cumulatives = new Array(sorted.length);
                                              let cum = 0;
                                              for (let i = sorted.length - 1; i >= 0; i--) {
                                                cum += sorted[i].totalPL || 0;
                                                cumulatives[i] = cum;
                                              }
                                              return sorted.map((strat, idx) => {
                                              const stratKey = `${weekRow.periodKey}|${strat.key}`;
                                              const isStratExpanded = expandedNested.has(stratKey);
                                              return (
                                                <React.Fragment key={stratKey}>
                                                  <TableRow
                                                    hover={strat.isMultiLeg}
                                                    sx={{ cursor: strat.isMultiLeg ? 'pointer' : 'default' }}
                                                    onClick={() => strat.isMultiLeg && toggleNested(stratKey)}
                                                  >
                                                    <TableCell sx={{ width: 32, px: 0.25 }}>
                                                      <AccountNameWithLogo accountName={(strat.trades[0] || {}).accountName || (strat.trades[0] || {}).account} accountId={(strat.trades[0] || {}).accountId || (strat.trades[0] || {}).account_id} accounts={accounts} size={28} />
                                                    </TableCell>
                                                    <TableCell sx={{ width: 28, px: 0.25 }}>
                                                      {strat.isMultiLeg && (
                                                        isStratExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />
                                                      )}
                                                    </TableCell>
                                                    <TableCell>{strat.closeDate || '-'}</TableCell>
                                                    <TableCell>{strat.openDate || '-'}</TableCell>
                                                    <TableCell>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Box sx={{ fontWeight: 'bold' }}>{strat.displaySymbol}{strat.isMultiLeg && (strat.legStrikes || []).length > 0 && (
                                                          <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1em', color: 'text.secondary', ml: 0.5 }}>
                                                            {strat.legStrikes.join('/')}
                                                          </Box>
                                                        )}</Box>
                                                        <Tooltip title="Retrade">
                                                          <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleOpenRetrade(strat); }}
                                                            sx={{ p: 0.5 }}
                                                          >
                                                            <Box component="img" src="/assets/retrade.png" alt="Retrade" sx={{ width: 20, height: 20 }} />
                                                          </IconButton>
                                                        </Tooltip>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                        <Chip label={strat.strategyType} size="small" color="primary" variant="contained" />
                                                        {getCloseReasons(strat).map(r => (
                                                          <Tooltip key={r.label} title={r.title}>
                                                            <Chip label={r.label} size="small" color={r.color} variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                                                          </Tooltip>
                                                        ))}
                                                        {getVisibleTags(strat).map(t => (
                                                          <Chip key={t} label={t} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                                                        ))}
                                                        <Tooltip title="Edit tags">
                                                          <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); openTagEditDialog(strat); }}>
                                                            <LabelOutlinedIcon sx={{ fontSize: 14 }} />
                                                          </IconButton>
                                                        </Tooltip>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="right">{strat.quantity}</TableCell>
                                                    <TableCell align="right">{strat.price !== 0 ? strat.price.toFixed(2) : '-'}</TableCell>
                                                    <TableCell align="right">{strat.totalDividend !== 0 ? formatCurrency(strat.totalDividend) : '-'}</TableCell>
                                                    <TableCell align="right">
                                                      {(() => {
                                                        const editableTrades = (strat.trades || []).filter(t => t && t.id);
                                                        const single = !strat.isMultiLeg && editableTrades.length === 1 ? editableTrades[0] : null;
                                                        return (
                                                          <Tooltip title={single ? 'Edit cost basis / realized P/L' : ''}>
                                                            <Chip
                                                              label={formatCurrency(strat.totalPL)}
                                                              color={getPLColor(strat.totalPL)}
                                                              size="small"
                                                              onClick={single ? (e) => { e.stopPropagation(); openPlEdit(single); } : undefined}
                                                              sx={single ? { cursor: 'pointer' } : undefined}
                                                            />
                                                          </Tooltip>
                                                        );
                                                      })()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Typography variant="body2" color={getPLColor(cumulatives[idx])}>
                                                        {formatCurrency(cumulatives[idx])}
                                                      </Typography>
                                                    </TableCell>
                                                  </TableRow>
                                                  {strat.isMultiLeg && isStratExpanded && (
                                                    <TableRow>
                                                      <TableCell colSpan={11} sx={{ py: 0.5, px: 1, backgroundColor: 'action.hover' }}>
                                                        <Table size="small">
                                                          <TableHead>
                                                            <TableRow>
                                                              <TableCell><strong>Symbol</strong></TableCell>
                                                              <TableCell><strong>Type</strong></TableCell>
                                                              <TableCell align="right"><strong>Qty</strong></TableCell>
                                                              <TableCell align="right"><strong>Price</strong></TableCell>
                                                              <TableCell align="right"><strong>Time</strong></TableCell>
                                                              <TableCell align="right"><strong>Realized P/L</strong></TableCell>
                                                            </TableRow>
                                                          </TableHead>
                                                          <TableBody>
                                                            {[...strat.trades].sort((a, b) => {
                                                              const ta = a.tradeTimestamp || a.tradeDate || '';
                                                              const tb = b.tradeTimestamp || b.tradeDate || '';
                                                              return ta.localeCompare(tb);
                                                            }).map((trade, idx) => {
                                                              const tradePL = getClosedTradeNetPL(trade);
                                                              const ts = trade.tradeTimestamp || '';
                                                              const timeStr = ts ? new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' }) : '';
                                                              return (
                                                                <TableRow key={idx}>
                                                                  <TableCell>{trade.originalSymbol || trade.symbol || '-'}</TableCell>
                                                                  <TableCell>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                      {trade.tradeType || trade.positionType || '-'}
                                                                    </Typography>
                                                                  </TableCell>
                                                                  <TableCell align="right">{trade.quantity || '-'}</TableCell>
                                                                  <TableCell align="right">{trade.price || '-'}</TableCell>
                                                                  <TableCell align="right">{timeStr || '-'}</TableCell>
                                                                  <TableCell align="right">
                                                                    <Tooltip title={trade.id ? 'Edit cost basis / realized P/L' : ''}>
                                                                      <Chip
                                                                        label={formatCurrency(tradePL)}
                                                                        color={getPLColor(tradePL)}
                                                                        size="small"
                                                                        onClick={trade.id ? (e) => { e.stopPropagation(); openPlEdit(trade); } : undefined}
                                                                        sx={trade.id ? { cursor: 'pointer' } : undefined}
                                                                      />
                                                                    </Tooltip>
                                                                  </TableCell>
                                                                </TableRow>
                                                              );
                                                            })}
                                                          </TableBody>
                                                        </Table>
                                                      </TableCell>
                                                    </TableRow>
                                                  )}
                                                </React.Fragment>
                                              );
                                              });
                                            })()}
                                          </TableBody>
                                        </Table>
                                      );
                                    })() : (
                                      <Typography variant="body2" color="textSecondary">
                                        No trades found for this week
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      }) : sortedSymbolData.map((row) => {
                        const closedPL = (row.totalPL || 0) - (row.totalDividendOnly || 0);
                        const isExpanded = expandedClosedSymbols.has(row.symbol);
                        // Open P/L split by instrument type — computed once above
                        const openPLOptions = getOpenPL(row.symbol, 'options');
                        const openPLEquity = getOpenPL(row.symbol, 'equity');

                        // Filter trades by strategy if filter is active
                        let filteredTrades = row.trades || [];

                        return (
                          <React.Fragment key={row.symbol}>
                            <TableRow
                              hover
                              sx={{ cursor: 'pointer', backgroundColor: isExpanded ? 'action.hover' : 'inherit' }}
                              onClick={() => toggleClosedSymbol(row.symbol)}
                            >
                              <TableCell component="th" scope="row">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  {row.symbol}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                {(() => {
                                  const editableTrades = (row.trades || []).filter(t => t && t.id);
                                  const single = editableTrades.length === 1 ? editableTrades[0] : null;
                                  return (
                                    <Tooltip title={single ? 'Edit cost basis / realized P/L' : ''}>
                                      <Chip
                                        label={formatCurrency(row.totalPL)}
                                        color={getPLColor(row.totalPL)}
                                        size="small"
                                        onClick={single ? (e) => { e.stopPropagation(); openPlEdit(single); } : undefined}
                                        sx={single ? { cursor: 'pointer' } : undefined}
                                      />
                                    </Tooltip>
                                  );
                                })()}
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="textSecondary">
                                  {formatCurrency(openPLOptions)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="textSecondary">
                                  {formatCurrency(openPLEquity)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(closedPL)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(row.totalDividendOnly || 0)}
                              </TableCell>
                              {closedPeriodColumns.map((col) => (
                                <TableCell key={`${row.symbol}-${col}`} align="right">
                                  {renderPeriodPLCell(row, col)}
                                </TableCell>
                              ))}
                            </TableRow>

                            {/* Expanded trades row — flat strategy table with sortable columns */}
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={6 + closedPeriodColumns.length} sx={{ py: 1, px: 2 }}>
                                  <Box sx={{ ml: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Underlying Trades ({filteredTrades.length})
                                    </Typography>
                                    {filteredTrades.length > 0 ? (() => {
                                      const allStrategies = groupTradesByStrategy(filteredTrades);
                                      const strategies = strategyFilter && strategyFilter !== 'all'
                                        ? allStrategies.filter(s => strategyMatchesFilter(s.strategyType))
                                        : allStrategies;
                                      const sorted = [...strategies].sort((a, b) => {
                                        const { field, direction } = strategySort;
                                        const dir = direction === 'asc' ? 1 : -1;
                                        let av, bv;
                                        switch (field) {
                                          case 'closeDate': av = a.closeDate || ''; bv = b.closeDate || ''; return dir * av.localeCompare(bv);
                                          case 'openDate': av = a.openDate || ''; bv = b.openDate || ''; return dir * av.localeCompare(bv);
                                          case 'symbol': av = a.displaySymbol || ''; bv = b.displaySymbol || ''; return dir * av.localeCompare(bv);
                                          case 'strategy': av = a.strategyType || ''; bv = b.strategyType || ''; return dir * av.localeCompare(bv);
                                          case 'quantity': av = a.quantity || 0; bv = b.quantity || 0; return dir * (av - bv);
                                          case 'price': av = a.price || 0; bv = b.price || 0; return dir * (av - bv);
                                          case 'dividend': av = a.totalDividend || 0; bv = b.totalDividend || 0; return dir * (av - bv);
                                          case 'realizedPL': av = a.totalPL || 0; bv = b.totalPL || 0; return dir * (av - bv);
                                          default: return 0;
                                        }
                                      });
                                      return (
                                        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.25, px: 0.5 } }}>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ width: 32, px: 0.25 }} />
                                              <TableCell sx={{ width: 28, px: 0.25 }} />
                                              <TableCell>
                                                <TableSortLabel active={strategySort.field === 'closeDate'} direction={strategySort.field === 'closeDate' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('closeDate')}>
                                                  <strong>Close Date</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell>
                                                <TableSortLabel active={strategySort.field === 'openDate'} direction={strategySort.field === 'openDate' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('openDate')}>
                                                  <strong>Open Date</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell>
                                                <TableSortLabel active={strategySort.field === 'symbol'} direction={strategySort.field === 'symbol' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('symbol')}>
                                                  <strong>Symbol</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell>
                                                <TableSortLabel active={strategySort.field === 'strategy'} direction={strategySort.field === 'strategy' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('strategy')}>
                                                  <strong>Strategy</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell align="right">
                                                <TableSortLabel active={strategySort.field === 'quantity'} direction={strategySort.field === 'quantity' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('quantity')}>
                                                  <strong>Qty</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell align="right">
                                                <TableSortLabel active={strategySort.field === 'price'} direction={strategySort.field === 'price' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('price')}>
                                                  <strong>Price</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell align="right">
                                                <TableSortLabel active={strategySort.field === 'dividend'} direction={strategySort.field === 'dividend' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('dividend')}>
                                                  <strong>Dividend</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell align="right">
                                                <TableSortLabel active={strategySort.field === 'realizedPL'} direction={strategySort.field === 'realizedPL' ? strategySort.direction : 'asc'} onClick={() => handleStrategySort('realizedPL')}>
                                                  <strong>Realized P/L</strong>
                                                </TableSortLabel>
                                              </TableCell>
                                              <TableCell align="right"><strong>Cumulative P/L</strong></TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(() => {
                                              const cumulatives = new Array(sorted.length);
                                              let cum = 0;
                                              for (let i = sorted.length - 1; i >= 0; i--) {
                                                cum += sorted[i].totalPL || 0;
                                                cumulatives[i] = cum;
                                              }
                                              return sorted.map((strat, idx) => {
                                              const stratKey = `${row.symbol}|${strat.key}`;
                                              const isStratExpanded = expandedNested.has(stratKey);
                                              return (
                                                <React.Fragment key={stratKey}>
                                                  <TableRow
                                                    hover={strat.isMultiLeg}
                                                    sx={{ cursor: strat.isMultiLeg ? 'pointer' : 'default' }}
                                                    onClick={() => strat.isMultiLeg && toggleNested(stratKey)}
                                                  >
                                                    <TableCell sx={{ width: 32, px: 0.25 }}>
                                                      <AccountNameWithLogo accountName={(strat.trades[0] || {}).accountName || (strat.trades[0] || {}).account} accountId={(strat.trades[0] || {}).accountId || (strat.trades[0] || {}).account_id} accounts={accounts} size={28} />
                                                    </TableCell>
                                                    <TableCell sx={{ width: 28, px: 0.25 }}>
                                                      {strat.isMultiLeg && (
                                                        isStratExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />
                                                      )}
                                                    </TableCell>
                                                    <TableCell>{strat.closeDate || '-'}</TableCell>
                                                    <TableCell>{strat.openDate || '-'}</TableCell>
                                                    <TableCell>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Box sx={{ fontWeight: 'bold' }}>{strat.displaySymbol}{strat.isMultiLeg && (strat.legStrikes || []).length > 0 && (
                                                          <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1em', color: 'text.secondary', ml: 0.5 }}>
                                                            {strat.legStrikes.join('/')}
                                                          </Box>
                                                        )}</Box>
                                                        <Tooltip title="Retrade">
                                                          <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleOpenRetrade(strat); }}
                                                            sx={{ p: 0.5 }}
                                                          >
                                                            <Box component="img" src="/assets/retrade.png" alt="Retrade" sx={{ width: 20, height: 20 }} />
                                                          </IconButton>
                                                        </Tooltip>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                        <Chip label={strat.strategyType} size="small" color="primary" variant="contained" />
                                                        {getCloseReasons(strat).map(r => (
                                                          <Tooltip key={r.label} title={r.title}>
                                                            <Chip label={r.label} size="small" color={r.color} variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                                                          </Tooltip>
                                                        ))}
                                                        {getVisibleTags(strat).map(t => (
                                                          <Chip key={t} label={t} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                                                        ))}
                                                        <Tooltip title="Edit tags">
                                                          <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); openTagEditDialog(strat); }}>
                                                            <LabelOutlinedIcon sx={{ fontSize: 14 }} />
                                                          </IconButton>
                                                        </Tooltip>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell align="right">{strat.quantity}</TableCell>
                                                    <TableCell align="right">{strat.price !== 0 ? strat.price.toFixed(2) : '-'}</TableCell>
                                                    <TableCell align="right">{strat.totalDividend !== 0 ? formatCurrency(strat.totalDividend) : '-'}</TableCell>
                                                    <TableCell align="right">
                                                      {(() => {
                                                        const editableTrades = (strat.trades || []).filter(t => t && t.id);
                                                        const single = !strat.isMultiLeg && editableTrades.length === 1 ? editableTrades[0] : null;
                                                        return (
                                                          <Tooltip title={single ? 'Edit cost basis / realized P/L' : ''}>
                                                            <Chip
                                                              label={formatCurrency(strat.totalPL)}
                                                              color={getPLColor(strat.totalPL)}
                                                              size="small"
                                                              onClick={single ? (e) => { e.stopPropagation(); openPlEdit(single); } : undefined}
                                                              sx={single ? { cursor: 'pointer' } : undefined}
                                                            />
                                                          </Tooltip>
                                                        );
                                                      })()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Typography variant="body2" color={getPLColor(cumulatives[idx])}>
                                                        {formatCurrency(cumulatives[idx])}
                                                      </Typography>
                                                    </TableCell>
                                                  </TableRow>
                                                  {strat.isMultiLeg && isStratExpanded && (
                                                    <TableRow>
                                                      <TableCell colSpan={11} sx={{ py: 0.5, px: 1, backgroundColor: 'action.hover' }}>
                                                        <Table size="small">
                                                          <TableHead>
                                                            <TableRow>
                                                              <TableCell><strong>Symbol</strong></TableCell>
                                                              <TableCell><strong>Type</strong></TableCell>
                                                              <TableCell align="right"><strong>Qty</strong></TableCell>
                                                              <TableCell align="right"><strong>Price</strong></TableCell>
                                                              <TableCell align="right"><strong>Time</strong></TableCell>
                                                              <TableCell align="right"><strong>Realized P/L</strong></TableCell>
                                                            </TableRow>
                                                          </TableHead>
                                                          <TableBody>
                                                            {[...strat.trades].sort((a, b) => {
                                                              const ta = a.tradeTimestamp || a.tradeDate || '';
                                                              const tb = b.tradeTimestamp || b.tradeDate || '';
                                                              return ta.localeCompare(tb);
                                                            }).map((trade, idx) => {
                                                              const tradePL = getClosedTradeNetPL(trade);
                                                              const ts = trade.tradeTimestamp || '';
                                                              const timeStr = ts ? new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' }) : '';
                                                              return (
                                                                <TableRow key={idx}>
                                                                  <TableCell>{trade.originalSymbol || trade.symbol || '-'}</TableCell>
                                                                  <TableCell>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                      {trade.tradeType || trade.positionType || '-'}
                                                                    </Typography>
                                                                  </TableCell>
                                                                  <TableCell align="right">{trade.quantity || '-'}</TableCell>
                                                                  <TableCell align="right">{trade.price || '-'}</TableCell>
                                                                  <TableCell align="right">{timeStr || '-'}</TableCell>
                                                                  <TableCell align="right">
                                                                    <Tooltip title={trade.id ? 'Edit cost basis / realized P/L' : ''}>
                                                                      <Chip
                                                                        label={formatCurrency(tradePL)}
                                                                        color={getPLColor(tradePL)}
                                                                        size="small"
                                                                        onClick={trade.id ? (e) => { e.stopPropagation(); openPlEdit(trade); } : undefined}
                                                                        sx={trade.id ? { cursor: 'pointer' } : undefined}
                                                                      />
                                                                    </Tooltip>
                                                                  </TableCell>
                                                                </TableRow>
                                                              );
                                                            })}
                                                          </TableBody>
                                                        </Table>
                                                      </TableCell>
                                                    </TableRow>
                                                  )}
                                                </React.Fragment>
                                              );
                                              });
                                            })()}
                                          </TableBody>
                                        </Table>
                                      );
                                    })() : (
                                      <Typography variant="body2" color="textSecondary">
                                        No trades found for this symbol.
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {symbolData.length === 0 && (
                  <Box sx={{ textAlign: 'center', mt: 4, mb: 2 }}>
                    <Typography variant="body1" color="textSecondary">
                      No trading data found for the selected period.
                    </Typography>
                  </Box>
                )}

                {groupByMode === 'calendar' && calendarViewMode === 'calendar' && (
                  <Box sx={{ p: 2 }}>
                    <MonthPLCalendar
                      data={filteredSymbolData}
                      formatCurrency={formatCurrency}
                      getPLColor={getPLColor}
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
        </Box>
      )}

      {/* Roll/Close Order Dialog */}
      <Dialog
        open={rollCloseDialog.open}
        onClose={handleCloseRollCloseDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={isMobile ? { py: 1 } : undefined}>
          {rollCloseDialog.mode === 'roll' ? 'Roll Position' : rollCloseDialog.mode === 'add' ? 'Add to Position' : 'Close Position'}
          {rollCloseDialog.underlying && (
            <Typography variant="body2" color="textSecondary" component="span" sx={{ ml: 1 }}>
              {rollCloseDialog.underlying} ({rollCloseDialog.accountName})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={isMobile ? { px: 1, py: 1 } : undefined}>
          {rollCloseDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rollCloseDialog.error}
              {rollCloseDialog.result && (
                <Box component="span" sx={{ ml: 1 }}>
                  (Order ID: {rollCloseDialog.result.orderId})
                </Box>
              )}
            </Alert>
          )}
          {rollCloseDialog.isModifyMode && !rollCloseDialog.error && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Modifying working order — Order ID: {rollCloseDialog.orderId || 'N/A'}
              {rollCloseDialog.orderStatus && (
                <Box component="span" sx={{ ml: 2, fontWeight: 'bold' }}>
                  Status: {rollCloseDialog.orderStatus}
                </Box>
              )}
            </Alert>
          )}
          {rollCloseDialog.result && rollCloseDialog.result.success && !rollCloseDialog.error && (
            <Alert severity={
              WORKING_ORDER_STATUSES.includes(String(rollCloseDialog.orderStatus || '').toUpperCase()) ? 'info' :
              rollCloseDialog.orderStatus === 'FILLED' ? 'success' :
              rollCloseDialog.orderStatus === 'CANCELLED' || rollCloseDialog.orderStatus === 'REJECTED' ? 'error' :
              'success'
            } sx={{ mb: 2 }}>
              {rollCloseDialog.result.message || 'Order placed successfully'}! Order ID: {rollCloseDialog.result.orderId || 'N/A'}
              {rollCloseDialog.result.orderType && (
                <Box component="span" sx={{ ml: 1 }}>
                  ({rollCloseDialog.result.orderType}
                  {rollCloseDialog.result.price != null ? ` @ $${rollCloseDialog.result.price}` : ''})
                </Box>
              )}
              {rollCloseDialog.orderStatus && (
                <Box component="span" sx={{ ml: 2, fontWeight: 'bold' }}>
                  Status: {rollCloseDialog.orderStatus}
                </Box>
              )}
            </Alert>
          )}

          {/* Underlying */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={4} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Underlying"
                value={rollCloseDialog.underlying}
                onChange={(e) => setRollCloseDialog(prev => ({ ...prev, underlying: e.target.value.toUpperCase() }))}
              />
            </Grid>
            <Grid item xs={8} sm={9} sx={{ display: 'flex', alignItems: 'center' }}>
              {(() => {
                const u = rollCloseDialog.underlying || '';
                const uq = rollCloseQuotes[u.replace(/\s+/g, '')] || rollCloseQuotes[u.replace(/\s+/g, '.')];
                if (!uq || (!uq.bid && !uq.ask && !uq.mid && !uq.last)) return null;
                const fmt = (v) => (v ? Number(v).toFixed(2) : '—');
                return uq.bid || uq.ask || uq.mid ? (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    Bid <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{fmt(uq.bid)}</Box>
                    {' '}· Ask <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{fmt(uq.ask)}</Box>
                    {' '}· Mid <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{fmt(uq.mid)}</Box>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    Last <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{fmt(uq.last)}</Box>
                  </Typography>
                );
              })()}
            </Grid>
          </Grid>

          {/* Legs — compact cards on mobile, table on desktop */}
          {isMobile ? (
          <Box sx={{ mb: 2 }}>
            {rollCloseDialog.legs.map((leg, idx) => {
              const isEquityLeg = leg.assetType === 'EQUITY';
              const sym = isEquityLeg
                ? rollCloseDialog.underlying
                : (leg.isFuturesOption && leg.quoteSymbol
                    ? leg.quoteSymbol
                    : ((leg.isClose && leg.contractSymbol) ? leg.contractSymbol
                      : (rollCloseDialog.underlying ? buildOCCSymbol(rollCloseDialog.underlying, leg.expiration, leg.putCall, parseFloat(leg.strike)) : '')));
              const symKey = rollCloseLegQuoteKey(leg, rollCloseDialog.underlying).replace(/\s+/g, '');
              const q = rollCloseQuotes[symKey];
              return (
                <Box key={idx} sx={{
                  mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1,
                  backgroundColor: leg.isNew ? 'rgba(25, 118, 210, 0.04)' : 'inherit',
                  opacity: leg.enabled === false ? 0.4 : 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Checkbox
                      checked={leg.enabled !== false}
                      onChange={(e) => {
                        const newLegs = [...rollCloseDialog.legs];
                        newLegs[idx] = { ...newLegs[idx], enabled: e.target.checked };
                        setRollCloseDialog(prev => ({ ...prev, legs: newLegs }));
                      }}
                      size="small"
                      sx={{ p: 0.25 }}
                    />
                    <Typography variant="caption" fontWeight={leg.isNew ? 'bold' : 'normal'} color={leg.isNew ? 'primary' : 'text.secondary'} sx={{ minWidth: 30 }}>
                      {leg.isClose ? 'Close' : 'Open'}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {isEquityLeg ? (leg.symbol || rollCloseDialog.underlying) : sym}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {q?.bid?.toFixed(2) || '-'}/{q?.ask?.toFixed(2) || '-'}/<b>{q?.mid?.toFixed(2) || '-'}</b>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {isEquityLeg ? (
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1, alignSelf: 'center' }}>Equity</Typography>
                    ) : (
                      <>
                        <TextField
                          size="small"
                          type="date"
                          value={leg.expiration}
                          onChange={(e) => patchRollCloseLeg(idx, { expiration: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          sx={{ flex: 1.3 }}
                        />
                        <TextField
                          size="small"
                          value={leg.strike}
                          onChange={(e) => patchRollCloseLeg(idx, { strike: e.target.value })}
                          sx={{ flex: 0.8 }}
                        />
                        <FormControl size="small" sx={{ flex: 0.7 }}>
                          <Select
                            value={leg.putCall}
                            onChange={(e) => patchRollCloseLeg(idx, { putCall: e.target.value })}
                          >
                            <MenuItem value="C">Call</MenuItem>
                            <MenuItem value="P">Put</MenuItem>
                          </Select>
                        </FormControl>
                      </>
                    )}
                    <FormControl size="small" sx={{ flex: 1.2 }}>
                      <Select
                        value={leg.instruction || ''}
                        displayEmpty
                        renderValue={(val) => {
                          const labels = {
                            BUY_TO_OPEN: 'Buy to Open',
                            SELL_TO_OPEN: 'Sell to Open',
                            BUY_TO_CLOSE: 'Buy to Close',
                            SELL_TO_CLOSE: 'Sell to Close',
                            BUY: 'Buy',
                            SELL: 'Sell',
                            BUY_TO_COVER: 'Buy to Cover',
                            SELL_SHORT: 'Sell Short',
                          };
                          return labels[val] || val || '';
                        }}
                        onChange={(e) => {
                          const newLegs = [...rollCloseDialog.legs];
                          newLegs[idx] = { ...newLegs[idx], instruction: e.target.value };
                          setRollCloseDialog(prev => ({ ...prev, legs: newLegs }));
                        }}
                      >
                        {isEquityLeg ? (
                          <>
                            <MenuItem value="BUY">Buy</MenuItem>
                            <MenuItem value="SELL">Sell</MenuItem>
                            <MenuItem value="SELL_SHORT">Sell Short</MenuItem>
                            <MenuItem value="BUY_TO_COVER">Buy to Cover</MenuItem>
                          </>
                        ) : leg.isClose ? (
                          <>
                            <MenuItem value="SELL_TO_CLOSE">Sell to Close</MenuItem>
                            <MenuItem value="BUY_TO_CLOSE">Buy to Close</MenuItem>
                          </>
                        ) : (
                          <>
                            <MenuItem value="BUY_TO_OPEN">Buy to Open</MenuItem>
                            <MenuItem value="SELL_TO_OPEN">Sell to Open</MenuItem>
                          </>
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              );
            })}
            {/* Net Mid line */}
            {(() => {
              const builtLegs = buildRollCloseLegSymbols();
              const netMid = calculateRollCloseNetMid(builtLegs, rollCloseQuotes);
              return (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.75, backgroundColor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2">Net Mid (auto-filled price)</Typography>
                  <Typography sx={{
                    fontWeight: 'bold',
                    color: netMid !== null ? (netMid < 0 ? '#4ade80' : '#f87171') : 'text.primary'
                  }}>
                    {netMid !== null ? netMid.toFixed(2) : '—'}
                  </Typography>
                </Box>
              );
            })()}
          </Box>
          ) : (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"><strong>Inc</strong></TableCell>
                <TableCell><strong>Leg</strong></TableCell>
                <TableCell><strong>Symbol</strong></TableCell>
                <TableCell><strong>Expiration</strong></TableCell>
                <TableCell><strong>Strike</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Side</strong></TableCell>
                <TableCell align="right"><strong>Bid</strong></TableCell>
                <TableCell align="right"><strong>Ask</strong></TableCell>
                <TableCell align="right"><strong>Mid</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rollCloseDialog.legs.map((leg, idx) => {
                const isEquityLeg = leg.assetType === 'EQUITY';
                const sym = isEquityLeg
                  ? rollCloseDialog.underlying
                  : (leg.isFuturesOption && leg.quoteSymbol
                      ? leg.quoteSymbol
                      : ((leg.isClose && leg.contractSymbol) ? leg.contractSymbol
                        : (rollCloseDialog.underlying ? buildOCCSymbol(rollCloseDialog.underlying, leg.expiration, leg.putCall, parseFloat(leg.strike)) : '')));
                const symKey = rollCloseLegQuoteKey(leg, rollCloseDialog.underlying).replace(/\s+/g, '');
                const q = rollCloseQuotes[symKey];
                return (
                  <TableRow key={idx} sx={{ backgroundColor: leg.isNew ? 'rgba(25, 118, 210, 0.04)' : 'inherit', opacity: leg.enabled === false ? 0.4 : 1 }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={leg.enabled !== false}
                        onChange={(e) => {
                          const newLegs = [...rollCloseDialog.legs];
                          newLegs[idx] = { ...newLegs[idx], enabled: e.target.checked };
                          setRollCloseDialog(prev => ({ ...prev, legs: newLegs }));
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={leg.isNew ? 'bold' : 'normal'} color={leg.isNew ? 'primary' : 'text.primary'}>
                        {leg.isClose ? 'Close' : 'Open'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {isEquityLeg ? (leg.symbol || rollCloseDialog.underlying) : sym}
                      </Typography>
                    </TableCell>
                    {isEquityLeg ? (
                      <>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="caption" color="text.secondary">Equity</Typography>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            value={leg.expiration}
                            onChange={(e) => patchRollCloseLeg(idx, { expiration: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 130 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={leg.strike}
                            onChange={(e) => patchRollCloseLeg(idx, { strike: e.target.value })}
                            sx={{ minWidth: 70 }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={leg.putCall}
                              onChange={(e) => patchRollCloseLeg(idx, { putCall: e.target.value })}
                            >
                              <MenuItem value="C">Call</MenuItem>
                              <MenuItem value="P">Put</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ minWidth: 130 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={leg.instruction || ''}
                          displayEmpty
                          renderValue={(val) => {
                            const labels = {
                              BUY_TO_OPEN: 'Buy to Open',
                              SELL_TO_OPEN: 'Sell to Open',
                              BUY_TO_CLOSE: 'Buy to Close',
                              SELL_TO_CLOSE: 'Sell to Close',
                              BUY: 'Buy',
                              SELL: 'Sell',
                              BUY_TO_COVER: 'Buy to Cover',
                              SELL_SHORT: 'Sell Short',
                            };
                            return labels[val] || val || '';
                          }}
                          onChange={(e) => {
                            const newLegs = [...rollCloseDialog.legs];
                            newLegs[idx] = { ...newLegs[idx], instruction: e.target.value };
                            setRollCloseDialog(prev => ({ ...prev, legs: newLegs }));
                          }}
                        >
                          {isEquityLeg ? (
                            <>
                              <MenuItem value="BUY">Buy</MenuItem>
                              <MenuItem value="SELL">Sell</MenuItem>
                              <MenuItem value="SELL_SHORT">Sell Short</MenuItem>
                              <MenuItem value="BUY_TO_COVER">Buy to Cover</MenuItem>
                            </>
                          ) : leg.isClose ? (
                            <>
                              <MenuItem value="SELL_TO_CLOSE">Sell to Close</MenuItem>
                              <MenuItem value="BUY_TO_CLOSE">Buy to Close</MenuItem>
                            </>
                          ) : (
                            <>
                              <MenuItem value="BUY_TO_OPEN">Buy to Open</MenuItem>
                              <MenuItem value="SELL_TO_OPEN">Sell to Open</MenuItem>
                            </>
                          )}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{q?.bid?.toFixed(2) || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{q?.ask?.toFixed(2) || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>{q?.mid?.toFixed(2) || '-'}</TableCell>
                  </TableRow>
                );
              })}
              {/* Net Mid row */}
              {(() => {
                const builtLegs = buildRollCloseLegSymbols();
                const netMid = calculateRollCloseNetMid(builtLegs, rollCloseQuotes);
                return (
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell colSpan={7}>
                      <Typography variant="subtitle2">Net Mid (auto-filled price)</Typography>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell align="right" sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: netMid !== null ? (netMid < 0 ? '#4ade80' : '#f87171') : 'text.primary'
                    }}>
                      {netMid !== null ? netMid.toFixed(2) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
          )}

          <Button
            size="small"
            variant="outlined"
            onClick={handleAddRollCloseLeg}
            disabled={rollCloseDialog.loading}
            sx={{ mb: 2, textTransform: 'none' }}
          >
            + Add Leg
          </Button>

          {/* Order Parameters */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1.5 }}>Order Parameters</Typography>
          <Grid container spacing={isMobile ? 1 : 2}>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Quantity"
                value={rollCloseDialog.quantity}
                onChange={(e) => setRollCloseDialog(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Order Type</InputLabel>
                <Select
                  value={rollCloseDialog.orderType}
                  label="Order Type"
                  onChange={(e) => setRollCloseDialog(prev => ({ ...prev, orderType: e.target.value }))}
                >
                  <MenuItem value="LIMIT">Limit</MenuItem>
                  <MenuItem value="MARKET">Market</MenuItem>
                  {rollCloseDialog.legs.length > 1 && (
                    <>
                      <MenuItem value="NET_DEBIT">Net Debit</MenuItem>
                      <MenuItem value="NET_CREDIT">Net Credit</MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Time in Force</InputLabel>
                <Select
                  value={rollCloseDialog.duration || 'DAY'}
                  label="Time in Force"
                  onChange={(e) => setRollCloseDialog(prev => ({ ...prev, duration: e.target.value }))}
                >
                  <MenuItem value="DAY">Day</MenuItem>
                  <MenuItem value="GOOD_TILL_CANCEL">GTC</MenuItem>
                  <MenuItem value="GOOD_TILL_DATE">GTD</MenuItem>
                  <MenuItem value="FILL_OR_KILL">Fill or Kill</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(rollCloseDialog.orderType === 'LIMIT' || rollCloseDialog.orderType === 'NET_DEBIT' || rollCloseDialog.orderType === 'NET_CREDIT') && (
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={rollCloseDialog.legs.length > 1 ? 'Net Price' : 'Limit Price'}
                  value={rollCloseDialog.price}
                  onChange={(e) => {
                    setRollCloseDialog(prev => ({ ...prev, price: e.target.value }));
                    setRollCloseAutoPrice(false);
                  }}
                  placeholder="e.g. 1.50"
                  helperText={rollCloseAutoPrice ? 'Auto from mid' : 'Manual'}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: isDark ? '#ffffff' : 'inherit'
                    }
                  }}
                  slotProps={{
                    htmlInput: { step: /^(\$?SPXW?)$/.test(String(rollCloseDialog.underlying || '').toUpperCase()) ? 0.05 : 0.01 },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Tooltip title={rollCloseAutoPrice ? 'Auto-update from mid. Click to lock manual price.' : 'Manual price locked. Click to auto-update from mid.'}>
                            <IconButton
                              size="small"
                              sx={{ p: 0.5 }}
                              onClick={() => setRollCloseAutoPrice(!rollCloseAutoPrice)}
                            >
                              {rollCloseAutoPrice ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {(() => {
                              const step = /^(\$?SPXW?)$/.test(String(rollCloseDialog.underlying || '').toUpperCase()) ? 0.05 : 0.01;
                              const bump = (dir) => {
                                const val = parseFloat(rollCloseDialog.price) || 0;
                                const next = Math.round(((val / step) + dir) * step * 100) / 100;
                                setRollCloseDialog(prev => ({ ...prev, price: String(next) }));
                                setRollCloseAutoPrice(false);
                              };
                              return (
                                <>
                                  <IconButton size="small" sx={{ p: 0.25 }} onClick={() => bump(1)}>
                                    <ArrowDropUpIcon />
                                  </IconButton>
                                  <IconButton size="small" sx={{ p: 0.25, mt: -1 }} onClick={() => bump(-1)}>
                                    <ArrowDropDownIcon />
                                  </IconButton>
                                </>
                              );
                            })()}
                          </Box>
                        </InputAdornment>
                      ),
                    }
                  }}
                />
              </Grid>
            )}
          </Grid>

          {/* Multi-Target Exit Orders */}
          {rollCloseDialog.mode === 'close' && !rollCloseDialog.orderId && (
            <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'primary.main', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={rollCloseDialog.multiTargetEnabled || false}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setRollCloseDialog(prev => {
                          const totalQty = prev.quantity || 1;
                          const base = Math.floor(totalQty / 4);
                          const remainder = totalQty % 4;
                          return {
                            ...prev,
                            multiTargetEnabled: enabled,
                            exitTargets: (prev.exitTargets || []).map((t, i) => ({
                              ...t,
                              qty: enabled ? String(base + (i < remainder ? 1 : 0)) : '',
                              enabled: enabled ? (base + (i < remainder ? 1 : 0)) > 0 : true,
                            })),
                          };
                        });
                      }}
                      size="small"
                      disabled={rollCloseDialog.loading}
                    />
                  }
                  label="Multiple Exit Targets — place separate limit orders at different prices"
                />
              </Box>
              {rollCloseDialog.multiTargetEnabled && (
                <Box sx={{ mt: 1.5, p: 1, border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'action.hover' }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                    Exit Targets (each places a separate {rollCloseDialog.duration === 'GOOD_TILL_CANCEL' ? 'GTC' : rollCloseDialog.duration || 'DAY'} order)
                  </Typography>
                  {(rollCloseDialog.exitTargets || []).map((target, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Switch
                        checked={target.enabled}
                        onChange={(e) => setRollCloseDialog(prev => ({
                          ...prev,
                          exitTargets: (prev.exitTargets || []).map((t, i) => i === idx ? { ...t, enabled: e.target.checked } : t),
                        }))}
                        size="small"
                        disabled={rollCloseDialog.loading}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Qty"
                        value={target.qty}
                        onChange={(e) => setRollCloseDialog(prev => ({
                          ...prev,
                          exitTargets: (prev.exitTargets || []).map((t, i) => i === idx ? { ...t, qty: e.target.value } : t),
                        }))}
                        disabled={rollCloseDialog.loading || !target.enabled}
                        sx={{ width: 80 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={rollCloseDialog.legs.length > 1 ? 'Net Price' : 'Limit Price'}
                        value={target.price}
                        onChange={(e) => setRollCloseDialog(prev => ({
                          ...prev,
                          exitTargets: (prev.exitTargets || []).map((t, i) => i === idx ? { ...t, price: e.target.value } : t),
                        }))}
                        disabled={rollCloseDialog.loading || !target.enabled}
                        sx={{ width: 120 }}
                        placeholder="e.g. 1.50"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {rollCloseDialog.orderType === 'MARKET' ? 'MKT' : (rollCloseDialog.duration || 'DAY')}
                      </Typography>
                    </Box>
                  ))}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Total: {(rollCloseDialog.exitTargets || []).filter(t => t.enabled && t.qty).reduce((sum, t) => sum + (parseInt(t.qty) || 0), 0)} / {rollCloseDialog.quantity} contracts
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {wideWarn && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {`Wide spread on ${wideWarn.sym}: ${(wideWarn.pct * 100).toFixed(0)}% bid/ask width — fills can land far from mid. Use a LIMIT price at mid or better, or wait for tighter quotes.`}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Checkbox size="small" checked={rollCloseWideAck} onChange={e => setRollCloseWideAck(e.target.checked)} sx={{ p: 0.25 }} />
                <Typography variant="caption">I understand the wide spread — proceed anyway</Typography>
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRollCloseDialog} variant="contained" color="inherit" disabled={rollCloseDialog.loading}>
            Close
          </Button>
          {rollCloseDialog.orderId && WORKING_ORDER_STATUSES.includes(String(rollCloseDialog.orderStatus || '').toUpperCase()) && (
            <>
              <Button
                onClick={handleRollCloseCancelOrder}
                variant="contained"
                color="error"
                disabled={rollCloseDialog.loading}
                startIcon={rollCloseDialog.loading ? <CircularProgress size={16} /> : null}
              >
                {rollCloseDialog.loading ? 'Cancelling...' : 'Cancel Order'}
              </Button>
              <Button
                onClick={handleRollCloseModifyOrder}
                variant="contained"
                color="primary"
                disabled={rollCloseDialog.loading || (wideWarn && !rollCloseWideAck)}
                startIcon={rollCloseDialog.loading ? <CircularProgress size={16} /> : null}
              >
                {rollCloseDialog.loading ? 'Modifying...' : 'Modify Order'}
              </Button>
            </>
          )}
          {/* Always allow submitting — edits can be re-placed without closing
              the dialog. When a working order is tracked, this places an
              additional order (use Modify Order to adjust it instead). */}
          <Button
            onClick={handleRollClosePlaceOrder}
            variant="contained"
            color="primary"
            disabled={rollCloseDialog.loading || (wideWarn && !rollCloseWideAck)}
            startIcon={rollCloseDialog.loading ? <CircularProgress size={16} /> : null}
          >
            {rollCloseDialog.loading ? 'Placing...' : rollCloseDialog.orderId ? 'Place New Order' : `Place ${rollCloseDialog.mode === 'roll' ? 'Roll' : rollCloseDialog.mode === 'add' ? 'Add' : 'Close'} Order`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Per-row actions menu (Roll / Close / Add / Alert, or Modify/Cancel when a working order exists) */}
      <Menu
        anchorEl={rowActionsMenu?.anchor}
        open={!!rowActionsMenu}
        onClose={() => setRowActionsMenu(null)}
      >
        {rowActionsMenu && (() => {
          const pos = rowActionsMenu.position;
          const wo = getWorkingOrderForPosition(pos);
          if (wo) {
            return [
              <MenuItem key="modify" dense onClick={() => { setRowActionsMenu(null); handleModifyOrderFromRow(pos, 'close'); }}>
                Modify order
              </MenuItem>,
              <MenuItem key="cancel" dense sx={{ color: 'error.main' }} onClick={() => {
                setRowActionsMenu(null);
                setCancelOrderConfirm({ open: true, orderId: wo.orderId, accountId: wo.accountId, positionLabel: pos.symbol || pos.underlying });
              }}>
                Cancel order
              </MenuItem>,
            ];
          }
          const alertActive = hasActivePriceAlert(pos);
          return [
            <MenuItem key="roll" dense sx={{ fontWeight: 'bold' }} onClick={() => { setRowActionsMenu(null); handleOpenRollCloseDialog(pos, 'roll'); }}>Roll</MenuItem>,
            <MenuItem key="close" dense sx={{ color: 'error.main', fontWeight: 'bold' }} onClick={() => { setRowActionsMenu(null); handleOpenRollCloseDialog(pos, 'close'); }}>Close</MenuItem>,
            <MenuItem key="add" dense sx={{ color: 'success.main', fontWeight: 'bold' }} onClick={() => { setRowActionsMenu(null); handleOpenRollCloseDialog(pos, 'add'); }}>Add</MenuItem>,
            <MenuItem key="alert" dense sx={{ fontWeight: 'bold' }} onClick={() => { setRowActionsMenu(null); handleOpenPriceAlertDialog(pos); }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {alertActive
                  ? <NotificationsActiveIcon fontSize="small" color="warning" />
                  : <NotificationsIcon fontSize="small" color="action" />}
                Price alert
              </Box>
            </MenuItem>,
          ];
        })()}
      </Menu>

      {/* Cancel Order Confirmation Dialog */}
      <Dialog
        open={cancelOrderConfirm.open}
        onClose={() => setCancelOrderConfirm(prev => ({ ...prev, open: false, error: null }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel Order?</DialogTitle>
        <DialogContent>
          {cancelOrderConfirm.error ? (
            <Alert severity="error" sx={{ mt: 1 }}>{cancelOrderConfirm.error}</Alert>
          ) : (
            <Typography>
              Are you sure you want to cancel the working order for{' '}
              <strong>{cancelOrderConfirm.positionLabel}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOrderConfirm({ open: false, orderId: null, accountId: null, positionLabel: '' })} variant="contained" color="inherit">
            {cancelOrderConfirm.error ? 'Close' : 'No, Keep Order'}
          </Button>
          {!cancelOrderConfirm.error && (
            <Button
              onClick={handleCancelOrderFromRow}
              variant="contained"
              color="error"
            >
              Yes, Cancel Order
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <TradeDialog
        open={retradeOpen}
        alert={retradeAlert}
        onClose={() => { setRetradeOpen(false); setRetradeAlert(null); }}
        showInputSection={true}
      />
      {/* Closed-position cost basis / realized P/L editor */}
      <Dialog
        open={!!plEdit}
        onClose={() => !plEdit?.saving && setPlEdit(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Edit Closing Record
        </DialogTitle>
        <DialogContent>
          {plEdit && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                {plEdit.trade.originalSymbol || plEdit.trade.symbol} — {plEdit.trade.tradeType || plEdit.trade.positionType || ''} {plEdit.trade.quantity} @ {plEdit.trade.price ?? '-'}
              </Typography>
              <TextField
                label="Cost Basis"
                type="number"
                value={plEdit.cost}
                onChange={(e) => {
                  const v = e.target.value;
                  setPlEdit(p => {
                    const cost = parseFloat(v);
                    const proceeds = parseFloat(p.proceeds);
                    const realized = (!isNaN(cost) && !isNaN(proceeds)) ? (proceeds - cost).toFixed(2) : p.realizedPL;
                    return { ...p, cost: v, realizedPL: realized };
                  });
                }}
                size="small"
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
              <TextField
                label="Proceeds"
                type="number"
                value={plEdit.proceeds}
                onChange={(e) => {
                  const v = e.target.value;
                  setPlEdit(p => {
                    const cost = parseFloat(p.cost);
                    const proceeds = parseFloat(v);
                    const realized = (!isNaN(cost) && !isNaN(proceeds)) ? (proceeds - cost).toFixed(2) : p.realizedPL;
                    return { ...p, proceeds: v, realizedPL: realized };
                  });
                }}
                size="small"
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
              <TextField
                label="Realized P/L"
                type="number"
                value={plEdit.realizedPL}
                onChange={(e) => setPlEdit(p => ({ ...p, realizedPL: e.target.value }))}
                size="small"
                helperText="Auto-fills as proceeds − cost; override if the broker reports a different figure"
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
              <TextField
                label="Closing Date"
                type="date"
                value={plEdit.closingDate}
                onChange={(e) => setPlEdit(p => ({ ...p, closingDate: e.target.value }))}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              {plEdit.error && <Alert severity="error">{plEdit.error}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlEdit(null)} variant="contained" color="inherit" disabled={plEdit?.saving}>
            Cancel
          </Button>
          <Button onClick={savePlEdit} variant="contained" color="primary" disabled={plEdit?.saving}>
            {plEdit?.saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag editor dialog — used by closed-position strategy rows */}
      <Dialog
        open={!!tagEditDialog}
        onClose={() => !tagEditDialog?.saving && setTagEditDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Tags</DialogTitle>
        <DialogContent>
          {tagEditDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {tagEditDialog.entity?.displaySymbol || tagEditDialog.entity?.symbol || ''} {tagEditDialog.entity?.strategyType || ''}
              </Typography>
              <Autocomplete
                multiple
                freeSolo
                autoSelect
                autoHighlight
                size="small"
                options={allTags}
                value={tagEditDialog.draft}
                onChange={(e, newValue) => setTagEditDialog(d => ({ ...d, draft: [...new Set(newValue.map(v => String(v).trim()).filter(Boolean))] }))}
                renderTags={(value, getTagProps) => value.map((t, i) => {
                  const { key, ...tagProps } = getTagProps({ index: i });
                  return <Chip key={key} {...tagProps} label={t} size="small" />;
                })}
                renderInput={(params) => <TextField {...params} label="Tags" placeholder="Select or type a new tag" />}
              />
              {tagEditDialog.error && <Alert severity="error">{tagEditDialog.error}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagEditDialog(null)} variant="contained" color="inherit" disabled={tagEditDialog?.saving}>
            Cancel
          </Button>
          <Button onClick={saveTagEditDialog} variant="contained" color="primary" disabled={tagEditDialog?.saving}>
            {tagEditDialog?.saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockSummary;
